/**
 * Weight Freeze (v2) — FROZEN DUMMY DATA for Nobero. Not production data.
 *
 * REAL (measured, from window.WF_DATA in the offline mockup):
 *   SKUS: sku, product, ships, hist (the actual charged-weight histogram as
 *   [kg, count] pairs), mpw and spread (recomputed from hist per the port's
 *   own formulas — the source's own mpw/spread fields disagree with the
 *   tie-broken-to-lower-weight rule on a handful of rows, so they are not
 *   trusted verbatim), and dq (genuinely bad/ambiguous product codes found in
 *   the source: either the "(sku - CODE)" marker present with the code itself
 *   truncated to nothing, or two products colliding on the identical
 *   truncated code — both real string-level defects, not invented ones).
 *   COMBOS: key, items (with real quantities), ships, hist, mpw, spread
 *   (recomputed for the same reason as above). key is NEVER rewritten: ten
 *   of the sixty combo keys embed exactly one "(sku - CODE)" marker even
 *   though their (upstream, naive "+"-split) items array lists two or
 *   three entries — the extra entries are artifacts of that split hitting a
 *   literal "+" inside one product's own name (e.g. "Laugh + Live + Love &
 *   Eat Bamboo...") or a second fragment that never carried a code at all
 *   (e.g. "... + Ocean Beach"), not a second real product. For exactly those
 *   ten, items is corrected down to the single entry whose own embedded
 *   code matches the key's embedded code; the key's raw, un-cleaned display
 *   text is left exactly as extracted from the source.
 *   MERCHANT.months (the per-month shipment series) and MERCHANT.totals
 *   (skus/single/multi/awbs) are the source document's own aggregate figures.
 *   MERCHANT.months is additionally sorted into chronological order here —
 *   the source array's own order is Apr, Feb, Jan, Jun, May (out of order);
 *   every value inside each month entry is untouched real data, only the
 *   array's order was fixed.
 *   Two real-data quirks intentionally left alone: this data bills in 0.75 kg
 *   steps, not 0.5, and several SKU labels are truncated mid-code in the
 *   source. Junk labels (#N/A, '0', empty, pure-alpha) are skipped entirely,
 *   matching scripts/gen-nobero-disputes.js exactly.
 *
 * NOT DERIVED — motherOf/childOf are null on every SKU:
 *   Several source labels are truncated mid-code, so two (or more) entirely
 *   distinct products can collide on the same truncated code prefix. Those
 *   collisions are real string-level defects: every member is flagged
 *   dq: true and, since sku must be unique (it is the Table's row key),
 *   every member after the first gets a disambiguating "(dup N)" suffix.
 *   That flagging and disambiguation is correct and kept. What is NOT done
 *   is treating the collision as evidence that the rows are duplicates of
 *   one product listed under two codes: a truncation collision means two
 *   labels happen to print the same after being cut off, nothing more. For
 *   example one such group pairs sku "1M-TWTS-C0" — "AND Life Goes On
 *   Classic Fit T-Shirt Olive Green / L" — with "1M-TWTS-C0 (dup 3)" —
 *   "All goes right Classic Fit T-Shirt Grape Nectar / XL": different
 *   garment, different design, different colour, different size. Populating
 *   motherOf/childOf from that pairing would be inventing a merge
 *   relationship that isn't in the source, so both fields are set to null
 *   on every SKU here. MERGE_CANDIDATES (below) is that deliberate seed: a
 *   small, explicitly-flagged example set built for the Catalogue module to
 *   demo its "suggested merges" flow against, rather than inheriting this
 *   accidental collision.
 *
 * DERIVED-BUT-NOT-OBSERVED — the dead/volumetric split:
 *   The source carries exactly ONE charged weight per shipment; the dead/
 *   volumetric split does not exist in it. chargeKg is pinned to the SKU's
 *   (or combo's) real mpw. A ~35% seeded roll makes the row volumetric-
 *   governed (volKg = chargeKg, deadKg fabricated at 40-85% of it);
 *   otherwise dead governs (deadKg = chargeKg, volKg fabricated at 40-85%).
 *   THE WINNER IS REAL. THE LOSER IS FABRICATED. Every "dead weight governs /
 *   volumetric governs" annotation any later screen renders off deadKg/volKg
 *   is displaying a split that is not in the source.
 *
 * SYNTHESIZED (seeded LCG, deterministic, entirely invented):
 *   The entire packing-material catalogue (PACKS — all nine materials'
 *   dimensions, self-weights and branding flags), packId assignment on every
 *   SKU and combo, cat/sub (regex classification of the product name; the
 *   source's own cat field is blank for every row), sorterCount and
 *   merchantCount (first-class evidence counts — merchantCount is seeded so
 *   roughly a third to a half of SKUs sit at zero (currently ~33%; the
 *   dataset's own test suite asserts this share stays within 30%-50%),
 *   which is deliberate: that manufactured evidence gap is the only reason
 *   the Initiate blocking gate ever fires),
 *   source on every SKU, and reviewed (meaning "this auto-added SKU has been
 *   confirmed" — only meaningful for source 'auto'/'oms'; those SKUs are
 *   seeded roughly 50/50 true/false via the same LCG, while every
 *   source: 'catalogue' SKU stays reviewed: false since it was never
 *   auto-added), every DWS_RECORDS entry (awb, courier, date, read, charged,
 *   dims, vol, and for 'upload' records the video/images/note placeholders —
 *   this is a frozen seed with no live session behind it, so even the
 *   'upload' records are invented, never a real photograph),
 *   MERCHANT.ratePerKg, and the entire REQUESTS collection
 *   (every id, date, actor, state, reason and cyclesElapsed).
 *
 *   MERCHANT.type is itself an assignment, not a fact: only Neemans and
 *   Swiggy are named in the source document. Nobero is assigned
 *   {freeze: 'prozo', billing: 'prepaid'} to fill out the matrix.
 *
 * COMBO SYNTHESIS — a SECOND, independent seeded LCG stream (rndC, seeded
 * separately from rnd) drives every combo-side random draw: COMBOS.
 * merchantCount and its accompanying DWS_RECORDS rows, and MERGE_CANDIDATES.
 * This is deliberate isolation, not an oversight — see the WARNING comment
 * above the SKU-side evidRoll for why a shared stream would be dangerous
 * here: any future edit confined to combo synthesis can never re-shift
 * SKUS, DWS_RECORDS' SKU rows, REQUESTS, or MERCHANT.ratePerKg, because none
 * of those draw from rndC and this block runs after all of them exist.
 *   COMBOS.merchantCount is seeded so ~45% of combos sit at zero (a roll
 *   below 0.45 on rndC), mirroring the SKU-side evidence gap at combo
 *   granularity; it is COMPUTED from the DWS_RECORDS rows this generator
 *   actually emits for that combo (source: 'upload', keyed by combo.key in
 *   the sku field), never rolled-and-assumed-equal.
 *   Ten of the sixty combos have sorterCount === 0 (computed earlier, on the
 *   shared rnd stream, well before this block runs) — those combos get
 *   ZERO courier-source DWS rows here. That is the honest outcome, not a
 *   gap to be filled: there is no real courier scanner reading behind them,
 *   and fabricating one to make an "every combo has records" invariant hold
 *   would violate the HONESTY RULE below. The other fifty combos each get a
 *   handful of courier rows exactly as the SKU side does.
 *   COMBOS.itemSkus/priceable are the one part of this block that draws NO
 *   randomness at all: itemSkus resolves each (corrected) item's label
 *   through the same splitLabel used for SKUS and keeps the result only if
 *   EVERY item matches a real SKUS[].sku string exactly (no fuzzy or
 *   substring matching — "exact-match only"); it is null the moment any
 *   item fails to resolve. priceable is true only when itemSkus is non-null
 *   AND has 2 or more entries — a single resolved leg (e.g. one of the ten
 *   items-corrected combos above) isn't a combination to price as one.
 *
 * MERGE_CANDIDATES — three deliberately SEEDED example pairs
 * ({a, b, sub, packId, mpw, seeded: true}), built for the Catalogue module's
 * "suggested merges" flow (see the motherOf/childOf note above: the source
 * offers no real evidence of duplicate SKU listings, so a demo example has
 * to be manufactured on purpose instead of inferred). a/b are two real
 * SKUS[].sku values drawn from the same synthesized sub via rndC; the
 * PAIRING is an editorial choice, not a measured duplicate-detection
 * result — that is exactly why seeded: true is on every entry, so no
 * later screen or reviewer mistakes these for something Nobero's data
 * actually evidences. packId and mpw, by contrast, ARE derived: mpw is
 * the real average of the two SKUs' own mpw values, and packId is
 * packFor(mpw) — the same capacity lookup used everywhere else, not a
 * separately invented figure.
 *
 * HONESTY RULE: there are no real DWS photographs anywhere in this dataset.
 * Every record with source: 'courier' carries images: [] — it is a scanner
 * reading, not a photograph. Never fabricate an image path or URL for a
 * courier record, and never fabricate a courier record at all for a combo
 * (or SKU) with no sorter evidence behind it.
 *
 * Regenerate with: node scripts/gen-nobero-freeze.js
 */
export const MERCHANT = {
  "id": "nobero",
  "name": "Nobero",
  "type": {
    "freeze": "prozo",
    "billing": "prepaid"
  },
  "typeLabel": "Prepaid · Prozo-initiated",
  "ratePerKg": 125,
  "totals": {
    "awbs": 86332,
    "single": 77250,
    "multi": 9082,
    "skus": 16613,
    "combos": 8722
  },
  "months": [
    [
      "Jan-26",
      25986
    ],
    [
      "Feb-26",
      14854
    ],
    [
      "Apr-26",
      17755
    ],
    [
      "May-26",
      15105
    ],
    [
      "Jun-26",
      12632
    ]
  ]
}

export const SKUS = [
  {
    "sku": "1M-TWTS-C0969-OG4",
    "product": "Hope Classic Fit T-Shirt L / Olive Green",
    "ships": 114,
    "hist": [
      [
        0.75,
        107
      ],
      [
        1,
        7
      ]
    ],
    "mpw": 0.75,
    "spread": 6.1,
    "dq": false,
    "cat": "Topwear",
    "sub": "T-Shirts",
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.56,
    "chargeKg": 0.75,
    "sorterCount": 78,
    "merchantCount": 0,
    "source": "oms",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-BWJG-R0149-ET4",
    "product": "Classic Straight Fit Joggers Eiffel Tower / L",
    "ships": 104,
    "hist": [
      [
        0.75,
        86
      ],
      [
        1,
        18
      ]
    ],
    "mpw": 0.75,
    "spread": 17.3,
    "dq": false,
    "cat": "Bottomwear",
    "sub": "Joggers",
    "packId": "FLY-GEN",
    "deadKg": 0.46,
    "volKg": 0.75,
    "chargeKg": 0.75,
    "sorterCount": 72,
    "merchantCount": 1,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-BWJG-R0137-BL5",
    "product": "Wanderer - Travel Pants Black / XL",
    "ships": 99,
    "hist": [
      [
        1,
        99
      ]
    ],
    "mpw": 1,
    "spread": 0,
    "dq": false,
    "cat": "Bottomwear",
    "sub": "Travel Wear",
    "packId": "FLY-L",
    "deadKg": 1,
    "volKg": 0.68,
    "chargeKg": 1,
    "sorterCount": 17,
    "merchantCount": 6,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-BWJG-R0137-BL4",
    "product": "Wanderer - Travel Pants Black / L",
    "ships": 98,
    "hist": [
      [
        1,
        88
      ],
      [
        1.5,
        4
      ],
      [
        2,
        6
      ]
    ],
    "mpw": 1,
    "spread": 10.2,
    "dq": false,
    "cat": "Bottomwear",
    "sub": "Travel Wear",
    "packId": "FLY-L",
    "deadKg": 1,
    "volKg": 0.62,
    "chargeKg": 1,
    "sorterCount": 59,
    "merchantCount": 2,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-BWJG-R0149-BL4",
    "product": "Classic Straight Fit Joggers Black / L",
    "ships": 94,
    "hist": [
      [
        0.75,
        79
      ],
      [
        1,
        15
      ]
    ],
    "mpw": 0.75,
    "spread": 16,
    "dq": false,
    "cat": "Bottomwear",
    "sub": "Joggers",
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.5,
    "chargeKg": 0.75,
    "sorterCount": 36,
    "merchantCount": 2,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-BWJG-R0149-ET",
    "product": "Classic Straight Fit Joggers Eiffel Tower / XL",
    "ships": 94,
    "hist": [
      [
        0.75,
        37
      ],
      [
        1,
        57
      ]
    ],
    "mpw": 1,
    "spread": 39.4,
    "dq": false,
    "cat": "Bottomwear",
    "sub": "Joggers",
    "packId": "FLY-L",
    "deadKg": 1,
    "volKg": 0.76,
    "chargeKg": 1,
    "sorterCount": 25,
    "merchantCount": 2,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-BWJG-R0149-ET3",
    "product": "Classic Straight Fit Joggers Eiffel Tower / M",
    "ships": 93,
    "hist": [
      [
        0.75,
        39
      ],
      [
        1,
        54
      ]
    ],
    "mpw": 1,
    "spread": 41.9,
    "dq": false,
    "cat": "Bottomwear",
    "sub": "Joggers",
    "packId": "FLY-L",
    "deadKg": 1,
    "volKg": 0.81,
    "chargeKg": 1,
    "sorterCount": 20,
    "merchantCount": 0,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-TWTS-C0889-PBL",
    "product": "THINK BIG Classic Fit T-Shirt L / Powder Blue",
    "ships": 92,
    "hist": [
      [
        0.75,
        91
      ],
      [
        1,
        1
      ]
    ],
    "mpw": 0.75,
    "spread": 1.1,
    "dq": true,
    "cat": "Topwear",
    "sub": "T-Shirts",
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.48,
    "chargeKg": 0.75,
    "sorterCount": 23,
    "merchantCount": 6,
    "source": "auto",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-TWTS-C0969-OG5",
    "product": "Hope Classic Fit T-Shirt XL / Olive Green",
    "ships": 89,
    "hist": [
      [
        0.75,
        89
      ]
    ],
    "mpw": 0.75,
    "spread": 0,
    "dq": false,
    "cat": "Topwear",
    "sub": "T-Shirts",
    "packId": "FLY-GEN",
    "deadKg": 0.3,
    "volKg": 0.75,
    "chargeKg": 0.75,
    "sorterCount": 33,
    "merchantCount": 0,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-TWPO-R0002-BU3",
    "product": "Classic Polo T-Shirt Burgundy / M",
    "ships": 82,
    "hist": [
      [
        0.75,
        82
      ]
    ],
    "mpw": 0.75,
    "spread": 0,
    "dq": false,
    "cat": "Topwear",
    "sub": "Polo T-Shirts",
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.48,
    "chargeKg": 0.75,
    "sorterCount": 35,
    "merchantCount": 0,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-BWJG-R0149-BL5",
    "product": "Classic Straight Fit Joggers Black / XL",
    "ships": 81,
    "hist": [
      [
        0.75,
        39
      ],
      [
        1,
        42
      ]
    ],
    "mpw": 1,
    "spread": 48.1,
    "dq": false,
    "cat": "Bottomwear",
    "sub": "Joggers",
    "packId": "FLY-L",
    "deadKg": 1,
    "volKg": 0.62,
    "chargeKg": 1,
    "sorterCount": 39,
    "merchantCount": 1,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-BWJG-R0149-PB4",
    "product": "Classic Straight Fit Joggers Pebble / L",
    "ships": 80,
    "hist": [
      [
        0.75,
        69
      ],
      [
        1,
        11
      ]
    ],
    "mpw": 0.75,
    "spread": 13.8,
    "dq": false,
    "cat": "Bottomwear",
    "sub": "Joggers",
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.53,
    "chargeKg": 0.75,
    "sorterCount": 21,
    "merchantCount": 6,
    "source": "oms",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-BWJG-R0149-BL3",
    "product": "Classic Straight Fit Joggers Black / M",
    "ships": 77,
    "hist": [
      [
        0.75,
        33
      ],
      [
        1,
        43
      ],
      [
        2,
        1
      ]
    ],
    "mpw": 1,
    "spread": 44.2,
    "dq": false,
    "cat": "Bottomwear",
    "sub": "Joggers",
    "packId": "FLY-L",
    "deadKg": 1,
    "volKg": 0.63,
    "chargeKg": 1,
    "sorterCount": 16,
    "merchantCount": 4,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-TWTS-C0",
    "product": "AND Life Goes On Classic Fit T-Shirt Olive Green / L",
    "ships": 77,
    "hist": [
      [
        0.75,
        76
      ],
      [
        1.5,
        1
      ]
    ],
    "mpw": 0.75,
    "spread": 1.3,
    "dq": true,
    "cat": "Topwear",
    "sub": "T-Shirts",
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.63,
    "chargeKg": 0.75,
    "sorterCount": 53,
    "merchantCount": 0,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-TWTS-C0268-MN4",
    "product": "Wander Soul Classic Fit T-Shirt Marine / L",
    "ships": 75,
    "hist": [
      [
        0.75,
        75
      ]
    ],
    "mpw": 0.75,
    "spread": 0,
    "dq": false,
    "cat": "Topwear",
    "sub": "T-Shirts",
    "packId": "FLY-GEN",
    "deadKg": 0.62,
    "volKg": 0.75,
    "chargeKg": 0.75,
    "sorterCount": 37,
    "merchantCount": 0,
    "source": "auto",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-TWZP-R0002-BL3",
    "product": "Wanderer - Travel Hoodie Black / M",
    "ships": 75,
    "hist": [
      [
        0.75,
        1
      ],
      [
        1,
        73
      ],
      [
        1.5,
        1
      ]
    ],
    "mpw": 1,
    "spread": 2.7,
    "dq": false,
    "cat": "Topwear",
    "sub": "Hoodies",
    "packId": "FLY-L",
    "deadKg": 1,
    "volKg": 0.58,
    "chargeKg": 1,
    "sorterCount": 34,
    "merchantCount": 6,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-TWPO-R0002-BU4",
    "product": "Classic Polo T-Shirt Burgundy / L",
    "ships": 74,
    "hist": [
      [
        0.75,
        74
      ]
    ],
    "mpw": 0.75,
    "spread": 0,
    "dq": false,
    "cat": "Topwear",
    "sub": "Polo T-Shirts",
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.46,
    "chargeKg": 0.75,
    "sorterCount": 24,
    "merchantCount": 5,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-B",
    "product": "2-Pack Classic Straight Fit Joggers Black/Eiffel Tower / M",
    "ships": 74,
    "hist": [
      [
        0.75,
        11
      ],
      [
        1,
        63
      ]
    ],
    "mpw": 1,
    "spread": 14.9,
    "dq": true,
    "cat": "Bottomwear",
    "sub": "Joggers",
    "packId": "FLY-L",
    "deadKg": 0.52,
    "volKg": 1,
    "chargeKg": 1,
    "sorterCount": 46,
    "merchantCount": 6,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "NBJUN24MJ05-Men-Joggers-Black-L",
    "product": "NBJUN24MJ05-Men-Joggers-Black-L",
    "ships": 73,
    "hist": [
      [
        0.75,
        8
      ],
      [
        1,
        65
      ]
    ],
    "mpw": 1,
    "spread": 11,
    "dq": false,
    "cat": "Bottomwear",
    "sub": "Joggers",
    "packId": "FLY-L",
    "deadKg": 1,
    "volKg": 0.71,
    "chargeKg": 1,
    "sorterCount": 12,
    "merchantCount": 0,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-TWSS-R0032-BL3",
    "product": "Mock Neck  Sweatshirt Black / M",
    "ships": 71,
    "hist": [
      [
        0.75,
        17
      ],
      [
        1,
        54
      ]
    ],
    "mpw": 1,
    "spread": 23.9,
    "dq": false,
    "cat": "Topwear",
    "sub": "Sweatshirts",
    "packId": "FLY-L",
    "deadKg": 1,
    "volKg": 0.44,
    "chargeKg": 1,
    "sorterCount": 17,
    "merchantCount": 1,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-TWTS-C0 (dup 2)",
    "product": "AND Life Goes On Classic Fit T-Shirt Olive Green / M",
    "ships": 70,
    "hist": [
      [
        0.75,
        70
      ]
    ],
    "mpw": 0.75,
    "spread": 0,
    "dq": true,
    "cat": "Topwear",
    "sub": "T-Shirts",
    "packId": "FLY-GEN",
    "deadKg": 0.43,
    "volKg": 0.75,
    "chargeKg": 0.75,
    "sorterCount": 40,
    "merchantCount": 3,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-BWCP-R0002-BL4",
    "product": "Men's Super Baggy Cargo Pants Black / L",
    "ships": 69,
    "hist": [
      [
        0.75,
        22
      ],
      [
        1,
        47
      ]
    ],
    "mpw": 1,
    "spread": 31.9,
    "dq": false,
    "cat": "Bottomwear",
    "sub": "Cargo Pants",
    "packId": "FLY-L",
    "deadKg": 1,
    "volKg": 0.49,
    "chargeKg": 1,
    "sorterCount": 23,
    "merchantCount": 0,
    "source": "auto",
    "reviewed": true,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-TWTS-C09",
    "product": "All goes right Classic Fit T-Shirt Grape Nectar / L",
    "ships": 68,
    "hist": [
      [
        0.75,
        53
      ],
      [
        1,
        15
      ]
    ],
    "mpw": 0.75,
    "spread": 22.1,
    "dq": true,
    "cat": "Topwear",
    "sub": "T-Shirts",
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.43,
    "chargeKg": 0.75,
    "sorterCount": 28,
    "merchantCount": 0,
    "source": "oms",
    "reviewed": true,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-TWTS-C0969-OG3",
    "product": "Hope Classic Fit T-Shirt M / Olive Green",
    "ships": 67,
    "hist": [
      [
        0.75,
        67
      ]
    ],
    "mpw": 0.75,
    "spread": 0,
    "dq": false,
    "cat": "Topwear",
    "sub": "T-Shirts",
    "packId": "FLY-GEN",
    "deadKg": 0.37,
    "volKg": 0.75,
    "chargeKg": 0.75,
    "sorterCount": 12,
    "merchantCount": 0,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-TWTS-C09 (dup 2)",
    "product": "All goes right Classic Fit T-Shirt Grape Nectar / M",
    "ships": 66,
    "hist": [
      [
        0.75,
        66
      ]
    ],
    "mpw": 0.75,
    "spread": 0,
    "dq": true,
    "cat": "Topwear",
    "sub": "T-Shirts",
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.62,
    "chargeKg": 0.75,
    "sorterCount": 26,
    "merchantCount": 0,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-",
    "product": "2-Pack Classic Straight Fit Joggers Black/Eiffel Tower / XL",
    "ships": 66,
    "hist": [
      [
        0.75,
        9
      ],
      [
        1,
        57
      ]
    ],
    "mpw": 1,
    "spread": 13.6,
    "dq": false,
    "cat": "Bottomwear",
    "sub": "Joggers",
    "packId": "FLY-L",
    "deadKg": 1,
    "volKg": 0.58,
    "chargeKg": 1,
    "sorterCount": 34,
    "merchantCount": 0,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-BWJG-R0149-NB5",
    "product": "Classic Straight Fit Joggers Navy Blue / XL",
    "ships": 65,
    "hist": [
      [
        0.75,
        40
      ],
      [
        1,
        25
      ]
    ],
    "mpw": 0.75,
    "spread": 38.5,
    "dq": false,
    "cat": "Bottomwear",
    "sub": "Joggers",
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.53,
    "chargeKg": 0.75,
    "sorterCount": 37,
    "merchantCount": 3,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-BWCP-R0002-BL3",
    "product": "Men's Super Baggy Cargo Pants Black / M",
    "ships": 65,
    "hist": [
      [
        0.75,
        49
      ],
      [
        1,
        16
      ]
    ],
    "mpw": 0.75,
    "spread": 24.6,
    "dq": false,
    "cat": "Bottomwear",
    "sub": "Cargo Pants",
    "packId": "FLY-GEN",
    "deadKg": 0.57,
    "volKg": 0.75,
    "chargeKg": 0.75,
    "sorterCount": 31,
    "merchantCount": 1,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-TWTS-C1059-BL4",
    "product": "Los Angeles V3 Classic Fit T-Shirt L / Black",
    "ships": 64,
    "hist": [
      [
        0.75,
        47
      ],
      [
        1,
        17
      ]
    ],
    "mpw": 0.75,
    "spread": 26.6,
    "dq": false,
    "cat": "Topwear",
    "sub": "T-Shirts",
    "packId": "FLY-GEN",
    "deadKg": 0.52,
    "volKg": 0.75,
    "chargeKg": 0.75,
    "sorterCount": 20,
    "merchantCount": 5,
    "source": "auto",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-LWCS-R0177-NB4",
    "product": "Oversized Justin Co-ord Set Navy Blue / L",
    "ships": 63,
    "hist": [
      [
        0.75,
        16
      ],
      [
        1,
        47
      ]
    ],
    "mpw": 1,
    "spread": 25.4,
    "dq": false,
    "cat": "Sets",
    "sub": "Co-ord Sets",
    "packId": "FLY-L",
    "deadKg": 1,
    "volKg": 0.41,
    "chargeKg": 1,
    "sorterCount": 13,
    "merchantCount": 6,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-TWTS-C0 (dup 3)",
    "product": "All goes right Classic Fit T-Shirt Grape Nectar / XL",
    "ships": 63,
    "hist": [
      [
        0.75,
        63
      ]
    ],
    "mpw": 0.75,
    "spread": 0,
    "dq": true,
    "cat": "Topwear",
    "sub": "T-Shirts",
    "packId": "FLY-GEN",
    "deadKg": 0.48,
    "volKg": 0.75,
    "chargeKg": 0.75,
    "sorterCount": 17,
    "merchantCount": 4,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-TWTS-C",
    "product": "AND Life Goes On Classic Fit T-Shirt Olive Green / XL",
    "ships": 63,
    "hist": [
      [
        0.75,
        62
      ],
      [
        1,
        1
      ]
    ],
    "mpw": 0.75,
    "spread": 1.6,
    "dq": false,
    "cat": "Topwear",
    "sub": "T-Shirts",
    "packId": "FLY-GEN",
    "deadKg": 0.48,
    "volKg": 0.75,
    "chargeKg": 0.75,
    "sorterCount": 26,
    "merchantCount": 0,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-BWJG-R0149-PB5",
    "product": "Classic Straight Fit Joggers Pebble / XL",
    "ships": 63,
    "hist": [
      [
        0.75,
        21
      ],
      [
        1,
        42
      ]
    ],
    "mpw": 1,
    "spread": 33.3,
    "dq": false,
    "cat": "Bottomwear",
    "sub": "Joggers",
    "packId": "FLY-L",
    "deadKg": 1,
    "volKg": 0.79,
    "chargeKg": 1,
    "sorterCount": 31,
    "merchantCount": 2,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-B (dup 2)",
    "product": "2-Pack Classic Straight Fit Joggers Black/Eiffel Tower / L",
    "ships": 63,
    "hist": [
      [
        0.75,
        56
      ],
      [
        1,
        7
      ]
    ],
    "mpw": 0.75,
    "spread": 11.1,
    "dq": true,
    "cat": "Bottomwear",
    "sub": "Joggers",
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.49,
    "chargeKg": 0.75,
    "sorterCount": 35,
    "merchantCount": 0,
    "source": "oms",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-TWTS-C0218",
    "product": "Believe in Yourself Classic Fit T-Shirt Black / L",
    "ships": 62,
    "hist": [
      [
        0.75,
        62
      ]
    ],
    "mpw": 0.75,
    "spread": 0,
    "dq": false,
    "cat": "Topwear",
    "sub": "T-Shirts",
    "packId": "FLY-GEN",
    "deadKg": 0.53,
    "volKg": 0.75,
    "chargeKg": 0.75,
    "sorterCount": 13,
    "merchantCount": 6,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-TWTS-C0889-PBL (dup 2)",
    "product": "THINK BIG Classic Fit T-Shirt M / Powder Blue",
    "ships": 62,
    "hist": [
      [
        0.75,
        62
      ]
    ],
    "mpw": 0.75,
    "spread": 0,
    "dq": true,
    "cat": "Topwear",
    "sub": "T-Shirts",
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.3,
    "chargeKg": 0.75,
    "sorterCount": 32,
    "merchantCount": 4,
    "source": "auto",
    "reviewed": true,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-BWJG-R0149-NB4",
    "product": "Classic Straight Fit Joggers Navy Blue / L",
    "ships": 60,
    "hist": [
      [
        0.75,
        47
      ],
      [
        1,
        13
      ]
    ],
    "mpw": 0.75,
    "spread": 21.7,
    "dq": false,
    "cat": "Bottomwear",
    "sub": "Joggers",
    "packId": "FLY-GEN",
    "deadKg": 0.43,
    "volKg": 0.75,
    "chargeKg": 0.75,
    "sorterCount": 29,
    "merchantCount": 6,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-TWPO-R0002-NB4",
    "product": "Classic Polo T-Shirt Navy Blue / L",
    "ships": 60,
    "hist": [
      [
        0.75,
        60
      ]
    ],
    "mpw": 0.75,
    "spread": 0,
    "dq": false,
    "cat": "Topwear",
    "sub": "Polo T-Shirts",
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.52,
    "chargeKg": 0.75,
    "sorterCount": 13,
    "merchantCount": 5,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-TWZP-R0002-BL4",
    "product": "Wanderer - Travel Hoodie Black / L",
    "ships": 60,
    "hist": [
      [
        1,
        49
      ],
      [
        1.5,
        10
      ],
      [
        2,
        1
      ]
    ],
    "mpw": 1,
    "spread": 18.3,
    "dq": false,
    "cat": "Topwear",
    "sub": "Hoodies",
    "packId": "FLY-L",
    "deadKg": 0.41,
    "volKg": 1,
    "chargeKg": 1,
    "sorterCount": 10,
    "merchantCount": 2,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-TWTS-C0561-WR5",
    "product": "Authentic Classic Fit T-Shirt Wine Red / XL",
    "ships": 58,
    "hist": [
      [
        0.75,
        58
      ]
    ],
    "mpw": 0.75,
    "spread": 0,
    "dq": false,
    "cat": "Topwear",
    "sub": "T-Shirts",
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.34,
    "chargeKg": 0.75,
    "sorterCount": 33,
    "merchantCount": 6,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-TWTS-C0561-WR4",
    "product": "Authentic Classic Fit T-Shirt Wine Red / L",
    "ships": 58,
    "hist": [
      [
        0.75,
        58
      ]
    ],
    "mpw": 0.75,
    "spread": 0,
    "dq": false,
    "cat": "Topwear",
    "sub": "T-Shirts",
    "packId": "FLY-GEN",
    "deadKg": 0.42,
    "volKg": 0.75,
    "chargeKg": 0.75,
    "sorterCount": 9,
    "merchantCount": 6,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-TWPO-R0002-CY3",
    "product": "Classic Polo T-Shirt Canary / M",
    "ships": 57,
    "hist": [
      [
        0.75,
        57
      ]
    ],
    "mpw": 0.75,
    "spread": 0,
    "dq": false,
    "cat": "Topwear",
    "sub": "Polo T-Shirts",
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.63,
    "chargeKg": 0.75,
    "sorterCount": 9,
    "merchantCount": 0,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-BWJG-R0149-PB3",
    "product": "Classic Straight Fit Joggers Pebble / M",
    "ships": 57,
    "hist": [
      [
        0.75,
        21
      ],
      [
        1,
        36
      ]
    ],
    "mpw": 1,
    "spread": 36.8,
    "dq": false,
    "cat": "Bottomwear",
    "sub": "Joggers",
    "packId": "FLY-L",
    "deadKg": 1,
    "volKg": 0.8,
    "chargeKg": 1,
    "sorterCount": 12,
    "merchantCount": 4,
    "source": "auto",
    "reviewed": true,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-BWJG-R0149-BL6",
    "product": "Classic Straight Fit Joggers Black / XXL",
    "ships": 57,
    "hist": [
      [
        0.75,
        43
      ],
      [
        1,
        14
      ]
    ],
    "mpw": 0.75,
    "spread": 24.6,
    "dq": false,
    "cat": "Bottomwear",
    "sub": "Joggers",
    "packId": "FLY-GEN",
    "deadKg": 0.3,
    "volKg": 0.75,
    "chargeKg": 0.75,
    "sorterCount": 9,
    "merchantCount": 6,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-TWPO-R0002-CY5",
    "product": "Classic Polo T-Shirt Canary / XL",
    "ships": 56,
    "hist": [
      [
        0.75,
        56
      ]
    ],
    "mpw": 0.75,
    "spread": 0,
    "dq": false,
    "cat": "Topwear",
    "sub": "Polo T-Shirts",
    "packId": "FLY-GEN",
    "deadKg": 0.48,
    "volKg": 0.75,
    "chargeKg": 0.75,
    "sorterCount": 28,
    "merchantCount": 2,
    "source": "oms",
    "reviewed": true,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-TWPO-R0002-NB3",
    "product": "Classic Polo T-Shirt Navy Blue / M",
    "ships": 56,
    "hist": [
      [
        0.75,
        55
      ],
      [
        1,
        1
      ]
    ],
    "mpw": 0.75,
    "spread": 1.8,
    "dq": false,
    "cat": "Topwear",
    "sub": "Polo T-Shirts",
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.59,
    "chargeKg": 0.75,
    "sorterCount": 21,
    "merchantCount": 1,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-BWJG-R0084-BL4",
    "product": "Zip-Pocket Joggers Black / L",
    "ships": 56,
    "hist": [
      [
        0.75,
        50
      ],
      [
        1,
        6
      ]
    ],
    "mpw": 0.75,
    "spread": 10.7,
    "dq": false,
    "cat": "Bottomwear",
    "sub": "Joggers",
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.45,
    "chargeKg": 0.75,
    "sorterCount": 21,
    "merchantCount": 0,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-TWTS-C0612-",
    "product": "Never Say No Classic Fit T-Shirt Powder Blue / L",
    "ships": 56,
    "hist": [
      [
        0.75,
        56
      ]
    ],
    "mpw": 0.75,
    "spread": 0,
    "dq": true,
    "cat": "Topwear",
    "sub": "T-Shirts",
    "packId": "FLY-GEN",
    "deadKg": 0.33,
    "volKg": 0.75,
    "chargeKg": 0.75,
    "sorterCount": 26,
    "merchantCount": 0,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-TWPO-R0002-CY4",
    "product": "Classic Polo T-Shirt Canary / L",
    "ships": 56,
    "hist": [
      [
        0.75,
        56
      ]
    ],
    "mpw": 0.75,
    "spread": 0,
    "dq": false,
    "cat": "Topwear",
    "sub": "Polo T-Shirts",
    "packId": "FLY-GEN",
    "deadKg": 0.39,
    "volKg": 0.75,
    "chargeKg": 0.75,
    "sorterCount": 19,
    "merchantCount": 2,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-BWJG-R0084-BL3",
    "product": "Zip-Pocket Joggers Black / M",
    "ships": 56,
    "hist": [
      [
        0.75,
        32
      ],
      [
        1,
        24
      ]
    ],
    "mpw": 0.75,
    "spread": 42.9,
    "dq": false,
    "cat": "Bottomwear",
    "sub": "Joggers",
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.39,
    "chargeKg": 0.75,
    "sorterCount": 37,
    "merchantCount": 0,
    "source": "auto",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-BWJG-R0",
    "product": "2-Pack Classic Straight Fit Joggers Black/Pebble / L",
    "ships": 56,
    "hist": [
      [
        0.75,
        51
      ],
      [
        1,
        5
      ]
    ],
    "mpw": 0.75,
    "spread": 8.9,
    "dq": true,
    "cat": "Bottomwear",
    "sub": "Joggers",
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.42,
    "chargeKg": 0.75,
    "sorterCount": 39,
    "merchantCount": 5,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-TWPO-R0002-BU5",
    "product": "Classic Polo T-Shirt Burgundy / XL",
    "ships": 55,
    "hist": [
      [
        0.75,
        55
      ]
    ],
    "mpw": 0.75,
    "spread": 0,
    "dq": false,
    "cat": "Topwear",
    "sub": "Polo T-Shirts",
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.37,
    "chargeKg": 0.75,
    "sorterCount": 26,
    "merchantCount": 1,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1W-BWJG-R0220-BL4",
    "product": "Straight Leg Women's Joggers Black / L",
    "ships": 55,
    "hist": [
      [
        0.75,
        55
      ]
    ],
    "mpw": 0.75,
    "spread": 0,
    "dq": false,
    "cat": "Bottomwear",
    "sub": "Joggers",
    "packId": "FLY-GEN",
    "deadKg": 0.32,
    "volKg": 0.75,
    "chargeKg": 0.75,
    "sorterCount": 25,
    "merchantCount": 0,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-TWTS-C0889-PB",
    "product": "THINK BIG Classic Fit T-Shirt XL / Powder Blue",
    "ships": 54,
    "hist": [
      [
        0.75,
        54
      ]
    ],
    "mpw": 0.75,
    "spread": 0,
    "dq": false,
    "cat": "Topwear",
    "sub": "T-Shirts",
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.49,
    "chargeKg": 0.75,
    "sorterCount": 14,
    "merchantCount": 1,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "NBAW24MT02-Men-Sweatshirts-Black-L",
    "product": "NBAW24MT02-Men-Sweatshirts-Black-L",
    "ships": 53,
    "hist": [
      [
        0.75,
        53
      ]
    ],
    "mpw": 0.75,
    "spread": 0,
    "dq": false,
    "cat": "Topwear",
    "sub": "Sweatshirts",
    "packId": "FLY-GEN",
    "deadKg": 0.46,
    "volKg": 0.75,
    "chargeKg": 0.75,
    "sorterCount": 27,
    "merchantCount": 3,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-BWJG-R0137-OL4",
    "product": "Wanderer - Travel Pants Oatmeal / L",
    "ships": 53,
    "hist": [
      [
        0.75,
        7
      ],
      [
        1,
        46
      ]
    ],
    "mpw": 1,
    "spread": 13.2,
    "dq": false,
    "cat": "Bottomwear",
    "sub": "Travel Wear",
    "packId": "FLY-L",
    "deadKg": 0.49,
    "volKg": 1,
    "chargeKg": 1,
    "sorterCount": 15,
    "merchantCount": 1,
    "source": "oms",
    "reviewed": true,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-BWJG-R0149-E",
    "product": "Classic Straight Fit Joggers Eiffel Tower / XXL",
    "ships": 53,
    "hist": [
      [
        0.75,
        48
      ],
      [
        1,
        5
      ]
    ],
    "mpw": 0.75,
    "spread": 9.4,
    "dq": false,
    "cat": "Bottomwear",
    "sub": "Joggers",
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.58,
    "chargeKg": 0.75,
    "sorterCount": 9,
    "merchantCount": 3,
    "source": "auto",
    "reviewed": true,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-BWJG-R0090-AW4",
    "product": "Relaxed Fit Cargo Joggers Arctic Wolf / L",
    "ships": 53,
    "hist": [
      [
        0.75,
        53
      ]
    ],
    "mpw": 0.75,
    "spread": 0,
    "dq": false,
    "cat": "Bottomwear",
    "sub": "Cargo Pants",
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.55,
    "chargeKg": 0.75,
    "sorterCount": 8,
    "merchantCount": 0,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-TWTS-C0561-WR3",
    "product": "Authentic Classic Fit T-Shirt Wine Red / M",
    "ships": 52,
    "hist": [
      [
        0.75,
        51
      ],
      [
        1.5,
        1
      ]
    ],
    "mpw": 0.75,
    "spread": 1.9,
    "dq": false,
    "cat": "Topwear",
    "sub": "T-Shirts",
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.59,
    "chargeKg": 0.75,
    "sorterCount": 14,
    "merchantCount": 1,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-TWPO-R0002-HG4",
    "product": "Classic Polo T-Shirt Highland Green / L",
    "ships": 52,
    "hist": [
      [
        0.75,
        44
      ],
      [
        1,
        8
      ]
    ],
    "mpw": 0.75,
    "spread": 15.4,
    "dq": false,
    "cat": "Topwear",
    "sub": "Polo T-Shirts",
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.34,
    "chargeKg": 0.75,
    "sorterCount": 22,
    "merchantCount": 3,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-BWJG-R0137-BL6",
    "product": "Wanderer - Travel Pants Black / XXL",
    "ships": 52,
    "hist": [
      [
        0.75,
        5
      ],
      [
        1,
        47
      ]
    ],
    "mpw": 1,
    "spread": 9.6,
    "dq": false,
    "cat": "Bottomwear",
    "sub": "Travel Wear",
    "packId": "FLY-L",
    "deadKg": 1,
    "volKg": 0.78,
    "chargeKg": 1,
    "sorterCount": 10,
    "merchantCount": 0,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-TWPO-R0002-PT3",
    "product": "Classic Polo T-Shirt Petunia / M",
    "ships": 52,
    "hist": [
      [
        0.75,
        52
      ]
    ],
    "mpw": 0.75,
    "spread": 0,
    "dq": false,
    "cat": "Topwear",
    "sub": "Polo T-Shirts",
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.45,
    "chargeKg": 0.75,
    "sorterCount": 27,
    "merchantCount": 6,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "NBJUN24MJ05-Men-Joggers-Eiffel Tower-XL",
    "product": "NBJUN24MJ05-Men-Joggers-Eiffel Tower-XL",
    "ships": 51,
    "hist": [
      [
        0.75,
        4
      ],
      [
        1,
        47
      ]
    ],
    "mpw": 1,
    "spread": 7.8,
    "dq": false,
    "cat": "Bottomwear",
    "sub": "Joggers",
    "packId": "FLY-L",
    "deadKg": 1,
    "volKg": 0.55,
    "chargeKg": 1,
    "sorterCount": 8,
    "merchantCount": 0,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-TWPO-R0002-AQB5",
    "product": "Classic Polo T-Shirt Aqua Blue / XL",
    "ships": 50,
    "hist": [
      [
        0.75,
        42
      ],
      [
        1,
        8
      ]
    ],
    "mpw": 0.75,
    "spread": 16,
    "dq": false,
    "cat": "Topwear",
    "sub": "Polo T-Shirts",
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.33,
    "chargeKg": 0.75,
    "sorterCount": 18,
    "merchantCount": 1,
    "source": "auto",
    "reviewed": true,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-BWJG-R0 (dup 2)",
    "product": "2-Pack Classic Straight Fit Joggers Black/Pebble / M",
    "ships": 50,
    "hist": [
      [
        0.75,
        5
      ],
      [
        1,
        45
      ]
    ],
    "mpw": 1,
    "spread": 10,
    "dq": true,
    "cat": "Bottomwear",
    "sub": "Joggers",
    "packId": "FLY-L",
    "deadKg": 1,
    "volKg": 0.53,
    "chargeKg": 1,
    "sorterCount": 33,
    "merchantCount": 0,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-TWZP-R0002-BL5",
    "product": "Wanderer - Travel Hoodie Black / XL",
    "ships": 50,
    "hist": [
      [
        1,
        50
      ]
    ],
    "mpw": 1,
    "spread": 0,
    "dq": false,
    "cat": "Topwear",
    "sub": "Hoodies",
    "packId": "FLY-L",
    "deadKg": 1,
    "volKg": 0.84,
    "chargeKg": 1,
    "sorterCount": 26,
    "merchantCount": 2,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-BWJG-R",
    "product": "2-Pack Classic Straight Fit Joggers Black/Pebble / XL",
    "ships": 50,
    "hist": [
      [
        0.75,
        15
      ],
      [
        1,
        35
      ]
    ],
    "mpw": 1,
    "spread": 30,
    "dq": false,
    "cat": "Bottomwear",
    "sub": "Joggers",
    "packId": "FLY-L",
    "deadKg": 0.74,
    "volKg": 1,
    "chargeKg": 1,
    "sorterCount": 10,
    "merchantCount": 0,
    "source": "oms",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-TWRT-C0694-WR3",
    "product": "Colorado Oversized T-Shirt Wine Red / M",
    "ships": 50,
    "hist": [
      [
        0.75,
        50
      ]
    ],
    "mpw": 0.75,
    "spread": 0,
    "dq": false,
    "cat": "Topwear",
    "sub": "T-Shirts",
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.36,
    "chargeKg": 0.75,
    "sorterCount": 31,
    "merchantCount": 0,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-TWRT-C13",
    "product": "Call Of The Ocean Oversized T-Shirt Powder Blue / L",
    "ships": 50,
    "hist": [
      [
        0.75,
        50
      ]
    ],
    "mpw": 0.75,
    "spread": 0,
    "dq": false,
    "cat": "Topwear",
    "sub": "T-Shirts",
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.37,
    "chargeKg": 0.75,
    "sorterCount": 27,
    "merchantCount": 4,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-TWPO-R0002-DR5",
    "product": "Classic Polo T-Shirt Dusty Rose / XL",
    "ships": 49,
    "hist": [
      [
        0.75,
        37
      ],
      [
        1,
        12
      ]
    ],
    "mpw": 0.75,
    "spread": 24.5,
    "dq": false,
    "cat": "Topwear",
    "sub": "Polo T-Shirts",
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.51,
    "chargeKg": 0.75,
    "sorterCount": 16,
    "merchantCount": 0,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "NBJUN24MJ05-Men-Joggers-Black-M",
    "product": "NBJUN24MJ05-Men-Joggers-Black-M",
    "ships": 49,
    "hist": [
      [
        0.75,
        31
      ],
      [
        1,
        16
      ],
      [
        1.5,
        2
      ]
    ],
    "mpw": 0.75,
    "spread": 36.7,
    "dq": false,
    "cat": "Bottomwear",
    "sub": "Joggers",
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.31,
    "chargeKg": 0.75,
    "sorterCount": 29,
    "merchantCount": 4,
    "source": "auto",
    "reviewed": true,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1W-TWTS-C0969-OG3",
    "product": "Hope Women Classic Fit Tee Olive Green / M",
    "ships": 49,
    "hist": [
      [
        0.75,
        49
      ]
    ],
    "mpw": 0.75,
    "spread": 0,
    "dq": false,
    "cat": "Topwear",
    "sub": "T-Shirts",
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.43,
    "chargeKg": 0.75,
    "sorterCount": 9,
    "merchantCount": 1,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-BWJG-R0090-AW5",
    "product": "Relaxed Fit Cargo Joggers Arctic Wolf / XL",
    "ships": 49,
    "hist": [
      [
        0.75,
        18
      ],
      [
        1,
        31
      ]
    ],
    "mpw": 1,
    "spread": 36.7,
    "dq": false,
    "cat": "Bottomwear",
    "sub": "Cargo Pants",
    "packId": "FLY-L",
    "deadKg": 1,
    "volKg": 0.78,
    "chargeKg": 1,
    "sorterCount": 11,
    "merchantCount": 0,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-TWPO-R0002-PT4",
    "product": "Classic Polo T-Shirt Petunia / L",
    "ships": 48,
    "hist": [
      [
        0.75,
        48
      ]
    ],
    "mpw": 0.75,
    "spread": 0,
    "dq": false,
    "cat": "Topwear",
    "sub": "Polo T-Shirts",
    "packId": "FLY-GEN",
    "deadKg": 0.36,
    "volKg": 0.75,
    "chargeKg": 0.75,
    "sorterCount": 9,
    "merchantCount": 0,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-TWPO-R0002-DR3",
    "product": "Classic Polo T-Shirt Dusty Rose / M",
    "ships": 48,
    "hist": [
      [
        0.75,
        46
      ],
      [
        1,
        2
      ]
    ],
    "mpw": 0.75,
    "spread": 4.2,
    "dq": false,
    "cat": "Topwear",
    "sub": "Polo T-Shirts",
    "packId": "FLY-GEN",
    "deadKg": 0.62,
    "volKg": 0.75,
    "chargeKg": 0.75,
    "sorterCount": 14,
    "merchantCount": 1,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-TWTS-C0268-MN3",
    "product": "Wander Soul Classic Fit T-Shirt Marine / M",
    "ships": 48,
    "hist": [
      [
        0.75,
        48
      ]
    ],
    "mpw": 0.75,
    "spread": 0,
    "dq": false,
    "cat": "Topwear",
    "sub": "T-Shirts",
    "packId": "FLY-GEN",
    "deadKg": 0.45,
    "volKg": 0.75,
    "chargeKg": 0.75,
    "sorterCount": 15,
    "merchantCount": 6,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-LWCS-R",
    "product": "Oversized Klaeinn Cargo Pocket Co-ord Set Cannoli / L",
    "ships": 48,
    "hist": [
      [
        0.75,
        10
      ],
      [
        1,
        38
      ]
    ],
    "mpw": 1,
    "spread": 20.8,
    "dq": false,
    "cat": "Sets",
    "sub": "Co-ord Sets",
    "packId": "FLY-L",
    "deadKg": 0.7,
    "volKg": 1,
    "chargeKg": 1,
    "sorterCount": 13,
    "merchantCount": 0,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-BWJG-R0137-BL3",
    "product": "Wanderer - Travel Pants Black / M",
    "ships": 48,
    "hist": [
      [
        1,
        45
      ],
      [
        1.5,
        3
      ]
    ],
    "mpw": 1,
    "spread": 6.3,
    "dq": false,
    "cat": "Bottomwear",
    "sub": "Travel Wear",
    "packId": "FLY-L",
    "deadKg": 0.45,
    "volKg": 1,
    "chargeKg": 1,
    "sorterCount": 15,
    "merchantCount": 0,
    "source": "oms",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "NBJUN24MJ05-Men-Joggers-Navy Blue-L",
    "product": "NBJUN24MJ05-Men-Joggers-Navy Blue-L",
    "ships": 48,
    "hist": [
      [
        0.75,
        11
      ],
      [
        1,
        37
      ]
    ],
    "mpw": 1,
    "spread": 22.9,
    "dq": false,
    "cat": "Bottomwear",
    "sub": "Joggers",
    "packId": "FLY-L",
    "deadKg": 1,
    "volKg": 0.53,
    "chargeKg": 1,
    "sorterCount": 28,
    "merchantCount": 2,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-BWJG-R0149-BL7",
    "product": "Classic Straight Fit Joggers Black / XXXL",
    "ships": 48,
    "hist": [
      [
        0.75,
        19
      ],
      [
        1.5,
        27
      ],
      [
        2,
        1
      ],
      [
        3,
        1
      ]
    ],
    "mpw": 1.5,
    "spread": 43.8,
    "dq": false,
    "cat": "Bottomwear",
    "sub": "Joggers",
    "packId": "BOX-S",
    "deadKg": 1.5,
    "volKg": 0.61,
    "chargeKg": 1.5,
    "sorterCount": 15,
    "merchantCount": 2,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-BWJG-R0137-OL5",
    "product": "Wanderer - Travel Pants Oatmeal / XL",
    "ships": 47,
    "hist": [
      [
        1,
        47
      ]
    ],
    "mpw": 1,
    "spread": 0,
    "dq": false,
    "cat": "Bottomwear",
    "sub": "Travel Wear",
    "packId": "FLY-L",
    "deadKg": 0.7,
    "volKg": 1,
    "chargeKg": 1,
    "sorterCount": 29,
    "merchantCount": 4,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-TWPO-R0002-PN5",
    "product": "Classic Polo T-Shirt Pecan / XL",
    "ships": 47,
    "hist": [
      [
        0.75,
        39
      ],
      [
        1,
        8
      ]
    ],
    "mpw": 0.75,
    "spread": 17,
    "dq": false,
    "cat": "Topwear",
    "sub": "Polo T-Shirts",
    "packId": "FLY-GEN",
    "deadKg": 0.52,
    "volKg": 0.75,
    "chargeKg": 0.75,
    "sorterCount": 15,
    "merchantCount": 0,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "NBSS24MC10-Men-Coord Set-Navy Blue-L",
    "product": "NBSS24MC10-Men-Coord Set-Navy Blue-L",
    "ships": 47,
    "hist": [
      [
        0.75,
        25
      ],
      [
        1,
        2
      ],
      [
        1.5,
        20
      ]
    ],
    "mpw": 0.75,
    "spread": 46.8,
    "dq": false,
    "cat": "Sets",
    "sub": "Co-ord Sets",
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.43,
    "chargeKg": 0.75,
    "sorterCount": 19,
    "merchantCount": 5,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-LWCS-R0174-NS4",
    "product": "Oversized Lucas Co-ord Set Nutshell / L",
    "ships": 47,
    "hist": [
      [
        0.75,
        21
      ],
      [
        1,
        26
      ]
    ],
    "mpw": 1,
    "spread": 44.7,
    "dq": false,
    "cat": "Sets",
    "sub": "Co-ord Sets",
    "packId": "FLY-L",
    "deadKg": 1,
    "volKg": 0.44,
    "chargeKg": 1,
    "sorterCount": 22,
    "merchantCount": 2,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-LWCS-R0179-OG4",
    "product": "West Coast Co-ord Sets Olive Green / L",
    "ships": 46,
    "hist": [
      [
        0.75,
        11
      ],
      [
        1,
        35
      ]
    ],
    "mpw": 1,
    "spread": 23.9,
    "dq": false,
    "cat": "Sets",
    "sub": "Co-ord Sets",
    "packId": "FLY-L",
    "deadKg": 1,
    "volKg": 0.73,
    "chargeKg": 1,
    "sorterCount": 15,
    "merchantCount": 2,
    "source": "auto",
    "reviewed": true,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-TWTS-C02",
    "product": "Imperfectly Perfect Classic Fit T-Shirt XL / Marine",
    "ships": 46,
    "hist": [
      [
        0.75,
        46
      ]
    ],
    "mpw": 0.75,
    "spread": 0,
    "dq": false,
    "cat": "Topwear",
    "sub": "T-Shirts",
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.34,
    "chargeKg": 0.75,
    "sorterCount": 12,
    "merchantCount": 1,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-TWPO-R0002-WT4",
    "product": "Classic Polo T-Shirt White / L",
    "ships": 46,
    "hist": [
      [
        0.75,
        36
      ],
      [
        1,
        10
      ]
    ],
    "mpw": 0.75,
    "spread": 21.7,
    "dq": false,
    "cat": "Topwear",
    "sub": "Polo T-Shirts",
    "packId": "FLY-GEN",
    "deadKg": 0.46,
    "volKg": 0.75,
    "chargeKg": 0.75,
    "sorterCount": 9,
    "merchantCount": 0,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-BWCP-R0002-BL5",
    "product": "Men's Super Baggy Cargo Pants Black / XL",
    "ships": 46,
    "hist": [
      [
        0.75,
        34
      ],
      [
        1,
        12
      ]
    ],
    "mpw": 0.75,
    "spread": 26.1,
    "dq": false,
    "cat": "Bottomwear",
    "sub": "Cargo Pants",
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.6,
    "chargeKg": 0.75,
    "sorterCount": 23,
    "merchantCount": 1,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-BWCP-R0002-NB4",
    "product": "Men's Super Baggy Cargo Pants Navy Blue / L",
    "ships": 46,
    "hist": [
      [
        0.75,
        11
      ],
      [
        1,
        34
      ],
      [
        1.5,
        1
      ]
    ],
    "mpw": 1,
    "spread": 26.1,
    "dq": false,
    "cat": "Bottomwear",
    "sub": "Cargo Pants",
    "packId": "FLY-L",
    "deadKg": 0.71,
    "volKg": 1,
    "chargeKg": 1,
    "sorterCount": 15,
    "merchantCount": 2,
    "source": "oms",
    "reviewed": true,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-TWRT-C1476-GM4",
    "product": "Mount Fuji Oversized T-Shirt Grey Melange / L",
    "ships": 46,
    "hist": [
      [
        0.75,
        46
      ]
    ],
    "mpw": 0.75,
    "spread": 0,
    "dq": false,
    "cat": "Topwear",
    "sub": "T-Shirts",
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.36,
    "chargeKg": 0.75,
    "sorterCount": 17,
    "merchantCount": 0,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-BWJG-R0137-OL6",
    "product": "Wanderer - Travel Pants Oatmeal / XXL",
    "ships": 45,
    "hist": [
      [
        0.75,
        5
      ],
      [
        1,
        40
      ]
    ],
    "mpw": 1,
    "spread": 11.1,
    "dq": false,
    "cat": "Bottomwear",
    "sub": "Travel Wear",
    "packId": "FLY-L",
    "deadKg": 1,
    "volKg": 0.49,
    "chargeKg": 1,
    "sorterCount": 22,
    "merchantCount": 2,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "2-Pack Classic Straight Fit Joggers Navy Blue/Eiffel Tower / L (sku -",
    "product": "2-Pack Classic Straight Fit Joggers Navy Blue/Eiffel Tower / L",
    "ships": 45,
    "hist": [
      [
        0.75,
        34
      ],
      [
        1,
        11
      ]
    ],
    "mpw": 0.75,
    "spread": 24.4,
    "dq": true,
    "cat": "Bottomwear",
    "sub": "Joggers",
    "packId": "FLY-GEN",
    "deadKg": 0.59,
    "volKg": 0.75,
    "chargeKg": 0.75,
    "sorterCount": 17,
    "merchantCount": 1,
    "source": "auto",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "NBAW24MJ14-Men-Joggers-Eiffel tower-XL",
    "product": "NBAW24MJ14-Men-Joggers-Eiffel tower-XL",
    "ships": 45,
    "hist": [
      [
        1,
        45
      ]
    ],
    "mpw": 1,
    "spread": 0,
    "dq": false,
    "cat": "Bottomwear",
    "sub": "Joggers",
    "packId": "FLY-L",
    "deadKg": 1,
    "volKg": 0.44,
    "chargeKg": 1,
    "sorterCount": 13,
    "merchantCount": 0,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-LWCS-R0175-B",
    "product": "Oversized Rio Colorblocked Co-ord Set Black / L",
    "ships": 45,
    "hist": [
      [
        0.75,
        5
      ],
      [
        1,
        40
      ]
    ],
    "mpw": 1,
    "spread": 11.1,
    "dq": false,
    "cat": "Sets",
    "sub": "Co-ord Sets",
    "packId": "FLY-L",
    "deadKg": 1,
    "volKg": 0.5,
    "chargeKg": 1,
    "sorterCount": 9,
    "merchantCount": 2,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-TWPO-R0002-BU6",
    "product": "Classic Polo T-Shirt Burgundy / XXL",
    "ships": 45,
    "hist": [
      [
        0.75,
        45
      ]
    ],
    "mpw": 0.75,
    "spread": 0,
    "dq": false,
    "cat": "Topwear",
    "sub": "Polo T-Shirts",
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.33,
    "chargeKg": 0.75,
    "sorterCount": 28,
    "merchantCount": 2,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "2-Pack Classic Straight Fit Joggers Navy Blue/Eiffel Tower / M (sku -",
    "product": "2-Pack Classic Straight Fit Joggers Navy Blue/Eiffel Tower / M",
    "ships": 45,
    "hist": [
      [
        0.75,
        7
      ],
      [
        1,
        38
      ]
    ],
    "mpw": 1,
    "spread": 15.6,
    "dq": true,
    "cat": "Bottomwear",
    "sub": "Joggers",
    "packId": "FLY-L",
    "deadKg": 1,
    "volKg": 0.64,
    "chargeKg": 1,
    "sorterCount": 29,
    "merchantCount": 0,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-TWSS-R0032-NB3",
    "product": "Mock Neck  Sweatshirt Navy Blue / M",
    "ships": 44,
    "hist": [
      [
        0.75,
        10
      ],
      [
        1,
        34
      ]
    ],
    "mpw": 1,
    "spread": 22.7,
    "dq": false,
    "cat": "Topwear",
    "sub": "Sweatshirts",
    "packId": "FLY-L",
    "deadKg": 0.6,
    "volKg": 1,
    "chargeKg": 1,
    "sorterCount": 27,
    "merchantCount": 1,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-TWPO-R0002-BL4",
    "product": "Classic Polo T-Shirt Black / L",
    "ships": 44,
    "hist": [
      [
        0.75,
        43
      ],
      [
        1,
        1
      ]
    ],
    "mpw": 0.75,
    "spread": 2.3,
    "dq": false,
    "cat": "Topwear",
    "sub": "Polo T-Shirts",
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.58,
    "chargeKg": 0.75,
    "sorterCount": 25,
    "merchantCount": 2,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-TWTS-C1036-WT4",
    "product": "Serendipity Classic Fit T-Shirt L / White",
    "ships": 44,
    "hist": [
      [
        0.75,
        44
      ]
    ],
    "mpw": 0.75,
    "spread": 0,
    "dq": false,
    "cat": "Topwear",
    "sub": "T-Shirts",
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.56,
    "chargeKg": 0.75,
    "sorterCount": 14,
    "merchantCount": 6,
    "source": "auto",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-TWTS-C0889-P",
    "product": "THINK BIG Classic Fit T-Shirt XXL / Powder Blue",
    "ships": 44,
    "hist": [
      [
        0.75,
        44
      ]
    ],
    "mpw": 0.75,
    "spread": 0,
    "dq": false,
    "cat": "Topwear",
    "sub": "T-Shirts",
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.42,
    "chargeKg": 0.75,
    "sorterCount": 27,
    "merchantCount": 6,
    "source": "oms",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-BWJG-R0149-PB6",
    "product": "Classic Straight Fit Joggers Pebble / XXL",
    "ships": 44,
    "hist": [
      [
        0.75,
        35
      ],
      [
        1,
        8
      ],
      [
        1.5,
        1
      ]
    ],
    "mpw": 0.75,
    "spread": 20.5,
    "dq": false,
    "cat": "Bottomwear",
    "sub": "Joggers",
    "packId": "FLY-GEN",
    "deadKg": 0.37,
    "volKg": 0.75,
    "chargeKg": 0.75,
    "sorterCount": 15,
    "merchantCount": 3,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-BWJG-R0137-STG5",
    "product": "Wanderer - Travel Pants Steel Grey / XL",
    "ships": 44,
    "hist": [
      [
        0.75,
        16
      ],
      [
        1,
        28
      ]
    ],
    "mpw": 1,
    "spread": 36.4,
    "dq": false,
    "cat": "Bottomwear",
    "sub": "Travel Wear",
    "packId": "FLY-L",
    "deadKg": 0.68,
    "volKg": 1,
    "chargeKg": 1,
    "sorterCount": 22,
    "merchantCount": 6,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-BWJG-R0090-AW3",
    "product": "Relaxed Fit Cargo Joggers Arctic Wolf / M",
    "ships": 44,
    "hist": [
      [
        0.75,
        17
      ],
      [
        1,
        27
      ]
    ],
    "mpw": 1,
    "spread": 38.6,
    "dq": false,
    "cat": "Bottomwear",
    "sub": "Cargo Pants",
    "packId": "FLY-L",
    "deadKg": 1,
    "volKg": 0.65,
    "chargeKg": 1,
    "sorterCount": 20,
    "merchantCount": 4,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-BWJG-R0137-STG4",
    "product": "Wanderer - Travel Pants Steel Grey / L",
    "ships": 43,
    "hist": [
      [
        0.75,
        13
      ],
      [
        1,
        30
      ]
    ],
    "mpw": 1,
    "spread": 30.2,
    "dq": false,
    "cat": "Bottomwear",
    "sub": "Travel Wear",
    "packId": "FLY-L",
    "deadKg": 1,
    "volKg": 0.81,
    "chargeKg": 1,
    "sorterCount": 29,
    "merchantCount": 2,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-TWPO-R0002-LT3",
    "product": "Classic Polo T-Shirt Latte / M",
    "ships": 43,
    "hist": [
      [
        0.75,
        43
      ]
    ],
    "mpw": 0.75,
    "spread": 0,
    "dq": false,
    "cat": "Topwear",
    "sub": "Polo T-Shirts",
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.62,
    "chargeKg": 0.75,
    "sorterCount": 23,
    "merchantCount": 2,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "NBJUN24MJ05 Joggers (Pack of 2)-Men-Joggers-BL/ET-L",
    "product": "NBJUN24MJ05 Joggers (Pack of 2)-Men-Joggers-BL/ET-L",
    "ships": 43,
    "hist": [
      [
        0.75,
        30
      ],
      [
        1,
        13
      ]
    ],
    "mpw": 0.75,
    "spread": 30.2,
    "dq": false,
    "cat": "Bottomwear",
    "sub": "Joggers",
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.52,
    "chargeKg": 0.75,
    "sorterCount": 27,
    "merchantCount": 2,
    "source": "auto",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-TWPO-R0002-MUM5",
    "product": "Classic Polo T-Shirt Mauve Mist / XL",
    "ships": 43,
    "hist": [
      [
        0.75,
        43
      ]
    ],
    "mpw": 0.75,
    "spread": 0,
    "dq": false,
    "cat": "Topwear",
    "sub": "Polo T-Shirts",
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.46,
    "chargeKg": 0.75,
    "sorterCount": 12,
    "merchantCount": 0,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-TWTS-C0612- (dup 2)",
    "product": "Never Say No Classic Fit T-Shirt Powder Blue / M",
    "ships": 43,
    "hist": [
      [
        0.75,
        43
      ]
    ],
    "mpw": 0.75,
    "spread": 0,
    "dq": true,
    "cat": "Topwear",
    "sub": "T-Shirts",
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.62,
    "chargeKg": 0.75,
    "sorterCount": 11,
    "merchantCount": 2,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "NBJUN24MJ05-Men-Joggers-Black-XL",
    "product": "NBJUN24MJ05-Men-Joggers-Black-XL",
    "ships": 43,
    "hist": [
      [
        0.75,
        2
      ],
      [
        1,
        41
      ]
    ],
    "mpw": 1,
    "spread": 4.7,
    "dq": false,
    "cat": "Bottomwear",
    "sub": "Joggers",
    "packId": "FLY-L",
    "deadKg": 0.75,
    "volKg": 1,
    "chargeKg": 1,
    "sorterCount": 23,
    "merchantCount": 5,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-TWTS-C0990-MN4",
    "product": "Bon Voyage Classic Fit T-Shirt L / Periwinkle",
    "ships": 42,
    "hist": [
      [
        0.75,
        42
      ]
    ],
    "mpw": 0.75,
    "spread": 0,
    "dq": false,
    "cat": "Topwear",
    "sub": "T-Shirts",
    "packId": "FLY-GEN",
    "deadKg": 0.37,
    "volKg": 0.75,
    "chargeKg": 0.75,
    "sorterCount": 27,
    "merchantCount": 0,
    "source": "catalogue",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  },
  {
    "sku": "1M-LWCS-R0174-NS5",
    "product": "Oversized Lucas Co-ord Set Nutshell / XL",
    "ships": 42,
    "hist": [
      [
        0.75,
        18
      ],
      [
        1,
        24
      ]
    ],
    "mpw": 1,
    "spread": 42.9,
    "dq": false,
    "cat": "Sets",
    "sub": "Co-ord Sets",
    "packId": "FLY-L",
    "deadKg": 1,
    "volKg": 0.66,
    "chargeKg": 1,
    "sorterCount": 29,
    "merchantCount": 1,
    "source": "oms",
    "reviewed": false,
    "motherOf": null,
    "childOf": null
  }
]

export const COMBOS = [
  {
    "key": "Laugh + Live + Love & Eat Bamboo Oversized T-Shirt Bone White / L (sku - 1M-TWRT-C2581-BWH4)",
    "items": [
      {
        "sku": "Love & Eat Bamboo Oversized T-Shirt Bone White / L (sku - 1M-TWRT-C2581-BWH4)",
        "qty": 1
      }
    ],
    "ships": 17,
    "hist": [
      [
        0.75,
        17
      ]
    ],
    "mpw": 0.75,
    "spread": 0,
    "packId": "FLY-GEN",
    "deadKg": 0.54,
    "volKg": 0.75,
    "chargeKg": 0.75,
    "sorterCount": 6,
    "merchantCount": 0,
    "itemSkus": null,
    "priceable": false
  },
  {
    "key": "Laugh + Live + Love & Eat Bamboo Oversized T-Shirt Bone White / M (sku - 1M-TWRT-C2581-BWH3)",
    "items": [
      {
        "sku": "Love & Eat Bamboo Oversized T-Shirt Bone White / M (sku - 1M-TWRT-C2581-BWH3)",
        "qty": 1
      }
    ],
    "ships": 9,
    "hist": [
      [
        0.75,
        9
      ]
    ],
    "mpw": 0.75,
    "spread": 0,
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.3,
    "chargeKg": 0.75,
    "sorterCount": 4,
    "merchantCount": 0,
    "itemSkus": null,
    "priceable": false
  },
  {
    "key": "Laugh + Live + Love & Eat Bamboo Oversized T-Shirt Bone White / XL (sku - 1M-TWRT-C2581-BWH5)",
    "items": [
      {
        "sku": "Love & Eat Bamboo Oversized T-Shirt Bone White / XL (sku - 1M-TWRT-C2581-BWH5)",
        "qty": 1
      }
    ],
    "ships": 8,
    "hist": [
      [
        0.75,
        8
      ]
    ],
    "mpw": 0.75,
    "spread": 0,
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.53,
    "chargeKg": 0.75,
    "sorterCount": 1,
    "merchantCount": 4,
    "itemSkus": null,
    "priceable": false
  },
  {
    "key": "Great Highway Classic Fit T-Shirt Wine Red / L (sku - 1M-TWTS-C2324-WR4) + Ocean Beach",
    "items": [
      {
        "sku": "Great Highway Classic Fit T-Shirt Wine Red / L (sku - 1M-TWTS-C2324-WR4)",
        "qty": 2
      }
    ],
    "ships": 7,
    "hist": [
      [
        0.75,
        7
      ]
    ],
    "mpw": 0.75,
    "spread": 0,
    "packId": "FLY-GEN",
    "deadKg": 0.47,
    "volKg": 0.75,
    "chargeKg": 0.75,
    "sorterCount": 2,
    "merchantCount": 2,
    "itemSkus": null,
    "priceable": false
  },
  {
    "key": "Laugh + Live + Love & Eat Bamboo Oversized T-Shirt Bone White / XXL (sku - 1M-TWRT-C2581-BWH6)",
    "items": [
      {
        "sku": "Love & Eat Bamboo Oversized T-Shirt Bone White / XXL (sku - 1M-TWRT-C2581-BWH6)",
        "qty": 1
      }
    ],
    "ships": 7,
    "hist": [
      [
        0.75,
        7
      ]
    ],
    "mpw": 0.75,
    "spread": 0,
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.56,
    "chargeKg": 0.75,
    "sorterCount": 2,
    "merchantCount": 0,
    "itemSkus": null,
    "priceable": false
  },
  {
    "key": "Great Highway Classic Fit T-Shirt Wine Red / XXL (sku - 1M-TWTS-C2324-WR6) + Ocean Beach",
    "items": [
      {
        "sku": "Great Highway Classic Fit T-Shirt Wine Red / XXL (sku - 1M-TWTS-C2324-WR6)",
        "qty": 1
      }
    ],
    "ships": 6,
    "hist": [
      [
        0.75,
        6
      ]
    ],
    "mpw": 0.75,
    "spread": 0,
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.63,
    "chargeKg": 0.75,
    "sorterCount": 1,
    "merchantCount": 0,
    "itemSkus": null,
    "priceable": false
  },
  {
    "key": "Nobero Jogger V2-Men Joggers-Black-M + Nobero Jogger V2-Men-Joggers-Arctic Wolf-M",
    "items": [
      {
        "sku": "Nobero Jogger V2-Men Joggers-Black-M",
        "qty": 1
      },
      {
        "sku": "Nobero Jogger V2-Men-Joggers-Arctic Wolf-M",
        "qty": 1
      }
    ],
    "ships": 5,
    "hist": [
      [
        0.75,
        1
      ],
      [
        1.5,
        2
      ],
      [
        2,
        2
      ]
    ],
    "mpw": 1.5,
    "spread": 60,
    "packId": "BOX-S",
    "deadKg": 1.5,
    "volKg": 0.87,
    "chargeKg": 1.5,
    "sorterCount": 2,
    "merchantCount": 5,
    "itemSkus": null,
    "priceable": false
  },
  {
    "key": "NBJUN24MJ05-Men-Joggers-Black-XXXL + NBJUN24MJ05-Men-Joggers-Navy Blue-XXXL",
    "items": [
      {
        "sku": "NBJUN24MJ05-Men-Joggers-Black-XXXL",
        "qty": 1
      },
      {
        "sku": "NBJUN24MJ05-Men-Joggers-Navy Blue-XXXL",
        "qty": 1
      }
    ],
    "ships": 5,
    "hist": [
      [
        0.75,
        3
      ],
      [
        2.5,
        2
      ]
    ],
    "mpw": 0.75,
    "spread": 40,
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.38,
    "chargeKg": 0.75,
    "sorterCount": 2,
    "merchantCount": 5,
    "itemSkus": null,
    "priceable": false
  },
  {
    "key": "Laugh + Live + Love & Eat Bamboo Oversized T-Shirt Bone White / XXXL (sku - 1M-TWRT-C2581-BWH7)",
    "items": [
      {
        "sku": "Love & Eat Bamboo Oversized T-Shirt Bone White / XXXL (sku - 1M-TWRT-C2581-BWH7)",
        "qty": 1
      }
    ],
    "ships": 5,
    "hist": [
      [
        0.75,
        5
      ]
    ],
    "mpw": 0.75,
    "spread": 0,
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.56,
    "chargeKg": 0.75,
    "sorterCount": 1,
    "merchantCount": 6,
    "itemSkus": null,
    "priceable": false
  },
  {
    "key": "Laugh + Live + Love & Eat Bamboo Oversized T-Shirt Bone White / S (sku - 1M-TWRT-C2581-BWH2)",
    "items": [
      {
        "sku": "Love & Eat Bamboo Oversized T-Shirt Bone White / S (sku - 1M-TWRT-C2581-BWH2)",
        "qty": 1
      }
    ],
    "ships": 5,
    "hist": [
      [
        0.75,
        5
      ]
    ],
    "mpw": 0.75,
    "spread": 0,
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.31,
    "chargeKg": 0.75,
    "sorterCount": 2,
    "merchantCount": 0,
    "itemSkus": null,
    "priceable": false
  },
  {
    "key": "NBJUN24MJ05-Men-Joggers-Black-L + NBJUN24MJ05-Men-Joggers-Eiffel Tower-L",
    "items": [
      {
        "sku": "NBJUN24MJ05-Men-Joggers-Black-L",
        "qty": 1
      },
      {
        "sku": "NBJUN24MJ05-Men-Joggers-Eiffel Tower-L",
        "qty": 1
      }
    ],
    "ships": 4,
    "hist": [
      [
        0.75,
        3
      ],
      [
        1,
        1
      ]
    ],
    "mpw": 0.75,
    "spread": 25,
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.57,
    "chargeKg": 0.75,
    "sorterCount": 2,
    "merchantCount": 0,
    "itemSkus": null,
    "priceable": false
  },
  {
    "key": "Nobero Shorts V2-Men Shorts-Black-L + Nobero Shorts V2-Men Shorts-Olive Green-L",
    "items": [
      {
        "sku": "Nobero Shorts V2-Men Shorts-Black-L",
        "qty": 1
      },
      {
        "sku": "Nobero Shorts V2-Men Shorts-Olive Green-L",
        "qty": 1
      }
    ],
    "ships": 4,
    "hist": [
      [
        0.75,
        1
      ],
      [
        1,
        3
      ]
    ],
    "mpw": 1,
    "spread": 25,
    "packId": "FLY-L",
    "deadKg": 1,
    "volKg": 0.84,
    "chargeKg": 1,
    "sorterCount": 1,
    "merchantCount": 3,
    "itemSkus": null,
    "priceable": false
  },
  {
    "key": "Classic Polo-Men-Polos-Burgundy-XL + Classic Polo-Men-Polos-Dusty Rose-XL",
    "items": [
      {
        "sku": "Classic Polo-Men-Polos-Burgundy-XL",
        "qty": 1
      },
      {
        "sku": "Classic Polo-Men-Polos-Dusty Rose-XL",
        "qty": 1
      }
    ],
    "ships": 4,
    "hist": [
      [
        0.75,
        1
      ],
      [
        1,
        3
      ]
    ],
    "mpw": 1,
    "spread": 25,
    "packId": "FLY-L",
    "deadKg": 0.72,
    "volKg": 1,
    "chargeKg": 1,
    "sorterCount": 1,
    "merchantCount": 2,
    "itemSkus": null,
    "priceable": false
  },
  {
    "key": "NBAW24MJ14-Men-Joggers-Eiffel tower-L + NBAW24MJ14-Men-Joggers-Olive Green-L",
    "items": [
      {
        "sku": "NBAW24MJ14-Men-Joggers-Eiffel tower-L",
        "qty": 1
      },
      {
        "sku": "NBAW24MJ14-Men-Joggers-Olive Green-L",
        "qty": 1
      }
    ],
    "ships": 4,
    "hist": [
      [
        1.5,
        4
      ]
    ],
    "mpw": 1.5,
    "spread": 0,
    "packId": "BOX-S",
    "deadKg": 1.5,
    "volKg": 0.7,
    "chargeKg": 1.5,
    "sorterCount": 1,
    "merchantCount": 0,
    "itemSkus": null,
    "priceable": false
  },
  {
    "key": "Nobero Jogger V2-Men Joggers-Grey Melange-XL + Nobero Jogger V2-Men-Joggers-Arctic Wolf-XL",
    "items": [
      {
        "sku": "Nobero Jogger V2-Men Joggers-Grey Melange-XL",
        "qty": 1
      },
      {
        "sku": "Nobero Jogger V2-Men-Joggers-Arctic Wolf-XL",
        "qty": 1
      }
    ],
    "ships": 3,
    "hist": [
      [
        0.75,
        2
      ],
      [
        2,
        1
      ]
    ],
    "mpw": 0.75,
    "spread": 33.3,
    "packId": "FLY-GEN",
    "deadKg": 0.43,
    "volKg": 0.75,
    "chargeKg": 0.75,
    "sorterCount": 2,
    "merchantCount": 6,
    "itemSkus": null,
    "priceable": false
  },
  {
    "key": "NBJUN24MJ05-Men-Joggers-Black-XL + NBJUN24MJ05-Men-Joggers-Navy Blue-XL",
    "items": [
      {
        "sku": "NBJUN24MJ05-Men-Joggers-Black-XL",
        "qty": 1
      },
      {
        "sku": "NBJUN24MJ05-Men-Joggers-Navy Blue-XL",
        "qty": 1
      }
    ],
    "ships": 3,
    "hist": [
      [
        0.75,
        3
      ]
    ],
    "mpw": 0.75,
    "spread": 0,
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.36,
    "chargeKg": 0.75,
    "sorterCount": 0,
    "merchantCount": 0,
    "itemSkus": null,
    "priceable": false
  },
  {
    "key": "NBJUN24MJ05-Men-Joggers-Eiffel Tower-XXL + NBJUN24MJ05-Men-Joggers-Twilight Mauve-XXL",
    "items": [
      {
        "sku": "NBJUN24MJ05-Men-Joggers-Eiffel Tower-XXL",
        "qty": 1
      },
      {
        "sku": "NBJUN24MJ05-Men-Joggers-Twilight Mauve-XXL",
        "qty": 1
      }
    ],
    "ships": 3,
    "hist": [
      [
        0.75,
        1
      ],
      [
        1,
        2
      ]
    ],
    "mpw": 1,
    "spread": 33.3,
    "packId": "FLY-L",
    "deadKg": 1,
    "volKg": 0.69,
    "chargeKg": 1,
    "sorterCount": 1,
    "merchantCount": 0,
    "itemSkus": null,
    "priceable": false
  },
  {
    "key": "Classic Straight Fit Joggers Eiffel Tower / M (sku - 1M-BWJG-R0149-ET3) + Classic Straight Fit Joggers Olive G",
    "items": [
      {
        "sku": "Classic Straight Fit Joggers Eiffel Tower / M (sku - 1M-BWJG-R0149-ET3)",
        "qty": 1
      }
    ],
    "ships": 3,
    "hist": [
      [
        1.5,
        2
      ],
      [
        2,
        1
      ]
    ],
    "mpw": 1.5,
    "spread": 33.3,
    "packId": "BOX-S",
    "deadKg": 1.5,
    "volKg": 0.72,
    "chargeKg": 1.5,
    "sorterCount": 1,
    "merchantCount": 1,
    "itemSkus": [
      "1M-BWJG-R0149-ET3"
    ],
    "priceable": false
  },
  {
    "key": "Nobero Jogger V2-Men Joggers-Black-L + Nobero Jogger V2-Men Joggers-Navy Blue-L",
    "items": [
      {
        "sku": "Nobero Jogger V2-Men Joggers-Black-L",
        "qty": 1
      },
      {
        "sku": "Nobero Jogger V2-Men Joggers-Navy Blue-L",
        "qty": 1
      }
    ],
    "ships": 3,
    "hist": [
      [
        0.75,
        1
      ],
      [
        1,
        2
      ]
    ],
    "mpw": 1,
    "spread": 33.3,
    "packId": "FLY-L",
    "deadKg": 1,
    "volKg": 0.65,
    "chargeKg": 1,
    "sorterCount": 0,
    "merchantCount": 0,
    "itemSkus": null,
    "priceable": false
  },
  {
    "key": "Classic Polo-Men-Polos-Burgundy-L + Classic Polo-Men-Polos-White-L",
    "items": [
      {
        "sku": "Classic Polo-Men-Polos-Burgundy-L",
        "qty": 1
      },
      {
        "sku": "Classic Polo-Men-Polos-White-L",
        "qty": 1
      }
    ],
    "ships": 3,
    "hist": [
      [
        0.75,
        2
      ],
      [
        1,
        1
      ]
    ],
    "mpw": 0.75,
    "spread": 33.3,
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.46,
    "chargeKg": 0.75,
    "sorterCount": 1,
    "merchantCount": 0,
    "itemSkus": null,
    "priceable": false
  },
  {
    "key": "NBJUN24MJ05-Men-Joggers-Eiffel Tower-XXXL + NBJUN24MJ05-Men-Joggers-Navy Blue-XXXL",
    "items": [
      {
        "sku": "NBJUN24MJ05-Men-Joggers-Eiffel Tower-XXXL",
        "qty": 1
      },
      {
        "sku": "NBJUN24MJ05-Men-Joggers-Navy Blue-XXXL",
        "qty": 1
      }
    ],
    "ships": 3,
    "hist": [
      [
        0.75,
        1
      ],
      [
        3,
        2
      ]
    ],
    "mpw": 3,
    "spread": 33.3,
    "packId": "BOX-GEN",
    "deadKg": 3,
    "volKg": 2.29,
    "chargeKg": 3,
    "sorterCount": 0,
    "merchantCount": 6,
    "itemSkus": null,
    "priceable": false
  },
  {
    "key": "Classic Polo-Men-Polos-Burgundy-S + Classic Polo-Polo-Black-S",
    "items": [
      {
        "sku": "Classic Polo-Men-Polos-Burgundy-S",
        "qty": 1
      },
      {
        "sku": "Classic Polo-Polo-Black-S",
        "qty": 1
      }
    ],
    "ships": 3,
    "hist": [
      [
        0.75,
        3
      ]
    ],
    "mpw": 0.75,
    "spread": 0,
    "packId": "FLY-GEN",
    "deadKg": 0.32,
    "volKg": 0.75,
    "chargeKg": 0.75,
    "sorterCount": 1,
    "merchantCount": 0,
    "itemSkus": null,
    "priceable": false
  },
  {
    "key": "NBJUN24MJ05-Men-Joggers-Black-XL + NBJUN24MJ05-Men-Joggers-Eiffel Tower-XL",
    "items": [
      {
        "sku": "NBJUN24MJ05-Men-Joggers-Black-XL",
        "qty": 1
      },
      {
        "sku": "NBJUN24MJ05-Men-Joggers-Eiffel Tower-XL",
        "qty": 1
      }
    ],
    "ships": 3,
    "hist": [
      [
        0.75,
        1
      ],
      [
        1.5,
        1
      ],
      [
        2.5,
        1
      ]
    ],
    "mpw": 0.75,
    "spread": 66.7,
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.43,
    "chargeKg": 0.75,
    "sorterCount": 1,
    "merchantCount": 3,
    "itemSkus": [
      "NBJUN24MJ05-Men-Joggers-Black-XL",
      "NBJUN24MJ05-Men-Joggers-Eiffel Tower-XL"
    ],
    "priceable": true
  },
  {
    "key": "Classic Polo-Men-Polos-Highland Green-XXL + Classic Polo-Men-Polos-White-XXL",
    "items": [
      {
        "sku": "Classic Polo-Men-Polos-Highland Green-XXL",
        "qty": 1
      },
      {
        "sku": "Classic Polo-Men-Polos-White-XXL",
        "qty": 1
      }
    ],
    "ships": 3,
    "hist": [
      [
        0.75,
        3
      ]
    ],
    "mpw": 0.75,
    "spread": 0,
    "packId": "FLY-GEN",
    "deadKg": 0.31,
    "volKg": 0.75,
    "chargeKg": 0.75,
    "sorterCount": 1,
    "merchantCount": 0,
    "itemSkus": null,
    "priceable": false
  },
  {
    "key": "NBAW24MJ14-Men-Joggers-Eiffel tower-XXL + NBAW24MJ14-Men-Joggers-Olive Green-XXL",
    "items": [
      {
        "sku": "NBAW24MJ14-Men-Joggers-Eiffel tower-XXL",
        "qty": 1
      },
      {
        "sku": "NBAW24MJ14-Men-Joggers-Olive Green-XXL",
        "qty": 1
      }
    ],
    "ships": 3,
    "hist": [
      [
        0.75,
        3
      ]
    ],
    "mpw": 0.75,
    "spread": 0,
    "packId": "FLY-GEN",
    "deadKg": 0.42,
    "volKg": 0.75,
    "chargeKg": 0.75,
    "sorterCount": 2,
    "merchantCount": 0,
    "itemSkus": null,
    "priceable": false
  },
  {
    "key": "NBJUN24MJ05-Men-Joggers-Eiffel Tower-M + NBJUN24MJ05-Men-Joggers-Mocha Bisque-M",
    "items": [
      {
        "sku": "NBJUN24MJ05-Men-Joggers-Eiffel Tower-M",
        "qty": 1
      },
      {
        "sku": "NBJUN24MJ05-Men-Joggers-Mocha Bisque-M",
        "qty": 1
      }
    ],
    "ships": 3,
    "hist": [
      [
        0.75,
        2
      ],
      [
        1,
        1
      ]
    ],
    "mpw": 0.75,
    "spread": 33.3,
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.33,
    "chargeKg": 0.75,
    "sorterCount": 0,
    "merchantCount": 1,
    "itemSkus": null,
    "priceable": false
  },
  {
    "key": "Classic Polo-Men-Polos-Cobalt Blue-L + Classic Polo-Men-Polos-Poppy Red-L",
    "items": [
      {
        "sku": "Classic Polo-Men-Polos-Cobalt Blue-L",
        "qty": 1
      },
      {
        "sku": "Classic Polo-Men-Polos-Poppy Red-L",
        "qty": 1
      }
    ],
    "ships": 3,
    "hist": [
      [
        0.75,
        2
      ],
      [
        1,
        1
      ]
    ],
    "mpw": 0.75,
    "spread": 33.3,
    "packId": "FLY-GEN",
    "deadKg": 0.57,
    "volKg": 0.75,
    "chargeKg": 0.75,
    "sorterCount": 0,
    "merchantCount": 2,
    "itemSkus": null,
    "priceable": false
  },
  {
    "key": "Classic Polo-Men-Polos-Aqua Blue-L + Classic Polo-Men-Polos-Burgundy-L",
    "items": [
      {
        "sku": "Classic Polo-Men-Polos-Aqua Blue-L",
        "qty": 1
      },
      {
        "sku": "Classic Polo-Men-Polos-Burgundy-L",
        "qty": 1
      }
    ],
    "ships": 3,
    "hist": [
      [
        0.75,
        3
      ]
    ],
    "mpw": 0.75,
    "spread": 0,
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.52,
    "chargeKg": 0.75,
    "sorterCount": 1,
    "merchantCount": 2,
    "itemSkus": null,
    "priceable": false
  },
  {
    "key": "NBAW24MJ15-Men-Joggers-Eiffel tower-L + NBAW24MJ15-Men-Joggers-Olive Green-L",
    "items": [
      {
        "sku": "NBAW24MJ15-Men-Joggers-Eiffel tower-L",
        "qty": 1
      },
      {
        "sku": "NBAW24MJ15-Men-Joggers-Olive Green-L",
        "qty": 1
      }
    ],
    "ships": 3,
    "hist": [
      [
        1.5,
        3
      ]
    ],
    "mpw": 1.5,
    "spread": 0,
    "packId": "BOX-S",
    "deadKg": 0.6,
    "volKg": 1.5,
    "chargeKg": 1.5,
    "sorterCount": 2,
    "merchantCount": 2,
    "itemSkus": null,
    "priceable": false
  },
  {
    "key": "NBAW24MJ15-Men-Joggers-Navy Blue-M + NBAW24MJ15-Men-Joggers-Olive Green-M",
    "items": [
      {
        "sku": "NBAW24MJ15-Men-Joggers-Navy Blue-M",
        "qty": 1
      },
      {
        "sku": "NBAW24MJ15-Men-Joggers-Olive Green-M",
        "qty": 1
      }
    ],
    "ships": 3,
    "hist": [
      [
        0.75,
        3
      ]
    ],
    "mpw": 0.75,
    "spread": 0,
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.36,
    "chargeKg": 0.75,
    "sorterCount": 1,
    "merchantCount": 1,
    "itemSkus": null,
    "priceable": false
  },
  {
    "key": "NBJUN24MJ05-Men-Joggers-Black-XL + NBJUN24MJ05-Men-Joggers-Eiffel Tower-XL + NBJUN24MJ05-Men-Joggers-Navy Blue",
    "items": [
      {
        "sku": "NBJUN24MJ05-Men-Joggers-Black-XL",
        "qty": 1
      },
      {
        "sku": "NBJUN24MJ05-Men-Joggers-Eiffel Tower-XL",
        "qty": 1
      },
      {
        "sku": "NBJUN24MJ05-Men-Joggers-Navy Blue-XL",
        "qty": 1
      }
    ],
    "ships": 3,
    "hist": [
      [
        1,
        1
      ],
      [
        2,
        2
      ]
    ],
    "mpw": 2,
    "spread": 33.3,
    "packId": "BOX-S",
    "deadKg": 1,
    "volKg": 2,
    "chargeKg": 2,
    "sorterCount": 1,
    "merchantCount": 0,
    "itemSkus": null,
    "priceable": false
  },
  {
    "key": "Classic Polo-Men-Polos-Canary-L + Classic Polo-Men-Polos-Latte-L",
    "items": [
      {
        "sku": "Classic Polo-Men-Polos-Canary-L",
        "qty": 1
      },
      {
        "sku": "Classic Polo-Men-Polos-Latte-L",
        "qty": 1
      }
    ],
    "ships": 3,
    "hist": [
      [
        0.75,
        2
      ],
      [
        1,
        1
      ]
    ],
    "mpw": 0.75,
    "spread": 33.3,
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.46,
    "chargeKg": 0.75,
    "sorterCount": 2,
    "merchantCount": 6,
    "itemSkus": null,
    "priceable": false
  },
  {
    "key": "NBJUN24MJ05-Men-Joggers-Black-L + NBJUN24MJ05-Men-Joggers-Navy Blue-L",
    "items": [
      {
        "sku": "NBJUN24MJ05-Men-Joggers-Black-L",
        "qty": 1
      },
      {
        "sku": "NBJUN24MJ05-Men-Joggers-Navy Blue-L",
        "qty": 1
      }
    ],
    "ships": 3,
    "hist": [
      [
        0.75,
        1
      ],
      [
        1,
        2
      ]
    ],
    "mpw": 1,
    "spread": 33.3,
    "packId": "FLY-L",
    "deadKg": 1,
    "volKg": 0.74,
    "chargeKg": 1,
    "sorterCount": 0,
    "merchantCount": 0,
    "itemSkus": [
      "NBJUN24MJ05-Men-Joggers-Black-L",
      "NBJUN24MJ05-Men-Joggers-Navy Blue-L"
    ],
    "priceable": true
  },
  {
    "key": "Nobero Shorts V2-Men Shorts-Olive Green-XL + Nobero Shorts V2-Men-Shorts-Arctic Wolf-XL",
    "items": [
      {
        "sku": "Nobero Shorts V2-Men Shorts-Olive Green-XL",
        "qty": 1
      },
      {
        "sku": "Nobero Shorts V2-Men-Shorts-Arctic Wolf-XL",
        "qty": 1
      }
    ],
    "ships": 3,
    "hist": [
      [
        0.75,
        1
      ],
      [
        1,
        2
      ]
    ],
    "mpw": 1,
    "spread": 33.3,
    "packId": "FLY-L",
    "deadKg": 1,
    "volKg": 0.45,
    "chargeKg": 1,
    "sorterCount": 1,
    "merchantCount": 3,
    "itemSkus": null,
    "priceable": false
  },
  {
    "key": "NBJUN24MJ05-Men-Joggers-Eiffel Tower-XXL + NBJUN24MJ05-Men-Joggers-Navy Blue-XXL",
    "items": [
      {
        "sku": "NBJUN24MJ05-Men-Joggers-Eiffel Tower-XXL",
        "qty": 2
      },
      {
        "sku": "NBJUN24MJ05-Men-Joggers-Navy Blue-XXL",
        "qty": 2
      }
    ],
    "ships": 3,
    "hist": [
      [
        1,
        3
      ]
    ],
    "mpw": 1,
    "spread": 0,
    "packId": "FLY-L",
    "deadKg": 0.67,
    "volKg": 1,
    "chargeKg": 1,
    "sorterCount": 1,
    "merchantCount": 4,
    "itemSkus": null,
    "priceable": false
  },
  {
    "key": "Great Highway Classic Fit T-Shirt Wine Red / M (sku - 1M-TWTS-C2324-WR3) + Ocean Beach",
    "items": [
      {
        "sku": "Great Highway Classic Fit T-Shirt Wine Red / M (sku - 1M-TWTS-C2324-WR3)",
        "qty": 2
      }
    ],
    "ships": 3,
    "hist": [
      [
        0.75,
        3
      ]
    ],
    "mpw": 0.75,
    "spread": 0,
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.55,
    "chargeKg": 0.75,
    "sorterCount": 1,
    "merchantCount": 4,
    "itemSkus": null,
    "priceable": false
  },
  {
    "key": "Nobero Shorts V2-Men Shorts-Black-XXXL + Nobero Shorts V2-Men Shorts-Olive Green-XXXL",
    "items": [
      {
        "sku": "Nobero Shorts V2-Men Shorts-Black-XXXL",
        "qty": 2
      },
      {
        "sku": "Nobero Shorts V2-Men Shorts-Olive Green-XXXL",
        "qty": 2
      }
    ],
    "ships": 3,
    "hist": [
      [
        1,
        3
      ]
    ],
    "mpw": 1,
    "spread": 0,
    "packId": "FLY-L",
    "deadKg": 1,
    "volKg": 0.41,
    "chargeKg": 1,
    "sorterCount": 0,
    "merchantCount": 0,
    "itemSkus": null,
    "priceable": false
  },
  {
    "key": "NBJUN24MJ05-Men-Joggers-Navy Blue-L + NBJUN24MJ05-Men-Joggers-Pebble-L",
    "items": [
      {
        "sku": "NBJUN24MJ05-Men-Joggers-Navy Blue-L",
        "qty": 2
      },
      {
        "sku": "NBJUN24MJ05-Men-Joggers-Pebble-L",
        "qty": 2
      }
    ],
    "ships": 3,
    "hist": [
      [
        1,
        3
      ]
    ],
    "mpw": 1,
    "spread": 0,
    "packId": "FLY-L",
    "deadKg": 0.65,
    "volKg": 1,
    "chargeKg": 1,
    "sorterCount": 1,
    "merchantCount": 4,
    "itemSkus": null,
    "priceable": false
  },
  {
    "key": "Classic Polo-Men-Polos-Canary-L + Classic Polo-Men-Polos-Petunia-L",
    "items": [
      {
        "sku": "Classic Polo-Men-Polos-Canary-L",
        "qty": 2
      },
      {
        "sku": "Classic Polo-Men-Polos-Petunia-L",
        "qty": 2
      }
    ],
    "ships": 3,
    "hist": [
      [
        0.75,
        1
      ],
      [
        1,
        2
      ]
    ],
    "mpw": 1,
    "spread": 33.3,
    "packId": "FLY-L",
    "deadKg": 1,
    "volKg": 0.64,
    "chargeKg": 1,
    "sorterCount": 1,
    "merchantCount": 2,
    "itemSkus": null,
    "priceable": false
  },
  {
    "key": "Nobero Shorts V2-Men Shorts-Olive Green-XL + Nobero Shorts V2-Men-Shorts-Eiffel tower-XL",
    "items": [
      {
        "sku": "Nobero Shorts V2-Men Shorts-Olive Green-XL",
        "qty": 2
      },
      {
        "sku": "Nobero Shorts V2-Men-Shorts-Eiffel tower-XL",
        "qty": 2
      }
    ],
    "ships": 3,
    "hist": [
      [
        0.75,
        1
      ],
      [
        1,
        1
      ],
      [
        1.5,
        1
      ]
    ],
    "mpw": 0.75,
    "spread": 66.7,
    "packId": "FLY-GEN",
    "deadKg": 0.43,
    "volKg": 0.75,
    "chargeKg": 0.75,
    "sorterCount": 1,
    "merchantCount": 0,
    "itemSkus": null,
    "priceable": false
  },
  {
    "key": "NBJUN24MJ05-Men-Joggers-Eiffel Tower :  : M + NBJUN24MJ05-Men-Joggers-Pebble :  : M",
    "items": [
      {
        "sku": "NBJUN24MJ05-Men-Joggers-Eiffel Tower :  : M",
        "qty": 1
      },
      {
        "sku": "NBJUN24MJ05-Men-Joggers-Pebble :  : M",
        "qty": 1
      }
    ],
    "ships": 3,
    "hist": [
      [
        2,
        3
      ]
    ],
    "mpw": 2,
    "spread": 0,
    "packId": "BOX-S",
    "deadKg": 2,
    "volKg": 1.07,
    "chargeKg": 2,
    "sorterCount": 1,
    "merchantCount": 2,
    "itemSkus": null,
    "priceable": false
  },
  {
    "key": "NBJUN24MJ05-Men-Joggers-Eiffel Tower :  : XXXL + NBJUN24MJ05-Men-Joggers-Navy Blue :  : XXXL",
    "items": [
      {
        "sku": "NBJUN24MJ05-Men-Joggers-Eiffel Tower :  : XXXL",
        "qty": 1
      },
      {
        "sku": "NBJUN24MJ05-Men-Joggers-Navy Blue :  : XXXL",
        "qty": 1
      }
    ],
    "ships": 3,
    "hist": [
      [
        3,
        3
      ]
    ],
    "mpw": 3,
    "spread": 0,
    "packId": "BOX-GEN",
    "deadKg": 3,
    "volKg": 1.34,
    "chargeKg": 3,
    "sorterCount": 1,
    "merchantCount": 0,
    "itemSkus": null,
    "priceable": false
  },
  {
    "key": "Travel Polo-Men-Polos-Niagara-L + Travel Polo-Men-Polos-Wine Red-L",
    "items": [
      {
        "sku": "Travel Polo-Men-Polos-Niagara-L",
        "qty": 1
      },
      {
        "sku": "Travel Polo-Men-Polos-Wine Red-L",
        "qty": 1
      }
    ],
    "ships": 3,
    "hist": [
      [
        0.75,
        1
      ],
      [
        1,
        2
      ]
    ],
    "mpw": 1,
    "spread": 33.3,
    "packId": "FLY-L",
    "deadKg": 0.71,
    "volKg": 1,
    "chargeKg": 1,
    "sorterCount": 1,
    "merchantCount": 0,
    "itemSkus": null,
    "priceable": false
  },
  {
    "key": "Travel Polo-Men-Polos-Cedar-L + Travel Polo-Men-Polos-Powder Pink-L",
    "items": [
      {
        "sku": "Travel Polo-Men-Polos-Cedar-L",
        "qty": 1
      },
      {
        "sku": "Travel Polo-Men-Polos-Powder Pink-L",
        "qty": 1
      }
    ],
    "ships": 3,
    "hist": [
      [
        1,
        3
      ]
    ],
    "mpw": 1,
    "spread": 0,
    "packId": "FLY-L",
    "deadKg": 1,
    "volKg": 0.73,
    "chargeKg": 1,
    "sorterCount": 1,
    "merchantCount": 6,
    "itemSkus": null,
    "priceable": false
  },
  {
    "key": "Classic Polo-Men-Polos-Cobalt Blue-L + Classic Polo-Men-Polos-Coral-L-L",
    "items": [
      {
        "sku": "Classic Polo-Men-Polos-Cobalt Blue-L",
        "qty": 1
      },
      {
        "sku": "Classic Polo-Men-Polos-Coral-L-L",
        "qty": 1
      }
    ],
    "ships": 3,
    "hist": [
      [
        0.75,
        1
      ],
      [
        1,
        2
      ]
    ],
    "mpw": 1,
    "spread": 33.3,
    "packId": "FLY-L",
    "deadKg": 0.71,
    "volKg": 1,
    "chargeKg": 1,
    "sorterCount": 1,
    "merchantCount": 0,
    "itemSkus": null,
    "priceable": false
  },
  {
    "key": "Ocean Dreams-Men-Relaxed Fit Hoodie-Navy Blue-XL + Over sized Sweatshirt-Sweatshirt-Pink Clay-XL",
    "items": [
      {
        "sku": "Ocean Dreams-Men-Relaxed Fit Hoodie-Navy Blue-XL",
        "qty": 1
      },
      {
        "sku": "Over sized Sweatshirt-Sweatshirt-Pink Clay-XL",
        "qty": 1
      }
    ],
    "ships": 2,
    "hist": [
      [
        0.75,
        2
      ]
    ],
    "mpw": 0.75,
    "spread": 0,
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.47,
    "chargeKg": 0.75,
    "sorterCount": 1,
    "merchantCount": 0,
    "itemSkus": null,
    "priceable": false
  },
  {
    "key": "PLAIN ZIPPER HOODIE-Men-Zipper Hoodie-Bone White-L + PLAIN ZIPPER HOODIE-Men-Zipper Hoodie-Chocolate Fondant-L",
    "items": [
      {
        "sku": "PLAIN ZIPPER HOODIE-Men-Zipper Hoodie-Bone White-L",
        "qty": 1
      },
      {
        "sku": "PLAIN ZIPPER HOODIE-Men-Zipper Hoodie-Chocolate Fondant-L",
        "qty": 1
      }
    ],
    "ships": 2,
    "hist": [
      [
        0.75,
        2
      ]
    ],
    "mpw": 0.75,
    "spread": 0,
    "packId": "FLY-GEN",
    "deadKg": 0.54,
    "volKg": 0.75,
    "chargeKg": 0.75,
    "sorterCount": 1,
    "merchantCount": 0,
    "itemSkus": null,
    "priceable": false
  },
  {
    "key": "Nobero Jogger V2-Men-Joggers-Eiffel tower-M + Nobero Jogger V2-Men-Joggers-Macchiato-M",
    "items": [
      {
        "sku": "Nobero Jogger V2-Men-Joggers-Eiffel tower-M",
        "qty": 1
      },
      {
        "sku": "Nobero Jogger V2-Men-Joggers-Macchiato-M",
        "qty": 1
      }
    ],
    "ships": 2,
    "hist": [
      [
        0.75,
        2
      ]
    ],
    "mpw": 0.75,
    "spread": 0,
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.6,
    "chargeKg": 0.75,
    "sorterCount": 1,
    "merchantCount": 6,
    "itemSkus": null,
    "priceable": false
  },
  {
    "key": "PLAIN ZIPPER HOODIE-Men-Zipper Hoodie-Bone White-L + PLAIN ZIPPER HOODIE-Men-Zipper Hoodie-Graphite-L",
    "items": [
      {
        "sku": "PLAIN ZIPPER HOODIE-Men-Zipper Hoodie-Bone White-L",
        "qty": 1
      },
      {
        "sku": "PLAIN ZIPPER HOODIE-Men-Zipper Hoodie-Graphite-L",
        "qty": 1
      }
    ],
    "ships": 2,
    "hist": [
      [
        0.75,
        2
      ]
    ],
    "mpw": 0.75,
    "spread": 0,
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.47,
    "chargeKg": 0.75,
    "sorterCount": 0,
    "merchantCount": 1,
    "itemSkus": null,
    "priceable": false
  },
  {
    "key": "Classic Polo-Men-Polos-Burgundy-L + Classic Polo-Men-Polos-Petunia-L",
    "items": [
      {
        "sku": "Classic Polo-Men-Polos-Burgundy-L",
        "qty": 1
      },
      {
        "sku": "Classic Polo-Men-Polos-Petunia-L",
        "qty": 1
      }
    ],
    "ships": 2,
    "hist": [
      [
        0.75,
        1
      ],
      [
        1,
        1
      ]
    ],
    "mpw": 0.75,
    "spread": 50,
    "packId": "FLY-GEN",
    "deadKg": 0.4,
    "volKg": 0.75,
    "chargeKg": 0.75,
    "sorterCount": 1,
    "merchantCount": 1,
    "itemSkus": null,
    "priceable": false
  },
  {
    "key": "Nobero Jogger V2-Men Joggers-Dark Oak-XL + Nobero Jogger V2-Men Joggers-Navy Blue-XL",
    "items": [
      {
        "sku": "Nobero Jogger V2-Men Joggers-Dark Oak-XL",
        "qty": 1
      },
      {
        "sku": "Nobero Jogger V2-Men Joggers-Navy Blue-XL",
        "qty": 1
      }
    ],
    "ships": 2,
    "hist": [
      [
        0.75,
        1
      ],
      [
        1.5,
        1
      ]
    ],
    "mpw": 0.75,
    "spread": 50,
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.46,
    "chargeKg": 0.75,
    "sorterCount": 1,
    "merchantCount": 2,
    "itemSkus": null,
    "priceable": false
  },
  {
    "key": "Classic Polo-Men-Polos-Burgundy-L + Classic Polo-Men-Polos-Pecan-L",
    "items": [
      {
        "sku": "Classic Polo-Men-Polos-Burgundy-L",
        "qty": 1
      },
      {
        "sku": "Classic Polo-Men-Polos-Pecan-L",
        "qty": 1
      }
    ],
    "ships": 2,
    "hist": [
      [
        0.75,
        2
      ]
    ],
    "mpw": 0.75,
    "spread": 0,
    "packId": "FLY-GEN",
    "deadKg": 0.54,
    "volKg": 0.75,
    "chargeKg": 0.75,
    "sorterCount": 1,
    "merchantCount": 2,
    "itemSkus": null,
    "priceable": false
  },
  {
    "key": "NBJUN24MJ05-Men-Joggers-Black-XXL + NBJUN24MJ05-Men-Joggers-Eiffel Tower-XXL",
    "items": [
      {
        "sku": "NBJUN24MJ05-Men-Joggers-Black-XXL",
        "qty": 1
      },
      {
        "sku": "NBJUN24MJ05-Men-Joggers-Eiffel Tower-XXL",
        "qty": 1
      }
    ],
    "ships": 2,
    "hist": [
      [
        0.75,
        2
      ]
    ],
    "mpw": 0.75,
    "spread": 0,
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.33,
    "chargeKg": 0.75,
    "sorterCount": 1,
    "merchantCount": 1,
    "itemSkus": null,
    "priceable": false
  },
  {
    "key": "Classic Polo-Men-Polos-Aqua Blue-L + Classic Polo-Men-Polos-Coral-L",
    "items": [
      {
        "sku": "Classic Polo-Men-Polos-Aqua Blue-L",
        "qty": 1
      },
      {
        "sku": "Classic Polo-Men-Polos-Coral-L",
        "qty": 1
      }
    ],
    "ships": 2,
    "hist": [
      [
        0.75,
        1
      ],
      [
        1,
        1
      ]
    ],
    "mpw": 0.75,
    "spread": 50,
    "packId": "FLY-GEN",
    "deadKg": 0.33,
    "volKg": 0.75,
    "chargeKg": 0.75,
    "sorterCount": 1,
    "merchantCount": 0,
    "itemSkus": null,
    "priceable": false
  },
  {
    "key": "Classic Polo-Men-Polos-Pecan-XL + Classic Polo-Men-Polos-Powder Blue-XL",
    "items": [
      {
        "sku": "Classic Polo-Men-Polos-Pecan-XL",
        "qty": 1
      },
      {
        "sku": "Classic Polo-Men-Polos-Powder Blue-XL",
        "qty": 1
      }
    ],
    "ships": 2,
    "hist": [
      [
        0.75,
        1
      ],
      [
        1,
        1
      ]
    ],
    "mpw": 0.75,
    "spread": 50,
    "packId": "FLY-GEN",
    "deadKg": 0.39,
    "volKg": 0.75,
    "chargeKg": 0.75,
    "sorterCount": 1,
    "merchantCount": 5,
    "itemSkus": null,
    "priceable": false
  },
  {
    "key": "NBJUN24MJ05-Men-Joggers-Black-XXXL + NBJUN24MJ05-Men-Joggers-Eiffel Tower-XXXL",
    "items": [
      {
        "sku": "NBJUN24MJ05-Men-Joggers-Black-XXXL",
        "qty": 1
      },
      {
        "sku": "NBJUN24MJ05-Men-Joggers-Eiffel Tower-XXXL",
        "qty": 1
      }
    ],
    "ships": 2,
    "hist": [
      [
        0.75,
        1
      ],
      [
        1,
        1
      ]
    ],
    "mpw": 0.75,
    "spread": 50,
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.33,
    "chargeKg": 0.75,
    "sorterCount": 0,
    "merchantCount": 0,
    "itemSkus": null,
    "priceable": false
  },
  {
    "key": "EBM-33-Jogger-Eiffel tower-L + EBM-33-Men-Joggers-Black-L",
    "items": [
      {
        "sku": "EBM-33-Jogger-Eiffel tower-L",
        "qty": 1
      },
      {
        "sku": "EBM-33-Men-Joggers-Black-L",
        "qty": 1
      }
    ],
    "ships": 2,
    "hist": [
      [
        0.75,
        1
      ],
      [
        1,
        1
      ]
    ],
    "mpw": 0.75,
    "spread": 50,
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.41,
    "chargeKg": 0.75,
    "sorterCount": 0,
    "merchantCount": 2,
    "itemSkus": null,
    "priceable": false
  },
  {
    "key": "ELEGANT HOODIE-Hoodie-Powder Blue-M + ELEGANT HOODIE-Men-Hoodie-Midnight Plum-M",
    "items": [
      {
        "sku": "ELEGANT HOODIE-Hoodie-Powder Blue-M",
        "qty": 1
      },
      {
        "sku": "ELEGANT HOODIE-Men-Hoodie-Midnight Plum-M",
        "qty": 1
      }
    ],
    "ships": 2,
    "hist": [
      [
        0.75,
        2
      ]
    ],
    "mpw": 0.75,
    "spread": 0,
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.39,
    "chargeKg": 0.75,
    "sorterCount": 1,
    "merchantCount": 0,
    "itemSkus": null,
    "priceable": false
  },
  {
    "key": "NBJUN24MJ05-Men-Joggers-Eiffel Tower-XL + NBJUN24MJ05-Men-Joggers-Navy Blue-XL",
    "items": [
      {
        "sku": "NBJUN24MJ05-Men-Joggers-Eiffel Tower-XL",
        "qty": 1
      },
      {
        "sku": "NBJUN24MJ05-Men-Joggers-Navy Blue-XL",
        "qty": 1
      }
    ],
    "ships": 2,
    "hist": [
      [
        0.75,
        1
      ],
      [
        1.5,
        1
      ]
    ],
    "mpw": 0.75,
    "spread": 50,
    "packId": "FLY-GEN",
    "deadKg": 0.75,
    "volKg": 0.4,
    "chargeKg": 0.75,
    "sorterCount": 1,
    "merchantCount": 2,
    "itemSkus": null,
    "priceable": false
  },
  {
    "key": "Classic Polo-Men-Polos-Amber Yellow-L + Classic Polo-Men-Polos-Burgundy-L",
    "items": [
      {
        "sku": "Classic Polo-Men-Polos-Amber Yellow-L",
        "qty": 1
      },
      {
        "sku": "Classic Polo-Men-Polos-Burgundy-L",
        "qty": 1
      }
    ],
    "ships": 2,
    "hist": [
      [
        0.75,
        2
      ]
    ],
    "mpw": 0.75,
    "spread": 0,
    "packId": "FLY-GEN",
    "deadKg": 0.43,
    "volKg": 0.75,
    "chargeKg": 0.75,
    "sorterCount": 1,
    "merchantCount": 4,
    "itemSkus": null,
    "priceable": false
  }
]

export const PACKS = [
  {
    "id": "FLY-S",
    "label": "Flyer, small",
    "branding": "self",
    "kind": "flyer",
    "dims": null,
    "selfWeightG": 11,
    "volKg": null
  },
  {
    "id": "FLY-M",
    "label": "Flyer, medium",
    "branding": "self",
    "kind": "flyer",
    "dims": null,
    "selfWeightG": 16,
    "volKg": null
  },
  {
    "id": "FLY-L",
    "label": "Flyer, large",
    "branding": "self",
    "kind": "flyer",
    "dims": null,
    "selfWeightG": 24,
    "volKg": null
  },
  {
    "id": "FLY-GEN",
    "label": "Flyer, generic",
    "branding": "generic",
    "kind": "flyer",
    "dims": null,
    "selfWeightG": 14,
    "volKg": null
  },
  {
    "id": "BOX-S",
    "label": "Carton, small",
    "branding": "self",
    "kind": "carton",
    "dims": [
      22,
      16,
      8
    ],
    "selfWeightG": 95,
    "volKg": 0.56
  },
  {
    "id": "BOX-M",
    "label": "Carton, medium",
    "branding": "self",
    "kind": "carton",
    "dims": [
      30,
      24,
      12
    ],
    "selfWeightG": 165,
    "volKg": 1.73
  },
  {
    "id": "BOX-L",
    "label": "Carton, large",
    "branding": "self",
    "kind": "carton",
    "dims": [
      40,
      32,
      20
    ],
    "selfWeightG": 280,
    "volKg": 5.12
  },
  {
    "id": "BOX-GEN",
    "label": "Carton, generic",
    "branding": "generic",
    "kind": "carton",
    "dims": [
      28,
      22,
      14
    ],
    "selfWeightG": 150,
    "volKg": 1.72
  },
  {
    "id": "PAP-W",
    "label": "Paper wrap",
    "branding": "self",
    "kind": "other",
    "dims": null,
    "selfWeightG": 30,
    "volKg": null
  }
]

export const SUBCATS = {
  "Topwear": [
    "Hoodies",
    "Polo T-Shirts",
    "Sweatshirts",
    "T-Shirts"
  ],
  "Bottomwear": [
    "Cargo Pants",
    "Joggers",
    "Travel Wear"
  ],
  "Sets": [
    "Co-ord Sets"
  ]
}

export const DWS_RECORDS = [
  {
    "id": "DWS-CO-00001",
    "sku": "1M-TWTS-C0969-OG4",
    "awb": "14900001466",
    "courier": "Shadowfax",
    "date": "2026-06-03",
    "read": 0.59,
    "charged": 0.75,
    "dims": [
      23,
      26,
      19
    ],
    "vol": 2.27,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00003",
    "sku": "1M-TWTS-C0969-OG4",
    "awb": "14900002932",
    "courier": "Shadowfax",
    "date": "2026-05-20",
    "read": 0.66,
    "charged": 0.75,
    "dims": [
      33,
      29,
      16
    ],
    "vol": 3.06,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00005",
    "sku": "1M-TWTS-C0969-OG4",
    "awb": "14900004398",
    "courier": "Shadowfax",
    "date": "2026-06-02",
    "read": 0.59,
    "charged": 0.75,
    "dims": [
      36,
      26,
      13
    ],
    "vol": 2.43,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00007",
    "sku": "1M-TWTS-C0969-OG4",
    "awb": "14900005864",
    "courier": "Delhivery",
    "date": "2026-07-07",
    "read": 0.61,
    "charged": 0.75,
    "dims": [
      35,
      27,
      16
    ],
    "vol": 3.02,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00009",
    "sku": "1M-TWTS-C0969-OG4",
    "awb": "14900007330",
    "courier": "Blitz",
    "date": "2026-05-11",
    "read": 0.9,
    "charged": 1,
    "dims": [
      31,
      19,
      9
    ],
    "vol": 1.06,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-00011",
    "sku": "1M-BWJG-R0149-ET4",
    "awb": "14900008796",
    "courier": "Delhivery",
    "date": "2026-08-01",
    "read": 0.75,
    "dims": [
      36,
      24,
      6
    ],
    "vol": 1.04,
    "source": "upload",
    "video": {
      "name": "evidence-12-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-12-len.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-00013",
    "sku": "1M-BWJG-R0149-ET4",
    "awb": "14900010262",
    "courier": "Bluedart",
    "date": "2026-05-26",
    "read": 0.92,
    "charged": 1,
    "dims": [
      27,
      29,
      18
    ],
    "vol": 2.82,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00015",
    "sku": "1M-BWJG-R0149-ET4",
    "awb": "14900011728",
    "courier": "Bluedart",
    "date": "2026-06-07",
    "read": 0.55,
    "charged": 0.75,
    "dims": [
      34,
      29,
      14
    ],
    "vol": 2.76,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00017",
    "sku": "1M-BWJG-R0149-ET4",
    "awb": "14900013194",
    "courier": "Xpressbees",
    "date": "2026-07-18",
    "read": 0.81,
    "charged": 1,
    "dims": [
      31,
      25,
      14
    ],
    "vol": 2.17,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00019",
    "sku": "1M-BWJG-R0149-ET4",
    "awb": "14900014660",
    "courier": "DTDC",
    "date": "2026-06-11",
    "read": 0.68,
    "charged": 0.75,
    "dims": [
      34,
      22,
      5
    ],
    "vol": 0.75,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-00021",
    "sku": "1M-BWJG-R0137-BL5",
    "awb": "14900016126",
    "courier": "Delhivery",
    "date": "2026-08-05",
    "read": 1,
    "dims": [
      23,
      22,
      8
    ],
    "vol": 0.81,
    "source": "upload",
    "video": {
      "name": "evidence-22-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-22-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-22-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-22-hgt.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00023",
    "sku": "1M-BWJG-R0137-BL5",
    "awb": "14900017592",
    "courier": "DTDC",
    "date": "2026-08-06",
    "read": 1,
    "dims": [
      38,
      27,
      19
    ],
    "vol": 3.9,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-24-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-24-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00025",
    "sku": "1M-BWJG-R0137-BL5",
    "awb": "14900019058",
    "courier": "Xpressbees",
    "date": "2026-08-06",
    "read": 1,
    "dims": [
      38,
      19,
      10
    ],
    "vol": 1.44,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-26-len.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00027",
    "sku": "1M-BWJG-R0137-BL5",
    "awb": "14900020524",
    "courier": "Bluedart",
    "date": "2026-08-02",
    "read": 1,
    "dims": [
      28,
      16,
      10
    ],
    "vol": 0.9,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-28-len.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00029",
    "sku": "1M-BWJG-R0137-BL5",
    "awb": "14900021990",
    "courier": "Bluedart",
    "date": "2026-07-30",
    "read": 1,
    "dims": [
      32,
      23,
      6
    ],
    "vol": 0.88,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-30-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-30-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-30-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-30-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00031",
    "sku": "1M-BWJG-R0137-BL5",
    "awb": "14900023456",
    "courier": "Delhivery",
    "date": "2026-08-03",
    "read": 1,
    "dims": [
      39,
      21,
      15
    ],
    "vol": 2.46,
    "source": "upload",
    "video": {
      "name": "evidence-32-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-32-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-32-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-00033",
    "sku": "1M-BWJG-R0137-BL5",
    "awb": "14900024922",
    "courier": "Xpressbees",
    "date": "2026-04-30",
    "read": 0.89,
    "charged": 1,
    "dims": [
      38,
      23,
      5
    ],
    "vol": 0.87,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00035",
    "sku": "1M-BWJG-R0137-BL5",
    "awb": "14900026388",
    "courier": "Delhivery",
    "date": "2026-05-10",
    "read": 0.85,
    "charged": 1,
    "dims": [
      27,
      19,
      18
    ],
    "vol": 1.85,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-00037",
    "sku": "1M-BWJG-R0137-BL4",
    "awb": "14900027854",
    "courier": "Xpressbees",
    "date": "2026-07-31",
    "read": 1,
    "dims": [
      22,
      27,
      5
    ],
    "vol": 0.59,
    "source": "upload",
    "video": {
      "name": "evidence-38-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-38-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-38-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00039",
    "sku": "1M-BWJG-R0137-BL4",
    "awb": "14900029320",
    "courier": "Blitz",
    "date": "2026-08-03",
    "read": 1,
    "dims": [
      29,
      24,
      6
    ],
    "vol": 0.84,
    "source": "upload",
    "video": {
      "name": "evidence-40-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-40-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-40-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-40-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-40-scale.jpg",
        "url": null
      },
      {
        "slot": "label",
        "name": "evidence-40-label.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-00041",
    "sku": "1M-BWJG-R0137-BL4",
    "awb": "14900030786",
    "courier": "Blitz",
    "date": "2026-06-07",
    "read": 0.87,
    "charged": 1,
    "dims": [
      25,
      23,
      14
    ],
    "vol": 1.61,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00043",
    "sku": "1M-BWJG-R0137-BL4",
    "awb": "14900032252",
    "courier": "Bluedart",
    "date": "2026-07-28",
    "read": 1.41,
    "charged": 1.5,
    "dims": [
      30,
      16,
      17
    ],
    "vol": 1.63,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00045",
    "sku": "1M-BWJG-R0137-BL4",
    "awb": "14900033718",
    "courier": "Bluedart",
    "date": "2026-07-19",
    "read": 0.93,
    "charged": 1,
    "dims": [
      29,
      22,
      11
    ],
    "vol": 1.4,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-00047",
    "sku": "1M-BWJG-R0149-BL4",
    "awb": "14900035184",
    "courier": "Xpressbees",
    "date": "2026-07-31",
    "read": 0.75,
    "dims": [
      21,
      27,
      15
    ],
    "vol": 1.7,
    "source": "upload",
    "video": {
      "name": "evidence-48-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-48-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-48-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-48-hgt.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00049",
    "sku": "1M-BWJG-R0149-BL4",
    "awb": "14900036650",
    "courier": "Ekart",
    "date": "2026-08-01",
    "read": 0.75,
    "dims": [
      35,
      22,
      15
    ],
    "vol": 2.31,
    "source": "upload",
    "video": {
      "name": "evidence-50-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-50-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-50-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-50-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-50-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-00051",
    "sku": "1M-BWJG-R0149-BL4",
    "awb": "14900038116",
    "courier": "Shadowfax",
    "date": "2026-07-21",
    "read": 0.72,
    "charged": 0.75,
    "dims": [
      25,
      17,
      16
    ],
    "vol": 1.36,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00053",
    "sku": "1M-BWJG-R0149-BL4",
    "awb": "14900039582",
    "courier": "Xpressbees",
    "date": "2026-05-24",
    "read": 0.71,
    "charged": 0.75,
    "dims": [
      22,
      18,
      11
    ],
    "vol": 0.87,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00055",
    "sku": "1M-BWJG-R0149-BL4",
    "awb": "14900041048",
    "courier": "Delhivery",
    "date": "2026-05-12",
    "read": 0.72,
    "charged": 0.75,
    "dims": [
      29,
      20,
      19
    ],
    "vol": 2.2,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-00057",
    "sku": "1M-BWJG-R0149-ET",
    "awb": "14900042514",
    "courier": "Bluedart",
    "date": "2026-07-30",
    "read": 1,
    "dims": [
      34,
      25,
      15
    ],
    "vol": 2.55,
    "source": "upload",
    "video": {
      "name": "evidence-58-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-58-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-58-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-58-hgt.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00059",
    "sku": "1M-BWJG-R0149-ET",
    "awb": "14900043980",
    "courier": "Ekart",
    "date": "2026-08-05",
    "read": 1,
    "dims": [
      30,
      29,
      13
    ],
    "vol": 2.26,
    "source": "upload",
    "video": {
      "name": "evidence-60-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-60-len.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-00061",
    "sku": "1M-BWJG-R0149-ET",
    "awb": "14900045446",
    "courier": "Xpressbees",
    "date": "2026-05-12",
    "read": 0.6,
    "charged": 0.75,
    "dims": [
      21,
      25,
      17
    ],
    "vol": 1.79,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00063",
    "sku": "1M-BWJG-R0149-ET",
    "awb": "14900046912",
    "courier": "Xpressbees",
    "date": "2026-04-30",
    "read": 0.67,
    "charged": 0.75,
    "dims": [
      29,
      24,
      9
    ],
    "vol": 1.25,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00065",
    "sku": "1M-BWJG-R0149-ET",
    "awb": "14900048378",
    "courier": "Ekart",
    "date": "2026-06-29",
    "read": 0.97,
    "charged": 1,
    "dims": [
      33,
      24,
      14
    ],
    "vol": 2.22,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00067",
    "sku": "1M-BWJG-R0149-ET",
    "awb": "14900049844",
    "courier": "Shadowfax",
    "date": "2026-07-13",
    "read": 0.56,
    "charged": 0.75,
    "dims": [
      29,
      18,
      8
    ],
    "vol": 0.84,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00069",
    "sku": "1M-BWJG-R0149-ET",
    "awb": "14900051310",
    "courier": "Ekart",
    "date": "2026-07-05",
    "read": 0.62,
    "charged": 0.75,
    "dims": [
      24,
      27,
      8
    ],
    "vol": 1.04,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00071",
    "sku": "1M-BWJG-R0149-ET",
    "awb": "14900052776",
    "courier": "Blitz",
    "date": "2026-06-09",
    "read": 0.92,
    "charged": 1,
    "dims": [
      32,
      26,
      9
    ],
    "vol": 1.5,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00073",
    "sku": "1M-BWJG-R0149-ET3",
    "awb": "14900054242",
    "courier": "Bluedart",
    "date": "2026-05-25",
    "read": 0.62,
    "charged": 0.75,
    "dims": [
      20,
      19,
      16
    ],
    "vol": 1.22,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00075",
    "sku": "1M-BWJG-R0149-ET3",
    "awb": "14900055708",
    "courier": "Blitz",
    "date": "2026-05-12",
    "read": 0.57,
    "charged": 0.75,
    "dims": [
      25,
      20,
      11
    ],
    "vol": 1.1,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00077",
    "sku": "1M-BWJG-R0149-ET3",
    "awb": "14900057174",
    "courier": "Delhivery",
    "date": "2026-06-10",
    "read": 0.65,
    "charged": 0.75,
    "dims": [
      36,
      22,
      17
    ],
    "vol": 2.69,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00079",
    "sku": "1M-BWJG-R0149-ET3",
    "awb": "14900058640",
    "courier": "Ekart",
    "date": "2026-06-27",
    "read": 0.6,
    "charged": 0.75,
    "dims": [
      30,
      28,
      10
    ],
    "vol": 1.68,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00081",
    "sku": "1M-BWJG-R0149-ET3",
    "awb": "14900060106",
    "courier": "Blitz",
    "date": "2026-05-13",
    "read": 0.81,
    "charged": 1,
    "dims": [
      28,
      18,
      17
    ],
    "vol": 1.71,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00083",
    "sku": "1M-BWJG-R0149-ET3",
    "awb": "14900061572",
    "courier": "DTDC",
    "date": "2026-05-30",
    "read": 0.59,
    "charged": 0.75,
    "dims": [
      30,
      15,
      17
    ],
    "vol": 1.53,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-00085",
    "sku": "1M-TWTS-C0889-PBL",
    "awb": "14900063038",
    "courier": "Xpressbees",
    "date": "2026-07-31",
    "read": 0.75,
    "dims": [
      20,
      26,
      5
    ],
    "vol": 0.52,
    "source": "upload",
    "video": {
      "name": "evidence-86-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-86-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-86-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-86-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-86-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00087",
    "sku": "1M-TWTS-C0889-PBL",
    "awb": "14900064504",
    "courier": "DTDC",
    "date": "2026-08-04",
    "read": 0.75,
    "dims": [
      36,
      25,
      17
    ],
    "vol": 3.06,
    "source": "upload",
    "video": {
      "name": "evidence-88-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-88-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-88-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-88-hgt.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00089",
    "sku": "1M-TWTS-C0889-PBL",
    "awb": "14900065970",
    "courier": "Blitz",
    "date": "2026-08-04",
    "read": 0.75,
    "dims": [
      34,
      25,
      5
    ],
    "vol": 0.85,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-90-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-90-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00091",
    "sku": "1M-TWTS-C0889-PBL",
    "awb": "14900067436",
    "courier": "DTDC",
    "date": "2026-08-04",
    "read": 0.75,
    "dims": [
      38,
      25,
      5
    ],
    "vol": 0.95,
    "source": "upload",
    "video": {
      "name": "evidence-92-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-92-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-92-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-92-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-92-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00093",
    "sku": "1M-TWTS-C0889-PBL",
    "awb": "14900068902",
    "courier": "Blitz",
    "date": "2026-08-06",
    "read": 0.75,
    "dims": [
      20,
      24,
      12
    ],
    "vol": 1.15,
    "source": "upload",
    "video": {
      "name": "evidence-94-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-94-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-94-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-94-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-94-scale.jpg",
        "url": null
      },
      {
        "slot": "label",
        "name": "evidence-94-label.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00095",
    "sku": "1M-TWTS-C0889-PBL",
    "awb": "14900070368",
    "courier": "Ekart",
    "date": "2026-08-05",
    "read": 0.75,
    "dims": [
      28,
      23,
      12
    ],
    "vol": 1.55,
    "source": "upload",
    "video": {
      "name": "evidence-96-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-96-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-96-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-96-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-96-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-00097",
    "sku": "1M-TWTS-C0889-PBL",
    "awb": "14900071834",
    "courier": "Ekart",
    "date": "2026-06-19",
    "read": 0.64,
    "charged": 0.75,
    "dims": [
      23,
      29,
      17
    ],
    "vol": 2.27,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00099",
    "sku": "1M-TWTS-C0889-PBL",
    "awb": "14900073300",
    "courier": "DTDC",
    "date": "2026-07-06",
    "read": 0.69,
    "charged": 0.75,
    "dims": [
      27,
      23,
      13
    ],
    "vol": 1.61,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00101",
    "sku": "1M-TWTS-C0889-PBL",
    "awb": "14900074766",
    "courier": "Delhivery",
    "date": "2026-07-22",
    "read": 0.61,
    "charged": 0.75,
    "dims": [
      20,
      20,
      7
    ],
    "vol": 0.56,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00103",
    "sku": "1M-TWTS-C0889-PBL",
    "awb": "14900076232",
    "courier": "DTDC",
    "date": "2026-05-29",
    "read": 0.69,
    "charged": 0.75,
    "dims": [
      38,
      29,
      7
    ],
    "vol": 1.54,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00105",
    "sku": "1M-TWTS-C0969-OG5",
    "awb": "14900077698",
    "courier": "Ekart",
    "date": "2026-07-12",
    "read": 0.6,
    "charged": 0.75,
    "dims": [
      29,
      21,
      6
    ],
    "vol": 0.73,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00107",
    "sku": "1M-TWTS-C0969-OG5",
    "awb": "14900079164",
    "courier": "Delhivery",
    "date": "2026-07-01",
    "read": 0.69,
    "charged": 0.75,
    "dims": [
      36,
      15,
      16
    ],
    "vol": 1.73,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00109",
    "sku": "1M-TWPO-R0002-BU3",
    "awb": "14900080630",
    "courier": "Blitz",
    "date": "2026-06-10",
    "read": 0.55,
    "charged": 0.75,
    "dims": [
      23,
      15,
      19
    ],
    "vol": 1.31,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00111",
    "sku": "1M-TWPO-R0002-BU3",
    "awb": "14900082096",
    "courier": "Shadowfax",
    "date": "2026-06-08",
    "read": 0.68,
    "charged": 0.75,
    "dims": [
      37,
      15,
      6
    ],
    "vol": 0.67,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00113",
    "sku": "1M-TWPO-R0002-BU3",
    "awb": "14900083562",
    "courier": "Delhivery",
    "date": "2026-06-21",
    "read": 0.61,
    "charged": 0.75,
    "dims": [
      20,
      21,
      11
    ],
    "vol": 0.92,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-00115",
    "sku": "1M-BWJG-R0149-BL5",
    "awb": "14900085028",
    "courier": "Bluedart",
    "date": "2026-08-05",
    "read": 1,
    "dims": [
      34,
      22,
      19
    ],
    "vol": 2.84,
    "source": "upload",
    "video": {
      "name": "evidence-116-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-116-len.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-00117",
    "sku": "1M-BWJG-R0149-BL5",
    "awb": "14900086494",
    "courier": "DTDC",
    "date": "2026-05-30",
    "read": 0.65,
    "charged": 0.75,
    "dims": [
      39,
      25,
      19
    ],
    "vol": 3.71,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00119",
    "sku": "1M-BWJG-R0149-BL5",
    "awb": "14900087960",
    "courier": "Bluedart",
    "date": "2026-05-13",
    "read": 0.53,
    "charged": 0.75,
    "dims": [
      35,
      21,
      16
    ],
    "vol": 2.35,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00121",
    "sku": "1M-BWJG-R0149-BL5",
    "awb": "14900089426",
    "courier": "Blitz",
    "date": "2026-05-10",
    "read": 0.85,
    "charged": 1,
    "dims": [
      25,
      19,
      9
    ],
    "vol": 0.86,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00123",
    "sku": "1M-BWJG-R0149-BL5",
    "awb": "14900090892",
    "courier": "Blitz",
    "date": "2026-05-26",
    "read": 0.55,
    "charged": 0.75,
    "dims": [
      38,
      17,
      18
    ],
    "vol": 2.33,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-00125",
    "sku": "1M-BWJG-R0149-PB4",
    "awb": "14900092358",
    "courier": "Bluedart",
    "date": "2026-08-05",
    "read": 0.75,
    "dims": [
      36,
      23,
      11
    ],
    "vol": 1.82,
    "source": "upload",
    "video": {
      "name": "evidence-126-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-126-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-126-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-126-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-126-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00127",
    "sku": "1M-BWJG-R0149-PB4",
    "awb": "14900093824",
    "courier": "Delhivery",
    "date": "2026-07-30",
    "read": 0.75,
    "dims": [
      38,
      26,
      7
    ],
    "vol": 1.38,
    "source": "upload",
    "video": {
      "name": "evidence-128-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-128-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-128-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-128-hgt.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00129",
    "sku": "1M-BWJG-R0149-PB4",
    "awb": "14900095290",
    "courier": "Blitz",
    "date": "2026-07-31",
    "read": 0.75,
    "dims": [
      30,
      21,
      8
    ],
    "vol": 1.01,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-130-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-130-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00131",
    "sku": "1M-BWJG-R0149-PB4",
    "awb": "14900096756",
    "courier": "Xpressbees",
    "date": "2026-07-31",
    "read": 0.75,
    "dims": [
      33,
      26,
      14
    ],
    "vol": 2.4,
    "source": "upload",
    "video": {
      "name": "evidence-132-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-132-len.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00133",
    "sku": "1M-BWJG-R0149-PB4",
    "awb": "14900098222",
    "courier": "Ekart",
    "date": "2026-07-30",
    "read": 0.75,
    "dims": [
      27,
      15,
      16
    ],
    "vol": 1.3,
    "source": "upload",
    "video": {
      "name": "evidence-134-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-134-len.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00135",
    "sku": "1M-BWJG-R0149-PB4",
    "awb": "14900099688",
    "courier": "Blitz",
    "date": "2026-08-03",
    "read": 0.75,
    "dims": [
      23,
      22,
      9
    ],
    "vol": 0.91,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-136-len.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-00137",
    "sku": "1M-BWJG-R0149-PB4",
    "awb": "14900101154",
    "courier": "Ekart",
    "date": "2026-06-06",
    "read": 0.69,
    "charged": 0.75,
    "dims": [
      39,
      17,
      18
    ],
    "vol": 2.39,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00139",
    "sku": "1M-BWJG-R0149-PB4",
    "awb": "14900102620",
    "courier": "Ekart",
    "date": "2026-05-12",
    "read": 0.6,
    "charged": 0.75,
    "dims": [
      28,
      18,
      6
    ],
    "vol": 0.6,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00141",
    "sku": "1M-BWJG-R0149-PB4",
    "awb": "14900104086",
    "courier": "Blitz",
    "date": "2026-06-16",
    "read": 0.7,
    "charged": 0.75,
    "dims": [
      22,
      22,
      9
    ],
    "vol": 0.87,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00143",
    "sku": "1M-BWJG-R0149-PB4",
    "awb": "14900105552",
    "courier": "Xpressbees",
    "date": "2026-05-19",
    "read": 0.72,
    "charged": 0.75,
    "dims": [
      31,
      22,
      14
    ],
    "vol": 1.91,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-00145",
    "sku": "1M-BWJG-R0149-BL3",
    "awb": "14900107018",
    "courier": "Blitz",
    "date": "2026-08-05",
    "read": 0.75,
    "dims": [
      20,
      22,
      16
    ],
    "vol": 1.41,
    "source": "upload",
    "video": {
      "name": "evidence-146-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-146-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-146-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00147",
    "sku": "1M-BWJG-R0149-BL3",
    "awb": "14900108484",
    "courier": "Xpressbees",
    "date": "2026-08-06",
    "read": 0.75,
    "dims": [
      23,
      18,
      13
    ],
    "vol": 1.08,
    "source": "upload",
    "video": {
      "name": "evidence-148-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-148-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-148-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-148-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-148-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00149",
    "sku": "1M-BWJG-R0149-BL3",
    "awb": "14900109950",
    "courier": "DTDC",
    "date": "2026-08-04",
    "read": 1,
    "dims": [
      25,
      28,
      6
    ],
    "vol": 0.84,
    "source": "upload",
    "video": {
      "name": "evidence-150-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-150-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-150-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-150-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-150-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00151",
    "sku": "1M-BWJG-R0149-BL3",
    "awb": "14900111416",
    "courier": "Delhivery",
    "date": "2026-08-04",
    "read": 0.75,
    "dims": [
      32,
      21,
      10
    ],
    "vol": 1.34,
    "source": "upload",
    "video": {
      "name": "evidence-152-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-152-len.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-00153",
    "sku": "1M-BWJG-R0149-BL3",
    "awb": "14900112882",
    "courier": "Delhivery",
    "date": "2026-06-10",
    "read": 0.58,
    "charged": 0.75,
    "dims": [
      24,
      20,
      14
    ],
    "vol": 1.34,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00155",
    "sku": "1M-BWJG-R0149-BL3",
    "awb": "14900114348",
    "courier": "Xpressbees",
    "date": "2026-06-21",
    "read": 0.81,
    "charged": 1,
    "dims": [
      23,
      24,
      6
    ],
    "vol": 0.66,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00157",
    "sku": "1M-BWJG-R0149-BL3",
    "awb": "14900115814",
    "courier": "Ekart",
    "date": "2026-06-24",
    "read": 0.88,
    "charged": 1,
    "dims": [
      23,
      17,
      6
    ],
    "vol": 0.47,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00159",
    "sku": "1M-BWJG-R0149-BL3",
    "awb": "14900117280",
    "courier": "Bluedart",
    "date": "2026-05-08",
    "read": 0.95,
    "charged": 1,
    "dims": [
      31,
      23,
      6
    ],
    "vol": 0.86,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00161",
    "sku": "1M-BWJG-R0149-BL3",
    "awb": "14900118746",
    "courier": "Shadowfax",
    "date": "2026-05-30",
    "read": 0.84,
    "charged": 1,
    "dims": [
      33,
      19,
      7
    ],
    "vol": 0.88,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00163",
    "sku": "1M-TWTS-C0",
    "awb": "14900120212",
    "courier": "Xpressbees",
    "date": "2026-05-20",
    "read": 0.68,
    "charged": 0.75,
    "dims": [
      24,
      24,
      17
    ],
    "vol": 1.96,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00165",
    "sku": "1M-TWTS-C0",
    "awb": "14900121678",
    "courier": "Delhivery",
    "date": "2026-07-20",
    "read": 0.69,
    "charged": 0.75,
    "dims": [
      22,
      21,
      13
    ],
    "vol": 1.2,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00167",
    "sku": "1M-TWTS-C0",
    "awb": "14900123144",
    "courier": "Shadowfax",
    "date": "2026-06-03",
    "read": 0.57,
    "charged": 0.75,
    "dims": [
      37,
      22,
      5
    ],
    "vol": 0.81,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00169",
    "sku": "1M-TWTS-C0",
    "awb": "14900124610",
    "courier": "Shadowfax",
    "date": "2026-06-12",
    "read": 0.66,
    "charged": 0.75,
    "dims": [
      39,
      20,
      9
    ],
    "vol": 1.4,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00171",
    "sku": "1M-TWTS-C0268-MN4",
    "awb": "14900126076",
    "courier": "DTDC",
    "date": "2026-05-08",
    "read": 0.7,
    "charged": 0.75,
    "dims": [
      33,
      19,
      5
    ],
    "vol": 0.63,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00173",
    "sku": "1M-TWTS-C0268-MN4",
    "awb": "14900127542",
    "courier": "Shadowfax",
    "date": "2026-06-11",
    "read": 0.64,
    "charged": 0.75,
    "dims": [
      36,
      16,
      7
    ],
    "vol": 0.81,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00175",
    "sku": "1M-TWTS-C0268-MN4",
    "awb": "14900129008",
    "courier": "Delhivery",
    "date": "2026-07-21",
    "read": 0.71,
    "charged": 0.75,
    "dims": [
      24,
      22,
      5
    ],
    "vol": 0.53,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-00177",
    "sku": "1M-TWZP-R0002-BL3",
    "awb": "14900130474",
    "courier": "DTDC",
    "date": "2026-08-02",
    "read": 1,
    "dims": [
      28,
      24,
      16
    ],
    "vol": 2.15,
    "source": "upload",
    "video": {
      "name": "evidence-178-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-178-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-178-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-178-hgt.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00179",
    "sku": "1M-TWZP-R0002-BL3",
    "awb": "14900131940",
    "courier": "Blitz",
    "date": "2026-08-01",
    "read": 1,
    "dims": [
      21,
      26,
      7
    ],
    "vol": 0.76,
    "source": "upload",
    "video": {
      "name": "evidence-180-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-180-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-180-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-180-hgt.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00181",
    "sku": "1M-TWZP-R0002-BL3",
    "awb": "14900133406",
    "courier": "Delhivery",
    "date": "2026-08-02",
    "read": 1,
    "dims": [
      21,
      17,
      9
    ],
    "vol": 0.64,
    "source": "upload",
    "video": {
      "name": "evidence-182-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-182-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-182-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-182-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-182-scale.jpg",
        "url": null
      },
      {
        "slot": "label",
        "name": "evidence-182-label.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00183",
    "sku": "1M-TWZP-R0002-BL3",
    "awb": "14900134872",
    "courier": "Xpressbees",
    "date": "2026-08-02",
    "read": 1,
    "dims": [
      22,
      18,
      7
    ],
    "vol": 0.55,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-184-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-184-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-184-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-184-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00185",
    "sku": "1M-TWZP-R0002-BL3",
    "awb": "14900136338",
    "courier": "Xpressbees",
    "date": "2026-08-03",
    "read": 1,
    "dims": [
      25,
      27,
      17
    ],
    "vol": 2.3,
    "source": "upload",
    "video": {
      "name": "evidence-186-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-186-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-186-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-186-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-186-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00187",
    "sku": "1M-TWZP-R0002-BL3",
    "awb": "14900137804",
    "courier": "Ekart",
    "date": "2026-07-31",
    "read": 1,
    "dims": [
      27,
      22,
      18
    ],
    "vol": 2.14,
    "source": "upload",
    "video": {
      "name": "evidence-188-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-188-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-188-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-188-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-188-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-00189",
    "sku": "1M-TWZP-R0002-BL3",
    "awb": "14900139270",
    "courier": "Delhivery",
    "date": "2026-06-24",
    "read": 0.97,
    "charged": 1,
    "dims": [
      29,
      28,
      10
    ],
    "vol": 1.62,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00191",
    "sku": "1M-TWZP-R0002-BL3",
    "awb": "14900140736",
    "courier": "Shadowfax",
    "date": "2026-07-04",
    "read": 0.87,
    "charged": 1,
    "dims": [
      34,
      23,
      13
    ],
    "vol": 2.03,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00193",
    "sku": "1M-TWZP-R0002-BL3",
    "awb": "14900142202",
    "courier": "DTDC",
    "date": "2026-05-13",
    "read": 0.94,
    "charged": 1,
    "dims": [
      25,
      19,
      16
    ],
    "vol": 1.52,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-00195",
    "sku": "1M-TWPO-R0002-BU4",
    "awb": "14900143668",
    "courier": "DTDC",
    "date": "2026-07-30",
    "read": 0.75,
    "dims": [
      31,
      26,
      16
    ],
    "vol": 2.58,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-196-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-196-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-196-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-196-scale.jpg",
        "url": null
      },
      {
        "slot": "label",
        "name": "evidence-196-label.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00197",
    "sku": "1M-TWPO-R0002-BU4",
    "awb": "14900145134",
    "courier": "DTDC",
    "date": "2026-08-04",
    "read": 0.75,
    "dims": [
      32,
      21,
      12
    ],
    "vol": 1.61,
    "source": "upload",
    "video": {
      "name": "evidence-198-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-198-len.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00199",
    "sku": "1M-TWPO-R0002-BU4",
    "awb": "14900146600",
    "courier": "Delhivery",
    "date": "2026-07-30",
    "read": 0.75,
    "dims": [
      34,
      19,
      10
    ],
    "vol": 1.29,
    "source": "upload",
    "video": {
      "name": "evidence-200-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-200-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-200-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-200-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-200-scale.jpg",
        "url": null
      },
      {
        "slot": "label",
        "name": "evidence-200-label.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00201",
    "sku": "1M-TWPO-R0002-BU4",
    "awb": "14900148066",
    "courier": "Bluedart",
    "date": "2026-08-03",
    "read": 0.75,
    "dims": [
      38,
      16,
      8
    ],
    "vol": 0.97,
    "source": "upload",
    "video": {
      "name": "evidence-202-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-202-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-202-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-202-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-202-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00203",
    "sku": "1M-TWPO-R0002-BU4",
    "awb": "14900149532",
    "courier": "Delhivery",
    "date": "2026-08-06",
    "read": 0.75,
    "dims": [
      20,
      19,
      14
    ],
    "vol": 1.06,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-204-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-204-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-00205",
    "sku": "1M-TWPO-R0002-BU4",
    "awb": "14900150998",
    "courier": "Blitz",
    "date": "2026-06-27",
    "read": 0.59,
    "charged": 0.75,
    "dims": [
      35,
      15,
      11
    ],
    "vol": 1.16,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00207",
    "sku": "1M-TWPO-R0002-BU4",
    "awb": "14900152464",
    "courier": "Xpressbees",
    "date": "2026-06-21",
    "read": 0.6,
    "charged": 0.75,
    "dims": [
      24,
      28,
      14
    ],
    "vol": 1.88,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00209",
    "sku": "1M-TWPO-R0002-BU4",
    "awb": "14900153930",
    "courier": "Xpressbees",
    "date": "2026-07-07",
    "read": 0.66,
    "charged": 0.75,
    "dims": [
      23,
      29,
      14
    ],
    "vol": 1.87,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00211",
    "sku": "1M-TWPO-R0002-BU4",
    "awb": "14900155396",
    "courier": "Shadowfax",
    "date": "2026-05-31",
    "read": 0.55,
    "charged": 0.75,
    "dims": [
      39,
      26,
      11
    ],
    "vol": 2.23,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00213",
    "sku": "1M-TWPO-R0002-BU4",
    "awb": "14900156862",
    "courier": "Delhivery",
    "date": "2026-06-11",
    "read": 0.73,
    "charged": 0.75,
    "dims": [
      24,
      20,
      7
    ],
    "vol": 0.67,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-00215",
    "sku": "1M-B",
    "awb": "14900158328",
    "courier": "Xpressbees",
    "date": "2026-07-30",
    "read": 1,
    "dims": [
      34,
      18,
      10
    ],
    "vol": 1.22,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-216-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-216-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-216-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-216-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00217",
    "sku": "1M-B",
    "awb": "14900159794",
    "courier": "Bluedart",
    "date": "2026-08-01",
    "read": 1,
    "dims": [
      23,
      28,
      9
    ],
    "vol": 1.16,
    "source": "upload",
    "video": {
      "name": "evidence-218-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-218-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-218-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00219",
    "sku": "1M-B",
    "awb": "14900161260",
    "courier": "Shadowfax",
    "date": "2026-08-02",
    "read": 1,
    "dims": [
      30,
      27,
      13
    ],
    "vol": 2.11,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-220-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-220-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-220-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-220-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00221",
    "sku": "1M-B",
    "awb": "14900162726",
    "courier": "Delhivery",
    "date": "2026-08-04",
    "read": 1,
    "dims": [
      36,
      23,
      16
    ],
    "vol": 2.65,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-222-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-222-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-222-hgt.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00223",
    "sku": "1M-B",
    "awb": "14900164192",
    "courier": "DTDC",
    "date": "2026-08-06",
    "read": 0.75,
    "dims": [
      22,
      22,
      13
    ],
    "vol": 1.26,
    "source": "upload",
    "video": {
      "name": "evidence-224-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-224-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-224-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-224-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-224-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00225",
    "sku": "1M-B",
    "awb": "14900165658",
    "courier": "DTDC",
    "date": "2026-08-05",
    "read": 1,
    "dims": [
      29,
      28,
      5
    ],
    "vol": 0.81,
    "source": "upload",
    "video": {
      "name": "evidence-226-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-226-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-226-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-00227",
    "sku": "1M-B",
    "awb": "14900167124",
    "courier": "Blitz",
    "date": "2026-07-11",
    "read": 0.86,
    "charged": 1,
    "dims": [
      28,
      26,
      13
    ],
    "vol": 1.89,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00229",
    "sku": "1M-B",
    "awb": "14900168590",
    "courier": "Blitz",
    "date": "2026-05-04",
    "read": 0.97,
    "charged": 1,
    "dims": [
      21,
      20,
      7
    ],
    "vol": 0.59,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00231",
    "sku": "1M-B",
    "awb": "14900170056",
    "courier": "Blitz",
    "date": "2026-06-12",
    "read": 0.89,
    "charged": 1,
    "dims": [
      28,
      19,
      9
    ],
    "vol": 0.96,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00233",
    "sku": "1M-B",
    "awb": "14900171522",
    "courier": "Bluedart",
    "date": "2026-07-10",
    "read": 0.65,
    "charged": 0.75,
    "dims": [
      36,
      26,
      19
    ],
    "vol": 3.56,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00235",
    "sku": "NBJUN24MJ05-Men-Joggers-Black-L",
    "awb": "14900172988",
    "courier": "Shadowfax",
    "date": "2026-06-12",
    "read": 0.71,
    "charged": 0.75,
    "dims": [
      25,
      22,
      12
    ],
    "vol": 1.32,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00237",
    "sku": "NBJUN24MJ05-Men-Joggers-Black-L",
    "awb": "14900174454",
    "courier": "Shadowfax",
    "date": "2026-05-17",
    "read": 0.87,
    "charged": 1,
    "dims": [
      38,
      18,
      8
    ],
    "vol": 1.09,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00239",
    "sku": "NBJUN24MJ05-Men-Joggers-Black-L",
    "awb": "14900175920",
    "courier": "Delhivery",
    "date": "2026-05-27",
    "read": 0.84,
    "charged": 1,
    "dims": [
      35,
      24,
      15
    ],
    "vol": 2.52,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-00241",
    "sku": "1M-TWSS-R0032-BL3",
    "awb": "14900177386",
    "courier": "Shadowfax",
    "date": "2026-08-03",
    "read": 1,
    "dims": [
      39,
      27,
      16
    ],
    "vol": 3.37,
    "source": "upload",
    "video": {
      "name": "evidence-242-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-242-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-242-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-242-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-242-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-00243",
    "sku": "1M-TWSS-R0032-BL3",
    "awb": "14900178852",
    "courier": "Delhivery",
    "date": "2026-05-14",
    "read": 0.84,
    "charged": 1,
    "dims": [
      26,
      23,
      14
    ],
    "vol": 1.67,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00245",
    "sku": "1M-TWSS-R0032-BL3",
    "awb": "14900180318",
    "courier": "DTDC",
    "date": "2026-06-18",
    "read": 0.64,
    "charged": 0.75,
    "dims": [
      36,
      29,
      5
    ],
    "vol": 1.04,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00247",
    "sku": "1M-TWSS-R0032-BL3",
    "awb": "14900181784",
    "courier": "Ekart",
    "date": "2026-05-08",
    "read": 0.81,
    "charged": 1,
    "dims": [
      22,
      23,
      11
    ],
    "vol": 1.11,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-00249",
    "sku": "1M-TWTS-C0 (dup 2)",
    "awb": "14900183250",
    "courier": "Shadowfax",
    "date": "2026-07-31",
    "read": 0.75,
    "dims": [
      33,
      21,
      16
    ],
    "vol": 2.22,
    "source": "upload",
    "video": {
      "name": "evidence-250-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-250-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-250-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-250-hgt.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00251",
    "sku": "1M-TWTS-C0 (dup 2)",
    "awb": "14900184716",
    "courier": "Blitz",
    "date": "2026-08-02",
    "read": 0.75,
    "dims": [
      27,
      18,
      18
    ],
    "vol": 1.75,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-252-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-252-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00253",
    "sku": "1M-TWTS-C0 (dup 2)",
    "awb": "14900186182",
    "courier": "Blitz",
    "date": "2026-08-03",
    "read": 0.75,
    "dims": [
      21,
      24,
      7
    ],
    "vol": 0.71,
    "source": "upload",
    "video": {
      "name": "evidence-254-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-254-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-254-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-254-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-254-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-00255",
    "sku": "1M-TWTS-C0 (dup 2)",
    "awb": "14900187648",
    "courier": "Delhivery",
    "date": "2026-07-08",
    "read": 0.54,
    "charged": 0.75,
    "dims": [
      26,
      17,
      8
    ],
    "vol": 0.71,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00257",
    "sku": "1M-TWTS-C0 (dup 2)",
    "awb": "14900189114",
    "courier": "Xpressbees",
    "date": "2026-06-07",
    "read": 0.66,
    "charged": 0.75,
    "dims": [
      36,
      29,
      8
    ],
    "vol": 1.67,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00259",
    "sku": "1M-BWCP-R0002-BL4",
    "awb": "14900190580",
    "courier": "Shadowfax",
    "date": "2026-06-05",
    "read": 0.85,
    "charged": 1,
    "dims": [
      29,
      18,
      16
    ],
    "vol": 1.67,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00261",
    "sku": "1M-BWCP-R0002-BL4",
    "awb": "14900192046",
    "courier": "Ekart",
    "date": "2026-06-12",
    "read": 0.91,
    "charged": 1,
    "dims": [
      22,
      23,
      16
    ],
    "vol": 1.62,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00263",
    "sku": "1M-TWTS-C09",
    "awb": "14900193512",
    "courier": "Xpressbees",
    "date": "2026-07-01",
    "read": 0.73,
    "charged": 0.75,
    "dims": [
      37,
      19,
      13
    ],
    "vol": 1.83,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00265",
    "sku": "1M-TWTS-C09",
    "awb": "14900194978",
    "courier": "Delhivery",
    "date": "2026-06-18",
    "read": 0.67,
    "charged": 0.75,
    "dims": [
      33,
      23,
      14
    ],
    "vol": 2.13,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00267",
    "sku": "1M-TWTS-C09",
    "awb": "14900196444",
    "courier": "Bluedart",
    "date": "2026-07-26",
    "read": 0.69,
    "charged": 0.75,
    "dims": [
      33,
      15,
      14
    ],
    "vol": 1.39,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00269",
    "sku": "1M-TWTS-C09",
    "awb": "14900197910",
    "courier": "DTDC",
    "date": "2026-06-02",
    "read": 0.55,
    "charged": 0.75,
    "dims": [
      29,
      16,
      6
    ],
    "vol": 0.56,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00271",
    "sku": "1M-TWTS-C09",
    "awb": "14900199376",
    "courier": "Delhivery",
    "date": "2026-06-11",
    "read": 0.54,
    "charged": 0.75,
    "dims": [
      33,
      23,
      11
    ],
    "vol": 1.67,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00273",
    "sku": "1M-TWTS-C0969-OG3",
    "awb": "14900200842",
    "courier": "Shadowfax",
    "date": "2026-05-21",
    "read": 0.6,
    "charged": 0.75,
    "dims": [
      26,
      20,
      17
    ],
    "vol": 1.77,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00275",
    "sku": "1M-TWTS-C0969-OG3",
    "awb": "14900202308",
    "courier": "Ekart",
    "date": "2026-06-19",
    "read": 0.66,
    "charged": 0.75,
    "dims": [
      32,
      22,
      8
    ],
    "vol": 1.13,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00277",
    "sku": "1M-TWTS-C0969-OG3",
    "awb": "14900203774",
    "courier": "Xpressbees",
    "date": "2026-05-01",
    "read": 0.64,
    "charged": 0.75,
    "dims": [
      31,
      19,
      8
    ],
    "vol": 0.94,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00279",
    "sku": "1M-TWTS-C09 (dup 2)",
    "awb": "14900205240",
    "courier": "Bluedart",
    "date": "2026-07-26",
    "read": 0.64,
    "charged": 0.75,
    "dims": [
      32,
      20,
      11
    ],
    "vol": 1.41,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00281",
    "sku": "1M-TWTS-C09 (dup 2)",
    "awb": "14900206706",
    "courier": "Delhivery",
    "date": "2026-06-12",
    "read": 0.6,
    "charged": 0.75,
    "dims": [
      26,
      15,
      19
    ],
    "vol": 1.48,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00283",
    "sku": "1M-TWTS-C09 (dup 2)",
    "awb": "14900208172",
    "courier": "Ekart",
    "date": "2026-07-13",
    "read": 0.7,
    "charged": 0.75,
    "dims": [
      27,
      16,
      15
    ],
    "vol": 1.3,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00285",
    "sku": "1M-TWTS-C09 (dup 2)",
    "awb": "14900209638",
    "courier": "Xpressbees",
    "date": "2026-06-03",
    "read": 0.71,
    "charged": 0.75,
    "dims": [
      29,
      22,
      5
    ],
    "vol": 0.64,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00287",
    "sku": "1M-TWTS-C09 (dup 2)",
    "awb": "14900211104",
    "courier": "Bluedart",
    "date": "2026-07-06",
    "read": 0.55,
    "charged": 0.75,
    "dims": [
      24,
      29,
      19
    ],
    "vol": 2.64,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00289",
    "sku": "1M-TWTS-C09 (dup 2)",
    "awb": "14900212570",
    "courier": "Bluedart",
    "date": "2026-05-01",
    "read": 0.67,
    "charged": 0.75,
    "dims": [
      21,
      19,
      12
    ],
    "vol": 0.96,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00291",
    "sku": "1M-",
    "awb": "14900214036",
    "courier": "Blitz",
    "date": "2026-06-14",
    "read": 0.55,
    "charged": 0.75,
    "dims": [
      35,
      16,
      7
    ],
    "vol": 0.78,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00293",
    "sku": "1M-",
    "awb": "14900215502",
    "courier": "Delhivery",
    "date": "2026-07-09",
    "read": 0.82,
    "charged": 1,
    "dims": [
      25,
      24,
      12
    ],
    "vol": 1.44,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00295",
    "sku": "1M-",
    "awb": "14900216968",
    "courier": "Blitz",
    "date": "2026-07-14",
    "read": 0.92,
    "charged": 1,
    "dims": [
      38,
      27,
      18
    ],
    "vol": 3.69,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-00297",
    "sku": "1M-BWJG-R0149-NB5",
    "awb": "14900218434",
    "courier": "Delhivery",
    "date": "2026-08-04",
    "read": 0.75,
    "dims": [
      30,
      29,
      18
    ],
    "vol": 3.13,
    "source": "upload",
    "video": {
      "name": "evidence-298-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-298-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-298-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-298-hgt.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00299",
    "sku": "1M-BWJG-R0149-NB5",
    "awb": "14900219900",
    "courier": "Ekart",
    "date": "2026-08-05",
    "read": 0.75,
    "dims": [
      27,
      23,
      16
    ],
    "vol": 1.99,
    "source": "upload",
    "video": {
      "name": "evidence-300-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-300-len.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00301",
    "sku": "1M-BWJG-R0149-NB5",
    "awb": "14900221366",
    "courier": "Xpressbees",
    "date": "2026-08-03",
    "read": 0.75,
    "dims": [
      24,
      17,
      8
    ],
    "vol": 0.65,
    "source": "upload",
    "video": {
      "name": "evidence-302-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-302-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-302-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-302-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-302-scale.jpg",
        "url": null
      },
      {
        "slot": "label",
        "name": "evidence-302-label.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-00303",
    "sku": "1M-BWJG-R0149-NB5",
    "awb": "14900222832",
    "courier": "Bluedart",
    "date": "2026-05-07",
    "read": 0.54,
    "charged": 0.75,
    "dims": [
      24,
      18,
      7
    ],
    "vol": 0.6,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00305",
    "sku": "1M-BWJG-R0149-NB5",
    "awb": "14900224298",
    "courier": "Shadowfax",
    "date": "2026-05-25",
    "read": 0.58,
    "charged": 0.75,
    "dims": [
      24,
      26,
      19
    ],
    "vol": 2.37,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00307",
    "sku": "1M-BWJG-R0149-NB5",
    "awb": "14900225764",
    "courier": "Ekart",
    "date": "2026-07-17",
    "read": 0.55,
    "charged": 0.75,
    "dims": [
      35,
      25,
      19
    ],
    "vol": 3.33,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00309",
    "sku": "1M-BWJG-R0149-NB5",
    "awb": "14900227230",
    "courier": "Delhivery",
    "date": "2026-05-08",
    "read": 0.63,
    "charged": 0.75,
    "dims": [
      27,
      22,
      18
    ],
    "vol": 2.14,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00311",
    "sku": "1M-BWJG-R0149-NB5",
    "awb": "14900228696",
    "courier": "Shadowfax",
    "date": "2026-05-11",
    "read": 0.59,
    "charged": 0.75,
    "dims": [
      21,
      20,
      14
    ],
    "vol": 1.18,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-00313",
    "sku": "1M-BWCP-R0002-BL3",
    "awb": "14900230162",
    "courier": "Shadowfax",
    "date": "2026-08-03",
    "read": 0.75,
    "dims": [
      22,
      15,
      10
    ],
    "vol": 0.66,
    "source": "upload",
    "video": {
      "name": "evidence-314-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-314-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-314-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-314-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-314-scale.jpg",
        "url": null
      },
      {
        "slot": "label",
        "name": "evidence-314-label.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-00315",
    "sku": "1M-BWCP-R0002-BL3",
    "awb": "14900231628",
    "courier": "Ekart",
    "date": "2026-05-12",
    "read": 0.71,
    "charged": 0.75,
    "dims": [
      35,
      20,
      15
    ],
    "vol": 2.1,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00317",
    "sku": "1M-BWCP-R0002-BL3",
    "awb": "14900233094",
    "courier": "Xpressbees",
    "date": "2026-05-21",
    "read": 0.83,
    "charged": 1,
    "dims": [
      38,
      20,
      7
    ],
    "vol": 1.06,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-00319",
    "sku": "1M-TWTS-C1059-BL4",
    "awb": "14900234560",
    "courier": "Shadowfax",
    "date": "2026-08-03",
    "read": 0.75,
    "dims": [
      24,
      25,
      11
    ],
    "vol": 1.32,
    "source": "upload",
    "video": {
      "name": "evidence-320-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-320-len.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00321",
    "sku": "1M-TWTS-C1059-BL4",
    "awb": "14900236026",
    "courier": "Ekart",
    "date": "2026-08-03",
    "read": 0.75,
    "dims": [
      33,
      20,
      17
    ],
    "vol": 2.24,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-322-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-322-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-322-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-322-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00323",
    "sku": "1M-TWTS-C1059-BL4",
    "awb": "14900237492",
    "courier": "Delhivery",
    "date": "2026-08-04",
    "read": 0.75,
    "dims": [
      38,
      25,
      17
    ],
    "vol": 3.23,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-324-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-324-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-324-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-324-scale.jpg",
        "url": null
      },
      {
        "slot": "label",
        "name": "evidence-324-label.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00325",
    "sku": "1M-TWTS-C1059-BL4",
    "awb": "14900238958",
    "courier": "Blitz",
    "date": "2026-08-04",
    "read": 0.75,
    "dims": [
      23,
      23,
      8
    ],
    "vol": 0.85,
    "source": "upload",
    "video": {
      "name": "evidence-326-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-326-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-326-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00327",
    "sku": "1M-TWTS-C1059-BL4",
    "awb": "14900240424",
    "courier": "Ekart",
    "date": "2026-08-03",
    "read": 1,
    "dims": [
      29,
      22,
      12
    ],
    "vol": 1.53,
    "source": "upload",
    "video": {
      "name": "evidence-328-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-328-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-328-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-328-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-328-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-00329",
    "sku": "1M-TWTS-C1059-BL4",
    "awb": "14900241890",
    "courier": "DTDC",
    "date": "2026-05-05",
    "read": 0.64,
    "charged": 0.75,
    "dims": [
      39,
      27,
      14
    ],
    "vol": 2.95,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00331",
    "sku": "1M-TWTS-C1059-BL4",
    "awb": "14900243356",
    "courier": "Bluedart",
    "date": "2026-05-31",
    "read": 0.56,
    "charged": 0.75,
    "dims": [
      24,
      15,
      5
    ],
    "vol": 0.36,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00333",
    "sku": "1M-TWTS-C1059-BL4",
    "awb": "14900244822",
    "courier": "Xpressbees",
    "date": "2026-07-20",
    "read": 0.65,
    "charged": 0.75,
    "dims": [
      23,
      27,
      17
    ],
    "vol": 2.11,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00335",
    "sku": "1M-TWTS-C1059-BL4",
    "awb": "14900246288",
    "courier": "Xpressbees",
    "date": "2026-05-30",
    "read": 0.63,
    "charged": 0.75,
    "dims": [
      24,
      27,
      18
    ],
    "vol": 2.33,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00337",
    "sku": "1M-TWTS-C1059-BL4",
    "awb": "14900247754",
    "courier": "Blitz",
    "date": "2026-06-26",
    "read": 0.84,
    "charged": 1,
    "dims": [
      22,
      23,
      5
    ],
    "vol": 0.51,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-00339",
    "sku": "1M-LWCS-R0177-NB4",
    "awb": "14900249220",
    "courier": "Shadowfax",
    "date": "2026-08-03",
    "read": 1,
    "dims": [
      32,
      26,
      17
    ],
    "vol": 2.83,
    "source": "upload",
    "video": {
      "name": "evidence-340-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-340-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-340-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00341",
    "sku": "1M-LWCS-R0177-NB4",
    "awb": "14900250686",
    "courier": "Shadowfax",
    "date": "2026-07-30",
    "read": 1,
    "dims": [
      37,
      15,
      14
    ],
    "vol": 1.55,
    "source": "upload",
    "video": {
      "name": "evidence-342-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-342-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-342-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-342-hgt.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00343",
    "sku": "1M-LWCS-R0177-NB4",
    "awb": "14900252152",
    "courier": "Shadowfax",
    "date": "2026-08-03",
    "read": 1,
    "dims": [
      38,
      24,
      13
    ],
    "vol": 2.37,
    "source": "upload",
    "video": {
      "name": "evidence-344-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-344-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-344-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-344-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-344-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00345",
    "sku": "1M-LWCS-R0177-NB4",
    "awb": "14900253618",
    "courier": "Delhivery",
    "date": "2026-08-04",
    "read": 1,
    "dims": [
      30,
      20,
      8
    ],
    "vol": 0.96,
    "source": "upload",
    "video": {
      "name": "evidence-346-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-346-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-346-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-346-hgt.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00347",
    "sku": "1M-LWCS-R0177-NB4",
    "awb": "14900255084",
    "courier": "DTDC",
    "date": "2026-07-30",
    "read": 1,
    "dims": [
      31,
      29,
      14
    ],
    "vol": 2.52,
    "source": "upload",
    "video": {
      "name": "evidence-348-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-348-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-348-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00349",
    "sku": "1M-LWCS-R0177-NB4",
    "awb": "14900256550",
    "courier": "Blitz",
    "date": "2026-08-05",
    "read": 1,
    "dims": [
      34,
      23,
      16
    ],
    "vol": 2.5,
    "source": "upload",
    "video": {
      "name": "evidence-350-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-350-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-350-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-350-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-350-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-00351",
    "sku": "1M-LWCS-R0177-NB4",
    "awb": "14900258016",
    "courier": "Ekart",
    "date": "2026-06-05",
    "read": 0.55,
    "charged": 0.75,
    "dims": [
      22,
      16,
      10
    ],
    "vol": 0.7,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00353",
    "sku": "1M-LWCS-R0177-NB4",
    "awb": "14900259482",
    "courier": "Bluedart",
    "date": "2026-07-15",
    "read": 0.82,
    "charged": 1,
    "dims": [
      36,
      26,
      14
    ],
    "vol": 2.62,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00355",
    "sku": "1M-LWCS-R0177-NB4",
    "awb": "14900260948",
    "courier": "Bluedart",
    "date": "2026-07-02",
    "read": 0.83,
    "charged": 1,
    "dims": [
      30,
      16,
      16
    ],
    "vol": 1.54,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00357",
    "sku": "1M-LWCS-R0177-NB4",
    "awb": "14900262414",
    "courier": "Shadowfax",
    "date": "2026-05-27",
    "read": 0.81,
    "charged": 1,
    "dims": [
      30,
      28,
      7
    ],
    "vol": 1.18,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-00359",
    "sku": "1M-TWTS-C0 (dup 3)",
    "awb": "14900263880",
    "courier": "Ekart",
    "date": "2026-08-04",
    "read": 0.75,
    "dims": [
      30,
      21,
      15
    ],
    "vol": 1.89,
    "source": "upload",
    "video": {
      "name": "evidence-360-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-360-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-360-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-360-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-360-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00361",
    "sku": "1M-TWTS-C0 (dup 3)",
    "awb": "14900265346",
    "courier": "Delhivery",
    "date": "2026-07-31",
    "read": 0.75,
    "dims": [
      31,
      24,
      15
    ],
    "vol": 2.23,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-362-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-362-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-362-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-362-scale.jpg",
        "url": null
      },
      {
        "slot": "label",
        "name": "evidence-362-label.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00363",
    "sku": "1M-TWTS-C0 (dup 3)",
    "awb": "14900266812",
    "courier": "Shadowfax",
    "date": "2026-07-31",
    "read": 0.75,
    "dims": [
      38,
      19,
      11
    ],
    "vol": 1.59,
    "source": "upload",
    "video": {
      "name": "evidence-364-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-364-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-364-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-364-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-364-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00365",
    "sku": "1M-TWTS-C0 (dup 3)",
    "awb": "14900268278",
    "courier": "Shadowfax",
    "date": "2026-08-02",
    "read": 0.75,
    "dims": [
      31,
      27,
      16
    ],
    "vol": 2.68,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-366-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-366-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-00367",
    "sku": "1M-TWTS-C0 (dup 3)",
    "awb": "14900269744",
    "courier": "Ekart",
    "date": "2026-05-16",
    "read": 0.67,
    "charged": 0.75,
    "dims": [
      33,
      17,
      11
    ],
    "vol": 1.23,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00369",
    "sku": "1M-TWTS-C0 (dup 3)",
    "awb": "14900271210",
    "courier": "Shadowfax",
    "date": "2026-05-02",
    "read": 0.63,
    "charged": 0.75,
    "dims": [
      29,
      29,
      8
    ],
    "vol": 1.35,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00371",
    "sku": "1M-TWTS-C0 (dup 3)",
    "awb": "14900272676",
    "courier": "Ekart",
    "date": "2026-07-20",
    "read": 0.7,
    "charged": 0.75,
    "dims": [
      32,
      15,
      16
    ],
    "vol": 1.54,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00373",
    "sku": "1M-TWTS-C0 (dup 3)",
    "awb": "14900274142",
    "courier": "DTDC",
    "date": "2026-05-04",
    "read": 0.68,
    "charged": 0.75,
    "dims": [
      28,
      21,
      6
    ],
    "vol": 0.71,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00375",
    "sku": "1M-TWTS-C0 (dup 3)",
    "awb": "14900275608",
    "courier": "Shadowfax",
    "date": "2026-05-12",
    "read": 0.63,
    "charged": 0.75,
    "dims": [
      32,
      19,
      9
    ],
    "vol": 1.09,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00377",
    "sku": "1M-TWTS-C0 (dup 3)",
    "awb": "14900277074",
    "courier": "DTDC",
    "date": "2026-05-15",
    "read": 0.64,
    "charged": 0.75,
    "dims": [
      20,
      21,
      12
    ],
    "vol": 1.01,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00379",
    "sku": "1M-TWTS-C",
    "awb": "14900278540",
    "courier": "Shadowfax",
    "date": "2026-05-10",
    "read": 0.69,
    "charged": 0.75,
    "dims": [
      33,
      21,
      7
    ],
    "vol": 0.97,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00381",
    "sku": "1M-TWTS-C",
    "awb": "14900280006",
    "courier": "Blitz",
    "date": "2026-07-11",
    "read": 0.66,
    "charged": 0.75,
    "dims": [
      36,
      17,
      18
    ],
    "vol": 2.2,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00383",
    "sku": "1M-TWTS-C",
    "awb": "14900281472",
    "courier": "DTDC",
    "date": "2026-05-10",
    "read": 0.65,
    "charged": 0.75,
    "dims": [
      28,
      28,
      17
    ],
    "vol": 2.67,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00385",
    "sku": "1M-TWTS-C",
    "awb": "14900282938",
    "courier": "DTDC",
    "date": "2026-05-04",
    "read": 0.65,
    "charged": 0.75,
    "dims": [
      33,
      23,
      15
    ],
    "vol": 2.28,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00387",
    "sku": "1M-TWTS-C",
    "awb": "14900284404",
    "courier": "DTDC",
    "date": "2026-05-28",
    "read": 0.67,
    "charged": 0.75,
    "dims": [
      25,
      18,
      11
    ],
    "vol": 0.99,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-00389",
    "sku": "1M-BWJG-R0149-PB5",
    "awb": "14900285870",
    "courier": "Delhivery",
    "date": "2026-08-02",
    "read": 1,
    "dims": [
      29,
      23,
      6
    ],
    "vol": 0.8,
    "source": "upload",
    "video": {
      "name": "evidence-390-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-390-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-390-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-390-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-390-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00391",
    "sku": "1M-BWJG-R0149-PB5",
    "awb": "14900287336",
    "courier": "Delhivery",
    "date": "2026-08-04",
    "read": 0.75,
    "dims": [
      34,
      15,
      5
    ],
    "vol": 0.51,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-392-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-392-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-392-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-392-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-00393",
    "sku": "1M-BWJG-R0149-PB5",
    "awb": "14900288802",
    "courier": "Bluedart",
    "date": "2026-04-30",
    "read": 0.7,
    "charged": 0.75,
    "dims": [
      39,
      22,
      10
    ],
    "vol": 1.72,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00395",
    "sku": "1M-BWJG-R0149-PB5",
    "awb": "14900290268",
    "courier": "Ekart",
    "date": "2026-05-01",
    "read": 0.96,
    "charged": 1,
    "dims": [
      36,
      15,
      10
    ],
    "vol": 1.08,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00397",
    "sku": "1M-B (dup 2)",
    "awb": "14900291734",
    "courier": "Ekart",
    "date": "2026-06-18",
    "read": 0.55,
    "charged": 0.75,
    "dims": [
      29,
      28,
      11
    ],
    "vol": 1.79,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00399",
    "sku": "1M-B (dup 2)",
    "awb": "14900293200",
    "courier": "Delhivery",
    "date": "2026-06-19",
    "read": 0.53,
    "charged": 0.75,
    "dims": [
      26,
      16,
      15
    ],
    "vol": 1.25,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00401",
    "sku": "1M-B (dup 2)",
    "awb": "14900294666",
    "courier": "Blitz",
    "date": "2026-05-30",
    "read": 0.61,
    "charged": 0.75,
    "dims": [
      21,
      18,
      9
    ],
    "vol": 0.68,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00403",
    "sku": "1M-B (dup 2)",
    "awb": "14900296132",
    "courier": "DTDC",
    "date": "2026-06-04",
    "read": 0.57,
    "charged": 0.75,
    "dims": [
      25,
      20,
      15
    ],
    "vol": 1.5,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00405",
    "sku": "1M-B (dup 2)",
    "awb": "14900297598",
    "courier": "Blitz",
    "date": "2026-07-28",
    "read": 0.72,
    "charged": 0.75,
    "dims": [
      21,
      24,
      6
    ],
    "vol": 0.6,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00407",
    "sku": "1M-B (dup 2)",
    "awb": "14900299064",
    "courier": "Shadowfax",
    "date": "2026-06-24",
    "read": 0.73,
    "charged": 0.75,
    "dims": [
      22,
      23,
      14
    ],
    "vol": 1.42,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-00409",
    "sku": "1M-TWTS-C0218",
    "awb": "14900300530",
    "courier": "Ekart",
    "date": "2026-08-05",
    "read": 0.75,
    "dims": [
      36,
      17,
      18
    ],
    "vol": 2.2,
    "source": "upload",
    "video": {
      "name": "evidence-410-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-410-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-410-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-410-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-410-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00411",
    "sku": "1M-TWTS-C0218",
    "awb": "14900301996",
    "courier": "Bluedart",
    "date": "2026-08-06",
    "read": 0.75,
    "dims": [
      39,
      15,
      12
    ],
    "vol": 1.4,
    "source": "upload",
    "video": {
      "name": "evidence-412-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-412-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-412-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00413",
    "sku": "1M-TWTS-C0218",
    "awb": "14900303462",
    "courier": "Shadowfax",
    "date": "2026-08-01",
    "read": 0.75,
    "dims": [
      23,
      27,
      7
    ],
    "vol": 0.87,
    "source": "upload",
    "video": {
      "name": "evidence-414-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-414-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-414-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00415",
    "sku": "1M-TWTS-C0218",
    "awb": "14900304928",
    "courier": "DTDC",
    "date": "2026-07-30",
    "read": 0.75,
    "dims": [
      27,
      26,
      19
    ],
    "vol": 2.67,
    "source": "upload",
    "video": {
      "name": "evidence-416-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-416-len.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00417",
    "sku": "1M-TWTS-C0218",
    "awb": "14900306394",
    "courier": "Shadowfax",
    "date": "2026-08-02",
    "read": 0.75,
    "dims": [
      25,
      15,
      14
    ],
    "vol": 1.05,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-418-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-418-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-418-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-418-scale.jpg",
        "url": null
      },
      {
        "slot": "label",
        "name": "evidence-418-label.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00419",
    "sku": "1M-TWTS-C0218",
    "awb": "14900307860",
    "courier": "Ekart",
    "date": "2026-08-03",
    "read": 0.75,
    "dims": [
      26,
      23,
      12
    ],
    "vol": 1.44,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-420-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-420-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-00421",
    "sku": "1M-TWTS-C0218",
    "awb": "14900309326",
    "courier": "Blitz",
    "date": "2026-05-17",
    "read": 0.65,
    "charged": 0.75,
    "dims": [
      23,
      27,
      19
    ],
    "vol": 2.36,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00423",
    "sku": "1M-TWTS-C0218",
    "awb": "14900310792",
    "courier": "Blitz",
    "date": "2026-07-02",
    "read": 0.71,
    "charged": 0.75,
    "dims": [
      20,
      25,
      7
    ],
    "vol": 0.7,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-00425",
    "sku": "1M-TWTS-C0889-PBL (dup 2)",
    "awb": "14900312258",
    "courier": "DTDC",
    "date": "2026-07-30",
    "read": 0.75,
    "dims": [
      27,
      23,
      16
    ],
    "vol": 1.99,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-426-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-426-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00427",
    "sku": "1M-TWTS-C0889-PBL (dup 2)",
    "awb": "14900313724",
    "courier": "Shadowfax",
    "date": "2026-08-04",
    "read": 0.75,
    "dims": [
      37,
      23,
      18
    ],
    "vol": 3.06,
    "source": "upload",
    "video": {
      "name": "evidence-428-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-428-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-428-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-428-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-428-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00429",
    "sku": "1M-TWTS-C0889-PBL (dup 2)",
    "awb": "14900315190",
    "courier": "Shadowfax",
    "date": "2026-08-01",
    "read": 0.75,
    "dims": [
      30,
      17,
      16
    ],
    "vol": 1.63,
    "source": "upload",
    "video": {
      "name": "evidence-430-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-430-len.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00431",
    "sku": "1M-TWTS-C0889-PBL (dup 2)",
    "awb": "14900316656",
    "courier": "DTDC",
    "date": "2026-07-31",
    "read": 0.75,
    "dims": [
      26,
      28,
      18
    ],
    "vol": 2.62,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-432-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-432-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-432-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-432-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-00433",
    "sku": "1M-TWTS-C0889-PBL (dup 2)",
    "awb": "14900318122",
    "courier": "DTDC",
    "date": "2026-06-15",
    "read": 0.58,
    "charged": 0.75,
    "dims": [
      30,
      19,
      17
    ],
    "vol": 1.94,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00435",
    "sku": "1M-TWTS-C0889-PBL (dup 2)",
    "awb": "14900319588",
    "courier": "Shadowfax",
    "date": "2026-07-06",
    "read": 0.66,
    "charged": 0.75,
    "dims": [
      31,
      24,
      7
    ],
    "vol": 1.04,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-00437",
    "sku": "1M-BWJG-R0149-NB4",
    "awb": "14900321054",
    "courier": "DTDC",
    "date": "2026-08-01",
    "read": 0.75,
    "dims": [
      20,
      19,
      14
    ],
    "vol": 1.06,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-438-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-438-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-438-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-438-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00439",
    "sku": "1M-BWJG-R0149-NB4",
    "awb": "14900322520",
    "courier": "Ekart",
    "date": "2026-08-04",
    "read": 0.75,
    "dims": [
      27,
      24,
      15
    ],
    "vol": 1.94,
    "source": "upload",
    "video": {
      "name": "evidence-440-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-440-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-440-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-440-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-440-scale.jpg",
        "url": null
      },
      {
        "slot": "label",
        "name": "evidence-440-label.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00441",
    "sku": "1M-BWJG-R0149-NB4",
    "awb": "14900323986",
    "courier": "Ekart",
    "date": "2026-08-06",
    "read": 0.75,
    "dims": [
      26,
      18,
      11
    ],
    "vol": 1.03,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-442-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-442-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-442-hgt.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00443",
    "sku": "1M-BWJG-R0149-NB4",
    "awb": "14900325452",
    "courier": "DTDC",
    "date": "2026-08-05",
    "read": 0.75,
    "dims": [
      28,
      25,
      13
    ],
    "vol": 1.82,
    "source": "upload",
    "video": {
      "name": "evidence-444-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-444-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-444-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-444-hgt.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00445",
    "sku": "1M-BWJG-R0149-NB4",
    "awb": "14900326918",
    "courier": "Blitz",
    "date": "2026-08-01",
    "read": 1,
    "dims": [
      31,
      20,
      12
    ],
    "vol": 1.49,
    "source": "upload",
    "video": {
      "name": "evidence-446-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-446-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-446-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-446-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-446-scale.jpg",
        "url": null
      },
      {
        "slot": "label",
        "name": "evidence-446-label.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00447",
    "sku": "1M-BWJG-R0149-NB4",
    "awb": "14900328384",
    "courier": "Xpressbees",
    "date": "2026-08-01",
    "read": 1,
    "dims": [
      38,
      21,
      15
    ],
    "vol": 2.39,
    "source": "upload",
    "video": {
      "name": "evidence-448-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-448-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-448-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-448-hgt.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-00449",
    "sku": "1M-BWJG-R0149-NB4",
    "awb": "14900329850",
    "courier": "Ekart",
    "date": "2026-05-09",
    "read": 0.69,
    "charged": 0.75,
    "dims": [
      28,
      23,
      11
    ],
    "vol": 1.42,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00451",
    "sku": "1M-BWJG-R0149-NB4",
    "awb": "14900331316",
    "courier": "Bluedart",
    "date": "2026-06-07",
    "read": 0.85,
    "charged": 1,
    "dims": [
      24,
      28,
      12
    ],
    "vol": 1.61,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00453",
    "sku": "1M-BWJG-R0149-NB4",
    "awb": "14900332782",
    "courier": "DTDC",
    "date": "2026-06-07",
    "read": 0.7,
    "charged": 0.75,
    "dims": [
      33,
      29,
      18
    ],
    "vol": 3.45,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-00455",
    "sku": "1M-TWPO-R0002-NB4",
    "awb": "14900334248",
    "courier": "Delhivery",
    "date": "2026-08-01",
    "read": 0.75,
    "dims": [
      24,
      20,
      11
    ],
    "vol": 1.06,
    "source": "upload",
    "video": {
      "name": "evidence-456-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-456-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-456-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00457",
    "sku": "1M-TWPO-R0002-NB4",
    "awb": "14900335714",
    "courier": "DTDC",
    "date": "2026-08-04",
    "read": 0.75,
    "dims": [
      32,
      29,
      11
    ],
    "vol": 2.04,
    "source": "upload",
    "video": {
      "name": "evidence-458-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-458-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-458-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-458-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-458-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00459",
    "sku": "1M-TWPO-R0002-NB4",
    "awb": "14900337180",
    "courier": "Ekart",
    "date": "2026-08-06",
    "read": 0.75,
    "dims": [
      30,
      25,
      12
    ],
    "vol": 1.8,
    "source": "upload",
    "video": {
      "name": "evidence-460-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-460-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-460-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-460-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-460-scale.jpg",
        "url": null
      },
      {
        "slot": "label",
        "name": "evidence-460-label.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00461",
    "sku": "1M-TWPO-R0002-NB4",
    "awb": "14900338646",
    "courier": "Ekart",
    "date": "2026-08-02",
    "read": 0.75,
    "dims": [
      29,
      27,
      8
    ],
    "vol": 1.25,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-462-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-462-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00463",
    "sku": "1M-TWPO-R0002-NB4",
    "awb": "14900340112",
    "courier": "Blitz",
    "date": "2026-08-03",
    "read": 0.75,
    "dims": [
      22,
      23,
      8
    ],
    "vol": 0.81,
    "source": "upload",
    "video": {
      "name": "evidence-464-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-464-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-464-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-464-hgt.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-00465",
    "sku": "1M-TWPO-R0002-NB4",
    "awb": "14900341578",
    "courier": "Bluedart",
    "date": "2026-05-12",
    "read": 0.71,
    "charged": 0.75,
    "dims": [
      38,
      25,
      17
    ],
    "vol": 3.23,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00467",
    "sku": "1M-TWPO-R0002-NB4",
    "awb": "14900343044",
    "courier": "Shadowfax",
    "date": "2026-07-20",
    "read": 0.72,
    "charged": 0.75,
    "dims": [
      38,
      28,
      5
    ],
    "vol": 1.06,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00469",
    "sku": "1M-TWPO-R0002-NB4",
    "awb": "14900344510",
    "courier": "Xpressbees",
    "date": "2026-05-14",
    "read": 0.59,
    "charged": 0.75,
    "dims": [
      22,
      22,
      8
    ],
    "vol": 0.77,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-00471",
    "sku": "1M-TWZP-R0002-BL4",
    "awb": "14900345976",
    "courier": "Blitz",
    "date": "2026-07-30",
    "read": 1,
    "dims": [
      34,
      22,
      18
    ],
    "vol": 2.69,
    "source": "upload",
    "video": {
      "name": "evidence-472-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-472-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-472-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-472-hgt.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00473",
    "sku": "1M-TWZP-R0002-BL4",
    "awb": "14900347442",
    "courier": "Shadowfax",
    "date": "2026-08-03",
    "read": 1,
    "dims": [
      34,
      21,
      18
    ],
    "vol": 2.57,
    "source": "upload",
    "video": {
      "name": "evidence-474-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-474-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-474-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-00475",
    "sku": "1M-TWZP-R0002-BL4",
    "awb": "14900348908",
    "courier": "Ekart",
    "date": "2026-07-17",
    "read": 0.96,
    "charged": 1,
    "dims": [
      30,
      19,
      13
    ],
    "vol": 1.48,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00477",
    "sku": "1M-TWZP-R0002-BL4",
    "awb": "14900350374",
    "courier": "Delhivery",
    "date": "2026-05-25",
    "read": 0.82,
    "charged": 1,
    "dims": [
      28,
      18,
      13
    ],
    "vol": 1.31,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00479",
    "sku": "1M-TWZP-R0002-BL4",
    "awb": "14900351840",
    "courier": "Shadowfax",
    "date": "2026-07-04",
    "read": 0.88,
    "charged": 1,
    "dims": [
      31,
      24,
      14
    ],
    "vol": 2.08,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00481",
    "sku": "1M-TWZP-R0002-BL4",
    "awb": "14900353306",
    "courier": "Delhivery",
    "date": "2026-06-23",
    "read": 0.93,
    "charged": 1,
    "dims": [
      21,
      18,
      16
    ],
    "vol": 1.21,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00483",
    "sku": "1M-TWZP-R0002-BL4",
    "awb": "14900354772",
    "courier": "Bluedart",
    "date": "2026-06-12",
    "read": 0.84,
    "charged": 1,
    "dims": [
      27,
      28,
      7
    ],
    "vol": 1.06,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00485",
    "sku": "1M-TWZP-R0002-BL4",
    "awb": "14900356238",
    "courier": "Blitz",
    "date": "2026-07-27",
    "read": 1.92,
    "charged": 2,
    "dims": [
      25,
      19,
      7
    ],
    "vol": 0.67,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-00487",
    "sku": "1M-TWTS-C0561-WR5",
    "awb": "14900357704",
    "courier": "Delhivery",
    "date": "2026-07-30",
    "read": 0.75,
    "dims": [
      34,
      17,
      13
    ],
    "vol": 1.5,
    "source": "upload",
    "video": {
      "name": "evidence-488-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-488-len.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00489",
    "sku": "1M-TWTS-C0561-WR5",
    "awb": "14900359170",
    "courier": "Delhivery",
    "date": "2026-08-02",
    "read": 0.75,
    "dims": [
      20,
      24,
      5
    ],
    "vol": 0.48,
    "source": "upload",
    "video": {
      "name": "evidence-490-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-490-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-490-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-490-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-490-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00491",
    "sku": "1M-TWTS-C0561-WR5",
    "awb": "14900360636",
    "courier": "Delhivery",
    "date": "2026-08-05",
    "read": 0.75,
    "dims": [
      33,
      17,
      16
    ],
    "vol": 1.8,
    "source": "upload",
    "video": {
      "name": "evidence-492-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-492-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-492-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-492-hgt.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00493",
    "sku": "1M-TWTS-C0561-WR5",
    "awb": "14900362102",
    "courier": "Xpressbees",
    "date": "2026-08-04",
    "read": 0.75,
    "dims": [
      35,
      29,
      19
    ],
    "vol": 3.86,
    "source": "upload",
    "video": {
      "name": "evidence-494-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-494-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-494-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00495",
    "sku": "1M-TWTS-C0561-WR5",
    "awb": "14900363568",
    "courier": "DTDC",
    "date": "2026-08-06",
    "read": 0.75,
    "dims": [
      38,
      17,
      9
    ],
    "vol": 1.16,
    "source": "upload",
    "video": {
      "name": "evidence-496-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-496-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-496-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-496-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-496-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00497",
    "sku": "1M-TWTS-C0561-WR5",
    "awb": "14900365034",
    "courier": "Bluedart",
    "date": "2026-08-05",
    "read": 0.75,
    "dims": [
      34,
      17,
      9
    ],
    "vol": 1.04,
    "source": "upload",
    "video": {
      "name": "evidence-498-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-498-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-498-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-498-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-498-scale.jpg",
        "url": null
      },
      {
        "slot": "label",
        "name": "evidence-498-label.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-00499",
    "sku": "1M-TWTS-C0561-WR5",
    "awb": "14900366500",
    "courier": "Ekart",
    "date": "2026-06-27",
    "read": 0.63,
    "charged": 0.75,
    "dims": [
      36,
      27,
      15
    ],
    "vol": 2.92,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00501",
    "sku": "1M-TWTS-C0561-WR5",
    "awb": "14900367966",
    "courier": "Ekart",
    "date": "2026-07-05",
    "read": 0.61,
    "charged": 0.75,
    "dims": [
      28,
      23,
      18
    ],
    "vol": 2.32,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00503",
    "sku": "1M-TWTS-C0561-WR5",
    "awb": "14900369432",
    "courier": "DTDC",
    "date": "2026-05-02",
    "read": 0.64,
    "charged": 0.75,
    "dims": [
      30,
      18,
      16
    ],
    "vol": 1.73,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-00505",
    "sku": "1M-TWTS-C0561-WR4",
    "awb": "14900370898",
    "courier": "DTDC",
    "date": "2026-07-30",
    "read": 0.75,
    "dims": [
      34,
      18,
      11
    ],
    "vol": 1.35,
    "source": "upload",
    "video": {
      "name": "evidence-506-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-506-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-506-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-506-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-506-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00507",
    "sku": "1M-TWTS-C0561-WR4",
    "awb": "14900372364",
    "courier": "Blitz",
    "date": "2026-08-01",
    "read": 0.75,
    "dims": [
      24,
      22,
      13
    ],
    "vol": 1.37,
    "source": "upload",
    "video": {
      "name": "evidence-508-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-508-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-508-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00509",
    "sku": "1M-TWTS-C0561-WR4",
    "awb": "14900373830",
    "courier": "Shadowfax",
    "date": "2026-08-03",
    "read": 0.75,
    "dims": [
      30,
      24,
      16
    ],
    "vol": 2.3,
    "source": "upload",
    "video": {
      "name": "evidence-510-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-510-len.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00511",
    "sku": "1M-TWTS-C0561-WR4",
    "awb": "14900375296",
    "courier": "Ekart",
    "date": "2026-08-04",
    "read": 0.75,
    "dims": [
      35,
      21,
      14
    ],
    "vol": 2.06,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-512-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-512-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00513",
    "sku": "1M-TWTS-C0561-WR4",
    "awb": "14900376762",
    "courier": "DTDC",
    "date": "2026-07-31",
    "read": 0.75,
    "dims": [
      38,
      18,
      6
    ],
    "vol": 0.82,
    "source": "upload",
    "video": {
      "name": "evidence-514-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-514-len.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00515",
    "sku": "1M-TWTS-C0561-WR4",
    "awb": "14900378228",
    "courier": "Shadowfax",
    "date": "2026-08-02",
    "read": 0.75,
    "dims": [
      34,
      29,
      11
    ],
    "vol": 2.17,
    "source": "upload",
    "video": {
      "name": "evidence-516-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-516-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-516-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-516-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-516-scale.jpg",
        "url": null
      },
      {
        "slot": "label",
        "name": "evidence-516-label.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-00517",
    "sku": "1M-TWTS-C0561-WR4",
    "awb": "14900379694",
    "courier": "Blitz",
    "date": "2026-07-04",
    "read": 0.59,
    "charged": 0.75,
    "dims": [
      33,
      29,
      17
    ],
    "vol": 3.25,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00519",
    "sku": "1M-TWTS-C0561-WR4",
    "awb": "14900381160",
    "courier": "Blitz",
    "date": "2026-07-28",
    "read": 0.71,
    "charged": 0.75,
    "dims": [
      36,
      23,
      14
    ],
    "vol": 2.32,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00521",
    "sku": "1M-TWPO-R0002-CY3",
    "awb": "14900382626",
    "courier": "Bluedart",
    "date": "2026-05-08",
    "read": 0.59,
    "charged": 0.75,
    "dims": [
      34,
      29,
      9
    ],
    "vol": 1.77,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00523",
    "sku": "1M-TWPO-R0002-CY3",
    "awb": "14900384092",
    "courier": "Bluedart",
    "date": "2026-07-25",
    "read": 0.6,
    "charged": 0.75,
    "dims": [
      39,
      17,
      12
    ],
    "vol": 1.59,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-00525",
    "sku": "1M-BWJG-R0149-PB3",
    "awb": "14900385558",
    "courier": "Xpressbees",
    "date": "2026-08-06",
    "read": 0.75,
    "dims": [
      34,
      20,
      19
    ],
    "vol": 2.58,
    "source": "upload",
    "video": {
      "name": "evidence-526-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-526-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-526-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-526-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-526-scale.jpg",
        "url": null
      },
      {
        "slot": "label",
        "name": "evidence-526-label.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00527",
    "sku": "1M-BWJG-R0149-PB3",
    "awb": "14900387024",
    "courier": "Delhivery",
    "date": "2026-08-02",
    "read": 0.75,
    "dims": [
      38,
      28,
      7
    ],
    "vol": 1.49,
    "source": "upload",
    "video": {
      "name": "evidence-528-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-528-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-528-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-528-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-528-scale.jpg",
        "url": null
      },
      {
        "slot": "label",
        "name": "evidence-528-label.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00529",
    "sku": "1M-BWJG-R0149-PB3",
    "awb": "14900388490",
    "courier": "Delhivery",
    "date": "2026-08-05",
    "read": 0.75,
    "dims": [
      38,
      21,
      17
    ],
    "vol": 2.71,
    "source": "upload",
    "video": {
      "name": "evidence-530-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-530-len.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00531",
    "sku": "1M-BWJG-R0149-PB3",
    "awb": "14900389956",
    "courier": "Bluedart",
    "date": "2026-07-30",
    "read": 0.75,
    "dims": [
      32,
      25,
      16
    ],
    "vol": 2.56,
    "source": "upload",
    "video": {
      "name": "evidence-532-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-532-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-532-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-532-hgt.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-00533",
    "sku": "1M-BWJG-R0149-PB3",
    "awb": "14900391422",
    "courier": "Ekart",
    "date": "2026-07-02",
    "read": 0.89,
    "charged": 1,
    "dims": [
      35,
      22,
      12
    ],
    "vol": 1.85,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00535",
    "sku": "1M-BWJG-R0149-PB3",
    "awb": "14900392888",
    "courier": "Ekart",
    "date": "2026-06-16",
    "read": 0.78,
    "charged": 1,
    "dims": [
      38,
      20,
      9
    ],
    "vol": 1.37,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-00537",
    "sku": "1M-BWJG-R0149-BL6",
    "awb": "14900394354",
    "courier": "Ekart",
    "date": "2026-08-04",
    "read": 0.75,
    "dims": [
      34,
      21,
      13
    ],
    "vol": 1.86,
    "source": "upload",
    "video": {
      "name": "evidence-538-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-538-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-538-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-538-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-538-scale.jpg",
        "url": null
      },
      {
        "slot": "label",
        "name": "evidence-538-label.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00539",
    "sku": "1M-BWJG-R0149-BL6",
    "awb": "14900395820",
    "courier": "Shadowfax",
    "date": "2026-07-31",
    "read": 0.75,
    "dims": [
      26,
      19,
      18
    ],
    "vol": 1.78,
    "source": "upload",
    "video": {
      "name": "evidence-540-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-540-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-540-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-540-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-540-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00541",
    "sku": "1M-BWJG-R0149-BL6",
    "awb": "14900397286",
    "courier": "DTDC",
    "date": "2026-08-01",
    "read": 1,
    "dims": [
      23,
      15,
      16
    ],
    "vol": 1.1,
    "source": "upload",
    "video": {
      "name": "evidence-542-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-542-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-542-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-542-hgt.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00543",
    "sku": "1M-BWJG-R0149-BL6",
    "awb": "14900398752",
    "courier": "DTDC",
    "date": "2026-08-05",
    "read": 0.75,
    "dims": [
      24,
      29,
      7
    ],
    "vol": 0.97,
    "source": "upload",
    "video": {
      "name": "evidence-544-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-544-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-544-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00545",
    "sku": "1M-BWJG-R0149-BL6",
    "awb": "14900400218",
    "courier": "Xpressbees",
    "date": "2026-08-04",
    "read": 0.75,
    "dims": [
      24,
      16,
      18
    ],
    "vol": 1.38,
    "source": "upload",
    "video": {
      "name": "evidence-546-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-546-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-546-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-546-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-546-scale.jpg",
        "url": null
      },
      {
        "slot": "label",
        "name": "evidence-546-label.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00547",
    "sku": "1M-BWJG-R0149-BL6",
    "awb": "14900401684",
    "courier": "DTDC",
    "date": "2026-07-30",
    "read": 0.75,
    "dims": [
      27,
      19,
      7
    ],
    "vol": 0.72,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-548-len.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-00549",
    "sku": "1M-BWJG-R0149-BL6",
    "awb": "14900403150",
    "courier": "Ekart",
    "date": "2026-07-25",
    "read": 0.81,
    "charged": 1,
    "dims": [
      35,
      29,
      5
    ],
    "vol": 1.01,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00551",
    "sku": "1M-BWJG-R0149-BL6",
    "awb": "14900404616",
    "courier": "Xpressbees",
    "date": "2026-07-25",
    "read": 0.69,
    "charged": 0.75,
    "dims": [
      30,
      22,
      7
    ],
    "vol": 0.92,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-00553",
    "sku": "1M-TWPO-R0002-CY5",
    "awb": "14900406082",
    "courier": "Bluedart",
    "date": "2026-08-05",
    "read": 0.75,
    "dims": [
      36,
      26,
      16
    ],
    "vol": 3,
    "source": "upload",
    "video": {
      "name": "evidence-554-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-554-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-554-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-554-hgt.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00555",
    "sku": "1M-TWPO-R0002-CY5",
    "awb": "14900407548",
    "courier": "Shadowfax",
    "date": "2026-08-04",
    "read": 0.75,
    "dims": [
      20,
      18,
      11
    ],
    "vol": 0.79,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-556-len.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-00557",
    "sku": "1M-TWPO-R0002-CY5",
    "awb": "14900409014",
    "courier": "Shadowfax",
    "date": "2026-06-19",
    "read": 0.66,
    "charged": 0.75,
    "dims": [
      32,
      23,
      7
    ],
    "vol": 1.03,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00559",
    "sku": "1M-TWPO-R0002-CY5",
    "awb": "14900410480",
    "courier": "Delhivery",
    "date": "2026-06-30",
    "read": 0.69,
    "charged": 0.75,
    "dims": [
      28,
      24,
      12
    ],
    "vol": 1.61,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00561",
    "sku": "1M-TWPO-R0002-CY5",
    "awb": "14900411946",
    "courier": "Blitz",
    "date": "2026-07-10",
    "read": 0.68,
    "charged": 0.75,
    "dims": [
      28,
      18,
      14
    ],
    "vol": 1.41,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00563",
    "sku": "1M-TWPO-R0002-CY5",
    "awb": "14900413412",
    "courier": "Delhivery",
    "date": "2026-07-11",
    "read": 0.72,
    "charged": 0.75,
    "dims": [
      27,
      29,
      6
    ],
    "vol": 0.94,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00565",
    "sku": "1M-TWPO-R0002-CY5",
    "awb": "14900414878",
    "courier": "Xpressbees",
    "date": "2026-07-09",
    "read": 0.55,
    "charged": 0.75,
    "dims": [
      35,
      20,
      7
    ],
    "vol": 0.98,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-00567",
    "sku": "1M-TWPO-R0002-NB3",
    "awb": "14900416344",
    "courier": "Delhivery",
    "date": "2026-08-02",
    "read": 0.75,
    "dims": [
      27,
      15,
      13
    ],
    "vol": 1.05,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-568-len.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-00569",
    "sku": "1M-TWPO-R0002-NB3",
    "awb": "14900417810",
    "courier": "Blitz",
    "date": "2026-06-17",
    "read": 0.66,
    "charged": 0.75,
    "dims": [
      25,
      18,
      11
    ],
    "vol": 0.99,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00571",
    "sku": "1M-TWPO-R0002-NB3",
    "awb": "14900419276",
    "courier": "Delhivery",
    "date": "2026-06-26",
    "read": 0.64,
    "charged": 0.75,
    "dims": [
      21,
      20,
      13
    ],
    "vol": 1.09,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00573",
    "sku": "1M-BWJG-R0084-BL4",
    "awb": "14900420742",
    "courier": "Bluedart",
    "date": "2026-06-27",
    "read": 0.56,
    "charged": 0.75,
    "dims": [
      23,
      28,
      9
    ],
    "vol": 1.16,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00575",
    "sku": "1M-BWJG-R0084-BL4",
    "awb": "14900422208",
    "courier": "Delhivery",
    "date": "2026-06-22",
    "read": 0.65,
    "charged": 0.75,
    "dims": [
      35,
      27,
      9
    ],
    "vol": 1.7,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00577",
    "sku": "1M-BWJG-R0084-BL4",
    "awb": "14900423674",
    "courier": "Ekart",
    "date": "2026-06-16",
    "read": 0.65,
    "charged": 0.75,
    "dims": [
      34,
      20,
      11
    ],
    "vol": 1.5,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00579",
    "sku": "1M-BWJG-R0084-BL4",
    "awb": "14900425140",
    "courier": "Ekart",
    "date": "2026-05-24",
    "read": 0.68,
    "charged": 0.75,
    "dims": [
      30,
      21,
      7
    ],
    "vol": 0.88,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00581",
    "sku": "1M-BWJG-R0084-BL4",
    "awb": "14900426606",
    "courier": "Bluedart",
    "date": "2026-07-12",
    "read": 0.66,
    "charged": 0.75,
    "dims": [
      28,
      28,
      5
    ],
    "vol": 0.78,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00583",
    "sku": "1M-TWTS-C0612-",
    "awb": "14900428072",
    "courier": "DTDC",
    "date": "2026-07-04",
    "read": 0.7,
    "charged": 0.75,
    "dims": [
      38,
      15,
      9
    ],
    "vol": 1.03,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00585",
    "sku": "1M-TWTS-C0612-",
    "awb": "14900429538",
    "courier": "Delhivery",
    "date": "2026-07-18",
    "read": 0.7,
    "charged": 0.75,
    "dims": [
      36,
      29,
      5
    ],
    "vol": 1.04,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00587",
    "sku": "1M-TWTS-C0612-",
    "awb": "14900431004",
    "courier": "Ekart",
    "date": "2026-07-05",
    "read": 0.55,
    "charged": 0.75,
    "dims": [
      39,
      15,
      19
    ],
    "vol": 2.22,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-00589",
    "sku": "1M-TWPO-R0002-CY4",
    "awb": "14900432470",
    "courier": "DTDC",
    "date": "2026-08-04",
    "read": 0.75,
    "dims": [
      21,
      21,
      9
    ],
    "vol": 0.79,
    "source": "upload",
    "video": {
      "name": "evidence-590-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-590-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-590-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00591",
    "sku": "1M-TWPO-R0002-CY4",
    "awb": "14900433936",
    "courier": "Shadowfax",
    "date": "2026-07-31",
    "read": 0.75,
    "dims": [
      34,
      26,
      19
    ],
    "vol": 3.36,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-592-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-592-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-592-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-592-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-00593",
    "sku": "1M-TWPO-R0002-CY4",
    "awb": "14900435402",
    "courier": "Shadowfax",
    "date": "2026-07-18",
    "read": 0.6,
    "charged": 0.75,
    "dims": [
      31,
      20,
      19
    ],
    "vol": 2.36,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00595",
    "sku": "1M-TWPO-R0002-CY4",
    "awb": "14900436868",
    "courier": "Xpressbees",
    "date": "2026-06-11",
    "read": 0.63,
    "charged": 0.75,
    "dims": [
      39,
      18,
      19
    ],
    "vol": 2.67,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00597",
    "sku": "1M-TWPO-R0002-CY4",
    "awb": "14900438334",
    "courier": "Ekart",
    "date": "2026-07-15",
    "read": 0.59,
    "charged": 0.75,
    "dims": [
      32,
      15,
      12
    ],
    "vol": 1.15,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00599",
    "sku": "1M-TWPO-R0002-CY4",
    "awb": "14900439800",
    "courier": "DTDC",
    "date": "2026-06-05",
    "read": 0.62,
    "charged": 0.75,
    "dims": [
      36,
      19,
      18
    ],
    "vol": 2.46,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00601",
    "sku": "1M-TWPO-R0002-CY4",
    "awb": "14900441266",
    "courier": "Ekart",
    "date": "2026-05-20",
    "read": 0.72,
    "charged": 0.75,
    "dims": [
      23,
      28,
      5
    ],
    "vol": 0.64,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00603",
    "sku": "1M-BWJG-R0084-BL3",
    "awb": "14900442732",
    "courier": "Blitz",
    "date": "2026-05-20",
    "read": 0.62,
    "charged": 0.75,
    "dims": [
      31,
      21,
      17
    ],
    "vol": 2.21,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00605",
    "sku": "1M-BWJG-R0084-BL3",
    "awb": "14900444198",
    "courier": "Shadowfax",
    "date": "2026-06-23",
    "read": 0.69,
    "charged": 0.75,
    "dims": [
      25,
      29,
      15
    ],
    "vol": 2.17,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-00607",
    "sku": "1M-BWJG-R0",
    "awb": "14900445664",
    "courier": "Bluedart",
    "date": "2026-08-04",
    "read": 0.75,
    "dims": [
      25,
      29,
      14
    ],
    "vol": 2.03,
    "source": "upload",
    "video": {
      "name": "evidence-608-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-608-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-608-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-608-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-608-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00609",
    "sku": "1M-BWJG-R0",
    "awb": "14900447130",
    "courier": "Delhivery",
    "date": "2026-08-04",
    "read": 0.75,
    "dims": [
      23,
      19,
      8
    ],
    "vol": 0.7,
    "source": "upload",
    "video": {
      "name": "evidence-610-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-610-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-610-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-610-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-610-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00611",
    "sku": "1M-BWJG-R0",
    "awb": "14900448596",
    "courier": "Delhivery",
    "date": "2026-08-02",
    "read": 0.75,
    "dims": [
      29,
      17,
      5
    ],
    "vol": 0.49,
    "source": "upload",
    "video": {
      "name": "evidence-612-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-612-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-612-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-612-hgt.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00613",
    "sku": "1M-BWJG-R0",
    "awb": "14900450062",
    "courier": "Bluedart",
    "date": "2026-07-30",
    "read": 0.75,
    "dims": [
      28,
      24,
      14
    ],
    "vol": 1.88,
    "source": "upload",
    "video": {
      "name": "evidence-614-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-614-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-614-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-614-hgt.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00615",
    "sku": "1M-BWJG-R0",
    "awb": "14900451528",
    "courier": "Bluedart",
    "date": "2026-08-03",
    "read": 0.75,
    "dims": [
      27,
      18,
      14
    ],
    "vol": 1.36,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-616-len.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-00617",
    "sku": "1M-BWJG-R0",
    "awb": "14900452994",
    "courier": "Bluedart",
    "date": "2026-06-20",
    "read": 0.61,
    "charged": 0.75,
    "dims": [
      35,
      26,
      15
    ],
    "vol": 2.73,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00619",
    "sku": "1M-BWJG-R0",
    "awb": "14900454460",
    "courier": "DTDC",
    "date": "2026-05-15",
    "read": 0.72,
    "charged": 0.75,
    "dims": [
      38,
      22,
      11
    ],
    "vol": 1.84,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-00621",
    "sku": "1M-TWPO-R0002-BU5",
    "awb": "14900455926",
    "courier": "Blitz",
    "date": "2026-08-02",
    "read": 0.75,
    "dims": [
      39,
      20,
      11
    ],
    "vol": 1.72,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-622-len.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-00623",
    "sku": "1M-TWPO-R0002-BU5",
    "awb": "14900457392",
    "courier": "Ekart",
    "date": "2026-07-22",
    "read": 0.64,
    "charged": 0.75,
    "dims": [
      27,
      28,
      16
    ],
    "vol": 2.42,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00625",
    "sku": "1M-TWPO-R0002-BU5",
    "awb": "14900458858",
    "courier": "Delhivery",
    "date": "2026-07-19",
    "read": 0.61,
    "charged": 0.75,
    "dims": [
      37,
      26,
      15
    ],
    "vol": 2.89,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00627",
    "sku": "1W-BWJG-R0220-BL4",
    "awb": "14900460324",
    "courier": "Bluedart",
    "date": "2026-07-26",
    "read": 0.55,
    "charged": 0.75,
    "dims": [
      33,
      26,
      15
    ],
    "vol": 2.57,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00629",
    "sku": "1W-BWJG-R0220-BL4",
    "awb": "14900461790",
    "courier": "Xpressbees",
    "date": "2026-05-10",
    "read": 0.56,
    "charged": 0.75,
    "dims": [
      35,
      20,
      6
    ],
    "vol": 0.84,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00631",
    "sku": "1W-BWJG-R0220-BL4",
    "awb": "14900463256",
    "courier": "DTDC",
    "date": "2026-06-30",
    "read": 0.68,
    "charged": 0.75,
    "dims": [
      29,
      22,
      12
    ],
    "vol": 1.53,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00633",
    "sku": "1W-BWJG-R0220-BL4",
    "awb": "14900464722",
    "courier": "Blitz",
    "date": "2026-06-08",
    "read": 0.69,
    "charged": 0.75,
    "dims": [
      38,
      22,
      7
    ],
    "vol": 1.17,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00635",
    "sku": "1W-BWJG-R0220-BL4",
    "awb": "14900466188",
    "courier": "Bluedart",
    "date": "2026-07-24",
    "read": 0.58,
    "charged": 0.75,
    "dims": [
      22,
      15,
      19
    ],
    "vol": 1.25,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00637",
    "sku": "1W-BWJG-R0220-BL4",
    "awb": "14900467654",
    "courier": "Delhivery",
    "date": "2026-06-14",
    "read": 0.58,
    "charged": 0.75,
    "dims": [
      21,
      25,
      18
    ],
    "vol": 1.89,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-00639",
    "sku": "1M-TWTS-C0889-PB",
    "awb": "14900469120",
    "courier": "Bluedart",
    "date": "2026-08-01",
    "read": 0.75,
    "dims": [
      36,
      15,
      11
    ],
    "vol": 1.19,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-640-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-640-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-640-hgt.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-00641",
    "sku": "1M-TWTS-C0889-PB",
    "awb": "14900470586",
    "courier": "Ekart",
    "date": "2026-07-09",
    "read": 0.61,
    "charged": 0.75,
    "dims": [
      22,
      16,
      10
    ],
    "vol": 0.7,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00643",
    "sku": "1M-TWTS-C0889-PB",
    "awb": "14900472052",
    "courier": "Bluedart",
    "date": "2026-07-11",
    "read": 0.57,
    "charged": 0.75,
    "dims": [
      30,
      18,
      13
    ],
    "vol": 1.4,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00645",
    "sku": "1M-TWTS-C0889-PB",
    "awb": "14900473518",
    "courier": "Ekart",
    "date": "2026-06-19",
    "read": 0.64,
    "charged": 0.75,
    "dims": [
      29,
      24,
      5
    ],
    "vol": 0.7,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00647",
    "sku": "1M-TWTS-C0889-PB",
    "awb": "14900474984",
    "courier": "Xpressbees",
    "date": "2026-07-12",
    "read": 0.7,
    "charged": 0.75,
    "dims": [
      26,
      28,
      14
    ],
    "vol": 2.04,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00649",
    "sku": "1M-TWTS-C0889-PB",
    "awb": "14900476450",
    "courier": "Bluedart",
    "date": "2026-05-22",
    "read": 0.53,
    "charged": 0.75,
    "dims": [
      39,
      17,
      10
    ],
    "vol": 1.33,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-00651",
    "sku": "NBAW24MT02-Men-Sweatshirts-Black-L",
    "awb": "14900477916",
    "courier": "Blitz",
    "date": "2026-07-31",
    "read": 0.75,
    "dims": [
      36,
      26,
      6
    ],
    "vol": 1.12,
    "source": "upload",
    "video": {
      "name": "evidence-652-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-652-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-652-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00653",
    "sku": "NBAW24MT02-Men-Sweatshirts-Black-L",
    "awb": "14900479382",
    "courier": "Blitz",
    "date": "2026-08-03",
    "read": 0.75,
    "dims": [
      32,
      22,
      7
    ],
    "vol": 0.99,
    "source": "upload",
    "video": {
      "name": "evidence-654-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-654-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-654-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00655",
    "sku": "NBAW24MT02-Men-Sweatshirts-Black-L",
    "awb": "14900480848",
    "courier": "Delhivery",
    "date": "2026-08-03",
    "read": 0.75,
    "dims": [
      26,
      21,
      14
    ],
    "vol": 1.53,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-656-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-656-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-00657",
    "sku": "NBAW24MT02-Men-Sweatshirts-Black-L",
    "awb": "14900482314",
    "courier": "Shadowfax",
    "date": "2026-06-14",
    "read": 0.7,
    "charged": 0.75,
    "dims": [
      39,
      20,
      9
    ],
    "vol": 1.4,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00659",
    "sku": "NBAW24MT02-Men-Sweatshirts-Black-L",
    "awb": "14900483780",
    "courier": "Shadowfax",
    "date": "2026-06-07",
    "read": 0.64,
    "charged": 0.75,
    "dims": [
      27,
      21,
      11
    ],
    "vol": 1.25,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00661",
    "sku": "NBAW24MT02-Men-Sweatshirts-Black-L",
    "awb": "14900485246",
    "courier": "Xpressbees",
    "date": "2026-05-01",
    "read": 0.72,
    "charged": 0.75,
    "dims": [
      24,
      21,
      8
    ],
    "vol": 0.81,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-00663",
    "sku": "1M-BWJG-R0137-OL4",
    "awb": "14900486712",
    "courier": "Delhivery",
    "date": "2026-08-05",
    "read": 1,
    "dims": [
      33,
      26,
      13
    ],
    "vol": 2.23,
    "source": "upload",
    "video": {
      "name": "evidence-664-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-664-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-664-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-664-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-664-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-00665",
    "sku": "1M-BWJG-R0137-OL4",
    "awb": "14900488178",
    "courier": "Blitz",
    "date": "2026-06-25",
    "read": 0.56,
    "charged": 0.75,
    "dims": [
      30,
      27,
      10
    ],
    "vol": 1.62,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00667",
    "sku": "1M-BWJG-R0137-OL4",
    "awb": "14900489644",
    "courier": "Xpressbees",
    "date": "2026-06-23",
    "read": 0.91,
    "charged": 1,
    "dims": [
      26,
      27,
      6
    ],
    "vol": 0.84,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00669",
    "sku": "1M-BWJG-R0137-OL4",
    "awb": "14900491110",
    "courier": "Xpressbees",
    "date": "2026-06-13",
    "read": 0.59,
    "charged": 0.75,
    "dims": [
      25,
      29,
      15
    ],
    "vol": 2.17,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00671",
    "sku": "1M-BWJG-R0137-OL4",
    "awb": "14900492576",
    "courier": "Shadowfax",
    "date": "2026-07-10",
    "read": 0.98,
    "charged": 1,
    "dims": [
      34,
      16,
      10
    ],
    "vol": 1.09,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-00673",
    "sku": "1M-BWJG-R0149-E",
    "awb": "14900494042",
    "courier": "Shadowfax",
    "date": "2026-08-04",
    "read": 0.75,
    "dims": [
      24,
      19,
      6
    ],
    "vol": 0.55,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-674-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-674-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-674-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-674-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00675",
    "sku": "1M-BWJG-R0149-E",
    "awb": "14900495508",
    "courier": "Shadowfax",
    "date": "2026-07-30",
    "read": 0.75,
    "dims": [
      35,
      19,
      15
    ],
    "vol": 2,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-676-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-676-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-676-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-676-scale.jpg",
        "url": null
      },
      {
        "slot": "label",
        "name": "evidence-676-label.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00677",
    "sku": "1M-BWJG-R0149-E",
    "awb": "14900496974",
    "courier": "Bluedart",
    "date": "2026-08-04",
    "read": 0.75,
    "dims": [
      28,
      22,
      5
    ],
    "vol": 0.62,
    "source": "upload",
    "video": {
      "name": "evidence-678-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-678-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-678-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-00679",
    "sku": "1M-BWJG-R0149-E",
    "awb": "14900498440",
    "courier": "Ekart",
    "date": "2026-05-01",
    "read": 0.97,
    "charged": 1,
    "dims": [
      24,
      29,
      18
    ],
    "vol": 2.51,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00681",
    "sku": "1M-BWJG-R0149-E",
    "awb": "14900499906",
    "courier": "Delhivery",
    "date": "2026-06-12",
    "read": 0.7,
    "charged": 0.75,
    "dims": [
      39,
      24,
      10
    ],
    "vol": 1.87,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00683",
    "sku": "1M-BWJG-R0090-AW4",
    "awb": "14900501372",
    "courier": "DTDC",
    "date": "2026-06-18",
    "read": 0.59,
    "charged": 0.75,
    "dims": [
      35,
      15,
      11
    ],
    "vol": 1.16,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00685",
    "sku": "1M-BWJG-R0090-AW4",
    "awb": "14900502838",
    "courier": "Ekart",
    "date": "2026-05-17",
    "read": 0.58,
    "charged": 0.75,
    "dims": [
      23,
      28,
      19
    ],
    "vol": 2.45,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00687",
    "sku": "1M-BWJG-R0090-AW4",
    "awb": "14900504304",
    "courier": "Xpressbees",
    "date": "2026-07-23",
    "read": 0.66,
    "charged": 0.75,
    "dims": [
      34,
      25,
      19
    ],
    "vol": 3.23,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-00689",
    "sku": "1M-TWTS-C0561-WR3",
    "awb": "14900505770",
    "courier": "Xpressbees",
    "date": "2026-08-03",
    "read": 0.75,
    "dims": [
      28,
      24,
      12
    ],
    "vol": 1.61,
    "source": "upload",
    "video": {
      "name": "evidence-690-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-690-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-690-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-00691",
    "sku": "1M-TWTS-C0561-WR3",
    "awb": "14900507236",
    "courier": "DTDC",
    "date": "2026-06-14",
    "read": 0.7,
    "charged": 0.75,
    "dims": [
      24,
      25,
      17
    ],
    "vol": 2.04,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00693",
    "sku": "1M-TWTS-C0561-WR3",
    "awb": "14900508702",
    "courier": "Delhivery",
    "date": "2026-05-15",
    "read": 0.57,
    "charged": 0.75,
    "dims": [
      24,
      28,
      5
    ],
    "vol": 0.67,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00695",
    "sku": "1M-TWTS-C0561-WR3",
    "awb": "14900510168",
    "courier": "Delhivery",
    "date": "2026-07-28",
    "read": 0.72,
    "charged": 0.75,
    "dims": [
      39,
      21,
      14
    ],
    "vol": 2.29,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00697",
    "sku": "1M-TWTS-C0561-WR3",
    "awb": "14900511634",
    "courier": "DTDC",
    "date": "2026-06-19",
    "read": 0.73,
    "charged": 0.75,
    "dims": [
      34,
      19,
      13
    ],
    "vol": 1.68,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00699",
    "sku": "1M-TWTS-C0561-WR3",
    "awb": "14900513100",
    "courier": "DTDC",
    "date": "2026-07-01",
    "read": 0.53,
    "charged": 0.75,
    "dims": [
      39,
      15,
      19
    ],
    "vol": 2.22,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00701",
    "sku": "1M-TWTS-C0561-WR3",
    "awb": "14900514566",
    "courier": "Shadowfax",
    "date": "2026-05-06",
    "read": 0.7,
    "charged": 0.75,
    "dims": [
      36,
      27,
      19
    ],
    "vol": 3.69,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-00703",
    "sku": "1M-TWPO-R0002-HG4",
    "awb": "14900516032",
    "courier": "Xpressbees",
    "date": "2026-08-04",
    "read": 0.75,
    "dims": [
      31,
      18,
      14
    ],
    "vol": 1.56,
    "source": "upload",
    "video": {
      "name": "evidence-704-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-704-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-704-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00705",
    "sku": "1M-TWPO-R0002-HG4",
    "awb": "14900517498",
    "courier": "Blitz",
    "date": "2026-08-03",
    "read": 0.75,
    "dims": [
      36,
      17,
      17
    ],
    "vol": 2.08,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-706-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-706-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-706-hgt.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00707",
    "sku": "1M-TWPO-R0002-HG4",
    "awb": "14900518964",
    "courier": "Blitz",
    "date": "2026-08-06",
    "read": 0.75,
    "dims": [
      27,
      16,
      12
    ],
    "vol": 1.04,
    "source": "upload",
    "video": {
      "name": "evidence-708-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-708-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-708-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-708-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-708-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-00709",
    "sku": "1M-TWPO-R0002-HG4",
    "awb": "14900520430",
    "courier": "Delhivery",
    "date": "2026-07-24",
    "read": 0.61,
    "charged": 0.75,
    "dims": [
      34,
      23,
      9
    ],
    "vol": 1.41,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00711",
    "sku": "1M-TWPO-R0002-HG4",
    "awb": "14900521896",
    "courier": "Ekart",
    "date": "2026-07-19",
    "read": 0.61,
    "charged": 0.75,
    "dims": [
      34,
      15,
      11
    ],
    "vol": 1.12,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00713",
    "sku": "1M-TWPO-R0002-HG4",
    "awb": "14900523362",
    "courier": "Xpressbees",
    "date": "2026-07-13",
    "read": 0.55,
    "charged": 0.75,
    "dims": [
      23,
      20,
      14
    ],
    "vol": 1.29,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00715",
    "sku": "1M-TWPO-R0002-HG4",
    "awb": "14900524828",
    "courier": "Delhivery",
    "date": "2026-07-09",
    "read": 0.62,
    "charged": 0.75,
    "dims": [
      25,
      25,
      16
    ],
    "vol": 2,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00717",
    "sku": "1M-TWPO-R0002-HG4",
    "awb": "14900526294",
    "courier": "Shadowfax",
    "date": "2026-05-07",
    "read": 0.72,
    "charged": 0.75,
    "dims": [
      24,
      22,
      10
    ],
    "vol": 1.06,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00719",
    "sku": "1M-TWPO-R0002-HG4",
    "awb": "14900527760",
    "courier": "DTDC",
    "date": "2026-06-20",
    "read": 0.55,
    "charged": 0.75,
    "dims": [
      26,
      19,
      7
    ],
    "vol": 0.69,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00721",
    "sku": "1M-BWJG-R0137-BL6",
    "awb": "14900529226",
    "courier": "Xpressbees",
    "date": "2026-07-27",
    "read": 0.86,
    "charged": 1,
    "dims": [
      32,
      15,
      19
    ],
    "vol": 1.82,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00723",
    "sku": "1M-BWJG-R0137-BL6",
    "awb": "14900530692",
    "courier": "Delhivery",
    "date": "2026-07-18",
    "read": 0.94,
    "charged": 1,
    "dims": [
      36,
      22,
      17
    ],
    "vol": 2.69,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00725",
    "sku": "1M-BWJG-R0137-BL6",
    "awb": "14900532158",
    "courier": "Xpressbees",
    "date": "2026-07-04",
    "read": 0.85,
    "charged": 1,
    "dims": [
      24,
      16,
      8
    ],
    "vol": 0.61,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00727",
    "sku": "1M-BWJG-R0137-BL6",
    "awb": "14900533624",
    "courier": "DTDC",
    "date": "2026-05-22",
    "read": 0.83,
    "charged": 1,
    "dims": [
      21,
      25,
      17
    ],
    "vol": 1.79,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00729",
    "sku": "1M-BWJG-R0137-BL6",
    "awb": "14900535090",
    "courier": "DTDC",
    "date": "2026-07-13",
    "read": 0.88,
    "charged": 1,
    "dims": [
      39,
      27,
      19
    ],
    "vol": 4,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-00731",
    "sku": "1M-TWPO-R0002-PT3",
    "awb": "14900536556",
    "courier": "DTDC",
    "date": "2026-07-30",
    "read": 0.75,
    "dims": [
      21,
      19,
      16
    ],
    "vol": 1.28,
    "source": "upload",
    "video": {
      "name": "evidence-732-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-732-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-732-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00733",
    "sku": "1M-TWPO-R0002-PT3",
    "awb": "14900538022",
    "courier": "Xpressbees",
    "date": "2026-08-04",
    "read": 0.75,
    "dims": [
      33,
      24,
      7
    ],
    "vol": 1.11,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-734-len.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00735",
    "sku": "1M-TWPO-R0002-PT3",
    "awb": "14900539488",
    "courier": "Bluedart",
    "date": "2026-08-03",
    "read": 0.75,
    "dims": [
      34,
      24,
      19
    ],
    "vol": 3.1,
    "source": "upload",
    "video": {
      "name": "evidence-736-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-736-len.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00737",
    "sku": "1M-TWPO-R0002-PT3",
    "awb": "14900540954",
    "courier": "Delhivery",
    "date": "2026-08-05",
    "read": 0.75,
    "dims": [
      38,
      19,
      8
    ],
    "vol": 1.16,
    "source": "upload",
    "video": {
      "name": "evidence-738-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-738-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-738-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-738-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-738-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00739",
    "sku": "1M-TWPO-R0002-PT3",
    "awb": "14900542420",
    "courier": "Xpressbees",
    "date": "2026-08-03",
    "read": 0.75,
    "dims": [
      39,
      17,
      19
    ],
    "vol": 2.52,
    "source": "upload",
    "video": {
      "name": "evidence-740-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-740-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-740-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00741",
    "sku": "1M-TWPO-R0002-PT3",
    "awb": "14900543886",
    "courier": "Shadowfax",
    "date": "2026-08-01",
    "read": 0.75,
    "dims": [
      22,
      27,
      7
    ],
    "vol": 0.83,
    "source": "upload",
    "video": {
      "name": "evidence-742-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-742-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-742-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-00743",
    "sku": "1M-TWPO-R0002-PT3",
    "awb": "14900545352",
    "courier": "Xpressbees",
    "date": "2026-06-03",
    "read": 0.72,
    "charged": 0.75,
    "dims": [
      28,
      15,
      19
    ],
    "vol": 1.6,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00745",
    "sku": "1M-TWPO-R0002-PT3",
    "awb": "14900546818",
    "courier": "Delhivery",
    "date": "2026-07-24",
    "read": 0.59,
    "charged": 0.75,
    "dims": [
      38,
      21,
      10
    ],
    "vol": 1.6,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00747",
    "sku": "1M-TWPO-R0002-PT3",
    "awb": "14900548284",
    "courier": "Blitz",
    "date": "2026-05-25",
    "read": 0.68,
    "charged": 0.75,
    "dims": [
      29,
      17,
      17
    ],
    "vol": 1.68,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00749",
    "sku": "1M-TWPO-R0002-PT3",
    "awb": "14900549750",
    "courier": "Delhivery",
    "date": "2026-05-11",
    "read": 0.6,
    "charged": 0.75,
    "dims": [
      25,
      18,
      18
    ],
    "vol": 1.62,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00751",
    "sku": "NBJUN24MJ05-Men-Joggers-Eiffel Tower-XL",
    "awb": "14900551216",
    "courier": "Shadowfax",
    "date": "2026-07-01",
    "read": 0.8,
    "charged": 1,
    "dims": [
      25,
      20,
      6
    ],
    "vol": 0.6,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00753",
    "sku": "NBJUN24MJ05-Men-Joggers-Eiffel Tower-XL",
    "awb": "14900552682",
    "courier": "Xpressbees",
    "date": "2026-07-27",
    "read": 0.97,
    "charged": 1,
    "dims": [
      39,
      16,
      7
    ],
    "vol": 0.87,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00755",
    "sku": "NBJUN24MJ05-Men-Joggers-Eiffel Tower-XL",
    "awb": "14900554148",
    "courier": "Bluedart",
    "date": "2026-05-14",
    "read": 0.84,
    "charged": 1,
    "dims": [
      26,
      20,
      7
    ],
    "vol": 0.73,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00757",
    "sku": "NBJUN24MJ05-Men-Joggers-Eiffel Tower-XL",
    "awb": "14900555614",
    "courier": "Bluedart",
    "date": "2026-07-01",
    "read": 0.95,
    "charged": 1,
    "dims": [
      36,
      19,
      5
    ],
    "vol": 0.68,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00759",
    "sku": "NBJUN24MJ05-Men-Joggers-Eiffel Tower-XL",
    "awb": "14900557080",
    "courier": "Delhivery",
    "date": "2026-05-11",
    "read": 0.9,
    "charged": 1,
    "dims": [
      31,
      19,
      7
    ],
    "vol": 0.82,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00761",
    "sku": "NBJUN24MJ05-Men-Joggers-Eiffel Tower-XL",
    "awb": "14900558546",
    "courier": "Shadowfax",
    "date": "2026-06-12",
    "read": 0.92,
    "charged": 1,
    "dims": [
      36,
      21,
      5
    ],
    "vol": 0.76,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-00763",
    "sku": "1M-TWPO-R0002-AQB5",
    "awb": "14900560012",
    "courier": "Blitz",
    "date": "2026-08-04",
    "read": 0.75,
    "dims": [
      39,
      25,
      12
    ],
    "vol": 2.34,
    "source": "upload",
    "video": {
      "name": "evidence-764-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-764-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-764-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-764-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-764-scale.jpg",
        "url": null
      },
      {
        "slot": "label",
        "name": "evidence-764-label.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-00765",
    "sku": "1M-TWPO-R0002-AQB5",
    "awb": "14900561478",
    "courier": "Blitz",
    "date": "2026-07-25",
    "read": 0.61,
    "charged": 0.75,
    "dims": [
      26,
      20,
      5
    ],
    "vol": 0.52,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00767",
    "sku": "1M-TWPO-R0002-AQB5",
    "awb": "14900562944",
    "courier": "Blitz",
    "date": "2026-07-02",
    "read": 0.6,
    "charged": 0.75,
    "dims": [
      33,
      16,
      9
    ],
    "vol": 0.95,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00769",
    "sku": "1M-TWPO-R0002-AQB5",
    "awb": "14900564410",
    "courier": "DTDC",
    "date": "2026-06-21",
    "read": 0.54,
    "charged": 0.75,
    "dims": [
      29,
      26,
      19
    ],
    "vol": 2.87,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00771",
    "sku": "1M-TWPO-R0002-AQB5",
    "awb": "14900565876",
    "courier": "Shadowfax",
    "date": "2026-06-05",
    "read": 0.59,
    "charged": 0.75,
    "dims": [
      32,
      25,
      16
    ],
    "vol": 2.56,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00773",
    "sku": "1M-TWPO-R0002-AQB5",
    "awb": "14900567342",
    "courier": "Delhivery",
    "date": "2026-07-19",
    "read": 0.67,
    "charged": 0.75,
    "dims": [
      39,
      25,
      17
    ],
    "vol": 3.32,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00775",
    "sku": "1M-TWPO-R0002-AQB5",
    "awb": "14900568808",
    "courier": "Bluedart",
    "date": "2026-07-27",
    "read": 0.9,
    "charged": 1,
    "dims": [
      36,
      25,
      5
    ],
    "vol": 0.9,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00777",
    "sku": "1M-BWJG-R0 (dup 2)",
    "awb": "14900570274",
    "courier": "Ekart",
    "date": "2026-07-08",
    "read": 0.97,
    "charged": 1,
    "dims": [
      21,
      19,
      6
    ],
    "vol": 0.48,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00779",
    "sku": "1M-BWJG-R0 (dup 2)",
    "awb": "14900571740",
    "courier": "DTDC",
    "date": "2026-06-19",
    "read": 0.95,
    "charged": 1,
    "dims": [
      32,
      16,
      7
    ],
    "vol": 0.72,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00781",
    "sku": "1M-BWJG-R0 (dup 2)",
    "awb": "14900573206",
    "courier": "DTDC",
    "date": "2026-06-14",
    "read": 0.78,
    "charged": 1,
    "dims": [
      30,
      23,
      9
    ],
    "vol": 1.24,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-00783",
    "sku": "1M-TWZP-R0002-BL5",
    "awb": "14900574672",
    "courier": "Bluedart",
    "date": "2026-08-01",
    "read": 1,
    "dims": [
      28,
      25,
      7
    ],
    "vol": 0.98,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-784-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-784-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00785",
    "sku": "1M-TWZP-R0002-BL5",
    "awb": "14900576138",
    "courier": "Delhivery",
    "date": "2026-08-02",
    "read": 1,
    "dims": [
      36,
      24,
      9
    ],
    "vol": 1.56,
    "source": "upload",
    "video": {
      "name": "evidence-786-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-786-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-786-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-786-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-786-scale.jpg",
        "url": null
      },
      {
        "slot": "label",
        "name": "evidence-786-label.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-00787",
    "sku": "1M-TWZP-R0002-BL5",
    "awb": "14900577604",
    "courier": "Blitz",
    "date": "2026-05-12",
    "read": 0.87,
    "charged": 1,
    "dims": [
      29,
      19,
      16
    ],
    "vol": 1.76,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00789",
    "sku": "1M-TWZP-R0002-BL5",
    "awb": "14900579070",
    "courier": "Delhivery",
    "date": "2026-05-16",
    "read": 0.92,
    "charged": 1,
    "dims": [
      28,
      21,
      13
    ],
    "vol": 1.53,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00791",
    "sku": "1M-TWZP-R0002-BL5",
    "awb": "14900580536",
    "courier": "Blitz",
    "date": "2026-05-14",
    "read": 0.97,
    "charged": 1,
    "dims": [
      21,
      19,
      5
    ],
    "vol": 0.4,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00793",
    "sku": "1M-TWZP-R0002-BL5",
    "awb": "14900582002",
    "courier": "Bluedart",
    "date": "2026-06-20",
    "read": 0.82,
    "charged": 1,
    "dims": [
      22,
      23,
      10
    ],
    "vol": 1.01,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00795",
    "sku": "1M-TWZP-R0002-BL5",
    "awb": "14900583468",
    "courier": "Delhivery",
    "date": "2026-07-25",
    "read": 0.9,
    "charged": 1,
    "dims": [
      36,
      24,
      13
    ],
    "vol": 2.25,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00797",
    "sku": "1M-TWZP-R0002-BL5",
    "awb": "14900584934",
    "courier": "Shadowfax",
    "date": "2026-05-26",
    "read": 0.92,
    "charged": 1,
    "dims": [
      24,
      27,
      18
    ],
    "vol": 2.33,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00799",
    "sku": "1M-BWJG-R",
    "awb": "14900586400",
    "courier": "DTDC",
    "date": "2026-06-06",
    "read": 0.96,
    "charged": 1,
    "dims": [
      27,
      24,
      14
    ],
    "vol": 1.81,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00801",
    "sku": "1M-BWJG-R",
    "awb": "14900587866",
    "courier": "Shadowfax",
    "date": "2026-05-08",
    "read": 0.95,
    "charged": 1,
    "dims": [
      30,
      29,
      11
    ],
    "vol": 1.91,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00803",
    "sku": "1M-BWJG-R",
    "awb": "14900589332",
    "courier": "Blitz",
    "date": "2026-05-08",
    "read": 0.95,
    "charged": 1,
    "dims": [
      21,
      15,
      15
    ],
    "vol": 0.95,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00805",
    "sku": "1M-BWJG-R",
    "awb": "14900590798",
    "courier": "Delhivery",
    "date": "2026-06-25",
    "read": 0.79,
    "charged": 1,
    "dims": [
      30,
      27,
      11
    ],
    "vol": 1.78,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00807",
    "sku": "1M-BWJG-R",
    "awb": "14900592264",
    "courier": "Xpressbees",
    "date": "2026-07-01",
    "read": 0.6,
    "charged": 0.75,
    "dims": [
      23,
      28,
      12
    ],
    "vol": 1.55,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00809",
    "sku": "1M-BWJG-R",
    "awb": "14900593730",
    "courier": "Shadowfax",
    "date": "2026-05-10",
    "read": 0.95,
    "charged": 1,
    "dims": [
      28,
      23,
      18
    ],
    "vol": 2.32,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00811",
    "sku": "1M-TWRT-C0694-WR3",
    "awb": "14900595196",
    "courier": "Delhivery",
    "date": "2026-05-25",
    "read": 0.61,
    "charged": 0.75,
    "dims": [
      21,
      26,
      5
    ],
    "vol": 0.55,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00813",
    "sku": "1M-TWRT-C0694-WR3",
    "awb": "14900596662",
    "courier": "Bluedart",
    "date": "2026-06-27",
    "read": 0.61,
    "charged": 0.75,
    "dims": [
      28,
      23,
      19
    ],
    "vol": 2.45,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00815",
    "sku": "1M-TWRT-C0694-WR3",
    "awb": "14900598128",
    "courier": "Xpressbees",
    "date": "2026-06-18",
    "read": 0.59,
    "charged": 0.75,
    "dims": [
      25,
      23,
      15
    ],
    "vol": 1.73,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00817",
    "sku": "1M-TWRT-C0694-WR3",
    "awb": "14900599594",
    "courier": "Shadowfax",
    "date": "2026-06-02",
    "read": 0.69,
    "charged": 0.75,
    "dims": [
      30,
      19,
      5
    ],
    "vol": 0.57,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-00819",
    "sku": "1M-TWRT-C13",
    "awb": "14900601060",
    "courier": "Bluedart",
    "date": "2026-08-04",
    "read": 0.75,
    "dims": [
      25,
      15,
      9
    ],
    "vol": 0.68,
    "source": "upload",
    "video": {
      "name": "evidence-820-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-820-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-820-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00821",
    "sku": "1M-TWRT-C13",
    "awb": "14900602526",
    "courier": "Delhivery",
    "date": "2026-08-05",
    "read": 0.75,
    "dims": [
      30,
      16,
      5
    ],
    "vol": 0.48,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-822-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-822-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-822-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-822-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00823",
    "sku": "1M-TWRT-C13",
    "awb": "14900603992",
    "courier": "Delhivery",
    "date": "2026-08-01",
    "read": 0.75,
    "dims": [
      21,
      16,
      15
    ],
    "vol": 1.01,
    "source": "upload",
    "video": {
      "name": "evidence-824-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-824-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-824-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-824-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-824-scale.jpg",
        "url": null
      },
      {
        "slot": "label",
        "name": "evidence-824-label.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00825",
    "sku": "1M-TWRT-C13",
    "awb": "14900605458",
    "courier": "Shadowfax",
    "date": "2026-07-31",
    "read": 0.75,
    "dims": [
      29,
      24,
      9
    ],
    "vol": 1.25,
    "source": "upload",
    "video": {
      "name": "evidence-826-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-826-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-826-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-826-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-826-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-00827",
    "sku": "1M-TWRT-C13",
    "awb": "14900606924",
    "courier": "Blitz",
    "date": "2026-06-07",
    "read": 0.7,
    "charged": 0.75,
    "dims": [
      30,
      23,
      16
    ],
    "vol": 2.21,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00829",
    "sku": "1M-TWRT-C13",
    "awb": "14900608390",
    "courier": "Xpressbees",
    "date": "2026-06-02",
    "read": 0.57,
    "charged": 0.75,
    "dims": [
      21,
      17,
      16
    ],
    "vol": 1.14,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00831",
    "sku": "1M-TWRT-C13",
    "awb": "14900609856",
    "courier": "Delhivery",
    "date": "2026-05-23",
    "read": 0.56,
    "charged": 0.75,
    "dims": [
      37,
      18,
      18
    ],
    "vol": 2.4,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00833",
    "sku": "1M-TWRT-C13",
    "awb": "14900611322",
    "courier": "Xpressbees",
    "date": "2026-05-05",
    "read": 0.56,
    "charged": 0.75,
    "dims": [
      26,
      25,
      17
    ],
    "vol": 2.21,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00835",
    "sku": "1M-TWPO-R0002-DR5",
    "awb": "14900612788",
    "courier": "Xpressbees",
    "date": "2026-05-29",
    "read": 0.58,
    "charged": 0.75,
    "dims": [
      31,
      28,
      12
    ],
    "vol": 2.08,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00837",
    "sku": "1M-TWPO-R0002-DR5",
    "awb": "14900614254",
    "courier": "DTDC",
    "date": "2026-05-26",
    "read": 0.69,
    "charged": 0.75,
    "dims": [
      29,
      16,
      10
    ],
    "vol": 0.93,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00839",
    "sku": "1M-TWPO-R0002-DR5",
    "awb": "14900615720",
    "courier": "Ekart",
    "date": "2026-06-18",
    "read": 0.56,
    "charged": 0.75,
    "dims": [
      22,
      26,
      10
    ],
    "vol": 1.14,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00841",
    "sku": "1M-TWPO-R0002-DR5",
    "awb": "14900617186",
    "courier": "Ekart",
    "date": "2026-05-30",
    "read": 0.64,
    "charged": 0.75,
    "dims": [
      30,
      18,
      17
    ],
    "vol": 1.84,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00843",
    "sku": "1M-TWPO-R0002-DR5",
    "awb": "14900618652",
    "courier": "Delhivery",
    "date": "2026-06-03",
    "read": 0.53,
    "charged": 0.75,
    "dims": [
      29,
      24,
      17
    ],
    "vol": 2.37,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-00845",
    "sku": "NBJUN24MJ05-Men-Joggers-Black-M",
    "awb": "14900620118",
    "courier": "Delhivery",
    "date": "2026-08-05",
    "read": 0.75,
    "dims": [
      35,
      23,
      16
    ],
    "vol": 2.58,
    "source": "upload",
    "video": {
      "name": "evidence-846-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-846-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-846-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-846-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-846-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00847",
    "sku": "NBJUN24MJ05-Men-Joggers-Black-M",
    "awb": "14900621584",
    "courier": "Blitz",
    "date": "2026-07-30",
    "read": 1,
    "dims": [
      26,
      24,
      9
    ],
    "vol": 1.12,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-848-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-848-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-848-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-848-scale.jpg",
        "url": null
      },
      {
        "slot": "label",
        "name": "evidence-848-label.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00849",
    "sku": "NBJUN24MJ05-Men-Joggers-Black-M",
    "awb": "14900623050",
    "courier": "Ekart",
    "date": "2026-07-30",
    "read": 0.75,
    "dims": [
      26,
      22,
      15
    ],
    "vol": 1.72,
    "source": "upload",
    "video": {
      "name": "evidence-850-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-850-len.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00851",
    "sku": "NBJUN24MJ05-Men-Joggers-Black-M",
    "awb": "14900624516",
    "courier": "Shadowfax",
    "date": "2026-07-31",
    "read": 0.75,
    "dims": [
      26,
      17,
      19
    ],
    "vol": 1.68,
    "source": "upload",
    "video": {
      "name": "evidence-852-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-852-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-852-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-00853",
    "sku": "NBJUN24MJ05-Men-Joggers-Black-M",
    "awb": "14900625982",
    "courier": "Shadowfax",
    "date": "2026-05-14",
    "read": 0.61,
    "charged": 0.75,
    "dims": [
      32,
      27,
      6
    ],
    "vol": 1.04,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00855",
    "sku": "NBJUN24MJ05-Men-Joggers-Black-M",
    "awb": "14900627448",
    "courier": "Bluedart",
    "date": "2026-07-17",
    "read": 0.61,
    "charged": 0.75,
    "dims": [
      32,
      19,
      15
    ],
    "vol": 1.82,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00857",
    "sku": "NBJUN24MJ05-Men-Joggers-Black-M",
    "awb": "14900628914",
    "courier": "Ekart",
    "date": "2026-05-19",
    "read": 0.97,
    "charged": 1,
    "dims": [
      31,
      18,
      10
    ],
    "vol": 1.12,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00859",
    "sku": "NBJUN24MJ05-Men-Joggers-Black-M",
    "awb": "14900630380",
    "courier": "DTDC",
    "date": "2026-05-22",
    "read": 0.59,
    "charged": 0.75,
    "dims": [
      36,
      19,
      5
    ],
    "vol": 0.68,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00861",
    "sku": "NBJUN24MJ05-Men-Joggers-Black-M",
    "awb": "14900631846",
    "courier": "Ekart",
    "date": "2026-07-03",
    "read": 0.55,
    "charged": 0.75,
    "dims": [
      20,
      21,
      8
    ],
    "vol": 0.67,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00863",
    "sku": "NBJUN24MJ05-Men-Joggers-Black-M",
    "awb": "14900633312",
    "courier": "Shadowfax",
    "date": "2026-05-04",
    "read": 0.61,
    "charged": 0.75,
    "dims": [
      32,
      16,
      12
    ],
    "vol": 1.23,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-00865",
    "sku": "1W-TWTS-C0969-OG3",
    "awb": "14900634778",
    "courier": "Ekart",
    "date": "2026-08-03",
    "read": 0.75,
    "dims": [
      25,
      20,
      14
    ],
    "vol": 1.4,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-866-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-866-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-866-hgt.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-00867",
    "sku": "1W-TWTS-C0969-OG3",
    "awb": "14900636244",
    "courier": "Bluedart",
    "date": "2026-05-20",
    "read": 0.54,
    "charged": 0.75,
    "dims": [
      38,
      19,
      5
    ],
    "vol": 0.72,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00869",
    "sku": "1W-TWTS-C0969-OG3",
    "awb": "14900637710",
    "courier": "Bluedart",
    "date": "2026-06-14",
    "read": 0.55,
    "charged": 0.75,
    "dims": [
      34,
      18,
      10
    ],
    "vol": 1.22,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00871",
    "sku": "1W-TWTS-C0969-OG3",
    "awb": "14900639176",
    "courier": "Delhivery",
    "date": "2026-05-07",
    "read": 0.66,
    "charged": 0.75,
    "dims": [
      24,
      21,
      10
    ],
    "vol": 1.01,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00873",
    "sku": "1W-TWTS-C0969-OG3",
    "awb": "14900640642",
    "courier": "DTDC",
    "date": "2026-07-23",
    "read": 0.63,
    "charged": 0.75,
    "dims": [
      21,
      18,
      16
    ],
    "vol": 1.21,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00875",
    "sku": "1M-BWJG-R0090-AW5",
    "awb": "14900642108",
    "courier": "DTDC",
    "date": "2026-06-30",
    "read": 0.68,
    "charged": 0.75,
    "dims": [
      24,
      18,
      6
    ],
    "vol": 0.52,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00877",
    "sku": "1M-BWJG-R0090-AW5",
    "awb": "14900643574",
    "courier": "Ekart",
    "date": "2026-05-03",
    "read": 0.78,
    "charged": 1,
    "dims": [
      21,
      26,
      13
    ],
    "vol": 1.42,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00879",
    "sku": "1M-BWJG-R0090-AW5",
    "awb": "14900645040",
    "courier": "Xpressbees",
    "date": "2026-07-02",
    "read": 0.64,
    "charged": 0.75,
    "dims": [
      32,
      18,
      15
    ],
    "vol": 1.73,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00881",
    "sku": "1M-BWJG-R0090-AW5",
    "awb": "14900646506",
    "courier": "DTDC",
    "date": "2026-06-08",
    "read": 0.69,
    "charged": 0.75,
    "dims": [
      38,
      19,
      10
    ],
    "vol": 1.44,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00883",
    "sku": "1M-BWJG-R0090-AW5",
    "awb": "14900647972",
    "courier": "Bluedart",
    "date": "2026-05-31",
    "read": 0.6,
    "charged": 0.75,
    "dims": [
      22,
      24,
      8
    ],
    "vol": 0.84,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00885",
    "sku": "1M-TWPO-R0002-PT4",
    "awb": "14900649438",
    "courier": "Shadowfax",
    "date": "2026-06-06",
    "read": 0.56,
    "charged": 0.75,
    "dims": [
      26,
      16,
      6
    ],
    "vol": 0.5,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00887",
    "sku": "1M-TWPO-R0002-PT4",
    "awb": "14900650904",
    "courier": "Delhivery",
    "date": "2026-05-15",
    "read": 0.7,
    "charged": 0.75,
    "dims": [
      23,
      24,
      12
    ],
    "vol": 1.32,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-00889",
    "sku": "1M-TWPO-R0002-DR3",
    "awb": "14900652370",
    "courier": "Blitz",
    "date": "2026-07-31",
    "read": 0.75,
    "dims": [
      25,
      20,
      10
    ],
    "vol": 1,
    "source": "upload",
    "video": {
      "name": "evidence-890-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-890-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-890-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-00891",
    "sku": "1M-TWPO-R0002-DR3",
    "awb": "14900653836",
    "courier": "DTDC",
    "date": "2026-05-21",
    "read": 0.54,
    "charged": 0.75,
    "dims": [
      24,
      17,
      13
    ],
    "vol": 1.06,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00893",
    "sku": "1M-TWPO-R0002-DR3",
    "awb": "14900655302",
    "courier": "Bluedart",
    "date": "2026-05-04",
    "read": 0.63,
    "charged": 0.75,
    "dims": [
      34,
      26,
      8
    ],
    "vol": 1.41,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00895",
    "sku": "1M-TWPO-R0002-DR3",
    "awb": "14900656768",
    "courier": "Shadowfax",
    "date": "2026-06-24",
    "read": 0.59,
    "charged": 0.75,
    "dims": [
      30,
      19,
      17
    ],
    "vol": 1.94,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00897",
    "sku": "1M-TWPO-R0002-DR3",
    "awb": "14900658234",
    "courier": "Xpressbees",
    "date": "2026-06-23",
    "read": 0.57,
    "charged": 0.75,
    "dims": [
      28,
      26,
      19
    ],
    "vol": 2.77,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-00899",
    "sku": "1M-TWTS-C0268-MN3",
    "awb": "14900659700",
    "courier": "Delhivery",
    "date": "2026-07-30",
    "read": 0.75,
    "dims": [
      26,
      25,
      17
    ],
    "vol": 2.21,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-900-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-900-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00901",
    "sku": "1M-TWTS-C0268-MN3",
    "awb": "14900661166",
    "courier": "Blitz",
    "date": "2026-08-01",
    "read": 0.75,
    "dims": [
      34,
      17,
      14
    ],
    "vol": 1.62,
    "source": "upload",
    "video": {
      "name": "evidence-902-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-902-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-902-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00903",
    "sku": "1M-TWTS-C0268-MN3",
    "awb": "14900662632",
    "courier": "Delhivery",
    "date": "2026-08-04",
    "read": 0.75,
    "dims": [
      26,
      22,
      10
    ],
    "vol": 1.14,
    "source": "upload",
    "video": {
      "name": "evidence-904-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-904-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-904-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-904-hgt.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00905",
    "sku": "1M-TWTS-C0268-MN3",
    "awb": "14900664098",
    "courier": "Delhivery",
    "date": "2026-07-31",
    "read": 0.75,
    "dims": [
      37,
      16,
      10
    ],
    "vol": 1.18,
    "source": "upload",
    "video": {
      "name": "evidence-906-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-906-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-906-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-906-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-906-scale.jpg",
        "url": null
      },
      {
        "slot": "label",
        "name": "evidence-906-label.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00907",
    "sku": "1M-TWTS-C0268-MN3",
    "awb": "14900665564",
    "courier": "Blitz",
    "date": "2026-07-30",
    "read": 0.75,
    "dims": [
      38,
      23,
      5
    ],
    "vol": 0.87,
    "source": "upload",
    "video": {
      "name": "evidence-908-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-908-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-908-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-908-hgt.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00909",
    "sku": "1M-TWTS-C0268-MN3",
    "awb": "14900667030",
    "courier": "Blitz",
    "date": "2026-08-03",
    "read": 0.75,
    "dims": [
      22,
      21,
      11
    ],
    "vol": 1.02,
    "source": "upload",
    "video": {
      "name": "evidence-910-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-910-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-910-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-910-hgt.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-00911",
    "sku": "1M-TWTS-C0268-MN3",
    "awb": "14900668496",
    "courier": "Delhivery",
    "date": "2026-07-20",
    "read": 0.64,
    "charged": 0.75,
    "dims": [
      38,
      16,
      12
    ],
    "vol": 1.46,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00913",
    "sku": "1M-TWTS-C0268-MN3",
    "awb": "14900669962",
    "courier": "Shadowfax",
    "date": "2026-06-05",
    "read": 0.64,
    "charged": 0.75,
    "dims": [
      38,
      20,
      6
    ],
    "vol": 0.91,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00915",
    "sku": "1M-TWTS-C0268-MN3",
    "awb": "14900671428",
    "courier": "DTDC",
    "date": "2026-06-28",
    "read": 0.55,
    "charged": 0.75,
    "dims": [
      36,
      16,
      8
    ],
    "vol": 0.92,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00917",
    "sku": "1M-LWCS-R",
    "awb": "14900672894",
    "courier": "Blitz",
    "date": "2026-05-15",
    "read": 0.87,
    "charged": 1,
    "dims": [
      21,
      24,
      17
    ],
    "vol": 1.71,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00919",
    "sku": "1M-LWCS-R",
    "awb": "14900674360",
    "courier": "DTDC",
    "date": "2026-07-27",
    "read": 0.91,
    "charged": 1,
    "dims": [
      33,
      23,
      6
    ],
    "vol": 0.91,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00921",
    "sku": "1M-LWCS-R",
    "awb": "14900675826",
    "courier": "Xpressbees",
    "date": "2026-07-14",
    "read": 0.91,
    "charged": 1,
    "dims": [
      29,
      26,
      8
    ],
    "vol": 1.21,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00923",
    "sku": "1M-LWCS-R",
    "awb": "14900677292",
    "courier": "Shadowfax",
    "date": "2026-05-25",
    "read": 0.78,
    "charged": 1,
    "dims": [
      39,
      18,
      14
    ],
    "vol": 1.97,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00925",
    "sku": "1M-LWCS-R",
    "awb": "14900678758",
    "courier": "Bluedart",
    "date": "2026-06-14",
    "read": 0.96,
    "charged": 1,
    "dims": [
      25,
      25,
      8
    ],
    "vol": 1,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00927",
    "sku": "1M-BWJG-R0137-BL3",
    "awb": "14900680224",
    "courier": "Blitz",
    "date": "2026-05-15",
    "read": 0.86,
    "charged": 1,
    "dims": [
      22,
      29,
      10
    ],
    "vol": 1.28,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00929",
    "sku": "1M-BWJG-R0137-BL3",
    "awb": "14900681690",
    "courier": "Ekart",
    "date": "2026-07-25",
    "read": 0.82,
    "charged": 1,
    "dims": [
      22,
      15,
      15
    ],
    "vol": 0.99,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00931",
    "sku": "1M-BWJG-R0137-BL3",
    "awb": "14900683156",
    "courier": "Ekart",
    "date": "2026-06-19",
    "read": 0.87,
    "charged": 1,
    "dims": [
      22,
      23,
      13
    ],
    "vol": 1.32,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00933",
    "sku": "1M-BWJG-R0137-BL3",
    "awb": "14900684622",
    "courier": "Blitz",
    "date": "2026-07-08",
    "read": 0.88,
    "charged": 1,
    "dims": [
      38,
      20,
      15
    ],
    "vol": 2.28,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-00935",
    "sku": "NBJUN24MJ05-Men-Joggers-Navy Blue-L",
    "awb": "14900686088",
    "courier": "Ekart",
    "date": "2026-08-04",
    "read": 1,
    "dims": [
      33,
      22,
      19
    ],
    "vol": 2.76,
    "source": "upload",
    "video": {
      "name": "evidence-936-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-936-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-936-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-936-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-936-scale.jpg",
        "url": null
      },
      {
        "slot": "label",
        "name": "evidence-936-label.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00937",
    "sku": "NBJUN24MJ05-Men-Joggers-Navy Blue-L",
    "awb": "14900687554",
    "courier": "Bluedart",
    "date": "2026-08-06",
    "read": 1,
    "dims": [
      25,
      15,
      18
    ],
    "vol": 1.35,
    "source": "upload",
    "video": {
      "name": "evidence-938-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-938-len.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-00939",
    "sku": "NBJUN24MJ05-Men-Joggers-Navy Blue-L",
    "awb": "14900689020",
    "courier": "Delhivery",
    "date": "2026-06-15",
    "read": 0.94,
    "charged": 1,
    "dims": [
      25,
      24,
      17
    ],
    "vol": 2.04,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00941",
    "sku": "NBJUN24MJ05-Men-Joggers-Navy Blue-L",
    "awb": "14900690486",
    "courier": "Bluedart",
    "date": "2026-06-03",
    "read": 0.91,
    "charged": 1,
    "dims": [
      26,
      20,
      11
    ],
    "vol": 1.14,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-00943",
    "sku": "1M-BWJG-R0149-BL7",
    "awb": "14900691952",
    "courier": "Delhivery",
    "date": "2026-07-31",
    "read": 0.75,
    "dims": [
      30,
      29,
      17
    ],
    "vol": 2.96,
    "source": "upload",
    "video": {
      "name": "evidence-944-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-944-len.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00945",
    "sku": "1M-BWJG-R0149-BL7",
    "awb": "14900693418",
    "courier": "Shadowfax",
    "date": "2026-08-03",
    "read": 1.5,
    "dims": [
      38,
      28,
      15
    ],
    "vol": 3.19,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-946-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-946-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-00947",
    "sku": "1M-BWJG-R0149-BL7",
    "awb": "14900694884",
    "courier": "Blitz",
    "date": "2026-05-16",
    "read": 0.69,
    "charged": 0.75,
    "dims": [
      25,
      25,
      13
    ],
    "vol": 1.63,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00949",
    "sku": "1M-BWJG-R0149-BL7",
    "awb": "14900696350",
    "courier": "Xpressbees",
    "date": "2026-07-06",
    "read": 1.43,
    "charged": 1.5,
    "dims": [
      38,
      20,
      16
    ],
    "vol": 2.43,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00951",
    "sku": "1M-BWJG-R0149-BL7",
    "awb": "14900697816",
    "courier": "Xpressbees",
    "date": "2026-06-16",
    "read": 1.41,
    "charged": 1.5,
    "dims": [
      31,
      24,
      9
    ],
    "vol": 1.34,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-00953",
    "sku": "1M-BWJG-R0137-OL5",
    "awb": "14900699282",
    "courier": "Bluedart",
    "date": "2026-08-01",
    "read": 1,
    "dims": [
      21,
      17,
      11
    ],
    "vol": 0.79,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-954-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-954-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00955",
    "sku": "1M-BWJG-R0137-OL5",
    "awb": "14900700748",
    "courier": "Blitz",
    "date": "2026-07-31",
    "read": 1,
    "dims": [
      31,
      24,
      13
    ],
    "vol": 1.93,
    "source": "upload",
    "video": {
      "name": "evidence-956-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-956-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-956-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-956-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-956-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00957",
    "sku": "1M-BWJG-R0137-OL5",
    "awb": "14900702214",
    "courier": "Xpressbees",
    "date": "2026-07-30",
    "read": 1,
    "dims": [
      34,
      16,
      19
    ],
    "vol": 2.07,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-958-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-958-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-958-hgt.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00959",
    "sku": "1M-BWJG-R0137-OL5",
    "awb": "14900703680",
    "courier": "Blitz",
    "date": "2026-07-31",
    "read": 1,
    "dims": [
      25,
      26,
      12
    ],
    "vol": 1.56,
    "source": "upload",
    "video": {
      "name": "evidence-960-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-960-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-960-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-960-hgt.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-00961",
    "sku": "1M-BWJG-R0137-OL5",
    "awb": "14900705146",
    "courier": "Delhivery",
    "date": "2026-06-21",
    "read": 0.94,
    "charged": 1,
    "dims": [
      24,
      28,
      15
    ],
    "vol": 2.02,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00963",
    "sku": "1M-BWJG-R0137-OL5",
    "awb": "14900706612",
    "courier": "Blitz",
    "date": "2026-06-04",
    "read": 0.83,
    "charged": 1,
    "dims": [
      23,
      29,
      8
    ],
    "vol": 1.07,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00965",
    "sku": "1M-BWJG-R0137-OL5",
    "awb": "14900708078",
    "courier": "Delhivery",
    "date": "2026-05-14",
    "read": 0.9,
    "charged": 1,
    "dims": [
      36,
      23,
      5
    ],
    "vol": 0.83,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00967",
    "sku": "1M-BWJG-R0137-OL5",
    "awb": "14900709544",
    "courier": "Ekart",
    "date": "2026-05-05",
    "read": 0.78,
    "charged": 1,
    "dims": [
      29,
      29,
      8
    ],
    "vol": 1.35,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00969",
    "sku": "1M-TWPO-R0002-PN5",
    "awb": "14900711010",
    "courier": "Delhivery",
    "date": "2026-06-30",
    "read": 0.57,
    "charged": 0.75,
    "dims": [
      29,
      18,
      19
    ],
    "vol": 1.98,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00971",
    "sku": "1M-TWPO-R0002-PN5",
    "awb": "14900712476",
    "courier": "Bluedart",
    "date": "2026-05-05",
    "read": 0.59,
    "charged": 0.75,
    "dims": [
      21,
      15,
      10
    ],
    "vol": 0.63,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00973",
    "sku": "1M-TWPO-R0002-PN5",
    "awb": "14900713942",
    "courier": "Shadowfax",
    "date": "2026-06-11",
    "read": 0.71,
    "charged": 0.75,
    "dims": [
      39,
      17,
      6
    ],
    "vol": 0.8,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00975",
    "sku": "1M-TWPO-R0002-PN5",
    "awb": "14900715408",
    "courier": "Delhivery",
    "date": "2026-06-12",
    "read": 0.56,
    "charged": 0.75,
    "dims": [
      28,
      16,
      18
    ],
    "vol": 1.61,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00977",
    "sku": "1M-TWPO-R0002-PN5",
    "awb": "14900716874",
    "courier": "Ekart",
    "date": "2026-05-30",
    "read": 0.97,
    "charged": 1,
    "dims": [
      24,
      19,
      17
    ],
    "vol": 1.55,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00979",
    "sku": "1M-TWPO-R0002-PN5",
    "awb": "14900718340",
    "courier": "Ekart",
    "date": "2026-05-26",
    "read": 0.55,
    "charged": 0.75,
    "dims": [
      37,
      22,
      19
    ],
    "vol": 3.09,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-00981",
    "sku": "NBSS24MC10-Men-Coord Set-Navy Blue-L",
    "awb": "14900719806",
    "courier": "Xpressbees",
    "date": "2026-08-05",
    "read": 1.5,
    "dims": [
      23,
      19,
      5
    ],
    "vol": 0.44,
    "source": "upload",
    "video": {
      "name": "evidence-982-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-982-len.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00983",
    "sku": "NBSS24MC10-Men-Coord Set-Navy Blue-L",
    "awb": "14900721272",
    "courier": "Bluedart",
    "date": "2026-08-02",
    "read": 1.5,
    "dims": [
      30,
      21,
      14
    ],
    "vol": 1.76,
    "source": "upload",
    "video": {
      "name": "evidence-984-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-984-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-984-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-984-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-984-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00985",
    "sku": "NBSS24MC10-Men-Coord Set-Navy Blue-L",
    "awb": "14900722738",
    "courier": "DTDC",
    "date": "2026-07-31",
    "read": 0.75,
    "dims": [
      35,
      16,
      17
    ],
    "vol": 1.9,
    "source": "upload",
    "video": {
      "name": "evidence-986-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-986-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-986-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00987",
    "sku": "NBSS24MC10-Men-Coord Set-Navy Blue-L",
    "awb": "14900724204",
    "courier": "Bluedart",
    "date": "2026-07-31",
    "read": 1.5,
    "dims": [
      31,
      28,
      16
    ],
    "vol": 2.78,
    "source": "upload",
    "video": {
      "name": "evidence-988-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-988-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-988-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-988-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-988-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-00989",
    "sku": "NBSS24MC10-Men-Coord Set-Navy Blue-L",
    "awb": "14900725670",
    "courier": "Xpressbees",
    "date": "2026-08-03",
    "read": 1.5,
    "dims": [
      26,
      24,
      15
    ],
    "vol": 1.87,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-990-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-990-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-990-hgt.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-00991",
    "sku": "NBSS24MC10-Men-Coord Set-Navy Blue-L",
    "awb": "14900727136",
    "courier": "Ekart",
    "date": "2026-05-25",
    "read": 0.73,
    "charged": 0.75,
    "dims": [
      30,
      24,
      16
    ],
    "vol": 2.3,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00993",
    "sku": "NBSS24MC10-Men-Coord Set-Navy Blue-L",
    "awb": "14900728602",
    "courier": "Blitz",
    "date": "2026-06-21",
    "read": 1.36,
    "charged": 1.5,
    "dims": [
      25,
      17,
      7
    ],
    "vol": 0.6,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00995",
    "sku": "NBSS24MC10-Men-Coord Set-Navy Blue-L",
    "awb": "14900730068",
    "courier": "Blitz",
    "date": "2026-07-20",
    "read": 0.72,
    "charged": 0.75,
    "dims": [
      28,
      16,
      5
    ],
    "vol": 0.45,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00997",
    "sku": "NBSS24MC10-Men-Coord Set-Navy Blue-L",
    "awb": "14900731534",
    "courier": "Blitz",
    "date": "2026-06-11",
    "read": 0.86,
    "charged": 1,
    "dims": [
      34,
      26,
      13
    ],
    "vol": 2.3,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-00999",
    "sku": "NBSS24MC10-Men-Coord Set-Navy Blue-L",
    "awb": "14900733000",
    "courier": "Xpressbees",
    "date": "2026-05-30",
    "read": 0.58,
    "charged": 0.75,
    "dims": [
      37,
      18,
      16
    ],
    "vol": 2.13,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-01001",
    "sku": "1M-LWCS-R0174-NS4",
    "awb": "14900734466",
    "courier": "DTDC",
    "date": "2026-08-05",
    "read": 0.75,
    "dims": [
      23,
      29,
      9
    ],
    "vol": 1.2,
    "source": "upload",
    "video": {
      "name": "evidence-1002-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1002-len.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01003",
    "sku": "1M-LWCS-R0174-NS4",
    "awb": "14900735932",
    "courier": "Xpressbees",
    "date": "2026-08-06",
    "read": 0.75,
    "dims": [
      32,
      27,
      5
    ],
    "vol": 0.86,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-1004-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1004-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1004-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-1004-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-01005",
    "sku": "1M-LWCS-R0174-NS4",
    "awb": "14900737398",
    "courier": "Xpressbees",
    "date": "2026-06-20",
    "read": 0.6,
    "charged": 0.75,
    "dims": [
      22,
      20,
      11
    ],
    "vol": 0.97,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01007",
    "sku": "1M-LWCS-R0174-NS4",
    "awb": "14900738864",
    "courier": "Shadowfax",
    "date": "2026-06-03",
    "read": 0.97,
    "charged": 1,
    "dims": [
      24,
      20,
      12
    ],
    "vol": 1.15,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01009",
    "sku": "1M-LWCS-R0174-NS4",
    "awb": "14900740330",
    "courier": "DTDC",
    "date": "2026-06-26",
    "read": 0.79,
    "charged": 1,
    "dims": [
      22,
      25,
      12
    ],
    "vol": 1.32,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01011",
    "sku": "1M-LWCS-R0174-NS4",
    "awb": "14900741796",
    "courier": "Blitz",
    "date": "2026-05-18",
    "read": 0.81,
    "charged": 1,
    "dims": [
      39,
      18,
      17
    ],
    "vol": 2.39,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01013",
    "sku": "1M-LWCS-R0174-NS4",
    "awb": "14900743262",
    "courier": "Blitz",
    "date": "2026-07-28",
    "read": 0.71,
    "charged": 0.75,
    "dims": [
      22,
      17,
      10
    ],
    "vol": 0.75,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-01015",
    "sku": "1M-LWCS-R0179-OG4",
    "awb": "14900744728",
    "courier": "DTDC",
    "date": "2026-08-02",
    "read": 1,
    "dims": [
      38,
      15,
      9
    ],
    "vol": 1.03,
    "source": "upload",
    "video": {
      "name": "evidence-1016-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1016-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1016-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01017",
    "sku": "1M-LWCS-R0179-OG4",
    "awb": "14900746194",
    "courier": "Ekart",
    "date": "2026-08-01",
    "read": 0.75,
    "dims": [
      38,
      26,
      8
    ],
    "vol": 1.58,
    "source": "upload",
    "video": {
      "name": "evidence-1018-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1018-len.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-01019",
    "sku": "1M-LWCS-R0179-OG4",
    "awb": "14900747660",
    "courier": "DTDC",
    "date": "2026-06-12",
    "read": 0.96,
    "charged": 1,
    "dims": [
      21,
      18,
      13
    ],
    "vol": 0.98,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01021",
    "sku": "1M-LWCS-R0179-OG4",
    "awb": "14900749126",
    "courier": "Xpressbees",
    "date": "2026-07-05",
    "read": 0.81,
    "charged": 1,
    "dims": [
      24,
      25,
      6
    ],
    "vol": 0.72,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01023",
    "sku": "1M-LWCS-R0179-OG4",
    "awb": "14900750592",
    "courier": "Ekart",
    "date": "2026-07-18",
    "read": 0.96,
    "charged": 1,
    "dims": [
      33,
      18,
      6
    ],
    "vol": 0.71,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01025",
    "sku": "1M-LWCS-R0179-OG4",
    "awb": "14900752058",
    "courier": "Blitz",
    "date": "2026-06-09",
    "read": 0.53,
    "charged": 0.75,
    "dims": [
      23,
      18,
      16
    ],
    "vol": 1.32,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01027",
    "sku": "1M-LWCS-R0179-OG4",
    "awb": "14900753524",
    "courier": "Delhivery",
    "date": "2026-06-26",
    "read": 0.83,
    "charged": 1,
    "dims": [
      35,
      21,
      14
    ],
    "vol": 2.06,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-01029",
    "sku": "1M-TWTS-C02",
    "awb": "14900754990",
    "courier": "Ekart",
    "date": "2026-08-02",
    "read": 0.75,
    "dims": [
      26,
      27,
      10
    ],
    "vol": 1.4,
    "source": "upload",
    "video": {
      "name": "evidence-1030-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1030-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1030-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1030-hgt.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-01031",
    "sku": "1M-TWTS-C02",
    "awb": "14900756456",
    "courier": "Shadowfax",
    "date": "2026-06-13",
    "read": 0.66,
    "charged": 0.75,
    "dims": [
      36,
      23,
      9
    ],
    "vol": 1.49,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01033",
    "sku": "1M-TWTS-C02",
    "awb": "14900757922",
    "courier": "Xpressbees",
    "date": "2026-06-03",
    "read": 0.55,
    "charged": 0.75,
    "dims": [
      32,
      28,
      11
    ],
    "vol": 1.97,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01035",
    "sku": "1M-TWTS-C02",
    "awb": "14900759388",
    "courier": "Ekart",
    "date": "2026-05-01",
    "read": 0.63,
    "charged": 0.75,
    "dims": [
      29,
      25,
      10
    ],
    "vol": 1.45,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01037",
    "sku": "1M-TWPO-R0002-WT4",
    "awb": "14900760854",
    "courier": "Delhivery",
    "date": "2026-06-29",
    "read": 0.69,
    "charged": 0.75,
    "dims": [
      31,
      18,
      19
    ],
    "vol": 2.12,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01039",
    "sku": "1M-TWPO-R0002-WT4",
    "awb": "14900762320",
    "courier": "Delhivery",
    "date": "2026-07-05",
    "read": 0.9,
    "charged": 1,
    "dims": [
      37,
      27,
      18
    ],
    "vol": 3.6,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01041",
    "sku": "1M-TWPO-R0002-WT4",
    "awb": "14900763786",
    "courier": "Ekart",
    "date": "2026-05-31",
    "read": 0.66,
    "charged": 0.75,
    "dims": [
      25,
      15,
      7
    ],
    "vol": 0.53,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01043",
    "sku": "1M-TWPO-R0002-WT4",
    "awb": "14900765252",
    "courier": "Shadowfax",
    "date": "2026-05-06",
    "read": 0.57,
    "charged": 0.75,
    "dims": [
      30,
      25,
      19
    ],
    "vol": 2.85,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-01045",
    "sku": "1M-BWCP-R0002-BL5",
    "awb": "14900766718",
    "courier": "Blitz",
    "date": "2026-07-30",
    "read": 0.75,
    "dims": [
      34,
      23,
      6
    ],
    "vol": 0.94,
    "source": "upload",
    "video": {
      "name": "evidence-1046-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1046-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1046-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1046-hgt.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-01047",
    "sku": "1M-BWCP-R0002-BL5",
    "awb": "14900768184",
    "courier": "Bluedart",
    "date": "2026-06-08",
    "read": 0.55,
    "charged": 0.75,
    "dims": [
      21,
      28,
      15
    ],
    "vol": 1.76,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01049",
    "sku": "1M-BWCP-R0002-BL5",
    "awb": "14900769650",
    "courier": "Delhivery",
    "date": "2026-06-27",
    "read": 0.62,
    "charged": 0.75,
    "dims": [
      20,
      26,
      6
    ],
    "vol": 0.62,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01051",
    "sku": "1M-BWCP-R0002-BL5",
    "awb": "14900771116",
    "courier": "Ekart",
    "date": "2026-07-28",
    "read": 0.85,
    "charged": 1,
    "dims": [
      37,
      17,
      16
    ],
    "vol": 2.01,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-01053",
    "sku": "1M-BWCP-R0002-NB4",
    "awb": "14900772582",
    "courier": "Xpressbees",
    "date": "2026-08-04",
    "read": 1,
    "dims": [
      36,
      21,
      8
    ],
    "vol": 1.21,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-1054-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1054-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1054-hgt.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01055",
    "sku": "1M-BWCP-R0002-NB4",
    "awb": "14900774048",
    "courier": "Xpressbees",
    "date": "2026-08-04",
    "read": 1,
    "dims": [
      34,
      16,
      6
    ],
    "vol": 0.65,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-1056-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1056-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1056-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-1056-scale.jpg",
        "url": null
      },
      {
        "slot": "label",
        "name": "evidence-1056-label.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-01057",
    "sku": "1M-BWCP-R0002-NB4",
    "awb": "14900775514",
    "courier": "Bluedart",
    "date": "2026-07-16",
    "read": 0.62,
    "charged": 0.75,
    "dims": [
      37,
      17,
      11
    ],
    "vol": 1.38,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01059",
    "sku": "1M-BWCP-R0002-NB4",
    "awb": "14900776980",
    "courier": "Delhivery",
    "date": "2026-05-27",
    "read": 0.92,
    "charged": 1,
    "dims": [
      34,
      25,
      11
    ],
    "vol": 1.87,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01061",
    "sku": "1M-BWCP-R0002-NB4",
    "awb": "14900778446",
    "courier": "Blitz",
    "date": "2026-07-10",
    "read": 0.91,
    "charged": 1,
    "dims": [
      39,
      22,
      9
    ],
    "vol": 1.54,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01063",
    "sku": "1M-TWRT-C1476-GM4",
    "awb": "14900779912",
    "courier": "DTDC",
    "date": "2026-06-14",
    "read": 0.69,
    "charged": 0.75,
    "dims": [
      30,
      18,
      19
    ],
    "vol": 2.05,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01065",
    "sku": "1M-TWRT-C1476-GM4",
    "awb": "14900781378",
    "courier": "Blitz",
    "date": "2026-05-14",
    "read": 0.54,
    "charged": 0.75,
    "dims": [
      39,
      18,
      16
    ],
    "vol": 2.25,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01067",
    "sku": "1M-TWRT-C1476-GM4",
    "awb": "14900782844",
    "courier": "Blitz",
    "date": "2026-05-29",
    "read": 0.69,
    "charged": 0.75,
    "dims": [
      30,
      29,
      19
    ],
    "vol": 3.31,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-01069",
    "sku": "1M-BWJG-R0137-OL6",
    "awb": "14900784310",
    "courier": "Delhivery",
    "date": "2026-08-02",
    "read": 1,
    "dims": [
      32,
      25,
      9
    ],
    "vol": 1.44,
    "source": "upload",
    "video": {
      "name": "evidence-1070-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1070-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1070-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1070-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-1070-scale.jpg",
        "url": null
      },
      {
        "slot": "label",
        "name": "evidence-1070-label.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01071",
    "sku": "1M-BWJG-R0137-OL6",
    "awb": "14900785776",
    "courier": "Xpressbees",
    "date": "2026-08-01",
    "read": 1,
    "dims": [
      38,
      24,
      9
    ],
    "vol": 1.64,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-1072-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1072-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-01073",
    "sku": "1M-BWJG-R0137-OL6",
    "awb": "14900787242",
    "courier": "Xpressbees",
    "date": "2026-07-25",
    "read": 0.88,
    "charged": 1,
    "dims": [
      30,
      26,
      11
    ],
    "vol": 1.72,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01075",
    "sku": "1M-BWJG-R0137-OL6",
    "awb": "14900788708",
    "courier": "DTDC",
    "date": "2026-05-15",
    "read": 0.54,
    "charged": 0.75,
    "dims": [
      39,
      26,
      13
    ],
    "vol": 2.64,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01077",
    "sku": "1M-BWJG-R0137-OL6",
    "awb": "14900790174",
    "courier": "Delhivery",
    "date": "2026-06-17",
    "read": 0.97,
    "charged": 1,
    "dims": [
      21,
      24,
      7
    ],
    "vol": 0.71,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01079",
    "sku": "1M-BWJG-R0137-OL6",
    "awb": "14900791640",
    "courier": "Blitz",
    "date": "2026-07-23",
    "read": 0.96,
    "charged": 1,
    "dims": [
      38,
      15,
      16
    ],
    "vol": 1.82,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-01081",
    "sku": "2-Pack Classic Straight Fit Joggers Navy Blue/Eiffel Tower / L (sku -",
    "awb": "14900793106",
    "courier": "Ekart",
    "date": "2026-08-03",
    "read": 0.75,
    "dims": [
      29,
      28,
      9
    ],
    "vol": 1.46,
    "source": "upload",
    "video": {
      "name": "evidence-1082-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1082-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1082-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1082-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-1082-scale.jpg",
        "url": null
      },
      {
        "slot": "label",
        "name": "evidence-1082-label.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-01083",
    "sku": "2-Pack Classic Straight Fit Joggers Navy Blue/Eiffel Tower / L (sku -",
    "awb": "14900794572",
    "courier": "Blitz",
    "date": "2026-05-29",
    "read": 0.71,
    "charged": 0.75,
    "dims": [
      27,
      29,
      19
    ],
    "vol": 2.98,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01085",
    "sku": "2-Pack Classic Straight Fit Joggers Navy Blue/Eiffel Tower / L (sku -",
    "awb": "14900796038",
    "courier": "Delhivery",
    "date": "2026-06-03",
    "read": 0.53,
    "charged": 0.75,
    "dims": [
      25,
      29,
      11
    ],
    "vol": 1.6,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01087",
    "sku": "2-Pack Classic Straight Fit Joggers Navy Blue/Eiffel Tower / L (sku -",
    "awb": "14900797504",
    "courier": "Blitz",
    "date": "2026-05-07",
    "read": 0.9,
    "charged": 1,
    "dims": [
      29,
      23,
      11
    ],
    "vol": 1.47,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01089",
    "sku": "NBAW24MJ14-Men-Joggers-Eiffel tower-XL",
    "awb": "14900798970",
    "courier": "Shadowfax",
    "date": "2026-07-08",
    "read": 0.98,
    "charged": 1,
    "dims": [
      36,
      25,
      14
    ],
    "vol": 2.52,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01091",
    "sku": "NBAW24MJ14-Men-Joggers-Eiffel tower-XL",
    "awb": "14900800436",
    "courier": "Blitz",
    "date": "2026-05-15",
    "read": 0.91,
    "charged": 1,
    "dims": [
      30,
      27,
      12
    ],
    "vol": 1.94,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01093",
    "sku": "NBAW24MJ14-Men-Joggers-Eiffel tower-XL",
    "awb": "14900801902",
    "courier": "DTDC",
    "date": "2026-06-06",
    "read": 0.97,
    "charged": 1,
    "dims": [
      25,
      24,
      13
    ],
    "vol": 1.56,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01095",
    "sku": "NBAW24MJ14-Men-Joggers-Eiffel tower-XL",
    "awb": "14900803368",
    "courier": "Ekart",
    "date": "2026-06-14",
    "read": 0.92,
    "charged": 1,
    "dims": [
      32,
      17,
      13
    ],
    "vol": 1.41,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-01097",
    "sku": "1M-LWCS-R0175-B",
    "awb": "14900804834",
    "courier": "DTDC",
    "date": "2026-08-03",
    "read": 0.75,
    "dims": [
      39,
      16,
      10
    ],
    "vol": 1.25,
    "source": "upload",
    "video": {
      "name": "evidence-1098-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1098-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1098-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1098-hgt.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01099",
    "sku": "1M-LWCS-R0175-B",
    "awb": "14900806300",
    "courier": "Shadowfax",
    "date": "2026-08-01",
    "read": 1,
    "dims": [
      29,
      28,
      9
    ],
    "vol": 1.46,
    "source": "upload",
    "video": {
      "name": "evidence-1100-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1100-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1100-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1100-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-1100-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-01101",
    "sku": "1M-LWCS-R0175-B",
    "awb": "14900807766",
    "courier": "Delhivery",
    "date": "2026-05-07",
    "read": 0.69,
    "charged": 0.75,
    "dims": [
      24,
      20,
      6
    ],
    "vol": 0.58,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01103",
    "sku": "1M-LWCS-R0175-B",
    "awb": "14900809232",
    "courier": "Delhivery",
    "date": "2026-06-25",
    "read": 0.88,
    "charged": 1,
    "dims": [
      35,
      16,
      18
    ],
    "vol": 2.02,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01105",
    "sku": "1M-LWCS-R0175-B",
    "awb": "14900810698",
    "courier": "Shadowfax",
    "date": "2026-05-19",
    "read": 0.96,
    "charged": 1,
    "dims": [
      23,
      17,
      19
    ],
    "vol": 1.49,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01107",
    "sku": "1M-LWCS-R0175-B",
    "awb": "14900812164",
    "courier": "Bluedart",
    "date": "2026-07-06",
    "read": 0.91,
    "charged": 1,
    "dims": [
      37,
      27,
      14
    ],
    "vol": 2.8,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-01109",
    "sku": "1M-TWPO-R0002-BU6",
    "awb": "14900813630",
    "courier": "Bluedart",
    "date": "2026-08-05",
    "read": 0.75,
    "dims": [
      36,
      27,
      19
    ],
    "vol": 3.69,
    "source": "upload",
    "video": {
      "name": "evidence-1110-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1110-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1110-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1110-hgt.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01111",
    "sku": "1M-TWPO-R0002-BU6",
    "awb": "14900815096",
    "courier": "Xpressbees",
    "date": "2026-07-31",
    "read": 0.75,
    "dims": [
      28,
      16,
      19
    ],
    "vol": 1.7,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-1112-len.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-01113",
    "sku": "1M-TWPO-R0002-BU6",
    "awb": "14900816562",
    "courier": "Ekart",
    "date": "2026-07-28",
    "read": 0.55,
    "charged": 0.75,
    "dims": [
      27,
      21,
      9
    ],
    "vol": 1.02,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01115",
    "sku": "1M-TWPO-R0002-BU6",
    "awb": "14900818028",
    "courier": "Ekart",
    "date": "2026-07-12",
    "read": 0.56,
    "charged": 0.75,
    "dims": [
      30,
      15,
      11
    ],
    "vol": 0.99,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01117",
    "sku": "1M-TWPO-R0002-BU6",
    "awb": "14900819494",
    "courier": "Bluedart",
    "date": "2026-07-02",
    "read": 0.55,
    "charged": 0.75,
    "dims": [
      31,
      19,
      13
    ],
    "vol": 1.53,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01119",
    "sku": "1M-TWPO-R0002-BU6",
    "awb": "14900820960",
    "courier": "DTDC",
    "date": "2026-06-16",
    "read": 0.69,
    "charged": 0.75,
    "dims": [
      33,
      26,
      9
    ],
    "vol": 1.54,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01121",
    "sku": "1M-TWPO-R0002-BU6",
    "awb": "14900822426",
    "courier": "Delhivery",
    "date": "2026-06-08",
    "read": 0.73,
    "charged": 0.75,
    "dims": [
      32,
      23,
      7
    ],
    "vol": 1.03,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01123",
    "sku": "2-Pack Classic Straight Fit Joggers Navy Blue/Eiffel Tower / M (sku -",
    "awb": "14900823892",
    "courier": "Blitz",
    "date": "2026-05-26",
    "read": 0.91,
    "charged": 1,
    "dims": [
      29,
      20,
      19
    ],
    "vol": 2.2,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01125",
    "sku": "2-Pack Classic Straight Fit Joggers Navy Blue/Eiffel Tower / M (sku -",
    "awb": "14900825358",
    "courier": "DTDC",
    "date": "2026-06-18",
    "read": 0.66,
    "charged": 0.75,
    "dims": [
      24,
      15,
      12
    ],
    "vol": 0.86,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-01127",
    "sku": "1M-TWSS-R0032-NB3",
    "awb": "14900826824",
    "courier": "Shadowfax",
    "date": "2026-07-31",
    "read": 0.75,
    "dims": [
      21,
      28,
      5
    ],
    "vol": 0.59,
    "source": "upload",
    "video": {
      "name": "evidence-1128-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1128-len.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-01129",
    "sku": "1M-TWSS-R0032-NB3",
    "awb": "14900828290",
    "courier": "Xpressbees",
    "date": "2026-06-26",
    "read": 0.9,
    "charged": 1,
    "dims": [
      30,
      22,
      15
    ],
    "vol": 1.98,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01131",
    "sku": "1M-TWSS-R0032-NB3",
    "awb": "14900829756",
    "courier": "Delhivery",
    "date": "2026-04-30",
    "read": 0.9,
    "charged": 1,
    "dims": [
      24,
      19,
      5
    ],
    "vol": 0.46,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01133",
    "sku": "1M-TWSS-R0032-NB3",
    "awb": "14900831222",
    "courier": "DTDC",
    "date": "2026-05-14",
    "read": 0.81,
    "charged": 1,
    "dims": [
      27,
      28,
      17
    ],
    "vol": 2.57,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-01135",
    "sku": "1M-TWPO-R0002-BL4",
    "awb": "14900832688",
    "courier": "Delhivery",
    "date": "2026-08-06",
    "read": 0.75,
    "dims": [
      37,
      21,
      6
    ],
    "vol": 0.93,
    "source": "upload",
    "video": {
      "name": "evidence-1136-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1136-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1136-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01137",
    "sku": "1M-TWPO-R0002-BL4",
    "awb": "14900834154",
    "courier": "DTDC",
    "date": "2026-08-05",
    "read": 0.75,
    "dims": [
      21,
      26,
      8
    ],
    "vol": 0.87,
    "source": "upload",
    "video": {
      "name": "evidence-1138-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1138-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1138-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1138-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-1138-scale.jpg",
        "url": null
      },
      {
        "slot": "label",
        "name": "evidence-1138-label.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-01139",
    "sku": "1M-TWPO-R0002-BL4",
    "awb": "14900835620",
    "courier": "DTDC",
    "date": "2026-06-22",
    "read": 0.62,
    "charged": 0.75,
    "dims": [
      33,
      24,
      11
    ],
    "vol": 1.74,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01141",
    "sku": "1M-TWPO-R0002-BL4",
    "awb": "14900837086",
    "courier": "Xpressbees",
    "date": "2026-07-17",
    "read": 0.59,
    "charged": 0.75,
    "dims": [
      29,
      26,
      7
    ],
    "vol": 1.06,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01143",
    "sku": "1M-TWPO-R0002-BL4",
    "awb": "14900838552",
    "courier": "Ekart",
    "date": "2026-07-18",
    "read": 0.56,
    "charged": 0.75,
    "dims": [
      37,
      20,
      19
    ],
    "vol": 2.81,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01145",
    "sku": "1M-TWPO-R0002-BL4",
    "awb": "14900840018",
    "courier": "DTDC",
    "date": "2026-04-30",
    "read": 0.68,
    "charged": 0.75,
    "dims": [
      23,
      28,
      18
    ],
    "vol": 2.32,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01147",
    "sku": "1M-TWPO-R0002-BL4",
    "awb": "14900841484",
    "courier": "Delhivery",
    "date": "2026-06-05",
    "read": 0.62,
    "charged": 0.75,
    "dims": [
      31,
      27,
      5
    ],
    "vol": 0.84,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-01149",
    "sku": "1M-TWTS-C1036-WT4",
    "awb": "14900842950",
    "courier": "Delhivery",
    "date": "2026-08-03",
    "read": 0.75,
    "dims": [
      28,
      26,
      12
    ],
    "vol": 1.75,
    "source": "upload",
    "video": {
      "name": "evidence-1150-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1150-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1150-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1150-hgt.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01151",
    "sku": "1M-TWTS-C1036-WT4",
    "awb": "14900844416",
    "courier": "DTDC",
    "date": "2026-07-30",
    "read": 0.75,
    "dims": [
      24,
      20,
      14
    ],
    "vol": 1.34,
    "source": "upload",
    "video": {
      "name": "evidence-1152-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1152-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1152-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01153",
    "sku": "1M-TWTS-C1036-WT4",
    "awb": "14900845882",
    "courier": "Bluedart",
    "date": "2026-08-06",
    "read": 0.75,
    "dims": [
      36,
      21,
      13
    ],
    "vol": 1.97,
    "source": "upload",
    "video": {
      "name": "evidence-1154-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1154-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1154-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01155",
    "sku": "1M-TWTS-C1036-WT4",
    "awb": "14900847348",
    "courier": "Xpressbees",
    "date": "2026-08-05",
    "read": 0.75,
    "dims": [
      37,
      22,
      19
    ],
    "vol": 3.09,
    "source": "upload",
    "video": {
      "name": "evidence-1156-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1156-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1156-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01157",
    "sku": "1M-TWTS-C1036-WT4",
    "awb": "14900848814",
    "courier": "Ekart",
    "date": "2026-08-04",
    "read": 0.75,
    "dims": [
      30,
      18,
      19
    ],
    "vol": 2.05,
    "source": "upload",
    "video": {
      "name": "evidence-1158-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1158-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1158-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1158-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-1158-scale.jpg",
        "url": null
      },
      {
        "slot": "label",
        "name": "evidence-1158-label.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01159",
    "sku": "1M-TWTS-C1036-WT4",
    "awb": "14900850280",
    "courier": "Shadowfax",
    "date": "2026-08-02",
    "read": 0.75,
    "dims": [
      27,
      23,
      19
    ],
    "vol": 2.36,
    "source": "upload",
    "video": {
      "name": "evidence-1160-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1160-len.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-01161",
    "sku": "1M-TWTS-C1036-WT4",
    "awb": "14900851746",
    "courier": "Shadowfax",
    "date": "2026-06-16",
    "read": 0.58,
    "charged": 0.75,
    "dims": [
      25,
      17,
      13
    ],
    "vol": 1.11,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01163",
    "sku": "1M-TWTS-C1036-WT4",
    "awb": "14900853212",
    "courier": "DTDC",
    "date": "2026-06-11",
    "read": 0.68,
    "charged": 0.75,
    "dims": [
      37,
      17,
      18
    ],
    "vol": 2.26,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01165",
    "sku": "1M-TWTS-C1036-WT4",
    "awb": "14900854678",
    "courier": "DTDC",
    "date": "2026-07-21",
    "read": 0.65,
    "charged": 0.75,
    "dims": [
      25,
      18,
      8
    ],
    "vol": 0.72,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01167",
    "sku": "1M-TWTS-C1036-WT4",
    "awb": "14900856144",
    "courier": "DTDC",
    "date": "2026-06-25",
    "read": 0.54,
    "charged": 0.75,
    "dims": [
      32,
      18,
      12
    ],
    "vol": 1.38,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-01169",
    "sku": "1M-TWTS-C0889-P",
    "awb": "14900857610",
    "courier": "Delhivery",
    "date": "2026-08-03",
    "read": 0.75,
    "dims": [
      31,
      27,
      5
    ],
    "vol": 0.84,
    "source": "upload",
    "video": {
      "name": "evidence-1170-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1170-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1170-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1170-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-1170-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01171",
    "sku": "1M-TWTS-C0889-P",
    "awb": "14900859076",
    "courier": "Delhivery",
    "date": "2026-08-06",
    "read": 0.75,
    "dims": [
      39,
      27,
      19
    ],
    "vol": 4,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-1172-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1172-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1172-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-1172-scale.jpg",
        "url": null
      },
      {
        "slot": "label",
        "name": "evidence-1172-label.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01173",
    "sku": "1M-TWTS-C0889-P",
    "awb": "14900860542",
    "courier": "Shadowfax",
    "date": "2026-08-04",
    "read": 0.75,
    "dims": [
      22,
      20,
      15
    ],
    "vol": 1.32,
    "source": "upload",
    "video": {
      "name": "evidence-1174-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1174-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1174-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1174-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-1174-scale.jpg",
        "url": null
      },
      {
        "slot": "label",
        "name": "evidence-1174-label.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01175",
    "sku": "1M-TWTS-C0889-P",
    "awb": "14900862008",
    "courier": "Ekart",
    "date": "2026-08-06",
    "read": 0.75,
    "dims": [
      35,
      26,
      19
    ],
    "vol": 3.46,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-1176-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1176-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1176-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-1176-scale.jpg",
        "url": null
      },
      {
        "slot": "label",
        "name": "evidence-1176-label.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01177",
    "sku": "1M-TWTS-C0889-P",
    "awb": "14900863474",
    "courier": "Ekart",
    "date": "2026-08-03",
    "read": 0.75,
    "dims": [
      31,
      20,
      8
    ],
    "vol": 0.99,
    "source": "upload",
    "video": {
      "name": "evidence-1178-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1178-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1178-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1178-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-1178-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01179",
    "sku": "1M-TWTS-C0889-P",
    "awb": "14900864940",
    "courier": "Ekart",
    "date": "2026-07-30",
    "read": 0.75,
    "dims": [
      22,
      23,
      19
    ],
    "vol": 1.92,
    "source": "upload",
    "video": {
      "name": "evidence-1180-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1180-len.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-01181",
    "sku": "1M-TWTS-C0889-P",
    "awb": "14900866406",
    "courier": "Shadowfax",
    "date": "2026-07-08",
    "read": 0.64,
    "charged": 0.75,
    "dims": [
      36,
      15,
      15
    ],
    "vol": 1.62,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01183",
    "sku": "1M-TWTS-C0889-P",
    "awb": "14900867872",
    "courier": "Blitz",
    "date": "2026-06-04",
    "read": 0.57,
    "charged": 0.75,
    "dims": [
      38,
      20,
      11
    ],
    "vol": 1.67,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-01185",
    "sku": "1M-BWJG-R0149-PB6",
    "awb": "14900869338",
    "courier": "Ekart",
    "date": "2026-08-01",
    "read": 0.75,
    "dims": [
      22,
      22,
      5
    ],
    "vol": 0.48,
    "source": "upload",
    "video": {
      "name": "evidence-1186-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1186-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1186-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1186-hgt.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01187",
    "sku": "1M-BWJG-R0149-PB6",
    "awb": "14900870804",
    "courier": "Shadowfax",
    "date": "2026-08-02",
    "read": 0.75,
    "dims": [
      35,
      18,
      15
    ],
    "vol": 1.89,
    "source": "upload",
    "video": {
      "name": "evidence-1188-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1188-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1188-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1188-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-1188-scale.jpg",
        "url": null
      },
      {
        "slot": "label",
        "name": "evidence-1188-label.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01189",
    "sku": "1M-BWJG-R0149-PB6",
    "awb": "14900872270",
    "courier": "DTDC",
    "date": "2026-08-05",
    "read": 1,
    "dims": [
      23,
      29,
      7
    ],
    "vol": 0.93,
    "source": "upload",
    "video": {
      "name": "evidence-1190-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1190-len.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-01191",
    "sku": "1M-BWJG-R0149-PB6",
    "awb": "14900873736",
    "courier": "Delhivery",
    "date": "2026-05-26",
    "read": 0.66,
    "charged": 0.75,
    "dims": [
      27,
      20,
      13
    ],
    "vol": 1.4,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01193",
    "sku": "1M-BWJG-R0149-PB6",
    "awb": "14900875202",
    "courier": "DTDC",
    "date": "2026-05-27",
    "read": 0.66,
    "charged": 0.75,
    "dims": [
      38,
      15,
      6
    ],
    "vol": 0.68,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-01195",
    "sku": "1M-BWJG-R0137-STG5",
    "awb": "14900876668",
    "courier": "Ekart",
    "date": "2026-07-30",
    "read": 1,
    "dims": [
      36,
      26,
      12
    ],
    "vol": 2.25,
    "source": "upload",
    "video": {
      "name": "evidence-1196-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1196-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1196-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1196-hgt.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01197",
    "sku": "1M-BWJG-R0137-STG5",
    "awb": "14900878134",
    "courier": "Bluedart",
    "date": "2026-07-31",
    "read": 1,
    "dims": [
      34,
      19,
      18
    ],
    "vol": 2.33,
    "source": "upload",
    "video": {
      "name": "evidence-1198-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1198-len.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01199",
    "sku": "1M-BWJG-R0137-STG5",
    "awb": "14900879600",
    "courier": "Blitz",
    "date": "2026-07-30",
    "read": 1,
    "dims": [
      31,
      24,
      16
    ],
    "vol": 2.38,
    "source": "upload",
    "video": {
      "name": "evidence-1200-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1200-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1200-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1200-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-1200-scale.jpg",
        "url": null
      },
      {
        "slot": "label",
        "name": "evidence-1200-label.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01201",
    "sku": "1M-BWJG-R0137-STG5",
    "awb": "14900881066",
    "courier": "Shadowfax",
    "date": "2026-08-06",
    "read": 1,
    "dims": [
      25,
      22,
      14
    ],
    "vol": 1.54,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-1202-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1202-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1202-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-1202-scale.jpg",
        "url": null
      },
      {
        "slot": "label",
        "name": "evidence-1202-label.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01203",
    "sku": "1M-BWJG-R0137-STG5",
    "awb": "14900882532",
    "courier": "Xpressbees",
    "date": "2026-08-03",
    "read": 1,
    "dims": [
      20,
      27,
      6
    ],
    "vol": 0.65,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-1204-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1204-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1204-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-1204-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01205",
    "sku": "1M-BWJG-R0137-STG5",
    "awb": "14900883998",
    "courier": "Bluedart",
    "date": "2026-08-04",
    "read": 1,
    "dims": [
      21,
      16,
      10
    ],
    "vol": 0.67,
    "source": "upload",
    "video": {
      "name": "evidence-1206-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1206-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1206-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1206-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-1206-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-01207",
    "sku": "1M-BWJG-R0137-STG5",
    "awb": "14900885464",
    "courier": "Xpressbees",
    "date": "2026-05-30",
    "read": 0.9,
    "charged": 1,
    "dims": [
      35,
      28,
      11
    ],
    "vol": 2.16,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01209",
    "sku": "1M-BWJG-R0137-STG5",
    "awb": "14900886930",
    "courier": "Xpressbees",
    "date": "2026-06-02",
    "read": 0.93,
    "charged": 1,
    "dims": [
      20,
      28,
      12
    ],
    "vol": 1.34,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01211",
    "sku": "1M-BWJG-R0137-STG5",
    "awb": "14900888396",
    "courier": "Xpressbees",
    "date": "2026-05-05",
    "read": 0.94,
    "charged": 1,
    "dims": [
      37,
      28,
      10
    ],
    "vol": 2.07,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01213",
    "sku": "1M-BWJG-R0137-STG5",
    "awb": "14900889862",
    "courier": "Shadowfax",
    "date": "2026-05-09",
    "read": 0.69,
    "charged": 0.75,
    "dims": [
      25,
      26,
      7
    ],
    "vol": 0.91,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-01215",
    "sku": "1M-BWJG-R0090-AW3",
    "awb": "14900891328",
    "courier": "Xpressbees",
    "date": "2026-08-02",
    "read": 1,
    "dims": [
      25,
      26,
      16
    ],
    "vol": 2.08,
    "source": "upload",
    "video": {
      "name": "evidence-1216-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1216-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1216-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01217",
    "sku": "1M-BWJG-R0090-AW3",
    "awb": "14900892794",
    "courier": "Bluedart",
    "date": "2026-07-31",
    "read": 0.75,
    "dims": [
      24,
      28,
      9
    ],
    "vol": 1.21,
    "source": "upload",
    "video": {
      "name": "evidence-1218-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1218-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1218-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1218-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-1218-scale.jpg",
        "url": null
      },
      {
        "slot": "label",
        "name": "evidence-1218-label.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01219",
    "sku": "1M-BWJG-R0090-AW3",
    "awb": "14900894260",
    "courier": "Blitz",
    "date": "2026-08-04",
    "read": 1,
    "dims": [
      33,
      16,
      14
    ],
    "vol": 1.48,
    "source": "upload",
    "video": {
      "name": "evidence-1220-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1220-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1220-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1220-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-1220-scale.jpg",
        "url": null
      },
      {
        "slot": "label",
        "name": "evidence-1220-label.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01221",
    "sku": "1M-BWJG-R0090-AW3",
    "awb": "14900895726",
    "courier": "Delhivery",
    "date": "2026-07-30",
    "read": 0.75,
    "dims": [
      22,
      21,
      18
    ],
    "vol": 1.66,
    "source": "upload",
    "video": {
      "name": "evidence-1222-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1222-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1222-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1222-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-1222-scale.jpg",
        "url": null
      },
      {
        "slot": "label",
        "name": "evidence-1222-label.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-01223",
    "sku": "1M-BWJG-R0090-AW3",
    "awb": "14900897192",
    "courier": "Delhivery",
    "date": "2026-06-30",
    "read": 0.94,
    "charged": 1,
    "dims": [
      31,
      18,
      6
    ],
    "vol": 0.67,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01225",
    "sku": "1M-BWJG-R0090-AW3",
    "awb": "14900898658",
    "courier": "Xpressbees",
    "date": "2026-05-21",
    "read": 0.58,
    "charged": 0.75,
    "dims": [
      27,
      22,
      10
    ],
    "vol": 1.19,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-01227",
    "sku": "1M-BWJG-R0137-STG4",
    "awb": "14900900124",
    "courier": "Delhivery",
    "date": "2026-07-30",
    "read": 1,
    "dims": [
      39,
      25,
      7
    ],
    "vol": 1.37,
    "source": "upload",
    "video": {
      "name": "evidence-1228-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1228-len.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01229",
    "sku": "1M-BWJG-R0137-STG4",
    "awb": "14900901590",
    "courier": "Shadowfax",
    "date": "2026-08-04",
    "read": 1,
    "dims": [
      27,
      17,
      18
    ],
    "vol": 1.65,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-1230-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1230-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1230-hgt.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-01231",
    "sku": "1M-BWJG-R0137-STG4",
    "awb": "14900903056",
    "courier": "Bluedart",
    "date": "2026-06-29",
    "read": 0.96,
    "charged": 1,
    "dims": [
      39,
      18,
      13
    ],
    "vol": 1.83,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01233",
    "sku": "1M-BWJG-R0137-STG4",
    "awb": "14900904522",
    "courier": "DTDC",
    "date": "2026-06-12",
    "read": 0.68,
    "charged": 0.75,
    "dims": [
      26,
      28,
      12
    ],
    "vol": 1.75,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01235",
    "sku": "1M-BWJG-R0137-STG4",
    "awb": "14900905988",
    "courier": "Xpressbees",
    "date": "2026-07-11",
    "read": 0.62,
    "charged": 0.75,
    "dims": [
      21,
      28,
      19
    ],
    "vol": 2.23,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-01237",
    "sku": "1M-TWPO-R0002-LT3",
    "awb": "14900907454",
    "courier": "Ekart",
    "date": "2026-07-30",
    "read": 0.75,
    "dims": [
      35,
      25,
      5
    ],
    "vol": 0.88,
    "source": "upload",
    "video": {
      "name": "evidence-1238-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1238-len.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01239",
    "sku": "1M-TWPO-R0002-LT3",
    "awb": "14900908920",
    "courier": "Shadowfax",
    "date": "2026-08-02",
    "read": 0.75,
    "dims": [
      24,
      18,
      14
    ],
    "vol": 1.21,
    "source": "upload",
    "video": {
      "name": "evidence-1240-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1240-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1240-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1240-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-1240-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-01241",
    "sku": "1M-TWPO-R0002-LT3",
    "awb": "14900910386",
    "courier": "Xpressbees",
    "date": "2026-07-23",
    "read": 0.69,
    "charged": 0.75,
    "dims": [
      27,
      25,
      5
    ],
    "vol": 0.68,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01243",
    "sku": "1M-TWPO-R0002-LT3",
    "awb": "14900911852",
    "courier": "Shadowfax",
    "date": "2026-06-18",
    "read": 0.7,
    "charged": 0.75,
    "dims": [
      27,
      28,
      6
    ],
    "vol": 0.91,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01245",
    "sku": "1M-TWPO-R0002-LT3",
    "awb": "14900913318",
    "courier": "Ekart",
    "date": "2026-07-20",
    "read": 0.73,
    "charged": 0.75,
    "dims": [
      29,
      15,
      5
    ],
    "vol": 0.44,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01247",
    "sku": "1M-TWPO-R0002-LT3",
    "awb": "14900914784",
    "courier": "Delhivery",
    "date": "2026-05-23",
    "read": 0.7,
    "charged": 0.75,
    "dims": [
      22,
      18,
      8
    ],
    "vol": 0.63,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01249",
    "sku": "1M-TWPO-R0002-LT3",
    "awb": "14900916250",
    "courier": "Delhivery",
    "date": "2026-07-04",
    "read": 0.66,
    "charged": 0.75,
    "dims": [
      23,
      21,
      12
    ],
    "vol": 1.16,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01251",
    "sku": "1M-TWPO-R0002-LT3",
    "awb": "14900917716",
    "courier": "DTDC",
    "date": "2026-05-19",
    "read": 0.71,
    "charged": 0.75,
    "dims": [
      30,
      15,
      12
    ],
    "vol": 1.08,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-01253",
    "sku": "NBJUN24MJ05 Joggers (Pack of 2)-Men-Joggers-BL/ET-L",
    "awb": "14900919182",
    "courier": "DTDC",
    "date": "2026-07-31",
    "read": 1,
    "dims": [
      23,
      17,
      18
    ],
    "vol": 1.41,
    "source": "upload",
    "video": {
      "name": "evidence-1254-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1254-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1254-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1254-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-1254-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01255",
    "sku": "NBJUN24MJ05 Joggers (Pack of 2)-Men-Joggers-BL/ET-L",
    "awb": "14900920648",
    "courier": "Bluedart",
    "date": "2026-08-02",
    "read": 0.75,
    "dims": [
      27,
      20,
      6
    ],
    "vol": 0.65,
    "source": "upload",
    "video": {
      "name": "evidence-1256-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1256-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1256-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1256-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-1256-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-01257",
    "sku": "NBJUN24MJ05 Joggers (Pack of 2)-Men-Joggers-BL/ET-L",
    "awb": "14900922114",
    "courier": "Delhivery",
    "date": "2026-06-30",
    "read": 0.64,
    "charged": 0.75,
    "dims": [
      24,
      28,
      10
    ],
    "vol": 1.34,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01259",
    "sku": "NBJUN24MJ05 Joggers (Pack of 2)-Men-Joggers-BL/ET-L",
    "awb": "14900923580",
    "courier": "DTDC",
    "date": "2026-05-24",
    "read": 0.65,
    "charged": 0.75,
    "dims": [
      20,
      15,
      6
    ],
    "vol": 0.36,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01261",
    "sku": "NBJUN24MJ05 Joggers (Pack of 2)-Men-Joggers-BL/ET-L",
    "awb": "14900925046",
    "courier": "Ekart",
    "date": "2026-05-20",
    "read": 0.72,
    "charged": 0.75,
    "dims": [
      20,
      25,
      15
    ],
    "vol": 1.5,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01263",
    "sku": "NBJUN24MJ05 Joggers (Pack of 2)-Men-Joggers-BL/ET-L",
    "awb": "14900926512",
    "courier": "Blitz",
    "date": "2026-06-29",
    "read": 0.67,
    "charged": 0.75,
    "dims": [
      32,
      24,
      17
    ],
    "vol": 2.61,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01265",
    "sku": "1M-TWPO-R0002-MUM5",
    "awb": "14900927978",
    "courier": "Delhivery",
    "date": "2026-07-03",
    "read": 0.66,
    "charged": 0.75,
    "dims": [
      37,
      24,
      10
    ],
    "vol": 1.78,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01267",
    "sku": "1M-TWPO-R0002-MUM5",
    "awb": "14900929444",
    "courier": "Xpressbees",
    "date": "2026-05-22",
    "read": 0.69,
    "charged": 0.75,
    "dims": [
      21,
      29,
      15
    ],
    "vol": 1.83,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01269",
    "sku": "1M-TWPO-R0002-MUM5",
    "awb": "14900930910",
    "courier": "Ekart",
    "date": "2026-05-17",
    "read": 0.56,
    "charged": 0.75,
    "dims": [
      20,
      27,
      7
    ],
    "vol": 0.76,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01271",
    "sku": "1M-TWPO-R0002-MUM5",
    "awb": "14900932376",
    "courier": "Bluedart",
    "date": "2026-06-09",
    "read": 0.61,
    "charged": 0.75,
    "dims": [
      38,
      25,
      19
    ],
    "vol": 3.61,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01273",
    "sku": "1M-TWPO-R0002-MUM5",
    "awb": "14900933842",
    "courier": "Bluedart",
    "date": "2026-07-25",
    "read": 0.61,
    "charged": 0.75,
    "dims": [
      21,
      17,
      8
    ],
    "vol": 0.57,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01275",
    "sku": "1M-TWPO-R0002-MUM5",
    "awb": "14900935308",
    "courier": "DTDC",
    "date": "2026-06-08",
    "read": 0.6,
    "charged": 0.75,
    "dims": [
      20,
      15,
      8
    ],
    "vol": 0.48,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-01277",
    "sku": "1M-TWTS-C0612- (dup 2)",
    "awb": "14900936774",
    "courier": "Shadowfax",
    "date": "2026-08-05",
    "read": 0.75,
    "dims": [
      27,
      23,
      12
    ],
    "vol": 1.49,
    "source": "upload",
    "video": {
      "name": "evidence-1278-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1278-len.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01279",
    "sku": "1M-TWTS-C0612- (dup 2)",
    "awb": "14900938240",
    "courier": "Ekart",
    "date": "2026-08-01",
    "read": 0.75,
    "dims": [
      20,
      27,
      5
    ],
    "vol": 0.54,
    "source": "upload",
    "video": {
      "name": "evidence-1280-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1280-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1280-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-01281",
    "sku": "1M-TWTS-C0612- (dup 2)",
    "awb": "14900939706",
    "courier": "Ekart",
    "date": "2026-04-30",
    "read": 0.71,
    "charged": 0.75,
    "dims": [
      25,
      24,
      19
    ],
    "vol": 2.28,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01283",
    "sku": "1M-TWTS-C0612- (dup 2)",
    "awb": "14900941172",
    "courier": "Delhivery",
    "date": "2026-04-30",
    "read": 0.56,
    "charged": 0.75,
    "dims": [
      37,
      22,
      7
    ],
    "vol": 1.14,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-01285",
    "sku": "NBJUN24MJ05-Men-Joggers-Black-XL",
    "awb": "14900942638",
    "courier": "Xpressbees",
    "date": "2026-07-30",
    "read": 1,
    "dims": [
      27,
      28,
      5
    ],
    "vol": 0.76,
    "source": "upload",
    "video": {
      "name": "evidence-1286-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1286-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1286-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1286-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-1286-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01287",
    "sku": "NBJUN24MJ05-Men-Joggers-Black-XL",
    "awb": "14900944104",
    "courier": "Shadowfax",
    "date": "2026-08-02",
    "read": 1,
    "dims": [
      24,
      23,
      14
    ],
    "vol": 1.55,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-1288-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1288-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01289",
    "sku": "NBJUN24MJ05-Men-Joggers-Black-XL",
    "awb": "14900945570",
    "courier": "Shadowfax",
    "date": "2026-08-01",
    "read": 0.75,
    "dims": [
      34,
      27,
      19
    ],
    "vol": 3.49,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-1290-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1290-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01291",
    "sku": "NBJUN24MJ05-Men-Joggers-Black-XL",
    "awb": "14900947036",
    "courier": "Xpressbees",
    "date": "2026-08-01",
    "read": 1,
    "dims": [
      37,
      22,
      13
    ],
    "vol": 2.12,
    "source": "upload",
    "video": {
      "name": "evidence-1292-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1292-len.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01293",
    "sku": "NBJUN24MJ05-Men-Joggers-Black-XL",
    "awb": "14900948502",
    "courier": "Bluedart",
    "date": "2026-07-31",
    "read": 1,
    "dims": [
      32,
      29,
      6
    ],
    "vol": 1.11,
    "source": "upload",
    "video": {
      "name": "evidence-1294-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1294-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1294-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-01295",
    "sku": "NBJUN24MJ05-Men-Joggers-Black-XL",
    "awb": "14900949968",
    "courier": "Ekart",
    "date": "2026-06-25",
    "read": 0.83,
    "charged": 1,
    "dims": [
      21,
      15,
      11
    ],
    "vol": 0.69,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01297",
    "sku": "NBJUN24MJ05-Men-Joggers-Black-XL",
    "awb": "14900951434",
    "courier": "Delhivery",
    "date": "2026-05-12",
    "read": 0.82,
    "charged": 1,
    "dims": [
      28,
      29,
      16
    ],
    "vol": 2.6,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01299",
    "sku": "NBJUN24MJ05-Men-Joggers-Black-XL",
    "awb": "14900952900",
    "courier": "Delhivery",
    "date": "2026-07-11",
    "read": 0.79,
    "charged": 1,
    "dims": [
      33,
      27,
      14
    ],
    "vol": 2.49,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01301",
    "sku": "NBJUN24MJ05-Men-Joggers-Black-XL",
    "awb": "14900954366",
    "courier": "Xpressbees",
    "date": "2026-07-20",
    "read": 0.97,
    "charged": 1,
    "dims": [
      25,
      17,
      10
    ],
    "vol": 0.85,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01303",
    "sku": "NBJUN24MJ05-Men-Joggers-Black-XL",
    "awb": "14900955832",
    "courier": "DTDC",
    "date": "2026-07-21",
    "read": 0.91,
    "charged": 1,
    "dims": [
      20,
      24,
      8
    ],
    "vol": 0.77,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01305",
    "sku": "1M-TWTS-C0990-MN4",
    "awb": "14900957298",
    "courier": "Bluedart",
    "date": "2026-05-12",
    "read": 0.66,
    "charged": 0.75,
    "dims": [
      30,
      19,
      5
    ],
    "vol": 0.57,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01307",
    "sku": "1M-TWTS-C0990-MN4",
    "awb": "14900958764",
    "courier": "Blitz",
    "date": "2026-05-03",
    "read": 0.72,
    "charged": 0.75,
    "dims": [
      28,
      15,
      14
    ],
    "vol": 1.18,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01309",
    "sku": "1M-TWTS-C0990-MN4",
    "awb": "14900960230",
    "courier": "Shadowfax",
    "date": "2026-05-22",
    "read": 0.65,
    "charged": 0.75,
    "dims": [
      29,
      20,
      8
    ],
    "vol": 0.93,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01311",
    "sku": "1M-TWTS-C0990-MN4",
    "awb": "14900961696",
    "courier": "Ekart",
    "date": "2026-05-11",
    "read": 0.55,
    "charged": 0.75,
    "dims": [
      27,
      22,
      17
    ],
    "vol": 2.02,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-01313",
    "sku": "1M-LWCS-R0174-NS5",
    "awb": "14900963162",
    "courier": "Delhivery",
    "date": "2026-07-31",
    "read": 1,
    "dims": [
      38,
      23,
      12
    ],
    "vol": 2.1,
    "source": "upload",
    "video": {
      "name": "evidence-1314-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1314-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1314-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1314-hgt.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-01315",
    "sku": "1M-LWCS-R0174-NS5",
    "awb": "14900964628",
    "courier": "Delhivery",
    "date": "2026-06-14",
    "read": 0.63,
    "charged": 0.75,
    "dims": [
      25,
      26,
      13
    ],
    "vol": 1.69,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01317",
    "sku": "1M-LWCS-R0174-NS5",
    "awb": "14900966094",
    "courier": "Shadowfax",
    "date": "2026-05-18",
    "read": 0.96,
    "charged": 1,
    "dims": [
      21,
      28,
      8
    ],
    "vol": 0.94,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01319",
    "sku": "1M-LWCS-R0174-NS5",
    "awb": "14900967560",
    "courier": "Blitz",
    "date": "2026-06-29",
    "read": 0.79,
    "charged": 1,
    "dims": [
      24,
      25,
      11
    ],
    "vol": 1.32,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01321",
    "sku": "1M-LWCS-R0174-NS5",
    "awb": "14900969026",
    "courier": "Bluedart",
    "date": "2026-07-03",
    "read": 0.64,
    "charged": 0.75,
    "dims": [
      23,
      16,
      18
    ],
    "vol": 1.32,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01323",
    "sku": "1M-LWCS-R0174-NS5",
    "awb": "14900970492",
    "courier": "Xpressbees",
    "date": "2026-05-02",
    "read": 0.71,
    "charged": 0.75,
    "dims": [
      38,
      26,
      19
    ],
    "vol": 3.75,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01325",
    "sku": "1M-LWCS-R0174-NS5",
    "awb": "14900971958",
    "courier": "Bluedart",
    "date": "2026-06-06",
    "read": 0.56,
    "charged": 0.75,
    "dims": [
      31,
      27,
      7
    ],
    "vol": 1.17,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01357",
    "sku": "Laugh + Live + Love & Eat Bamboo Oversized T-Shirt Bone White / L (sku - 1M-TWRT-C2581-BWH4)",
    "awb": "14900995414",
    "courier": "Shadowfax",
    "date": "2026-06-08",
    "read": 0.6,
    "charged": 0.75,
    "dims": [
      27,
      22,
      18
    ],
    "vol": 2.14,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01359",
    "sku": "Laugh + Live + Love & Eat Bamboo Oversized T-Shirt Bone White / L (sku - 1M-TWRT-C2581-BWH4)",
    "awb": "14900996880",
    "courier": "Blitz",
    "date": "2026-05-13",
    "read": 0.63,
    "charged": 0.75,
    "dims": [
      20,
      16,
      15
    ],
    "vol": 0.96,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01361",
    "sku": "Laugh + Live + Love & Eat Bamboo Oversized T-Shirt Bone White / L (sku - 1M-TWRT-C2581-BWH4)",
    "awb": "14900998346",
    "courier": "Shadowfax",
    "date": "2026-07-02",
    "read": 0.72,
    "charged": 0.75,
    "dims": [
      23,
      22,
      15
    ],
    "vol": 1.52,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01363",
    "sku": "Laugh + Live + Love & Eat Bamboo Oversized T-Shirt Bone White / L (sku - 1M-TWRT-C2581-BWH4)",
    "awb": "14900999812",
    "courier": "Xpressbees",
    "date": "2026-06-26",
    "read": 0.54,
    "charged": 0.75,
    "dims": [
      32,
      27,
      8
    ],
    "vol": 1.38,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01365",
    "sku": "Laugh + Live + Love & Eat Bamboo Oversized T-Shirt Bone White / L (sku - 1M-TWRT-C2581-BWH4)",
    "awb": "14901001278",
    "courier": "Ekart",
    "date": "2026-05-09",
    "read": 0.56,
    "charged": 0.75,
    "dims": [
      20,
      17,
      16
    ],
    "vol": 1.09,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01367",
    "sku": "Laugh + Live + Love & Eat Bamboo Oversized T-Shirt Bone White / M (sku - 1M-TWRT-C2581-BWH3)",
    "awb": "14901002744",
    "courier": "DTDC",
    "date": "2026-06-28",
    "read": 0.57,
    "charged": 0.75,
    "dims": [
      33,
      21,
      13
    ],
    "vol": 1.8,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01369",
    "sku": "Laugh + Live + Love & Eat Bamboo Oversized T-Shirt Bone White / M (sku - 1M-TWRT-C2581-BWH3)",
    "awb": "14901004210",
    "courier": "Xpressbees",
    "date": "2026-07-21",
    "read": 0.6,
    "charged": 0.75,
    "dims": [
      38,
      20,
      14
    ],
    "vol": 2.13,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-01371",
    "sku": "Laugh + Live + Love & Eat Bamboo Oversized T-Shirt Bone White / XL (sku - 1M-TWRT-C2581-BWH5)",
    "awb": "14901005676",
    "courier": "Delhivery",
    "date": "2026-07-30",
    "read": 0.75,
    "dims": [
      28,
      17,
      6
    ],
    "vol": 0.57,
    "source": "upload",
    "video": {
      "name": "evidence-1372-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1372-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1372-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1372-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-1372-scale.jpg",
        "url": null
      },
      {
        "slot": "label",
        "name": "evidence-1372-label.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01373",
    "sku": "Laugh + Live + Love & Eat Bamboo Oversized T-Shirt Bone White / XL (sku - 1M-TWRT-C2581-BWH5)",
    "awb": "14901007142",
    "courier": "Xpressbees",
    "date": "2026-08-01",
    "read": 0.75,
    "dims": [
      31,
      29,
      9
    ],
    "vol": 1.62,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-1374-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1374-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01375",
    "sku": "Laugh + Live + Love & Eat Bamboo Oversized T-Shirt Bone White / XL (sku - 1M-TWRT-C2581-BWH5)",
    "awb": "14901008608",
    "courier": "DTDC",
    "date": "2026-08-04",
    "read": 0.75,
    "dims": [
      27,
      17,
      15
    ],
    "vol": 1.38,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-1376-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1376-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1376-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-1376-scale.jpg",
        "url": null
      },
      {
        "slot": "label",
        "name": "evidence-1376-label.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01377",
    "sku": "Laugh + Live + Love & Eat Bamboo Oversized T-Shirt Bone White / XL (sku - 1M-TWRT-C2581-BWH5)",
    "awb": "14901010074",
    "courier": "Xpressbees",
    "date": "2026-08-04",
    "read": 0.75,
    "dims": [
      25,
      28,
      19
    ],
    "vol": 2.66,
    "source": "upload",
    "video": {
      "name": "evidence-1378-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1378-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1378-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1378-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-1378-scale.jpg",
        "url": null
      },
      {
        "slot": "label",
        "name": "evidence-1378-label.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-01379",
    "sku": "Laugh + Live + Love & Eat Bamboo Oversized T-Shirt Bone White / XL (sku - 1M-TWRT-C2581-BWH5)",
    "awb": "14901011540",
    "courier": "Bluedart",
    "date": "2026-06-18",
    "read": 0.57,
    "charged": 0.75,
    "dims": [
      21,
      23,
      16
    ],
    "vol": 1.55,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01381",
    "sku": "Laugh + Live + Love & Eat Bamboo Oversized T-Shirt Bone White / XL (sku - 1M-TWRT-C2581-BWH5)",
    "awb": "14901013006",
    "courier": "Xpressbees",
    "date": "2026-05-28",
    "read": 0.62,
    "charged": 0.75,
    "dims": [
      29,
      21,
      15
    ],
    "vol": 1.83,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01383",
    "sku": "Laugh + Live + Love & Eat Bamboo Oversized T-Shirt Bone White / XL (sku - 1M-TWRT-C2581-BWH5)",
    "awb": "14901014472",
    "courier": "DTDC",
    "date": "2026-06-04",
    "read": 0.73,
    "charged": 0.75,
    "dims": [
      34,
      18,
      10
    ],
    "vol": 1.22,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01385",
    "sku": "Laugh + Live + Love & Eat Bamboo Oversized T-Shirt Bone White / XL (sku - 1M-TWRT-C2581-BWH5)",
    "awb": "14901015938",
    "courier": "Delhivery",
    "date": "2026-05-10",
    "read": 0.59,
    "charged": 0.75,
    "dims": [
      33,
      18,
      17
    ],
    "vol": 2.02,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-01387",
    "sku": "Great Highway Classic Fit T-Shirt Wine Red / L (sku - 1M-TWTS-C2324-WR4) + Ocean Beach",
    "awb": "14901017404",
    "courier": "Ekart",
    "date": "2026-08-04",
    "read": 0.75,
    "dims": [
      23,
      23,
      17
    ],
    "vol": 1.8,
    "source": "upload",
    "video": {
      "name": "evidence-1388-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1388-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1388-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1388-hgt.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01389",
    "sku": "Great Highway Classic Fit T-Shirt Wine Red / L (sku - 1M-TWTS-C2324-WR4) + Ocean Beach",
    "awb": "14901018870",
    "courier": "Shadowfax",
    "date": "2026-08-04",
    "read": 0.75,
    "dims": [
      31,
      23,
      14
    ],
    "vol": 2,
    "source": "upload",
    "video": {
      "name": "evidence-1390-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1390-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1390-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1390-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-1390-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-01391",
    "sku": "Great Highway Classic Fit T-Shirt Wine Red / L (sku - 1M-TWTS-C2324-WR4) + Ocean Beach",
    "awb": "14901020336",
    "courier": "Blitz",
    "date": "2026-06-30",
    "read": 0.58,
    "charged": 0.75,
    "dims": [
      37,
      23,
      14
    ],
    "vol": 2.38,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01393",
    "sku": "Great Highway Classic Fit T-Shirt Wine Red / L (sku - 1M-TWTS-C2324-WR4) + Ocean Beach",
    "awb": "14901021802",
    "courier": "Xpressbees",
    "date": "2026-07-10",
    "read": 0.64,
    "charged": 0.75,
    "dims": [
      34,
      20,
      10
    ],
    "vol": 1.36,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01395",
    "sku": "Great Highway Classic Fit T-Shirt Wine Red / L (sku - 1M-TWTS-C2324-WR4) + Ocean Beach",
    "awb": "14901023268",
    "courier": "Shadowfax",
    "date": "2026-06-20",
    "read": 0.55,
    "charged": 0.75,
    "dims": [
      23,
      20,
      13
    ],
    "vol": 1.2,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01397",
    "sku": "Laugh + Live + Love & Eat Bamboo Oversized T-Shirt Bone White / XXL (sku - 1M-TWRT-C2581-BWH6)",
    "awb": "14901024734",
    "courier": "Blitz",
    "date": "2026-06-25",
    "read": 0.55,
    "charged": 0.75,
    "dims": [
      30,
      16,
      18
    ],
    "vol": 1.73,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01399",
    "sku": "Laugh + Live + Love & Eat Bamboo Oversized T-Shirt Bone White / XXL (sku - 1M-TWRT-C2581-BWH6)",
    "awb": "14901026200",
    "courier": "Bluedart",
    "date": "2026-06-01",
    "read": 0.58,
    "charged": 0.75,
    "dims": [
      28,
      22,
      14
    ],
    "vol": 1.72,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01401",
    "sku": "Laugh + Live + Love & Eat Bamboo Oversized T-Shirt Bone White / XXL (sku - 1M-TWRT-C2581-BWH6)",
    "awb": "14901027666",
    "courier": "DTDC",
    "date": "2026-06-23",
    "read": 0.6,
    "charged": 0.75,
    "dims": [
      39,
      16,
      6
    ],
    "vol": 0.75,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01403",
    "sku": "Laugh + Live + Love & Eat Bamboo Oversized T-Shirt Bone White / XXL (sku - 1M-TWRT-C2581-BWH6)",
    "awb": "14901029132",
    "courier": "Ekart",
    "date": "2026-05-15",
    "read": 0.64,
    "charged": 0.75,
    "dims": [
      20,
      15,
      19
    ],
    "vol": 1.14,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01405",
    "sku": "Laugh + Live + Love & Eat Bamboo Oversized T-Shirt Bone White / XXL (sku - 1M-TWRT-C2581-BWH6)",
    "awb": "14901030598",
    "courier": "Delhivery",
    "date": "2026-06-23",
    "read": 0.56,
    "charged": 0.75,
    "dims": [
      36,
      18,
      11
    ],
    "vol": 1.43,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01407",
    "sku": "Great Highway Classic Fit T-Shirt Wine Red / XXL (sku - 1M-TWTS-C2324-WR6) + Ocean Beach",
    "awb": "14901032064",
    "courier": "Shadowfax",
    "date": "2026-05-20",
    "read": 0.54,
    "charged": 0.75,
    "dims": [
      38,
      23,
      10
    ],
    "vol": 1.75,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01409",
    "sku": "Great Highway Classic Fit T-Shirt Wine Red / XXL (sku - 1M-TWTS-C2324-WR6) + Ocean Beach",
    "awb": "14901033530",
    "courier": "Xpressbees",
    "date": "2026-05-07",
    "read": 0.67,
    "charged": 0.75,
    "dims": [
      36,
      26,
      14
    ],
    "vol": 2.62,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01411",
    "sku": "Great Highway Classic Fit T-Shirt Wine Red / XXL (sku - 1M-TWTS-C2324-WR6) + Ocean Beach",
    "awb": "14901034996",
    "courier": "DTDC",
    "date": "2026-05-05",
    "read": 0.57,
    "charged": 0.75,
    "dims": [
      34,
      16,
      8
    ],
    "vol": 0.87,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01413",
    "sku": "Great Highway Classic Fit T-Shirt Wine Red / XXL (sku - 1M-TWTS-C2324-WR6) + Ocean Beach",
    "awb": "14901036462",
    "courier": "DTDC",
    "date": "2026-06-13",
    "read": 0.61,
    "charged": 0.75,
    "dims": [
      20,
      25,
      10
    ],
    "vol": 1,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01415",
    "sku": "Great Highway Classic Fit T-Shirt Wine Red / XXL (sku - 1M-TWTS-C2324-WR6) + Ocean Beach",
    "awb": "14901037928",
    "courier": "Delhivery",
    "date": "2026-06-18",
    "read": 0.65,
    "charged": 0.75,
    "dims": [
      29,
      18,
      11
    ],
    "vol": 1.15,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-01417",
    "sku": "Nobero Jogger V2-Men Joggers-Black-M + Nobero Jogger V2-Men-Joggers-Arctic Wolf-M",
    "awb": "14901039394",
    "courier": "Bluedart",
    "date": "2026-08-04",
    "read": 2,
    "dims": [
      34,
      21,
      10
    ],
    "vol": 1.43,
    "source": "upload",
    "video": {
      "name": "evidence-1418-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1418-len.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01419",
    "sku": "Nobero Jogger V2-Men Joggers-Black-M + Nobero Jogger V2-Men-Joggers-Arctic Wolf-M",
    "awb": "14901040860",
    "courier": "Blitz",
    "date": "2026-08-05",
    "read": 1.5,
    "dims": [
      39,
      26,
      9
    ],
    "vol": 1.83,
    "source": "upload",
    "video": {
      "name": "evidence-1420-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1420-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1420-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1420-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-1420-scale.jpg",
        "url": null
      },
      {
        "slot": "label",
        "name": "evidence-1420-label.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01421",
    "sku": "Nobero Jogger V2-Men Joggers-Black-M + Nobero Jogger V2-Men-Joggers-Arctic Wolf-M",
    "awb": "14901042326",
    "courier": "Delhivery",
    "date": "2026-08-05",
    "read": 1.5,
    "dims": [
      35,
      18,
      5
    ],
    "vol": 0.63,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-1422-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1422-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01423",
    "sku": "Nobero Jogger V2-Men Joggers-Black-M + Nobero Jogger V2-Men-Joggers-Arctic Wolf-M",
    "awb": "14901043792",
    "courier": "DTDC",
    "date": "2026-08-01",
    "read": 1.5,
    "dims": [
      28,
      17,
      9
    ],
    "vol": 0.86,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-1424-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1424-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1424-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-1424-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01425",
    "sku": "Nobero Jogger V2-Men Joggers-Black-M + Nobero Jogger V2-Men-Joggers-Arctic Wolf-M",
    "awb": "14901045258",
    "courier": "Blitz",
    "date": "2026-07-31",
    "read": 2,
    "dims": [
      23,
      16,
      8
    ],
    "vol": 0.59,
    "source": "upload",
    "video": {
      "name": "evidence-1426-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1426-len.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-01427",
    "sku": "Nobero Jogger V2-Men Joggers-Black-M + Nobero Jogger V2-Men-Joggers-Arctic Wolf-M",
    "awb": "14901046724",
    "courier": "Xpressbees",
    "date": "2026-06-14",
    "read": 1.84,
    "charged": 2,
    "dims": [
      35,
      19,
      9
    ],
    "vol": 1.2,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01429",
    "sku": "Nobero Jogger V2-Men Joggers-Black-M + Nobero Jogger V2-Men-Joggers-Arctic Wolf-M",
    "awb": "14901048190",
    "courier": "Shadowfax",
    "date": "2026-06-07",
    "read": 1.81,
    "charged": 2,
    "dims": [
      38,
      28,
      18
    ],
    "vol": 3.83,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01431",
    "sku": "Nobero Jogger V2-Men Joggers-Black-M + Nobero Jogger V2-Men-Joggers-Arctic Wolf-M",
    "awb": "14901049656",
    "courier": "DTDC",
    "date": "2026-05-29",
    "read": 1.97,
    "charged": 2,
    "dims": [
      21,
      17,
      15
    ],
    "vol": 1.07,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-01433",
    "sku": "NBJUN24MJ05-Men-Joggers-Black-XXXL + NBJUN24MJ05-Men-Joggers-Navy Blue-XXXL",
    "awb": "14901051122",
    "courier": "Shadowfax",
    "date": "2026-07-31",
    "read": 0.75,
    "dims": [
      39,
      21,
      7
    ],
    "vol": 1.15,
    "source": "upload",
    "video": {
      "name": "evidence-1434-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1434-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1434-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01435",
    "sku": "NBJUN24MJ05-Men-Joggers-Black-XXXL + NBJUN24MJ05-Men-Joggers-Navy Blue-XXXL",
    "awb": "14901052588",
    "courier": "Xpressbees",
    "date": "2026-08-04",
    "read": 2.5,
    "dims": [
      32,
      20,
      5
    ],
    "vol": 0.64,
    "source": "upload",
    "video": {
      "name": "evidence-1436-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1436-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1436-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01437",
    "sku": "NBJUN24MJ05-Men-Joggers-Black-XXXL + NBJUN24MJ05-Men-Joggers-Navy Blue-XXXL",
    "awb": "14901054054",
    "courier": "Shadowfax",
    "date": "2026-07-31",
    "read": 2.5,
    "dims": [
      21,
      24,
      7
    ],
    "vol": 0.71,
    "source": "upload",
    "video": {
      "name": "evidence-1438-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1438-len.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01439",
    "sku": "NBJUN24MJ05-Men-Joggers-Black-XXXL + NBJUN24MJ05-Men-Joggers-Navy Blue-XXXL",
    "awb": "14901055520",
    "courier": "Delhivery",
    "date": "2026-08-01",
    "read": 0.75,
    "dims": [
      23,
      20,
      18
    ],
    "vol": 1.66,
    "source": "upload",
    "video": {
      "name": "evidence-1440-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1440-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1440-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01441",
    "sku": "NBJUN24MJ05-Men-Joggers-Black-XXXL + NBJUN24MJ05-Men-Joggers-Navy Blue-XXXL",
    "awb": "14901056986",
    "courier": "Delhivery",
    "date": "2026-08-04",
    "read": 2.5,
    "dims": [
      36,
      26,
      19
    ],
    "vol": 3.56,
    "source": "upload",
    "video": {
      "name": "evidence-1442-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1442-len.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-01443",
    "sku": "NBJUN24MJ05-Men-Joggers-Black-XXXL + NBJUN24MJ05-Men-Joggers-Navy Blue-XXXL",
    "awb": "14901058452",
    "courier": "Ekart",
    "date": "2026-05-30",
    "read": 2.3,
    "charged": 2.5,
    "dims": [
      35,
      25,
      12
    ],
    "vol": 2.1,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01445",
    "sku": "NBJUN24MJ05-Men-Joggers-Black-XXXL + NBJUN24MJ05-Men-Joggers-Navy Blue-XXXL",
    "awb": "14901059918",
    "courier": "Ekart",
    "date": "2026-07-08",
    "read": 2.41,
    "charged": 2.5,
    "dims": [
      32,
      15,
      14
    ],
    "vol": 1.34,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01447",
    "sku": "NBJUN24MJ05-Men-Joggers-Black-XXXL + NBJUN24MJ05-Men-Joggers-Navy Blue-XXXL",
    "awb": "14901061384",
    "courier": "DTDC",
    "date": "2026-07-01",
    "read": 2.37,
    "charged": 2.5,
    "dims": [
      35,
      16,
      9
    ],
    "vol": 1.01,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01449",
    "sku": "NBJUN24MJ05-Men-Joggers-Black-XXXL + NBJUN24MJ05-Men-Joggers-Navy Blue-XXXL",
    "awb": "14901062850",
    "courier": "Shadowfax",
    "date": "2026-07-13",
    "read": 2.41,
    "charged": 2.5,
    "dims": [
      27,
      20,
      6
    ],
    "vol": 0.65,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01451",
    "sku": "NBJUN24MJ05-Men-Joggers-Black-XXXL + NBJUN24MJ05-Men-Joggers-Navy Blue-XXXL",
    "awb": "14901064316",
    "courier": "Bluedart",
    "date": "2026-07-23",
    "read": 0.71,
    "charged": 0.75,
    "dims": [
      39,
      15,
      14
    ],
    "vol": 1.64,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-01453",
    "sku": "Laugh + Live + Love & Eat Bamboo Oversized T-Shirt Bone White / XXXL (sku - 1M-TWRT-C2581-BWH7)",
    "awb": "14901065782",
    "courier": "Blitz",
    "date": "2026-08-06",
    "read": 0.75,
    "dims": [
      33,
      22,
      14
    ],
    "vol": 2.03,
    "source": "upload",
    "video": {
      "name": "evidence-1454-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1454-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1454-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1454-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-1454-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01455",
    "sku": "Laugh + Live + Love & Eat Bamboo Oversized T-Shirt Bone White / XXXL (sku - 1M-TWRT-C2581-BWH7)",
    "awb": "14901067248",
    "courier": "Bluedart",
    "date": "2026-08-06",
    "read": 0.75,
    "dims": [
      21,
      24,
      10
    ],
    "vol": 1.01,
    "source": "upload",
    "video": {
      "name": "evidence-1456-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1456-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1456-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1456-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-1456-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01457",
    "sku": "Laugh + Live + Love & Eat Bamboo Oversized T-Shirt Bone White / XXXL (sku - 1M-TWRT-C2581-BWH7)",
    "awb": "14901068714",
    "courier": "Bluedart",
    "date": "2026-08-06",
    "read": 0.75,
    "dims": [
      37,
      23,
      18
    ],
    "vol": 3.06,
    "source": "upload",
    "video": {
      "name": "evidence-1458-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1458-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1458-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1458-hgt.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01459",
    "sku": "Laugh + Live + Love & Eat Bamboo Oversized T-Shirt Bone White / XXXL (sku - 1M-TWRT-C2581-BWH7)",
    "awb": "14901070180",
    "courier": "Bluedart",
    "date": "2026-08-01",
    "read": 0.75,
    "dims": [
      30,
      23,
      8
    ],
    "vol": 1.1,
    "source": "upload",
    "video": {
      "name": "evidence-1460-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1460-len.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01461",
    "sku": "Laugh + Live + Love & Eat Bamboo Oversized T-Shirt Bone White / XXXL (sku - 1M-TWRT-C2581-BWH7)",
    "awb": "14901071646",
    "courier": "Bluedart",
    "date": "2026-08-06",
    "read": 0.75,
    "dims": [
      28,
      25,
      9
    ],
    "vol": 1.26,
    "source": "upload",
    "video": {
      "name": "evidence-1462-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1462-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1462-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1462-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-1462-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01463",
    "sku": "Laugh + Live + Love & Eat Bamboo Oversized T-Shirt Bone White / XXXL (sku - 1M-TWRT-C2581-BWH7)",
    "awb": "14901073112",
    "courier": "Shadowfax",
    "date": "2026-08-05",
    "read": 0.75,
    "dims": [
      39,
      19,
      8
    ],
    "vol": 1.19,
    "source": "upload",
    "video": {
      "name": "evidence-1464-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1464-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1464-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1464-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-1464-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-01465",
    "sku": "Laugh + Live + Love & Eat Bamboo Oversized T-Shirt Bone White / XXXL (sku - 1M-TWRT-C2581-BWH7)",
    "awb": "14901074578",
    "courier": "Delhivery",
    "date": "2026-06-03",
    "read": 0.56,
    "charged": 0.75,
    "dims": [
      29,
      28,
      11
    ],
    "vol": 1.79,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01467",
    "sku": "Laugh + Live + Love & Eat Bamboo Oversized T-Shirt Bone White / XXXL (sku - 1M-TWRT-C2581-BWH7)",
    "awb": "14901076044",
    "courier": "Shadowfax",
    "date": "2026-06-06",
    "read": 0.73,
    "charged": 0.75,
    "dims": [
      32,
      19,
      10
    ],
    "vol": 1.22,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01469",
    "sku": "Laugh + Live + Love & Eat Bamboo Oversized T-Shirt Bone White / XXXL (sku - 1M-TWRT-C2581-BWH7)",
    "awb": "14901077510",
    "courier": "Xpressbees",
    "date": "2026-07-02",
    "read": 0.59,
    "charged": 0.75,
    "dims": [
      32,
      24,
      10
    ],
    "vol": 1.54,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01471",
    "sku": "Laugh + Live + Love & Eat Bamboo Oversized T-Shirt Bone White / XXXL (sku - 1M-TWRT-C2581-BWH7)",
    "awb": "14901078976",
    "courier": "Ekart",
    "date": "2026-07-04",
    "read": 0.73,
    "charged": 0.75,
    "dims": [
      28,
      19,
      16
    ],
    "vol": 1.7,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01473",
    "sku": "Laugh + Live + Love & Eat Bamboo Oversized T-Shirt Bone White / S (sku - 1M-TWRT-C2581-BWH2)",
    "awb": "14901080442",
    "courier": "Delhivery",
    "date": "2026-07-12",
    "read": 0.61,
    "charged": 0.75,
    "dims": [
      29,
      22,
      8
    ],
    "vol": 1.02,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01475",
    "sku": "Laugh + Live + Love & Eat Bamboo Oversized T-Shirt Bone White / S (sku - 1M-TWRT-C2581-BWH2)",
    "awb": "14901081908",
    "courier": "Shadowfax",
    "date": "2026-06-20",
    "read": 0.65,
    "charged": 0.75,
    "dims": [
      21,
      23,
      6
    ],
    "vol": 0.58,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01477",
    "sku": "Laugh + Live + Love & Eat Bamboo Oversized T-Shirt Bone White / S (sku - 1M-TWRT-C2581-BWH2)",
    "awb": "14901083374",
    "courier": "Blitz",
    "date": "2026-06-25",
    "read": 0.56,
    "charged": 0.75,
    "dims": [
      24,
      19,
      15
    ],
    "vol": 1.37,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01479",
    "sku": "Laugh + Live + Love & Eat Bamboo Oversized T-Shirt Bone White / S (sku - 1M-TWRT-C2581-BWH2)",
    "awb": "14901084840",
    "courier": "DTDC",
    "date": "2026-07-13",
    "read": 0.7,
    "charged": 0.75,
    "dims": [
      38,
      21,
      13
    ],
    "vol": 2.07,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01481",
    "sku": "Laugh + Live + Love & Eat Bamboo Oversized T-Shirt Bone White / S (sku - 1M-TWRT-C2581-BWH2)",
    "awb": "14901086306",
    "courier": "Bluedart",
    "date": "2026-05-11",
    "read": 0.58,
    "charged": 0.75,
    "dims": [
      27,
      17,
      5
    ],
    "vol": 0.46,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01483",
    "sku": "NBJUN24MJ05-Men-Joggers-Black-L + NBJUN24MJ05-Men-Joggers-Eiffel Tower-L",
    "awb": "14901087772",
    "courier": "Bluedart",
    "date": "2026-05-17",
    "read": 0.86,
    "charged": 1,
    "dims": [
      30,
      23,
      9
    ],
    "vol": 1.24,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01485",
    "sku": "NBJUN24MJ05-Men-Joggers-Black-L + NBJUN24MJ05-Men-Joggers-Eiffel Tower-L",
    "awb": "14901089238",
    "courier": "DTDC",
    "date": "2026-05-11",
    "read": 0.61,
    "charged": 0.75,
    "dims": [
      24,
      22,
      10
    ],
    "vol": 1.06,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-01487",
    "sku": "Nobero Shorts V2-Men Shorts-Black-L + Nobero Shorts V2-Men Shorts-Olive Green-L",
    "awb": "14901090704",
    "courier": "Blitz",
    "date": "2026-08-05",
    "read": 1,
    "dims": [
      33,
      18,
      16
    ],
    "vol": 1.9,
    "source": "upload",
    "video": {
      "name": "evidence-1488-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1488-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1488-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01489",
    "sku": "Nobero Shorts V2-Men Shorts-Black-L + Nobero Shorts V2-Men Shorts-Olive Green-L",
    "awb": "14901092170",
    "courier": "Shadowfax",
    "date": "2026-08-02",
    "read": 1,
    "dims": [
      23,
      25,
      15
    ],
    "vol": 1.73,
    "source": "upload",
    "video": {
      "name": "evidence-1490-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1490-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1490-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1490-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-1490-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01491",
    "sku": "Nobero Shorts V2-Men Shorts-Black-L + Nobero Shorts V2-Men Shorts-Olive Green-L",
    "awb": "14901093636",
    "courier": "Shadowfax",
    "date": "2026-07-31",
    "read": 1,
    "dims": [
      28,
      19,
      6
    ],
    "vol": 0.64,
    "source": "upload",
    "video": {
      "name": "evidence-1492-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1492-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1492-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-01493",
    "sku": "Nobero Shorts V2-Men Shorts-Black-L + Nobero Shorts V2-Men Shorts-Olive Green-L",
    "awb": "14901095102",
    "courier": "Delhivery",
    "date": "2026-05-25",
    "read": 0.84,
    "charged": 1,
    "dims": [
      37,
      25,
      7
    ],
    "vol": 1.3,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01495",
    "sku": "Nobero Shorts V2-Men Shorts-Black-L + Nobero Shorts V2-Men Shorts-Olive Green-L",
    "awb": "14901096568",
    "courier": "DTDC",
    "date": "2026-06-13",
    "read": 0.91,
    "charged": 1,
    "dims": [
      33,
      20,
      17
    ],
    "vol": 2.24,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-01497",
    "sku": "Classic Polo-Men-Polos-Burgundy-XL + Classic Polo-Men-Polos-Dusty Rose-XL",
    "awb": "14901098034",
    "courier": "Shadowfax",
    "date": "2026-08-04",
    "read": 1,
    "dims": [
      32,
      21,
      10
    ],
    "vol": 1.34,
    "source": "upload",
    "video": {
      "name": "evidence-1498-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1498-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1498-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1498-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-1498-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01499",
    "sku": "Classic Polo-Men-Polos-Burgundy-XL + Classic Polo-Men-Polos-Dusty Rose-XL",
    "awb": "14901099500",
    "courier": "Xpressbees",
    "date": "2026-07-30",
    "read": 1,
    "dims": [
      36,
      22,
      10
    ],
    "vol": 1.58,
    "source": "upload",
    "video": {
      "name": "evidence-1500-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1500-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1500-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-01501",
    "sku": "Classic Polo-Men-Polos-Burgundy-XL + Classic Polo-Men-Polos-Dusty Rose-XL",
    "awb": "14901100966",
    "courier": "Blitz",
    "date": "2026-06-17",
    "read": 0.87,
    "charged": 1,
    "dims": [
      32,
      20,
      17
    ],
    "vol": 2.18,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01503",
    "sku": "Classic Polo-Men-Polos-Burgundy-XL + Classic Polo-Men-Polos-Dusty Rose-XL",
    "awb": "14901102432",
    "courier": "Delhivery",
    "date": "2026-05-15",
    "read": 0.7,
    "charged": 0.75,
    "dims": [
      32,
      19,
      17
    ],
    "vol": 2.07,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01505",
    "sku": "NBAW24MJ14-Men-Joggers-Eiffel tower-L + NBAW24MJ14-Men-Joggers-Olive Green-L",
    "awb": "14901103898",
    "courier": "Blitz",
    "date": "2026-07-15",
    "read": 1.46,
    "charged": 1.5,
    "dims": [
      36,
      29,
      6
    ],
    "vol": 1.25,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01507",
    "sku": "NBAW24MJ14-Men-Joggers-Eiffel tower-L + NBAW24MJ14-Men-Joggers-Olive Green-L",
    "awb": "14901105364",
    "courier": "Delhivery",
    "date": "2026-07-25",
    "read": 1.35,
    "charged": 1.5,
    "dims": [
      38,
      29,
      13
    ],
    "vol": 2.87,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01509",
    "sku": "NBAW24MJ14-Men-Joggers-Eiffel tower-L + NBAW24MJ14-Men-Joggers-Olive Green-L",
    "awb": "14901106830",
    "courier": "Shadowfax",
    "date": "2026-07-15",
    "read": 1.37,
    "charged": 1.5,
    "dims": [
      33,
      19,
      18
    ],
    "vol": 2.26,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01511",
    "sku": "NBAW24MJ14-Men-Joggers-Eiffel tower-L + NBAW24MJ14-Men-Joggers-Olive Green-L",
    "awb": "14901108296",
    "courier": "DTDC",
    "date": "2026-06-26",
    "read": 1.3,
    "charged": 1.5,
    "dims": [
      25,
      29,
      12
    ],
    "vol": 1.74,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01513",
    "sku": "NBAW24MJ14-Men-Joggers-Eiffel tower-L + NBAW24MJ14-Men-Joggers-Olive Green-L",
    "awb": "14901109762",
    "courier": "Ekart",
    "date": "2026-05-17",
    "read": 1.32,
    "charged": 1.5,
    "dims": [
      37,
      27,
      10
    ],
    "vol": 2,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01515",
    "sku": "NBAW24MJ14-Men-Joggers-Eiffel tower-L + NBAW24MJ14-Men-Joggers-Olive Green-L",
    "awb": "14901111228",
    "courier": "Shadowfax",
    "date": "2026-05-05",
    "read": 1.4,
    "charged": 1.5,
    "dims": [
      30,
      25,
      17
    ],
    "vol": 2.55,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-01517",
    "sku": "Nobero Jogger V2-Men Joggers-Grey Melange-XL + Nobero Jogger V2-Men-Joggers-Arctic Wolf-XL",
    "awb": "14901112694",
    "courier": "DTDC",
    "date": "2026-08-01",
    "read": 0.75,
    "dims": [
      32,
      15,
      19
    ],
    "vol": 1.82,
    "source": "upload",
    "video": {
      "name": "evidence-1518-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1518-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1518-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01519",
    "sku": "Nobero Jogger V2-Men Joggers-Grey Melange-XL + Nobero Jogger V2-Men-Joggers-Arctic Wolf-XL",
    "awb": "14901114160",
    "courier": "Xpressbees",
    "date": "2026-08-05",
    "read": 0.75,
    "dims": [
      25,
      28,
      15
    ],
    "vol": 2.1,
    "source": "upload",
    "video": {
      "name": "evidence-1520-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1520-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1520-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1520-hgt.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01521",
    "sku": "Nobero Jogger V2-Men Joggers-Grey Melange-XL + Nobero Jogger V2-Men-Joggers-Arctic Wolf-XL",
    "awb": "14901115626",
    "courier": "Shadowfax",
    "date": "2026-07-30",
    "read": 0.75,
    "dims": [
      24,
      25,
      19
    ],
    "vol": 2.28,
    "source": "upload",
    "video": {
      "name": "evidence-1522-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1522-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1522-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1522-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-1522-scale.jpg",
        "url": null
      },
      {
        "slot": "label",
        "name": "evidence-1522-label.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01523",
    "sku": "Nobero Jogger V2-Men Joggers-Grey Melange-XL + Nobero Jogger V2-Men-Joggers-Arctic Wolf-XL",
    "awb": "14901117092",
    "courier": "Shadowfax",
    "date": "2026-08-05",
    "read": 0.75,
    "dims": [
      38,
      28,
      10
    ],
    "vol": 2.13,
    "source": "upload",
    "video": {
      "name": "evidence-1524-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1524-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1524-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1524-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-1524-scale.jpg",
        "url": null
      },
      {
        "slot": "label",
        "name": "evidence-1524-label.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01525",
    "sku": "Nobero Jogger V2-Men Joggers-Grey Melange-XL + Nobero Jogger V2-Men-Joggers-Arctic Wolf-XL",
    "awb": "14901118558",
    "courier": "Blitz",
    "date": "2026-08-06",
    "read": 0.75,
    "dims": [
      39,
      26,
      15
    ],
    "vol": 3.04,
    "source": "upload",
    "video": {
      "name": "evidence-1526-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1526-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1526-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1526-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-1526-scale.jpg",
        "url": null
      },
      {
        "slot": "label",
        "name": "evidence-1526-label.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01527",
    "sku": "Nobero Jogger V2-Men Joggers-Grey Melange-XL + Nobero Jogger V2-Men-Joggers-Arctic Wolf-XL",
    "awb": "14901120024",
    "courier": "Ekart",
    "date": "2026-08-06",
    "read": 0.75,
    "dims": [
      24,
      21,
      15
    ],
    "vol": 1.51,
    "source": "upload",
    "video": {
      "name": "evidence-1528-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1528-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1528-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-01529",
    "sku": "Nobero Jogger V2-Men Joggers-Grey Melange-XL + Nobero Jogger V2-Men-Joggers-Arctic Wolf-XL",
    "awb": "14901121490",
    "courier": "Bluedart",
    "date": "2026-07-17",
    "read": 0.57,
    "charged": 0.75,
    "dims": [
      39,
      22,
      16
    ],
    "vol": 2.75,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01531",
    "sku": "Nobero Jogger V2-Men Joggers-Grey Melange-XL + Nobero Jogger V2-Men-Joggers-Arctic Wolf-XL",
    "awb": "14901122956",
    "courier": "Blitz",
    "date": "2026-07-14",
    "read": 0.54,
    "charged": 0.75,
    "dims": [
      39,
      18,
      10
    ],
    "vol": 1.4,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01533",
    "sku": "Nobero Jogger V2-Men Joggers-Grey Melange-XL + Nobero Jogger V2-Men-Joggers-Arctic Wolf-XL",
    "awb": "14901124422",
    "courier": "Xpressbees",
    "date": "2026-05-21",
    "read": 1.95,
    "charged": 2,
    "dims": [
      32,
      18,
      18
    ],
    "vol": 2.07,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01535",
    "sku": "Nobero Jogger V2-Men Joggers-Grey Melange-XL + Nobero Jogger V2-Men-Joggers-Arctic Wolf-XL",
    "awb": "14901125888",
    "courier": "Bluedart",
    "date": "2026-05-02",
    "read": 0.7,
    "charged": 0.75,
    "dims": [
      24,
      26,
      19
    ],
    "vol": 2.37,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01537",
    "sku": "NBJUN24MJ05-Men-Joggers-Eiffel Tower-XXL + NBJUN24MJ05-Men-Joggers-Twilight Mauve-XXL",
    "awb": "14901127354",
    "courier": "Xpressbees",
    "date": "2026-05-24",
    "read": 0.84,
    "charged": 1,
    "dims": [
      20,
      21,
      13
    ],
    "vol": 1.09,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01539",
    "sku": "NBJUN24MJ05-Men-Joggers-Eiffel Tower-XXL + NBJUN24MJ05-Men-Joggers-Twilight Mauve-XXL",
    "awb": "14901128820",
    "courier": "Shadowfax",
    "date": "2026-07-12",
    "read": 0.91,
    "charged": 1,
    "dims": [
      31,
      17,
      5
    ],
    "vol": 0.53,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01541",
    "sku": "NBJUN24MJ05-Men-Joggers-Eiffel Tower-XXL + NBJUN24MJ05-Men-Joggers-Twilight Mauve-XXL",
    "awb": "14901130286",
    "courier": "Xpressbees",
    "date": "2026-05-31",
    "read": 0.8,
    "charged": 1,
    "dims": [
      22,
      26,
      18
    ],
    "vol": 2.06,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01543",
    "sku": "NBJUN24MJ05-Men-Joggers-Eiffel Tower-XXL + NBJUN24MJ05-Men-Joggers-Twilight Mauve-XXL",
    "awb": "14901131752",
    "courier": "Delhivery",
    "date": "2026-05-29",
    "read": 0.87,
    "charged": 1,
    "dims": [
      34,
      15,
      18
    ],
    "vol": 1.84,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01545",
    "sku": "NBJUN24MJ05-Men-Joggers-Eiffel Tower-XXL + NBJUN24MJ05-Men-Joggers-Twilight Mauve-XXL",
    "awb": "14901133218",
    "courier": "Shadowfax",
    "date": "2026-06-05",
    "read": 0.86,
    "charged": 1,
    "dims": [
      26,
      27,
      13
    ],
    "vol": 1.83,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-01547",
    "sku": "Classic Straight Fit Joggers Eiffel Tower / M (sku - 1M-BWJG-R0149-ET3) + Classic Straight Fit Joggers Olive G",
    "awb": "14901134684",
    "courier": "Shadowfax",
    "date": "2026-07-30",
    "read": 1.5,
    "dims": [
      27,
      27,
      6
    ],
    "vol": 0.87,
    "source": "upload",
    "video": {
      "name": "evidence-1548-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1548-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1548-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-01549",
    "sku": "Classic Straight Fit Joggers Eiffel Tower / M (sku - 1M-BWJG-R0149-ET3) + Classic Straight Fit Joggers Olive G",
    "awb": "14901136150",
    "courier": "Shadowfax",
    "date": "2026-06-03",
    "read": 1.41,
    "charged": 1.5,
    "dims": [
      21,
      15,
      14
    ],
    "vol": 0.88,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01551",
    "sku": "Classic Straight Fit Joggers Eiffel Tower / M (sku - 1M-BWJG-R0149-ET3) + Classic Straight Fit Joggers Olive G",
    "awb": "14901137616",
    "courier": "Delhivery",
    "date": "2026-04-30",
    "read": 1.43,
    "charged": 1.5,
    "dims": [
      21,
      27,
      8
    ],
    "vol": 0.91,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01553",
    "sku": "Classic Polo-Men-Polos-Burgundy-L + Classic Polo-Men-Polos-White-L",
    "awb": "14901139082",
    "courier": "Xpressbees",
    "date": "2026-05-27",
    "read": 0.59,
    "charged": 0.75,
    "dims": [
      28,
      29,
      15
    ],
    "vol": 2.44,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01555",
    "sku": "Classic Polo-Men-Polos-Burgundy-L + Classic Polo-Men-Polos-White-L",
    "awb": "14901140548",
    "courier": "Xpressbees",
    "date": "2026-05-03",
    "read": 0.59,
    "charged": 0.75,
    "dims": [
      26,
      16,
      6
    ],
    "vol": 0.5,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01557",
    "sku": "Classic Polo-Men-Polos-Burgundy-L + Classic Polo-Men-Polos-White-L",
    "awb": "14901142014",
    "courier": "Delhivery",
    "date": "2026-05-26",
    "read": 0.96,
    "charged": 1,
    "dims": [
      39,
      21,
      18
    ],
    "vol": 2.95,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-01559",
    "sku": "NBJUN24MJ05-Men-Joggers-Eiffel Tower-XXXL + NBJUN24MJ05-Men-Joggers-Navy Blue-XXXL",
    "awb": "14901143480",
    "courier": "Ekart",
    "date": "2026-08-06",
    "read": 0.75,
    "dims": [
      23,
      27,
      17
    ],
    "vol": 2.11,
    "source": "upload",
    "video": {
      "name": "evidence-1560-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1560-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1560-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01561",
    "sku": "NBJUN24MJ05-Men-Joggers-Eiffel Tower-XXXL + NBJUN24MJ05-Men-Joggers-Navy Blue-XXXL",
    "awb": "14901144946",
    "courier": "Xpressbees",
    "date": "2026-07-30",
    "read": 3,
    "dims": [
      20,
      29,
      8
    ],
    "vol": 0.93,
    "source": "upload",
    "video": {
      "name": "evidence-1562-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1562-len.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01563",
    "sku": "NBJUN24MJ05-Men-Joggers-Eiffel Tower-XXXL + NBJUN24MJ05-Men-Joggers-Navy Blue-XXXL",
    "awb": "14901146412",
    "courier": "DTDC",
    "date": "2026-08-02",
    "read": 3,
    "dims": [
      23,
      15,
      17
    ],
    "vol": 1.17,
    "source": "upload",
    "video": {
      "name": "evidence-1564-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1564-len.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01565",
    "sku": "NBJUN24MJ05-Men-Joggers-Eiffel Tower-XXXL + NBJUN24MJ05-Men-Joggers-Navy Blue-XXXL",
    "awb": "14901147878",
    "courier": "Bluedart",
    "date": "2026-07-31",
    "read": 3,
    "dims": [
      28,
      29,
      7
    ],
    "vol": 1.14,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-1566-len.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01567",
    "sku": "NBJUN24MJ05-Men-Joggers-Eiffel Tower-XXXL + NBJUN24MJ05-Men-Joggers-Navy Blue-XXXL",
    "awb": "14901149344",
    "courier": "Xpressbees",
    "date": "2026-08-05",
    "read": 0.75,
    "dims": [
      25,
      15,
      7
    ],
    "vol": 0.53,
    "source": "upload",
    "video": {
      "name": "evidence-1568-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1568-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1568-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1568-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-1568-scale.jpg",
        "url": null
      },
      {
        "slot": "label",
        "name": "evidence-1568-label.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01569",
    "sku": "NBJUN24MJ05-Men-Joggers-Eiffel Tower-XXXL + NBJUN24MJ05-Men-Joggers-Navy Blue-XXXL",
    "awb": "14901150810",
    "courier": "Delhivery",
    "date": "2026-08-03",
    "read": 3,
    "dims": [
      22,
      20,
      17
    ],
    "vol": 1.5,
    "source": "upload",
    "video": {
      "name": "evidence-1570-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1570-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1570-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1570-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-1570-scale.jpg",
        "url": null
      },
      {
        "slot": "label",
        "name": "evidence-1570-label.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-01571",
    "sku": "Classic Polo-Men-Polos-Burgundy-S + Classic Polo-Polo-Black-S",
    "awb": "14901152276",
    "courier": "Xpressbees",
    "date": "2026-06-15",
    "read": 0.61,
    "charged": 0.75,
    "dims": [
      34,
      18,
      14
    ],
    "vol": 1.71,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01573",
    "sku": "Classic Polo-Men-Polos-Burgundy-S + Classic Polo-Polo-Black-S",
    "awb": "14901153742",
    "courier": "Bluedart",
    "date": "2026-07-28",
    "read": 0.56,
    "charged": 0.75,
    "dims": [
      22,
      26,
      16
    ],
    "vol": 1.83,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-01575",
    "sku": "NBJUN24MJ05-Men-Joggers-Black-XL + NBJUN24MJ05-Men-Joggers-Eiffel Tower-XL",
    "awb": "14901155208",
    "courier": "Ekart",
    "date": "2026-08-01",
    "read": 0.75,
    "dims": [
      29,
      19,
      6
    ],
    "vol": 0.66,
    "source": "upload",
    "video": {
      "name": "evidence-1576-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1576-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1576-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1576-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-1576-scale.jpg",
        "url": null
      },
      {
        "slot": "label",
        "name": "evidence-1576-label.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01577",
    "sku": "NBJUN24MJ05-Men-Joggers-Black-XL + NBJUN24MJ05-Men-Joggers-Eiffel Tower-XL",
    "awb": "14901156674",
    "courier": "Ekart",
    "date": "2026-08-02",
    "read": 1.5,
    "dims": [
      23,
      20,
      15
    ],
    "vol": 1.38,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-1578-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1578-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1578-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-1578-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01579",
    "sku": "NBJUN24MJ05-Men-Joggers-Black-XL + NBJUN24MJ05-Men-Joggers-Eiffel Tower-XL",
    "awb": "14901158140",
    "courier": "Xpressbees",
    "date": "2026-07-31",
    "read": 1.5,
    "dims": [
      24,
      24,
      8
    ],
    "vol": 0.92,
    "source": "upload",
    "video": {
      "name": "evidence-1580-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1580-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1580-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-01581",
    "sku": "NBJUN24MJ05-Men-Joggers-Black-XL + NBJUN24MJ05-Men-Joggers-Eiffel Tower-XL",
    "awb": "14901159606",
    "courier": "Delhivery",
    "date": "2026-06-05",
    "read": 1.42,
    "charged": 1.5,
    "dims": [
      31,
      27,
      10
    ],
    "vol": 1.67,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01583",
    "sku": "NBJUN24MJ05-Men-Joggers-Black-XL + NBJUN24MJ05-Men-Joggers-Eiffel Tower-XL",
    "awb": "14901161072",
    "courier": "Bluedart",
    "date": "2026-06-08",
    "read": 0.57,
    "charged": 0.75,
    "dims": [
      29,
      26,
      17
    ],
    "vol": 2.56,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01585",
    "sku": "NBJUN24MJ05-Men-Joggers-Black-XL + NBJUN24MJ05-Men-Joggers-Eiffel Tower-XL",
    "awb": "14901162538",
    "courier": "DTDC",
    "date": "2026-06-22",
    "read": 1.47,
    "charged": 1.5,
    "dims": [
      20,
      17,
      16
    ],
    "vol": 1.09,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01587",
    "sku": "NBJUN24MJ05-Men-Joggers-Black-XL + NBJUN24MJ05-Men-Joggers-Eiffel Tower-XL",
    "awb": "14901164004",
    "courier": "Xpressbees",
    "date": "2026-06-10",
    "read": 1.4,
    "charged": 1.5,
    "dims": [
      21,
      21,
      10
    ],
    "vol": 0.88,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01589",
    "sku": "Classic Polo-Men-Polos-Highland Green-XXL + Classic Polo-Men-Polos-White-XXL",
    "awb": "14901165470",
    "courier": "Bluedart",
    "date": "2026-05-16",
    "read": 0.66,
    "charged": 0.75,
    "dims": [
      34,
      24,
      11
    ],
    "vol": 1.8,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01591",
    "sku": "Classic Polo-Men-Polos-Highland Green-XXL + Classic Polo-Men-Polos-White-XXL",
    "awb": "14901166936",
    "courier": "Ekart",
    "date": "2026-06-04",
    "read": 0.58,
    "charged": 0.75,
    "dims": [
      25,
      17,
      5
    ],
    "vol": 0.43,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01593",
    "sku": "Classic Polo-Men-Polos-Highland Green-XXL + Classic Polo-Men-Polos-White-XXL",
    "awb": "14901168402",
    "courier": "Blitz",
    "date": "2026-07-02",
    "read": 0.59,
    "charged": 0.75,
    "dims": [
      30,
      21,
      5
    ],
    "vol": 0.63,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01595",
    "sku": "Classic Polo-Men-Polos-Highland Green-XXL + Classic Polo-Men-Polos-White-XXL",
    "awb": "14901169868",
    "courier": "Bluedart",
    "date": "2026-05-28",
    "read": 0.57,
    "charged": 0.75,
    "dims": [
      28,
      20,
      15
    ],
    "vol": 1.68,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01597",
    "sku": "Classic Polo-Men-Polos-Highland Green-XXL + Classic Polo-Men-Polos-White-XXL",
    "awb": "14901171334",
    "courier": "DTDC",
    "date": "2026-06-26",
    "read": 0.59,
    "charged": 0.75,
    "dims": [
      25,
      19,
      15
    ],
    "vol": 1.43,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01599",
    "sku": "NBAW24MJ14-Men-Joggers-Eiffel tower-XXL + NBAW24MJ14-Men-Joggers-Olive Green-XXL",
    "awb": "14901172800",
    "courier": "Xpressbees",
    "date": "2026-07-05",
    "read": 0.62,
    "charged": 0.75,
    "dims": [
      35,
      16,
      18
    ],
    "vol": 2.02,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01601",
    "sku": "NBAW24MJ14-Men-Joggers-Eiffel tower-XXL + NBAW24MJ14-Men-Joggers-Olive Green-XXL",
    "awb": "14901174266",
    "courier": "Xpressbees",
    "date": "2026-07-21",
    "read": 0.7,
    "charged": 0.75,
    "dims": [
      26,
      28,
      15
    ],
    "vol": 2.18,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01603",
    "sku": "NBAW24MJ14-Men-Joggers-Eiffel tower-XXL + NBAW24MJ14-Men-Joggers-Olive Green-XXL",
    "awb": "14901175732",
    "courier": "Blitz",
    "date": "2026-05-26",
    "read": 0.7,
    "charged": 0.75,
    "dims": [
      25,
      22,
      17
    ],
    "vol": 1.87,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01605",
    "sku": "NBAW24MJ14-Men-Joggers-Eiffel tower-XXL + NBAW24MJ14-Men-Joggers-Olive Green-XXL",
    "awb": "14901177198",
    "courier": "Shadowfax",
    "date": "2026-05-26",
    "read": 0.58,
    "charged": 0.75,
    "dims": [
      20,
      26,
      11
    ],
    "vol": 1.14,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01607",
    "sku": "NBAW24MJ14-Men-Joggers-Eiffel tower-XXL + NBAW24MJ14-Men-Joggers-Olive Green-XXL",
    "awb": "14901178664",
    "courier": "Delhivery",
    "date": "2026-05-16",
    "read": 0.73,
    "charged": 0.75,
    "dims": [
      38,
      19,
      15
    ],
    "vol": 2.17,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-01609",
    "sku": "NBJUN24MJ05-Men-Joggers-Eiffel Tower-M + NBJUN24MJ05-Men-Joggers-Mocha Bisque-M",
    "awb": "14901180130",
    "courier": "Delhivery",
    "date": "2026-07-31",
    "read": 1,
    "dims": [
      21,
      17,
      16
    ],
    "vol": 1.14,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-1610-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1610-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1610-hgt.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01611",
    "sku": "Classic Polo-Men-Polos-Cobalt Blue-L + Classic Polo-Men-Polos-Poppy Red-L",
    "awb": "14901181596",
    "courier": "Xpressbees",
    "date": "2026-08-05",
    "read": 1,
    "dims": [
      38,
      18,
      17
    ],
    "vol": 2.33,
    "source": "upload",
    "video": {
      "name": "evidence-1612-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1612-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1612-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1612-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-1612-scale.jpg",
        "url": null
      },
      {
        "slot": "label",
        "name": "evidence-1612-label.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01613",
    "sku": "Classic Polo-Men-Polos-Cobalt Blue-L + Classic Polo-Men-Polos-Poppy Red-L",
    "awb": "14901183062",
    "courier": "Xpressbees",
    "date": "2026-08-04",
    "read": 0.75,
    "dims": [
      33,
      18,
      10
    ],
    "vol": 1.19,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-1614-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1614-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1614-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-1614-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01615",
    "sku": "Classic Polo-Men-Polos-Aqua Blue-L + Classic Polo-Men-Polos-Burgundy-L",
    "awb": "14901184528",
    "courier": "Blitz",
    "date": "2026-07-30",
    "read": 0.75,
    "dims": [
      20,
      15,
      17
    ],
    "vol": 1.02,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-1616-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1616-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1616-hgt.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01617",
    "sku": "Classic Polo-Men-Polos-Aqua Blue-L + Classic Polo-Men-Polos-Burgundy-L",
    "awb": "14901185994",
    "courier": "DTDC",
    "date": "2026-08-04",
    "read": 0.75,
    "dims": [
      23,
      22,
      5
    ],
    "vol": 0.51,
    "source": "upload",
    "video": {
      "name": "evidence-1618-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1618-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1618-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1618-hgt.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-01619",
    "sku": "Classic Polo-Men-Polos-Aqua Blue-L + Classic Polo-Men-Polos-Burgundy-L",
    "awb": "14901187460",
    "courier": "Blitz",
    "date": "2026-06-29",
    "read": 0.69,
    "charged": 0.75,
    "dims": [
      32,
      26,
      18
    ],
    "vol": 3,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01621",
    "sku": "Classic Polo-Men-Polos-Aqua Blue-L + Classic Polo-Men-Polos-Burgundy-L",
    "awb": "14901188926",
    "courier": "Blitz",
    "date": "2026-05-08",
    "read": 0.61,
    "charged": 0.75,
    "dims": [
      36,
      22,
      6
    ],
    "vol": 0.95,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01623",
    "sku": "Classic Polo-Men-Polos-Aqua Blue-L + Classic Polo-Men-Polos-Burgundy-L",
    "awb": "14901190392",
    "courier": "Ekart",
    "date": "2026-07-24",
    "read": 0.66,
    "charged": 0.75,
    "dims": [
      20,
      26,
      18
    ],
    "vol": 1.87,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01625",
    "sku": "Classic Polo-Men-Polos-Aqua Blue-L + Classic Polo-Men-Polos-Burgundy-L",
    "awb": "14901191858",
    "courier": "Xpressbees",
    "date": "2026-07-09",
    "read": 0.59,
    "charged": 0.75,
    "dims": [
      25,
      29,
      12
    ],
    "vol": 1.74,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-01627",
    "sku": "NBAW24MJ15-Men-Joggers-Eiffel tower-L + NBAW24MJ15-Men-Joggers-Olive Green-L",
    "awb": "14901193324",
    "courier": "Ekart",
    "date": "2026-08-04",
    "read": 1.5,
    "dims": [
      25,
      23,
      18
    ],
    "vol": 2.07,
    "source": "upload",
    "video": {
      "name": "evidence-1628-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1628-len.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01629",
    "sku": "NBAW24MJ15-Men-Joggers-Eiffel tower-L + NBAW24MJ15-Men-Joggers-Olive Green-L",
    "awb": "14901194790",
    "courier": "Bluedart",
    "date": "2026-07-31",
    "read": 1.5,
    "dims": [
      33,
      21,
      12
    ],
    "vol": 1.66,
    "source": "upload",
    "video": {
      "name": "evidence-1630-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1630-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1630-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1630-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-1630-scale.jpg",
        "url": null
      },
      {
        "slot": "label",
        "name": "evidence-1630-label.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-01631",
    "sku": "NBAW24MJ15-Men-Joggers-Eiffel tower-L + NBAW24MJ15-Men-Joggers-Olive Green-L",
    "awb": "14901196256",
    "courier": "Shadowfax",
    "date": "2026-07-21",
    "read": 1.43,
    "charged": 1.5,
    "dims": [
      26,
      24,
      12
    ],
    "vol": 1.5,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01633",
    "sku": "NBAW24MJ15-Men-Joggers-Eiffel tower-L + NBAW24MJ15-Men-Joggers-Olive Green-L",
    "awb": "14901197722",
    "courier": "DTDC",
    "date": "2026-06-25",
    "read": 1.47,
    "charged": 1.5,
    "dims": [
      26,
      25,
      17
    ],
    "vol": 2.21,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01635",
    "sku": "NBAW24MJ15-Men-Joggers-Eiffel tower-L + NBAW24MJ15-Men-Joggers-Olive Green-L",
    "awb": "14901199188",
    "courier": "Xpressbees",
    "date": "2026-07-05",
    "read": 1.35,
    "charged": 1.5,
    "dims": [
      20,
      18,
      5
    ],
    "vol": 0.36,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01637",
    "sku": "NBAW24MJ15-Men-Joggers-Eiffel tower-L + NBAW24MJ15-Men-Joggers-Olive Green-L",
    "awb": "14901200654",
    "courier": "DTDC",
    "date": "2026-05-05",
    "read": 1.44,
    "charged": 1.5,
    "dims": [
      27,
      21,
      12
    ],
    "vol": 1.36,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01639",
    "sku": "NBAW24MJ15-Men-Joggers-Eiffel tower-L + NBAW24MJ15-Men-Joggers-Olive Green-L",
    "awb": "14901202120",
    "courier": "Delhivery",
    "date": "2026-06-10",
    "read": 1.3,
    "charged": 1.5,
    "dims": [
      23,
      28,
      10
    ],
    "vol": 1.29,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01641",
    "sku": "NBAW24MJ15-Men-Joggers-Eiffel tower-L + NBAW24MJ15-Men-Joggers-Olive Green-L",
    "awb": "14901203586",
    "courier": "Ekart",
    "date": "2026-05-31",
    "read": 1.34,
    "charged": 1.5,
    "dims": [
      31,
      28,
      5
    ],
    "vol": 0.87,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-01643",
    "sku": "NBAW24MJ15-Men-Joggers-Navy Blue-M + NBAW24MJ15-Men-Joggers-Olive Green-M",
    "awb": "14901205052",
    "courier": "Delhivery",
    "date": "2026-08-01",
    "read": 0.75,
    "dims": [
      20,
      28,
      16
    ],
    "vol": 1.79,
    "source": "upload",
    "video": {
      "name": "evidence-1644-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1644-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1644-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1644-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-1644-scale.jpg",
        "url": null
      },
      {
        "slot": "label",
        "name": "evidence-1644-label.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-01645",
    "sku": "NBAW24MJ15-Men-Joggers-Navy Blue-M + NBAW24MJ15-Men-Joggers-Olive Green-M",
    "awb": "14901206518",
    "courier": "Bluedart",
    "date": "2026-05-15",
    "read": 0.63,
    "charged": 0.75,
    "dims": [
      32,
      16,
      5
    ],
    "vol": 0.51,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01647",
    "sku": "NBAW24MJ15-Men-Joggers-Navy Blue-M + NBAW24MJ15-Men-Joggers-Olive Green-M",
    "awb": "14901207984",
    "courier": "Shadowfax",
    "date": "2026-05-15",
    "read": 0.6,
    "charged": 0.75,
    "dims": [
      36,
      15,
      18
    ],
    "vol": 1.94,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01649",
    "sku": "NBAW24MJ15-Men-Joggers-Navy Blue-M + NBAW24MJ15-Men-Joggers-Olive Green-M",
    "awb": "14901209450",
    "courier": "Blitz",
    "date": "2026-07-22",
    "read": 0.56,
    "charged": 0.75,
    "dims": [
      26,
      22,
      6
    ],
    "vol": 0.69,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01651",
    "sku": "NBAW24MJ15-Men-Joggers-Navy Blue-M + NBAW24MJ15-Men-Joggers-Olive Green-M",
    "awb": "14901210916",
    "courier": "Shadowfax",
    "date": "2026-06-16",
    "read": 0.69,
    "charged": 0.75,
    "dims": [
      33,
      26,
      14
    ],
    "vol": 2.4,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01653",
    "sku": "NBAW24MJ15-Men-Joggers-Navy Blue-M + NBAW24MJ15-Men-Joggers-Olive Green-M",
    "awb": "14901212382",
    "courier": "Delhivery",
    "date": "2026-06-26",
    "read": 0.59,
    "charged": 0.75,
    "dims": [
      36,
      21,
      7
    ],
    "vol": 1.06,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01655",
    "sku": "NBAW24MJ15-Men-Joggers-Navy Blue-M + NBAW24MJ15-Men-Joggers-Olive Green-M",
    "awb": "14901213848",
    "courier": "Blitz",
    "date": "2026-07-06",
    "read": 0.69,
    "charged": 0.75,
    "dims": [
      21,
      27,
      17
    ],
    "vol": 1.93,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01657",
    "sku": "NBJUN24MJ05-Men-Joggers-Black-XL + NBJUN24MJ05-Men-Joggers-Eiffel Tower-XL + NBJUN24MJ05-Men-Joggers-Navy Blue",
    "awb": "14901215314",
    "courier": "Delhivery",
    "date": "2026-04-30",
    "read": 1.86,
    "charged": 2,
    "dims": [
      37,
      29,
      10
    ],
    "vol": 2.15,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01659",
    "sku": "NBJUN24MJ05-Men-Joggers-Black-XL + NBJUN24MJ05-Men-Joggers-Eiffel Tower-XL + NBJUN24MJ05-Men-Joggers-Navy Blue",
    "awb": "14901216780",
    "courier": "Xpressbees",
    "date": "2026-06-01",
    "read": 1.8,
    "charged": 2,
    "dims": [
      31,
      16,
      10
    ],
    "vol": 0.99,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01661",
    "sku": "NBJUN24MJ05-Men-Joggers-Black-XL + NBJUN24MJ05-Men-Joggers-Eiffel Tower-XL + NBJUN24MJ05-Men-Joggers-Navy Blue",
    "awb": "14901218246",
    "courier": "Shadowfax",
    "date": "2026-06-13",
    "read": 0.96,
    "charged": 1,
    "dims": [
      25,
      24,
      19
    ],
    "vol": 2.28,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01663",
    "sku": "NBJUN24MJ05-Men-Joggers-Black-XL + NBJUN24MJ05-Men-Joggers-Eiffel Tower-XL + NBJUN24MJ05-Men-Joggers-Navy Blue",
    "awb": "14901219712",
    "courier": "Ekart",
    "date": "2026-06-07",
    "read": 1.94,
    "charged": 2,
    "dims": [
      30,
      29,
      19
    ],
    "vol": 3.31,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01665",
    "sku": "NBJUN24MJ05-Men-Joggers-Black-XL + NBJUN24MJ05-Men-Joggers-Eiffel Tower-XL + NBJUN24MJ05-Men-Joggers-Navy Blue",
    "awb": "14901221178",
    "courier": "Delhivery",
    "date": "2026-06-12",
    "read": 1.98,
    "charged": 2,
    "dims": [
      26,
      29,
      9
    ],
    "vol": 1.36,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01667",
    "sku": "NBJUN24MJ05-Men-Joggers-Black-XL + NBJUN24MJ05-Men-Joggers-Eiffel Tower-XL + NBJUN24MJ05-Men-Joggers-Navy Blue",
    "awb": "14901222644",
    "courier": "Shadowfax",
    "date": "2026-05-25",
    "read": 1.79,
    "charged": 2,
    "dims": [
      37,
      20,
      8
    ],
    "vol": 1.18,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-01669",
    "sku": "Classic Polo-Men-Polos-Canary-L + Classic Polo-Men-Polos-Latte-L",
    "awb": "14901224110",
    "courier": "Xpressbees",
    "date": "2026-08-02",
    "read": 0.75,
    "dims": [
      23,
      22,
      14
    ],
    "vol": 1.42,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-1670-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1670-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01671",
    "sku": "Classic Polo-Men-Polos-Canary-L + Classic Polo-Men-Polos-Latte-L",
    "awb": "14901225576",
    "courier": "Xpressbees",
    "date": "2026-08-05",
    "read": 0.75,
    "dims": [
      29,
      28,
      12
    ],
    "vol": 1.95,
    "source": "upload",
    "video": {
      "name": "evidence-1672-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1672-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1672-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1672-hgt.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01673",
    "sku": "Classic Polo-Men-Polos-Canary-L + Classic Polo-Men-Polos-Latte-L",
    "awb": "14901227042",
    "courier": "Ekart",
    "date": "2026-08-01",
    "read": 0.75,
    "dims": [
      36,
      29,
      8
    ],
    "vol": 1.67,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-1674-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1674-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1674-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-1674-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01675",
    "sku": "Classic Polo-Men-Polos-Canary-L + Classic Polo-Men-Polos-Latte-L",
    "awb": "14901228508",
    "courier": "Delhivery",
    "date": "2026-08-04",
    "read": 1,
    "dims": [
      31,
      27,
      7
    ],
    "vol": 1.17,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-1676-len.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01677",
    "sku": "Classic Polo-Men-Polos-Canary-L + Classic Polo-Men-Polos-Latte-L",
    "awb": "14901229974",
    "courier": "Xpressbees",
    "date": "2026-08-02",
    "read": 1,
    "dims": [
      38,
      24,
      13
    ],
    "vol": 2.37,
    "source": "upload",
    "video": {
      "name": "evidence-1678-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1678-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1678-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1678-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-1678-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01679",
    "sku": "Classic Polo-Men-Polos-Canary-L + Classic Polo-Men-Polos-Latte-L",
    "awb": "14901231440",
    "courier": "DTDC",
    "date": "2026-08-03",
    "read": 1,
    "dims": [
      28,
      25,
      16
    ],
    "vol": 2.24,
    "source": "upload",
    "video": {
      "name": "evidence-1680-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1680-len.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-01681",
    "sku": "Classic Polo-Men-Polos-Canary-L + Classic Polo-Men-Polos-Latte-L",
    "awb": "14901232906",
    "courier": "DTDC",
    "date": "2026-05-13",
    "read": 0.71,
    "charged": 0.75,
    "dims": [
      39,
      22,
      9
    ],
    "vol": 1.54,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01683",
    "sku": "Classic Polo-Men-Polos-Canary-L + Classic Polo-Men-Polos-Latte-L",
    "awb": "14901234372",
    "courier": "DTDC",
    "date": "2026-07-09",
    "read": 0.87,
    "charged": 1,
    "dims": [
      32,
      19,
      12
    ],
    "vol": 1.46,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01685",
    "sku": "Classic Polo-Men-Polos-Canary-L + Classic Polo-Men-Polos-Latte-L",
    "awb": "14901235838",
    "courier": "Xpressbees",
    "date": "2026-05-25",
    "read": 0.69,
    "charged": 0.75,
    "dims": [
      29,
      27,
      17
    ],
    "vol": 2.66,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01687",
    "sku": "Classic Polo-Men-Polos-Canary-L + Classic Polo-Men-Polos-Latte-L",
    "awb": "14901237304",
    "courier": "Xpressbees",
    "date": "2026-06-13",
    "read": 0.71,
    "charged": 0.75,
    "dims": [
      31,
      26,
      6
    ],
    "vol": 0.97,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-01689",
    "sku": "Nobero Shorts V2-Men Shorts-Olive Green-XL + Nobero Shorts V2-Men-Shorts-Arctic Wolf-XL",
    "awb": "14901238770",
    "courier": "Shadowfax",
    "date": "2026-08-03",
    "read": 1,
    "dims": [
      39,
      17,
      15
    ],
    "vol": 1.99,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-1690-len.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01691",
    "sku": "Nobero Shorts V2-Men Shorts-Olive Green-XL + Nobero Shorts V2-Men-Shorts-Arctic Wolf-XL",
    "awb": "14901240236",
    "courier": "Xpressbees",
    "date": "2026-08-03",
    "read": 0.75,
    "dims": [
      21,
      28,
      8
    ],
    "vol": 0.94,
    "source": "upload",
    "video": {
      "name": "evidence-1692-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1692-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1692-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1692-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-1692-scale.jpg",
        "url": null
      },
      {
        "slot": "label",
        "name": "evidence-1692-label.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01693",
    "sku": "Nobero Shorts V2-Men Shorts-Olive Green-XL + Nobero Shorts V2-Men-Shorts-Arctic Wolf-XL",
    "awb": "14901241702",
    "courier": "Ekart",
    "date": "2026-08-02",
    "read": 1,
    "dims": [
      24,
      25,
      16
    ],
    "vol": 1.92,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-1694-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1694-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1694-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-1694-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-01695",
    "sku": "Nobero Shorts V2-Men Shorts-Olive Green-XL + Nobero Shorts V2-Men-Shorts-Arctic Wolf-XL",
    "awb": "14901243168",
    "courier": "Ekart",
    "date": "2026-05-21",
    "read": 0.93,
    "charged": 1,
    "dims": [
      29,
      24,
      6
    ],
    "vol": 0.84,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01697",
    "sku": "Nobero Shorts V2-Men Shorts-Olive Green-XL + Nobero Shorts V2-Men-Shorts-Arctic Wolf-XL",
    "awb": "14901244634",
    "courier": "Shadowfax",
    "date": "2026-06-01",
    "read": 0.8,
    "charged": 1,
    "dims": [
      23,
      22,
      15
    ],
    "vol": 1.52,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01699",
    "sku": "Nobero Shorts V2-Men Shorts-Olive Green-XL + Nobero Shorts V2-Men-Shorts-Arctic Wolf-XL",
    "awb": "14901246100",
    "courier": "Delhivery",
    "date": "2026-06-24",
    "read": 0.91,
    "charged": 1,
    "dims": [
      35,
      21,
      12
    ],
    "vol": 1.76,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01701",
    "sku": "Nobero Shorts V2-Men Shorts-Olive Green-XL + Nobero Shorts V2-Men-Shorts-Arctic Wolf-XL",
    "awb": "14901247566",
    "courier": "Shadowfax",
    "date": "2026-05-05",
    "read": 0.92,
    "charged": 1,
    "dims": [
      32,
      28,
      5
    ],
    "vol": 0.9,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01703",
    "sku": "Nobero Shorts V2-Men Shorts-Olive Green-XL + Nobero Shorts V2-Men-Shorts-Arctic Wolf-XL",
    "awb": "14901249032",
    "courier": "Blitz",
    "date": "2026-05-27",
    "read": 0.94,
    "charged": 1,
    "dims": [
      35,
      17,
      11
    ],
    "vol": 1.31,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01705",
    "sku": "Nobero Shorts V2-Men Shorts-Olive Green-XL + Nobero Shorts V2-Men-Shorts-Arctic Wolf-XL",
    "awb": "14901250498",
    "courier": "Shadowfax",
    "date": "2026-06-19",
    "read": 0.93,
    "charged": 1,
    "dims": [
      24,
      28,
      11
    ],
    "vol": 1.48,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-01707",
    "sku": "NBJUN24MJ05-Men-Joggers-Eiffel Tower-XXL + NBJUN24MJ05-Men-Joggers-Navy Blue-XXL",
    "awb": "14901251964",
    "courier": "Bluedart",
    "date": "2026-07-31",
    "read": 1,
    "dims": [
      39,
      26,
      19
    ],
    "vol": 3.85,
    "source": "upload",
    "video": {
      "name": "evidence-1708-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1708-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1708-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1708-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-1708-scale.jpg",
        "url": null
      },
      {
        "slot": "label",
        "name": "evidence-1708-label.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01709",
    "sku": "NBJUN24MJ05-Men-Joggers-Eiffel Tower-XXL + NBJUN24MJ05-Men-Joggers-Navy Blue-XXL",
    "awb": "14901253430",
    "courier": "Blitz",
    "date": "2026-08-06",
    "read": 1,
    "dims": [
      29,
      24,
      19
    ],
    "vol": 2.64,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-1710-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1710-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1710-hgt.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01711",
    "sku": "NBJUN24MJ05-Men-Joggers-Eiffel Tower-XXL + NBJUN24MJ05-Men-Joggers-Navy Blue-XXL",
    "awb": "14901254896",
    "courier": "DTDC",
    "date": "2026-08-02",
    "read": 1,
    "dims": [
      31,
      21,
      13
    ],
    "vol": 1.69,
    "source": "upload",
    "video": {
      "name": "evidence-1712-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1712-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1712-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1712-hgt.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01713",
    "sku": "NBJUN24MJ05-Men-Joggers-Eiffel Tower-XXL + NBJUN24MJ05-Men-Joggers-Navy Blue-XXL",
    "awb": "14901256362",
    "courier": "Xpressbees",
    "date": "2026-07-30",
    "read": 1,
    "dims": [
      28,
      29,
      18
    ],
    "vol": 2.92,
    "source": "upload",
    "video": {
      "name": "evidence-1714-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1714-len.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-01715",
    "sku": "NBJUN24MJ05-Men-Joggers-Eiffel Tower-XXL + NBJUN24MJ05-Men-Joggers-Navy Blue-XXL",
    "awb": "14901257828",
    "courier": "DTDC",
    "date": "2026-06-01",
    "read": 0.8,
    "charged": 1,
    "dims": [
      24,
      15,
      18
    ],
    "vol": 1.3,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01717",
    "sku": "NBJUN24MJ05-Men-Joggers-Eiffel Tower-XXL + NBJUN24MJ05-Men-Joggers-Navy Blue-XXL",
    "awb": "14901259294",
    "courier": "Xpressbees",
    "date": "2026-06-13",
    "read": 0.81,
    "charged": 1,
    "dims": [
      35,
      22,
      14
    ],
    "vol": 2.16,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01719",
    "sku": "NBJUN24MJ05-Men-Joggers-Eiffel Tower-XXL + NBJUN24MJ05-Men-Joggers-Navy Blue-XXL",
    "awb": "14901260760",
    "courier": "DTDC",
    "date": "2026-07-18",
    "read": 0.89,
    "charged": 1,
    "dims": [
      28,
      21,
      10
    ],
    "vol": 1.18,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-01721",
    "sku": "Great Highway Classic Fit T-Shirt Wine Red / M (sku - 1M-TWTS-C2324-WR3) + Ocean Beach",
    "awb": "14901262226",
    "courier": "Xpressbees",
    "date": "2026-08-01",
    "read": 0.75,
    "dims": [
      28,
      22,
      18
    ],
    "vol": 2.22,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-1722-len.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01723",
    "sku": "Great Highway Classic Fit T-Shirt Wine Red / M (sku - 1M-TWTS-C2324-WR3) + Ocean Beach",
    "awb": "14901263692",
    "courier": "Xpressbees",
    "date": "2026-07-31",
    "read": 0.75,
    "dims": [
      31,
      16,
      7
    ],
    "vol": 0.69,
    "source": "upload",
    "video": {
      "name": "evidence-1724-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1724-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1724-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1724-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-1724-scale.jpg",
        "url": null
      },
      {
        "slot": "label",
        "name": "evidence-1724-label.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01725",
    "sku": "Great Highway Classic Fit T-Shirt Wine Red / M (sku - 1M-TWTS-C2324-WR3) + Ocean Beach",
    "awb": "14901265158",
    "courier": "Delhivery",
    "date": "2026-07-31",
    "read": 0.75,
    "dims": [
      23,
      20,
      18
    ],
    "vol": 1.66,
    "source": "upload",
    "video": {
      "name": "evidence-1726-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1726-len.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01727",
    "sku": "Great Highway Classic Fit T-Shirt Wine Red / M (sku - 1M-TWTS-C2324-WR3) + Ocean Beach",
    "awb": "14901266624",
    "courier": "Blitz",
    "date": "2026-08-01",
    "read": 0.75,
    "dims": [
      25,
      16,
      11
    ],
    "vol": 0.88,
    "source": "upload",
    "video": {
      "name": "evidence-1728-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1728-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1728-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1728-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-1728-scale.jpg",
        "url": null
      },
      {
        "slot": "label",
        "name": "evidence-1728-label.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-01729",
    "sku": "Great Highway Classic Fit T-Shirt Wine Red / M (sku - 1M-TWTS-C2324-WR3) + Ocean Beach",
    "awb": "14901268090",
    "courier": "Shadowfax",
    "date": "2026-07-25",
    "read": 0.64,
    "charged": 0.75,
    "dims": [
      34,
      29,
      18
    ],
    "vol": 3.55,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01731",
    "sku": "Great Highway Classic Fit T-Shirt Wine Red / M (sku - 1M-TWTS-C2324-WR3) + Ocean Beach",
    "awb": "14901269556",
    "courier": "DTDC",
    "date": "2026-07-22",
    "read": 0.58,
    "charged": 0.75,
    "dims": [
      36,
      22,
      15
    ],
    "vol": 2.38,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01733",
    "sku": "Great Highway Classic Fit T-Shirt Wine Red / M (sku - 1M-TWTS-C2324-WR3) + Ocean Beach",
    "awb": "14901271022",
    "courier": "Blitz",
    "date": "2026-05-03",
    "read": 0.58,
    "charged": 0.75,
    "dims": [
      34,
      25,
      16
    ],
    "vol": 2.72,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-01735",
    "sku": "NBJUN24MJ05-Men-Joggers-Navy Blue-L + NBJUN24MJ05-Men-Joggers-Pebble-L",
    "awb": "14901272488",
    "courier": "DTDC",
    "date": "2026-08-05",
    "read": 1,
    "dims": [
      38,
      16,
      5
    ],
    "vol": 0.61,
    "source": "upload",
    "video": {
      "name": "evidence-1736-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1736-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1736-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01737",
    "sku": "NBJUN24MJ05-Men-Joggers-Navy Blue-L + NBJUN24MJ05-Men-Joggers-Pebble-L",
    "awb": "14901273954",
    "courier": "Blitz",
    "date": "2026-08-01",
    "read": 1,
    "dims": [
      26,
      24,
      15
    ],
    "vol": 1.87,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-1738-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1738-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1738-hgt.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01739",
    "sku": "NBJUN24MJ05-Men-Joggers-Navy Blue-L + NBJUN24MJ05-Men-Joggers-Pebble-L",
    "awb": "14901275420",
    "courier": "Bluedart",
    "date": "2026-08-06",
    "read": 1,
    "dims": [
      24,
      24,
      5
    ],
    "vol": 0.58,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-1740-len.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01741",
    "sku": "NBJUN24MJ05-Men-Joggers-Navy Blue-L + NBJUN24MJ05-Men-Joggers-Pebble-L",
    "awb": "14901276886",
    "courier": "Ekart",
    "date": "2026-08-06",
    "read": 1,
    "dims": [
      36,
      25,
      8
    ],
    "vol": 1.44,
    "source": "upload",
    "video": {
      "name": "evidence-1742-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1742-len.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-01743",
    "sku": "NBJUN24MJ05-Men-Joggers-Navy Blue-L + NBJUN24MJ05-Men-Joggers-Pebble-L",
    "awb": "14901278352",
    "courier": "Blitz",
    "date": "2026-06-23",
    "read": 0.96,
    "charged": 1,
    "dims": [
      24,
      18,
      13
    ],
    "vol": 1.12,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01745",
    "sku": "NBJUN24MJ05-Men-Joggers-Navy Blue-L + NBJUN24MJ05-Men-Joggers-Pebble-L",
    "awb": "14901279818",
    "courier": "Blitz",
    "date": "2026-05-17",
    "read": 0.89,
    "charged": 1,
    "dims": [
      30,
      16,
      10
    ],
    "vol": 0.96,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01747",
    "sku": "NBJUN24MJ05-Men-Joggers-Navy Blue-L + NBJUN24MJ05-Men-Joggers-Pebble-L",
    "awb": "14901281284",
    "courier": "Bluedart",
    "date": "2026-06-24",
    "read": 0.93,
    "charged": 1,
    "dims": [
      24,
      15,
      14
    ],
    "vol": 1.01,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01749",
    "sku": "NBJUN24MJ05-Men-Joggers-Navy Blue-L + NBJUN24MJ05-Men-Joggers-Pebble-L",
    "awb": "14901282750",
    "courier": "Blitz",
    "date": "2026-05-05",
    "read": 0.93,
    "charged": 1,
    "dims": [
      36,
      26,
      11
    ],
    "vol": 2.06,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01751",
    "sku": "NBJUN24MJ05-Men-Joggers-Navy Blue-L + NBJUN24MJ05-Men-Joggers-Pebble-L",
    "awb": "14901284216",
    "courier": "Blitz",
    "date": "2026-06-26",
    "read": 0.95,
    "charged": 1,
    "dims": [
      33,
      21,
      16
    ],
    "vol": 2.22,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-01753",
    "sku": "Classic Polo-Men-Polos-Canary-L + Classic Polo-Men-Polos-Petunia-L",
    "awb": "14901285682",
    "courier": "Ekart",
    "date": "2026-08-05",
    "read": 1,
    "dims": [
      30,
      21,
      9
    ],
    "vol": 1.13,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-1754-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1754-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1754-hgt.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01755",
    "sku": "Classic Polo-Men-Polos-Canary-L + Classic Polo-Men-Polos-Petunia-L",
    "awb": "14901287148",
    "courier": "DTDC",
    "date": "2026-08-05",
    "read": 0.75,
    "dims": [
      22,
      17,
      8
    ],
    "vol": 0.6,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-1756-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1756-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1756-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-1756-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-01757",
    "sku": "Classic Polo-Men-Polos-Canary-L + Classic Polo-Men-Polos-Petunia-L",
    "awb": "14901288614",
    "courier": "Bluedart",
    "date": "2026-05-19",
    "read": 0.97,
    "charged": 1,
    "dims": [
      35,
      24,
      9
    ],
    "vol": 1.51,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01759",
    "sku": "Classic Polo-Men-Polos-Canary-L + Classic Polo-Men-Polos-Petunia-L",
    "awb": "14901290080",
    "courier": "Xpressbees",
    "date": "2026-05-11",
    "read": 0.97,
    "charged": 1,
    "dims": [
      35,
      26,
      15
    ],
    "vol": 2.73,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01761",
    "sku": "Classic Polo-Men-Polos-Canary-L + Classic Polo-Men-Polos-Petunia-L",
    "awb": "14901291546",
    "courier": "Shadowfax",
    "date": "2026-06-01",
    "read": 0.83,
    "charged": 1,
    "dims": [
      27,
      25,
      6
    ],
    "vol": 0.81,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01763",
    "sku": "Classic Polo-Men-Polos-Canary-L + Classic Polo-Men-Polos-Petunia-L",
    "awb": "14901293012",
    "courier": "Bluedart",
    "date": "2026-06-02",
    "read": 0.65,
    "charged": 0.75,
    "dims": [
      31,
      24,
      19
    ],
    "vol": 2.83,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01765",
    "sku": "Nobero Shorts V2-Men Shorts-Olive Green-XL + Nobero Shorts V2-Men-Shorts-Eiffel tower-XL",
    "awb": "14901294478",
    "courier": "Ekart",
    "date": "2026-06-11",
    "read": 0.89,
    "charged": 1,
    "dims": [
      23,
      28,
      11
    ],
    "vol": 1.42,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01767",
    "sku": "Nobero Shorts V2-Men Shorts-Olive Green-XL + Nobero Shorts V2-Men-Shorts-Eiffel tower-XL",
    "awb": "14901295944",
    "courier": "Delhivery",
    "date": "2026-06-04",
    "read": 0.84,
    "charged": 1,
    "dims": [
      29,
      18,
      16
    ],
    "vol": 1.67,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01769",
    "sku": "Nobero Shorts V2-Men Shorts-Olive Green-XL + Nobero Shorts V2-Men-Shorts-Eiffel tower-XL",
    "awb": "14901297410",
    "courier": "Shadowfax",
    "date": "2026-05-07",
    "read": 1.43,
    "charged": 1.5,
    "dims": [
      25,
      22,
      18
    ],
    "vol": 1.98,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01771",
    "sku": "Nobero Shorts V2-Men Shorts-Olive Green-XL + Nobero Shorts V2-Men-Shorts-Eiffel tower-XL",
    "awb": "14901298876",
    "courier": "Delhivery",
    "date": "2026-07-13",
    "read": 1.29,
    "charged": 1.5,
    "dims": [
      26,
      17,
      8
    ],
    "vol": 0.71,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01773",
    "sku": "Nobero Shorts V2-Men Shorts-Olive Green-XL + Nobero Shorts V2-Men-Shorts-Eiffel tower-XL",
    "awb": "14901300342",
    "courier": "Delhivery",
    "date": "2026-05-10",
    "read": 0.93,
    "charged": 1,
    "dims": [
      36,
      17,
      10
    ],
    "vol": 1.22,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01775",
    "sku": "Nobero Shorts V2-Men Shorts-Olive Green-XL + Nobero Shorts V2-Men-Shorts-Eiffel tower-XL",
    "awb": "14901301808",
    "courier": "Bluedart",
    "date": "2026-05-21",
    "read": 1.44,
    "charged": 1.5,
    "dims": [
      27,
      21,
      16
    ],
    "vol": 1.81,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-01777",
    "sku": "NBJUN24MJ05-Men-Joggers-Eiffel Tower :  : M + NBJUN24MJ05-Men-Joggers-Pebble :  : M",
    "awb": "14901303274",
    "courier": "DTDC",
    "date": "2026-08-02",
    "read": 2,
    "dims": [
      29,
      26,
      6
    ],
    "vol": 0.9,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-1778-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1778-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1778-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-1778-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01779",
    "sku": "NBJUN24MJ05-Men-Joggers-Eiffel Tower :  : M + NBJUN24MJ05-Men-Joggers-Pebble :  : M",
    "awb": "14901304740",
    "courier": "DTDC",
    "date": "2026-08-04",
    "read": 2,
    "dims": [
      37,
      22,
      14
    ],
    "vol": 2.28,
    "source": "upload",
    "video": {
      "name": "evidence-1780-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1780-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1780-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1780-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-1780-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-01781",
    "sku": "NBJUN24MJ05-Men-Joggers-Eiffel Tower :  : M + NBJUN24MJ05-Men-Joggers-Pebble :  : M",
    "awb": "14901306206",
    "courier": "Bluedart",
    "date": "2026-05-20",
    "read": 1.85,
    "charged": 2,
    "dims": [
      33,
      19,
      19
    ],
    "vol": 2.38,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01783",
    "sku": "NBJUN24MJ05-Men-Joggers-Eiffel Tower :  : M + NBJUN24MJ05-Men-Joggers-Pebble :  : M",
    "awb": "14901307672",
    "courier": "Xpressbees",
    "date": "2026-05-03",
    "read": 1.79,
    "charged": 2,
    "dims": [
      21,
      23,
      8
    ],
    "vol": 0.77,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01785",
    "sku": "NBJUN24MJ05-Men-Joggers-Eiffel Tower :  : M + NBJUN24MJ05-Men-Joggers-Pebble :  : M",
    "awb": "14901309138",
    "courier": "Delhivery",
    "date": "2026-05-11",
    "read": 1.95,
    "charged": 2,
    "dims": [
      22,
      18,
      11
    ],
    "vol": 0.87,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01787",
    "sku": "NBJUN24MJ05-Men-Joggers-Eiffel Tower :  : M + NBJUN24MJ05-Men-Joggers-Pebble :  : M",
    "awb": "14901310604",
    "courier": "Bluedart",
    "date": "2026-06-23",
    "read": 1.81,
    "charged": 2,
    "dims": [
      20,
      22,
      7
    ],
    "vol": 0.62,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01789",
    "sku": "NBJUN24MJ05-Men-Joggers-Eiffel Tower :  : M + NBJUN24MJ05-Men-Joggers-Pebble :  : M",
    "awb": "14901312070",
    "courier": "Ekart",
    "date": "2026-06-25",
    "read": 1.85,
    "charged": 2,
    "dims": [
      28,
      29,
      16
    ],
    "vol": 2.6,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01791",
    "sku": "NBJUN24MJ05-Men-Joggers-Eiffel Tower :  : XXXL + NBJUN24MJ05-Men-Joggers-Navy Blue :  : XXXL",
    "awb": "14901313536",
    "courier": "Bluedart",
    "date": "2026-07-18",
    "read": 2.88,
    "charged": 3,
    "dims": [
      25,
      20,
      6
    ],
    "vol": 0.6,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01793",
    "sku": "NBJUN24MJ05-Men-Joggers-Eiffel Tower :  : XXXL + NBJUN24MJ05-Men-Joggers-Navy Blue :  : XXXL",
    "awb": "14901315002",
    "courier": "Bluedart",
    "date": "2026-05-29",
    "read": 2.87,
    "charged": 3,
    "dims": [
      27,
      29,
      13
    ],
    "vol": 2.04,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01795",
    "sku": "NBJUN24MJ05-Men-Joggers-Eiffel Tower :  : XXXL + NBJUN24MJ05-Men-Joggers-Navy Blue :  : XXXL",
    "awb": "14901316468",
    "courier": "DTDC",
    "date": "2026-07-01",
    "read": 2.92,
    "charged": 3,
    "dims": [
      30,
      22,
      9
    ],
    "vol": 1.19,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01797",
    "sku": "NBJUN24MJ05-Men-Joggers-Eiffel Tower :  : XXXL + NBJUN24MJ05-Men-Joggers-Navy Blue :  : XXXL",
    "awb": "14901317934",
    "courier": "Shadowfax",
    "date": "2026-06-28",
    "read": 2.95,
    "charged": 3,
    "dims": [
      23,
      21,
      14
    ],
    "vol": 1.35,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01799",
    "sku": "NBJUN24MJ05-Men-Joggers-Eiffel Tower :  : XXXL + NBJUN24MJ05-Men-Joggers-Navy Blue :  : XXXL",
    "awb": "14901319400",
    "courier": "Ekart",
    "date": "2026-06-19",
    "read": 2.92,
    "charged": 3,
    "dims": [
      22,
      18,
      14
    ],
    "vol": 1.11,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01801",
    "sku": "Travel Polo-Men-Polos-Niagara-L + Travel Polo-Men-Polos-Wine Red-L",
    "awb": "14901320866",
    "courier": "DTDC",
    "date": "2026-05-11",
    "read": 0.83,
    "charged": 1,
    "dims": [
      28,
      29,
      14
    ],
    "vol": 2.27,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01803",
    "sku": "Travel Polo-Men-Polos-Niagara-L + Travel Polo-Men-Polos-Wine Red-L",
    "awb": "14901322332",
    "courier": "Xpressbees",
    "date": "2026-07-04",
    "read": 0.54,
    "charged": 0.75,
    "dims": [
      33,
      24,
      16
    ],
    "vol": 2.53,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01805",
    "sku": "Travel Polo-Men-Polos-Niagara-L + Travel Polo-Men-Polos-Wine Red-L",
    "awb": "14901323798",
    "courier": "Bluedart",
    "date": "2026-05-16",
    "read": 0.67,
    "charged": 0.75,
    "dims": [
      21,
      25,
      17
    ],
    "vol": 1.79,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-01807",
    "sku": "Travel Polo-Men-Polos-Cedar-L + Travel Polo-Men-Polos-Powder Pink-L",
    "awb": "14901325264",
    "courier": "Shadowfax",
    "date": "2026-08-01",
    "read": 1,
    "dims": [
      32,
      21,
      9
    ],
    "vol": 1.21,
    "source": "upload",
    "video": {
      "name": "evidence-1808-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1808-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1808-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01809",
    "sku": "Travel Polo-Men-Polos-Cedar-L + Travel Polo-Men-Polos-Powder Pink-L",
    "awb": "14901326730",
    "courier": "Xpressbees",
    "date": "2026-08-05",
    "read": 1,
    "dims": [
      29,
      28,
      14
    ],
    "vol": 2.27,
    "source": "upload",
    "video": {
      "name": "evidence-1810-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1810-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1810-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1810-hgt.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01811",
    "sku": "Travel Polo-Men-Polos-Cedar-L + Travel Polo-Men-Polos-Powder Pink-L",
    "awb": "14901328196",
    "courier": "Shadowfax",
    "date": "2026-08-04",
    "read": 1,
    "dims": [
      36,
      21,
      7
    ],
    "vol": 1.06,
    "source": "upload",
    "video": {
      "name": "evidence-1812-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1812-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1812-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1812-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-1812-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01813",
    "sku": "Travel Polo-Men-Polos-Cedar-L + Travel Polo-Men-Polos-Powder Pink-L",
    "awb": "14901329662",
    "courier": "Ekart",
    "date": "2026-08-05",
    "read": 1,
    "dims": [
      38,
      25,
      13
    ],
    "vol": 2.47,
    "source": "upload",
    "video": {
      "name": "evidence-1814-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1814-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1814-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1814-hgt.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01815",
    "sku": "Travel Polo-Men-Polos-Cedar-L + Travel Polo-Men-Polos-Powder Pink-L",
    "awb": "14901331128",
    "courier": "DTDC",
    "date": "2026-08-06",
    "read": 1,
    "dims": [
      30,
      19,
      15
    ],
    "vol": 1.71,
    "source": "upload",
    "video": {
      "name": "evidence-1816-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1816-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1816-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01817",
    "sku": "Travel Polo-Men-Polos-Cedar-L + Travel Polo-Men-Polos-Powder Pink-L",
    "awb": "14901332594",
    "courier": "Blitz",
    "date": "2026-07-30",
    "read": 1,
    "dims": [
      34,
      27,
      18
    ],
    "vol": 3.3,
    "source": "upload",
    "video": {
      "name": "evidence-1818-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1818-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1818-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1818-hgt.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-01819",
    "sku": "Travel Polo-Men-Polos-Cedar-L + Travel Polo-Men-Polos-Powder Pink-L",
    "awb": "14901334060",
    "courier": "Ekart",
    "date": "2026-07-26",
    "read": 0.94,
    "charged": 1,
    "dims": [
      33,
      17,
      10
    ],
    "vol": 1.12,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01821",
    "sku": "Travel Polo-Men-Polos-Cedar-L + Travel Polo-Men-Polos-Powder Pink-L",
    "awb": "14901335526",
    "courier": "DTDC",
    "date": "2026-06-27",
    "read": 0.89,
    "charged": 1,
    "dims": [
      20,
      20,
      12
    ],
    "vol": 0.96,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01823",
    "sku": "Classic Polo-Men-Polos-Cobalt Blue-L + Classic Polo-Men-Polos-Coral-L-L",
    "awb": "14901336992",
    "courier": "Ekart",
    "date": "2026-07-20",
    "read": 0.57,
    "charged": 0.75,
    "dims": [
      33,
      21,
      8
    ],
    "vol": 1.11,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01825",
    "sku": "Classic Polo-Men-Polos-Cobalt Blue-L + Classic Polo-Men-Polos-Coral-L-L",
    "awb": "14901338458",
    "courier": "Ekart",
    "date": "2026-07-27",
    "read": 0.82,
    "charged": 1,
    "dims": [
      27,
      19,
      9
    ],
    "vol": 0.92,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01827",
    "sku": "Classic Polo-Men-Polos-Cobalt Blue-L + Classic Polo-Men-Polos-Coral-L-L",
    "awb": "14901339924",
    "courier": "Xpressbees",
    "date": "2026-05-06",
    "read": 0.79,
    "charged": 1,
    "dims": [
      36,
      23,
      16
    ],
    "vol": 2.65,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01829",
    "sku": "Ocean Dreams-Men-Relaxed Fit Hoodie-Navy Blue-XL + Over sized Sweatshirt-Sweatshirt-Pink Clay-XL",
    "awb": "14901341390",
    "courier": "Ekart",
    "date": "2026-05-18",
    "read": 0.65,
    "charged": 0.75,
    "dims": [
      25,
      28,
      14
    ],
    "vol": 1.96,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01831",
    "sku": "Ocean Dreams-Men-Relaxed Fit Hoodie-Navy Blue-XL + Over sized Sweatshirt-Sweatshirt-Pink Clay-XL",
    "awb": "14901342856",
    "courier": "DTDC",
    "date": "2026-06-04",
    "read": 0.69,
    "charged": 0.75,
    "dims": [
      31,
      25,
      8
    ],
    "vol": 1.24,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01833",
    "sku": "Ocean Dreams-Men-Relaxed Fit Hoodie-Navy Blue-XL + Over sized Sweatshirt-Sweatshirt-Pink Clay-XL",
    "awb": "14901344322",
    "courier": "Delhivery",
    "date": "2026-07-08",
    "read": 0.54,
    "charged": 0.75,
    "dims": [
      33,
      23,
      15
    ],
    "vol": 2.28,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01835",
    "sku": "Ocean Dreams-Men-Relaxed Fit Hoodie-Navy Blue-XL + Over sized Sweatshirt-Sweatshirt-Pink Clay-XL",
    "awb": "14901345788",
    "courier": "Bluedart",
    "date": "2026-06-16",
    "read": 0.61,
    "charged": 0.75,
    "dims": [
      28,
      26,
      9
    ],
    "vol": 1.31,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01837",
    "sku": "Ocean Dreams-Men-Relaxed Fit Hoodie-Navy Blue-XL + Over sized Sweatshirt-Sweatshirt-Pink Clay-XL",
    "awb": "14901347254",
    "courier": "Bluedart",
    "date": "2026-05-04",
    "read": 0.56,
    "charged": 0.75,
    "dims": [
      26,
      16,
      17
    ],
    "vol": 1.41,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01839",
    "sku": "Ocean Dreams-Men-Relaxed Fit Hoodie-Navy Blue-XL + Over sized Sweatshirt-Sweatshirt-Pink Clay-XL",
    "awb": "14901348720",
    "courier": "Shadowfax",
    "date": "2026-06-06",
    "read": 0.68,
    "charged": 0.75,
    "dims": [
      27,
      20,
      12
    ],
    "vol": 1.3,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01841",
    "sku": "PLAIN ZIPPER HOODIE-Men-Zipper Hoodie-Bone White-L + PLAIN ZIPPER HOODIE-Men-Zipper Hoodie-Chocolate Fondant-L",
    "awb": "14901350186",
    "courier": "Bluedart",
    "date": "2026-07-25",
    "read": 0.69,
    "charged": 0.75,
    "dims": [
      21,
      19,
      15
    ],
    "vol": 1.2,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01843",
    "sku": "PLAIN ZIPPER HOODIE-Men-Zipper Hoodie-Bone White-L + PLAIN ZIPPER HOODIE-Men-Zipper Hoodie-Chocolate Fondant-L",
    "awb": "14901351652",
    "courier": "Delhivery",
    "date": "2026-07-24",
    "read": 0.71,
    "charged": 0.75,
    "dims": [
      37,
      16,
      7
    ],
    "vol": 0.83,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01845",
    "sku": "PLAIN ZIPPER HOODIE-Men-Zipper Hoodie-Bone White-L + PLAIN ZIPPER HOODIE-Men-Zipper Hoodie-Chocolate Fondant-L",
    "awb": "14901353118",
    "courier": "Delhivery",
    "date": "2026-06-28",
    "read": 0.59,
    "charged": 0.75,
    "dims": [
      23,
      21,
      9
    ],
    "vol": 0.87,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01847",
    "sku": "PLAIN ZIPPER HOODIE-Men-Zipper Hoodie-Bone White-L + PLAIN ZIPPER HOODIE-Men-Zipper Hoodie-Chocolate Fondant-L",
    "awb": "14901354584",
    "courier": "Bluedart",
    "date": "2026-05-01",
    "read": 0.71,
    "charged": 0.75,
    "dims": [
      22,
      15,
      17
    ],
    "vol": 1.12,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01849",
    "sku": "PLAIN ZIPPER HOODIE-Men-Zipper Hoodie-Bone White-L + PLAIN ZIPPER HOODIE-Men-Zipper Hoodie-Chocolate Fondant-L",
    "awb": "14901356050",
    "courier": "DTDC",
    "date": "2026-07-08",
    "read": 0.66,
    "charged": 0.75,
    "dims": [
      24,
      25,
      15
    ],
    "vol": 1.8,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-01851",
    "sku": "Nobero Jogger V2-Men-Joggers-Eiffel tower-M + Nobero Jogger V2-Men-Joggers-Macchiato-M",
    "awb": "14901357516",
    "courier": "Bluedart",
    "date": "2026-08-06",
    "read": 0.75,
    "dims": [
      38,
      16,
      16
    ],
    "vol": 1.95,
    "source": "upload",
    "video": {
      "name": "evidence-1852-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1852-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1852-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01853",
    "sku": "Nobero Jogger V2-Men-Joggers-Eiffel tower-M + Nobero Jogger V2-Men-Joggers-Macchiato-M",
    "awb": "14901358982",
    "courier": "Blitz",
    "date": "2026-08-05",
    "read": 0.75,
    "dims": [
      32,
      20,
      6
    ],
    "vol": 0.77,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-1854-len.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01855",
    "sku": "Nobero Jogger V2-Men-Joggers-Eiffel tower-M + Nobero Jogger V2-Men-Joggers-Macchiato-M",
    "awb": "14901360448",
    "courier": "Shadowfax",
    "date": "2026-08-04",
    "read": 0.75,
    "dims": [
      33,
      16,
      10
    ],
    "vol": 1.06,
    "source": "upload",
    "video": {
      "name": "evidence-1856-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1856-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1856-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1856-hgt.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01857",
    "sku": "Nobero Jogger V2-Men-Joggers-Eiffel tower-M + Nobero Jogger V2-Men-Joggers-Macchiato-M",
    "awb": "14901361914",
    "courier": "Ekart",
    "date": "2026-08-04",
    "read": 0.75,
    "dims": [
      37,
      25,
      19
    ],
    "vol": 3.52,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-1858-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1858-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01859",
    "sku": "Nobero Jogger V2-Men-Joggers-Eiffel tower-M + Nobero Jogger V2-Men-Joggers-Macchiato-M",
    "awb": "14901363380",
    "courier": "Blitz",
    "date": "2026-08-03",
    "read": 0.75,
    "dims": [
      28,
      29,
      7
    ],
    "vol": 1.14,
    "source": "upload",
    "video": {
      "name": "evidence-1860-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1860-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1860-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1860-hgt.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01861",
    "sku": "Nobero Jogger V2-Men-Joggers-Eiffel tower-M + Nobero Jogger V2-Men-Joggers-Macchiato-M",
    "awb": "14901364846",
    "courier": "Ekart",
    "date": "2026-08-06",
    "read": 0.75,
    "dims": [
      33,
      16,
      12
    ],
    "vol": 1.27,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-1862-len.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-01863",
    "sku": "Nobero Jogger V2-Men-Joggers-Eiffel tower-M + Nobero Jogger V2-Men-Joggers-Macchiato-M",
    "awb": "14901366312",
    "courier": "Delhivery",
    "date": "2026-06-30",
    "read": 0.57,
    "charged": 0.75,
    "dims": [
      26,
      21,
      12
    ],
    "vol": 1.31,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01865",
    "sku": "Nobero Jogger V2-Men-Joggers-Eiffel tower-M + Nobero Jogger V2-Men-Joggers-Macchiato-M",
    "awb": "14901367778",
    "courier": "Delhivery",
    "date": "2026-07-15",
    "read": 0.67,
    "charged": 0.75,
    "dims": [
      20,
      24,
      18
    ],
    "vol": 1.73,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01867",
    "sku": "Nobero Jogger V2-Men-Joggers-Eiffel tower-M + Nobero Jogger V2-Men-Joggers-Macchiato-M",
    "awb": "14901369244",
    "courier": "Blitz",
    "date": "2026-07-21",
    "read": 0.54,
    "charged": 0.75,
    "dims": [
      30,
      27,
      16
    ],
    "vol": 2.59,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01869",
    "sku": "Nobero Jogger V2-Men-Joggers-Eiffel tower-M + Nobero Jogger V2-Men-Joggers-Macchiato-M",
    "awb": "14901370710",
    "courier": "Delhivery",
    "date": "2026-06-21",
    "read": 0.71,
    "charged": 0.75,
    "dims": [
      36,
      27,
      6
    ],
    "vol": 1.17,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-01871",
    "sku": "PLAIN ZIPPER HOODIE-Men-Zipper Hoodie-Bone White-L + PLAIN ZIPPER HOODIE-Men-Zipper Hoodie-Graphite-L",
    "awb": "14901372176",
    "courier": "Bluedart",
    "date": "2026-08-02",
    "read": 0.75,
    "dims": [
      27,
      19,
      17
    ],
    "vol": 1.74,
    "source": "upload",
    "video": {
      "name": "evidence-1872-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1872-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1872-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1872-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-1872-scale.jpg",
        "url": null
      },
      {
        "slot": "label",
        "name": "evidence-1872-label.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01873",
    "sku": "Classic Polo-Men-Polos-Burgundy-L + Classic Polo-Men-Polos-Petunia-L",
    "awb": "14901373642",
    "courier": "Ekart",
    "date": "2026-07-30",
    "read": 1,
    "dims": [
      28,
      18,
      8
    ],
    "vol": 0.81,
    "source": "upload",
    "video": {
      "name": "evidence-1874-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1874-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1874-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-01875",
    "sku": "Classic Polo-Men-Polos-Burgundy-L + Classic Polo-Men-Polos-Petunia-L",
    "awb": "14901375108",
    "courier": "DTDC",
    "date": "2026-05-04",
    "read": 0.79,
    "charged": 1,
    "dims": [
      27,
      21,
      10
    ],
    "vol": 1.13,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01877",
    "sku": "Classic Polo-Men-Polos-Burgundy-L + Classic Polo-Men-Polos-Petunia-L",
    "awb": "14901376574",
    "courier": "Xpressbees",
    "date": "2026-05-22",
    "read": 0.63,
    "charged": 0.75,
    "dims": [
      38,
      29,
      17
    ],
    "vol": 3.75,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-01879",
    "sku": "Nobero Jogger V2-Men Joggers-Dark Oak-XL + Nobero Jogger V2-Men Joggers-Navy Blue-XL",
    "awb": "14901378040",
    "courier": "Delhivery",
    "date": "2026-07-31",
    "read": 0.75,
    "dims": [
      31,
      24,
      7
    ],
    "vol": 1.04,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-1880-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1880-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01881",
    "sku": "Nobero Jogger V2-Men Joggers-Dark Oak-XL + Nobero Jogger V2-Men Joggers-Navy Blue-XL",
    "awb": "14901379506",
    "courier": "Blitz",
    "date": "2026-08-03",
    "read": 0.75,
    "dims": [
      23,
      27,
      13
    ],
    "vol": 1.61,
    "source": "upload",
    "video": {
      "name": "evidence-1882-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1882-len.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-01883",
    "sku": "Nobero Jogger V2-Men Joggers-Dark Oak-XL + Nobero Jogger V2-Men Joggers-Navy Blue-XL",
    "awb": "14901380972",
    "courier": "Bluedart",
    "date": "2026-05-27",
    "read": 1.3,
    "charged": 1.5,
    "dims": [
      20,
      27,
      7
    ],
    "vol": 0.76,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01885",
    "sku": "Nobero Jogger V2-Men Joggers-Dark Oak-XL + Nobero Jogger V2-Men Joggers-Navy Blue-XL",
    "awb": "14901382438",
    "courier": "Xpressbees",
    "date": "2026-07-15",
    "read": 1.42,
    "charged": 1.5,
    "dims": [
      33,
      22,
      12
    ],
    "vol": 1.74,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-01887",
    "sku": "Classic Polo-Men-Polos-Burgundy-L + Classic Polo-Men-Polos-Pecan-L",
    "awb": "14901383904",
    "courier": "Delhivery",
    "date": "2026-08-06",
    "read": 0.75,
    "dims": [
      22,
      15,
      7
    ],
    "vol": 0.46,
    "source": "upload",
    "video": {
      "name": "evidence-1888-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1888-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1888-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1888-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-1888-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01889",
    "sku": "Classic Polo-Men-Polos-Burgundy-L + Classic Polo-Men-Polos-Pecan-L",
    "awb": "14901385370",
    "courier": "Blitz",
    "date": "2026-08-02",
    "read": 0.75,
    "dims": [
      31,
      29,
      18
    ],
    "vol": 3.24,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-1890-len.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-01891",
    "sku": "Classic Polo-Men-Polos-Burgundy-L + Classic Polo-Men-Polos-Pecan-L",
    "awb": "14901386836",
    "courier": "Delhivery",
    "date": "2026-05-23",
    "read": 0.57,
    "charged": 0.75,
    "dims": [
      21,
      21,
      5
    ],
    "vol": 0.44,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01893",
    "sku": "Classic Polo-Men-Polos-Burgundy-L + Classic Polo-Men-Polos-Pecan-L",
    "awb": "14901388302",
    "courier": "Xpressbees",
    "date": "2026-07-28",
    "read": 0.6,
    "charged": 0.75,
    "dims": [
      27,
      20,
      5
    ],
    "vol": 0.54,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01895",
    "sku": "Classic Polo-Men-Polos-Burgundy-L + Classic Polo-Men-Polos-Pecan-L",
    "awb": "14901389768",
    "courier": "DTDC",
    "date": "2026-07-21",
    "read": 0.53,
    "charged": 0.75,
    "dims": [
      22,
      15,
      11
    ],
    "vol": 0.73,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01897",
    "sku": "Classic Polo-Men-Polos-Burgundy-L + Classic Polo-Men-Polos-Pecan-L",
    "awb": "14901391234",
    "courier": "Shadowfax",
    "date": "2026-05-03",
    "read": 0.72,
    "charged": 0.75,
    "dims": [
      28,
      28,
      9
    ],
    "vol": 1.41,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01899",
    "sku": "Classic Polo-Men-Polos-Burgundy-L + Classic Polo-Men-Polos-Pecan-L",
    "awb": "14901392700",
    "courier": "Delhivery",
    "date": "2026-06-12",
    "read": 0.55,
    "charged": 0.75,
    "dims": [
      25,
      16,
      10
    ],
    "vol": 0.8,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01901",
    "sku": "Classic Polo-Men-Polos-Burgundy-L + Classic Polo-Men-Polos-Pecan-L",
    "awb": "14901394166",
    "courier": "Ekart",
    "date": "2026-05-09",
    "read": 0.65,
    "charged": 0.75,
    "dims": [
      23,
      29,
      19
    ],
    "vol": 2.53,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-01903",
    "sku": "NBJUN24MJ05-Men-Joggers-Black-XXL + NBJUN24MJ05-Men-Joggers-Eiffel Tower-XXL",
    "awb": "14901395632",
    "courier": "Xpressbees",
    "date": "2026-07-30",
    "read": 0.75,
    "dims": [
      30,
      16,
      12
    ],
    "vol": 1.15,
    "source": "upload",
    "video": {
      "name": "evidence-1904-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1904-len.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-01905",
    "sku": "NBJUN24MJ05-Men-Joggers-Black-XXL + NBJUN24MJ05-Men-Joggers-Eiffel Tower-XXL",
    "awb": "14901397098",
    "courier": "DTDC",
    "date": "2026-06-24",
    "read": 0.61,
    "charged": 0.75,
    "dims": [
      28,
      21,
      14
    ],
    "vol": 1.65,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01907",
    "sku": "NBJUN24MJ05-Men-Joggers-Black-XXL + NBJUN24MJ05-Men-Joggers-Eiffel Tower-XXL",
    "awb": "14901398564",
    "courier": "Xpressbees",
    "date": "2026-07-20",
    "read": 0.58,
    "charged": 0.75,
    "dims": [
      28,
      15,
      13
    ],
    "vol": 1.09,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01909",
    "sku": "Classic Polo-Men-Polos-Aqua Blue-L + Classic Polo-Men-Polos-Coral-L",
    "awb": "14901400030",
    "courier": "DTDC",
    "date": "2026-07-16",
    "read": 0.72,
    "charged": 0.75,
    "dims": [
      36,
      28,
      14
    ],
    "vol": 2.82,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01911",
    "sku": "Classic Polo-Men-Polos-Aqua Blue-L + Classic Polo-Men-Polos-Coral-L",
    "awb": "14901401496",
    "courier": "Bluedart",
    "date": "2026-06-26",
    "read": 0.57,
    "charged": 0.75,
    "dims": [
      35,
      25,
      18
    ],
    "vol": 3.15,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01913",
    "sku": "Classic Polo-Men-Polos-Aqua Blue-L + Classic Polo-Men-Polos-Coral-L",
    "awb": "14901402962",
    "courier": "DTDC",
    "date": "2026-07-14",
    "read": 0.97,
    "charged": 1,
    "dims": [
      36,
      17,
      5
    ],
    "vol": 0.61,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01915",
    "sku": "Classic Polo-Men-Polos-Aqua Blue-L + Classic Polo-Men-Polos-Coral-L",
    "awb": "14901404428",
    "courier": "Shadowfax",
    "date": "2026-07-08",
    "read": 0.98,
    "charged": 1,
    "dims": [
      34,
      25,
      9
    ],
    "vol": 1.53,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01917",
    "sku": "Classic Polo-Men-Polos-Aqua Blue-L + Classic Polo-Men-Polos-Coral-L",
    "awb": "14901405894",
    "courier": "DTDC",
    "date": "2026-06-13",
    "read": 0.61,
    "charged": 0.75,
    "dims": [
      28,
      26,
      6
    ],
    "vol": 0.87,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01919",
    "sku": "Classic Polo-Men-Polos-Aqua Blue-L + Classic Polo-Men-Polos-Coral-L",
    "awb": "14901407360",
    "courier": "Shadowfax",
    "date": "2026-05-03",
    "read": 0.73,
    "charged": 0.75,
    "dims": [
      31,
      18,
      16
    ],
    "vol": 1.79,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-01921",
    "sku": "Classic Polo-Men-Polos-Pecan-XL + Classic Polo-Men-Polos-Powder Blue-XL",
    "awb": "14901408826",
    "courier": "DTDC",
    "date": "2026-08-04",
    "read": 0.75,
    "dims": [
      23,
      25,
      15
    ],
    "vol": 1.73,
    "source": "upload",
    "video": {
      "name": "evidence-1922-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1922-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1922-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01923",
    "sku": "Classic Polo-Men-Polos-Pecan-XL + Classic Polo-Men-Polos-Powder Blue-XL",
    "awb": "14901410292",
    "courier": "Blitz",
    "date": "2026-08-03",
    "read": 0.75,
    "dims": [
      35,
      22,
      10
    ],
    "vol": 1.54,
    "source": "upload",
    "video": {
      "name": "evidence-1924-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1924-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1924-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01925",
    "sku": "Classic Polo-Men-Polos-Pecan-XL + Classic Polo-Men-Polos-Powder Blue-XL",
    "awb": "14901411758",
    "courier": "Xpressbees",
    "date": "2026-08-06",
    "read": 1,
    "dims": [
      36,
      27,
      5
    ],
    "vol": 0.97,
    "source": "upload",
    "video": {
      "name": "evidence-1926-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1926-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1926-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1926-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-1926-scale.jpg",
        "url": null
      },
      {
        "slot": "label",
        "name": "evidence-1926-label.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01927",
    "sku": "Classic Polo-Men-Polos-Pecan-XL + Classic Polo-Men-Polos-Powder Blue-XL",
    "awb": "14901413224",
    "courier": "Shadowfax",
    "date": "2026-07-30",
    "read": 1,
    "dims": [
      21,
      27,
      19
    ],
    "vol": 2.15,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-1928-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1928-wid.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01929",
    "sku": "Classic Polo-Men-Polos-Pecan-XL + Classic Polo-Men-Polos-Powder Blue-XL",
    "awb": "14901414690",
    "courier": "Bluedart",
    "date": "2026-08-06",
    "read": 1,
    "dims": [
      24,
      28,
      9
    ],
    "vol": 1.21,
    "source": "upload",
    "video": null,
    "images": [
      {
        "slot": "len",
        "name": "evidence-1930-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1930-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1930-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-1930-scale.jpg",
        "url": null
      },
      {
        "slot": "label",
        "name": "evidence-1930-label.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-01931",
    "sku": "Classic Polo-Men-Polos-Pecan-XL + Classic Polo-Men-Polos-Powder Blue-XL",
    "awb": "14901416156",
    "courier": "Delhivery",
    "date": "2026-06-11",
    "read": 0.64,
    "charged": 0.75,
    "dims": [
      24,
      23,
      14
    ],
    "vol": 1.55,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01933",
    "sku": "Classic Polo-Men-Polos-Pecan-XL + Classic Polo-Men-Polos-Powder Blue-XL",
    "awb": "14901417622",
    "courier": "Xpressbees",
    "date": "2026-05-22",
    "read": 0.86,
    "charged": 1,
    "dims": [
      24,
      20,
      10
    ],
    "vol": 0.96,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-01935",
    "sku": "EBM-33-Jogger-Eiffel tower-L + EBM-33-Men-Joggers-Black-L",
    "awb": "14901419088",
    "courier": "Ekart",
    "date": "2026-08-03",
    "read": 1,
    "dims": [
      26,
      17,
      16
    ],
    "vol": 1.41,
    "source": "upload",
    "video": {
      "name": "evidence-1936-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1936-len.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01937",
    "sku": "EBM-33-Jogger-Eiffel tower-L + EBM-33-Men-Joggers-Black-L",
    "awb": "14901420554",
    "courier": "Blitz",
    "date": "2026-08-05",
    "read": 1,
    "dims": [
      32,
      17,
      16
    ],
    "vol": 1.74,
    "source": "upload",
    "video": {
      "name": "evidence-1938-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1938-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1938-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1938-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-1938-scale.jpg",
        "url": null
      },
      {
        "slot": "label",
        "name": "evidence-1938-label.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-01939",
    "sku": "ELEGANT HOODIE-Hoodie-Powder Blue-M + ELEGANT HOODIE-Men-Hoodie-Midnight Plum-M",
    "awb": "14901422020",
    "courier": "Bluedart",
    "date": "2026-05-30",
    "read": 0.63,
    "charged": 0.75,
    "dims": [
      24,
      20,
      13
    ],
    "vol": 1.25,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01941",
    "sku": "ELEGANT HOODIE-Hoodie-Powder Blue-M + ELEGANT HOODIE-Men-Hoodie-Midnight Plum-M",
    "awb": "14901423486",
    "courier": "Bluedart",
    "date": "2026-07-28",
    "read": 0.54,
    "charged": 0.75,
    "dims": [
      36,
      24,
      12
    ],
    "vol": 2.07,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01943",
    "sku": "ELEGANT HOODIE-Hoodie-Powder Blue-M + ELEGANT HOODIE-Men-Hoodie-Midnight Plum-M",
    "awb": "14901424952",
    "courier": "Blitz",
    "date": "2026-05-21",
    "read": 0.73,
    "charged": 0.75,
    "dims": [
      20,
      20,
      11
    ],
    "vol": 0.88,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01945",
    "sku": "ELEGANT HOODIE-Hoodie-Powder Blue-M + ELEGANT HOODIE-Men-Hoodie-Midnight Plum-M",
    "awb": "14901426418",
    "courier": "Shadowfax",
    "date": "2026-05-04",
    "read": 0.54,
    "charged": 0.75,
    "dims": [
      39,
      26,
      19
    ],
    "vol": 3.85,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-01947",
    "sku": "NBJUN24MJ05-Men-Joggers-Eiffel Tower-XL + NBJUN24MJ05-Men-Joggers-Navy Blue-XL",
    "awb": "14901427884",
    "courier": "DTDC",
    "date": "2026-08-04",
    "read": 1.5,
    "dims": [
      32,
      20,
      19
    ],
    "vol": 2.43,
    "source": "upload",
    "video": {
      "name": "evidence-1948-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1948-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1948-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1948-hgt.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01949",
    "sku": "NBJUN24MJ05-Men-Joggers-Eiffel Tower-XL + NBJUN24MJ05-Men-Joggers-Navy Blue-XL",
    "awb": "14901429350",
    "courier": "DTDC",
    "date": "2026-08-05",
    "read": 1.5,
    "dims": [
      23,
      26,
      10
    ],
    "vol": 1.2,
    "source": "upload",
    "video": {
      "name": "evidence-1950-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1950-len.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-01951",
    "sku": "NBJUN24MJ05-Men-Joggers-Eiffel Tower-XL + NBJUN24MJ05-Men-Joggers-Navy Blue-XL",
    "awb": "14901430816",
    "courier": "Xpressbees",
    "date": "2026-07-26",
    "read": 0.57,
    "charged": 0.75,
    "dims": [
      33,
      19,
      16
    ],
    "vol": 2.01,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01953",
    "sku": "NBJUN24MJ05-Men-Joggers-Eiffel Tower-XL + NBJUN24MJ05-Men-Joggers-Navy Blue-XL",
    "awb": "14901432282",
    "courier": "Bluedart",
    "date": "2026-05-25",
    "read": 1.36,
    "charged": 1.5,
    "dims": [
      25,
      16,
      14
    ],
    "vol": 1.12,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01955",
    "sku": "NBJUN24MJ05-Men-Joggers-Eiffel Tower-XL + NBJUN24MJ05-Men-Joggers-Navy Blue-XL",
    "awb": "14901433748",
    "courier": "Xpressbees",
    "date": "2026-06-12",
    "read": 0.71,
    "charged": 0.75,
    "dims": [
      34,
      18,
      9
    ],
    "vol": 1.1,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-UP-01957",
    "sku": "Classic Polo-Men-Polos-Amber Yellow-L + Classic Polo-Men-Polos-Burgundy-L",
    "awb": "14901435214",
    "courier": "Bluedart",
    "date": "2026-08-04",
    "read": 0.75,
    "dims": [
      34,
      17,
      16
    ],
    "vol": 1.85,
    "source": "upload",
    "video": {
      "name": "evidence-1958-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1958-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1958-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1958-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-1958-scale.jpg",
        "url": null
      },
      {
        "slot": "label",
        "name": "evidence-1958-label.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01959",
    "sku": "Classic Polo-Men-Polos-Amber Yellow-L + Classic Polo-Men-Polos-Burgundy-L",
    "awb": "14901436680",
    "courier": "Xpressbees",
    "date": "2026-07-31",
    "read": 0.75,
    "dims": [
      35,
      25,
      6
    ],
    "vol": 1.05,
    "source": "upload",
    "video": {
      "name": "evidence-1960-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1960-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1960-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1960-hgt.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01961",
    "sku": "Classic Polo-Men-Polos-Amber Yellow-L + Classic Polo-Men-Polos-Burgundy-L",
    "awb": "14901438146",
    "courier": "DTDC",
    "date": "2026-07-30",
    "read": 0.75,
    "dims": [
      31,
      15,
      17
    ],
    "vol": 1.58,
    "source": "upload",
    "video": {
      "name": "evidence-1962-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1962-len.jpg",
        "url": null
      },
      {
        "slot": "wid",
        "name": "evidence-1962-wid.jpg",
        "url": null
      },
      {
        "slot": "hgt",
        "name": "evidence-1962-hgt.jpg",
        "url": null
      },
      {
        "slot": "scale",
        "name": "evidence-1962-scale.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-UP-01963",
    "sku": "Classic Polo-Men-Polos-Amber Yellow-L + Classic Polo-Men-Polos-Burgundy-L",
    "awb": "14901439612",
    "courier": "DTDC",
    "date": "2026-08-02",
    "read": 0.75,
    "dims": [
      36,
      25,
      11
    ],
    "vol": 1.98,
    "source": "upload",
    "video": {
      "name": "evidence-1964-video.mp4",
      "url": null
    },
    "images": [
      {
        "slot": "len",
        "name": "evidence-1964-len.jpg",
        "url": null
      }
    ],
    "note": "Submitted by merchant."
  },
  {
    "id": "DWS-CO-01965",
    "sku": "Classic Polo-Men-Polos-Amber Yellow-L + Classic Polo-Men-Polos-Burgundy-L",
    "awb": "14901441078",
    "courier": "Ekart",
    "date": "2026-06-23",
    "read": 0.58,
    "charged": 0.75,
    "dims": [
      36,
      16,
      16
    ],
    "vol": 1.84,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  },
  {
    "id": "DWS-CO-01967",
    "sku": "Classic Polo-Men-Polos-Amber Yellow-L + Classic Polo-Men-Polos-Burgundy-L",
    "awb": "14901442544",
    "courier": "Bluedart",
    "date": "2026-07-24",
    "read": 0.71,
    "charged": 0.75,
    "dims": [
      39,
      27,
      9
    ],
    "vol": 1.9,
    "source": "courier",
    "video": null,
    "images": [],
    "note": null
  }
]

export const REQUESTS = [
  {
    "id": "WF-NB-2601",
    "level": "SKU",
    "subject": "1M-BWJG-R0149-BL5",
    "deadKg": 1,
    "volKg": 0.62,
    "chargeKg": 1,
    "packId": "FLY-L",
    "evidence": 1,
    "evidenceAwbs": [
      "14900972691",
      "14900973424",
      "14900974157"
    ],
    "covers": [
      "1M-BWJG-R0149-BL5"
    ],
    "remarks": "Requested via Initiate — evidence attached where available.",
    "raisedBy": "Prozo Ops",
    "raisedOn": "2026-01-19",
    "history": [
      {
        "date": "2026-01-19",
        "actor": "Prozo Ops",
        "round": 1,
        "kind": "RAISED",
        "text": "Freeze request raised."
      },
      {
        "date": "2026-01-24",
        "actor": "R. Menon",
        "round": 1,
        "kind": "APPROVED",
        "text": "Approved by R. Menon."
      }
    ],
    "kind": "approved",
    "approvedBy": "R. Menon",
    "effectiveFrom": "2026-01-24",
    "cyclesElapsed": 1
  },
  {
    "id": "WF-NB-2602",
    "level": "Sub-category",
    "subject": "Hoodies",
    "deadKg": 1,
    "volKg": 0.58,
    "chargeKg": 1,
    "packId": "FLY-L",
    "evidence": 6,
    "evidenceAwbs": [
      "14900974890",
      "14900975623",
      "14900976356"
    ],
    "covers": [
      "1M-TWZP-R0002-BL3",
      "1M-TWZP-R0002-BL4",
      "1M-TWZP-R0002-BL5"
    ],
    "remarks": "Requested via Initiate — evidence attached where available.",
    "raisedBy": "Prozo Ops",
    "raisedOn": "2025-09-01",
    "history": [
      {
        "date": "2025-09-01",
        "actor": "Prozo Ops",
        "round": 1,
        "kind": "RAISED",
        "text": "Freeze request raised."
      },
      {
        "date": "2025-09-06",
        "actor": "A. Bhatia",
        "round": 1,
        "kind": "APPROVED",
        "text": "Approved by A. Bhatia."
      }
    ],
    "kind": "approved",
    "approvedBy": "A. Bhatia",
    "effectiveFrom": "2025-09-06",
    "cyclesElapsed": 4
  },
  {
    "id": "WF-NB-2603",
    "level": "Combo",
    "subject": "Laugh + Live + Love & Eat Bamboo Oversized T-Shirt Bone White / L (sku - 1M-TWRT-C2581-BWH4)",
    "deadKg": 0.54,
    "volKg": 0.75,
    "chargeKg": 0.75,
    "packId": "FLY-GEN",
    "evidence": 0,
    "evidenceAwbs": [
      "14900977089",
      "14900977822",
      "14900978555"
    ],
    "covers": [
      "Laugh + Live + Love & Eat Bamboo Oversized T-Shirt Bone White / L (sku - 1M-TWRT-C2581-BWH4)"
    ],
    "remarks": "Requested via Initiate — evidence attached where available.",
    "raisedBy": "Prozo Ops",
    "raisedOn": "2025-11-20",
    "history": [
      {
        "date": "2025-11-20",
        "actor": "Prozo Ops",
        "round": 1,
        "kind": "RAISED",
        "text": "Freeze request raised."
      },
      {
        "date": "2025-11-25",
        "actor": "S. Kulkarni",
        "round": 1,
        "kind": "APPROVED",
        "text": "Approved by S. Kulkarni."
      }
    ],
    "kind": "approved",
    "approvedBy": "S. Kulkarni",
    "effectiveFrom": "2025-11-25",
    "cyclesElapsed": 2
  },
  {
    "id": "WF-NB-2604",
    "level": "SKU",
    "subject": "NBSS24MC10-Men-Coord Set-Navy Blue-L",
    "deadKg": 0.75,
    "volKg": 0.43,
    "chargeKg": 0.75,
    "packId": "FLY-GEN",
    "evidence": 5,
    "evidenceAwbs": [
      "14900979288",
      "14900980021",
      "14900980754"
    ],
    "covers": [
      "NBSS24MC10-Men-Coord Set-Navy Blue-L"
    ],
    "remarks": "Requested via Initiate — evidence attached where available.",
    "raisedBy": "Prozo Ops",
    "raisedOn": "2026-07-28",
    "history": [
      {
        "date": "2026-07-28",
        "actor": "Prozo Ops",
        "round": 1,
        "kind": "RAISED",
        "text": "Freeze request raised."
      }
    ],
    "kind": "review",
    "state": "Awaiting Prozo review",
    "round": 1,
    "submissions": 1
  },
  {
    "id": "WF-NB-2605",
    "level": "Combo",
    "subject": "Laugh + Live + Love & Eat Bamboo Oversized T-Shirt Bone White / M (sku - 1M-TWRT-C2581-BWH3)",
    "deadKg": 0.75,
    "volKg": 0.3,
    "chargeKg": 0.75,
    "packId": "FLY-GEN",
    "evidence": 0,
    "evidenceAwbs": [
      "14900981487",
      "14900982220",
      "14900982953"
    ],
    "covers": [
      "Laugh + Live + Love & Eat Bamboo Oversized T-Shirt Bone White / M (sku - 1M-TWRT-C2581-BWH3)"
    ],
    "remarks": "Requested via Initiate — evidence attached where available.",
    "raisedBy": "Prozo Ops",
    "raisedOn": "2026-07-18",
    "history": [
      {
        "date": "2026-07-18",
        "actor": "Prozo Ops",
        "round": 1,
        "kind": "RAISED",
        "text": "Freeze request raised."
      },
      {
        "date": "2026-07-21",
        "actor": "Prozo Ops",
        "round": 2,
        "kind": "INFO_REQUESTED",
        "text": "Additional data requested: clearer video evidence and 2 more stills.",
        "requestedItems": [
          "Video evidence",
          "Additional stills"
        ]
      }
    ],
    "kind": "review",
    "state": "Additional data requested",
    "round": 2,
    "submissions": 1
  },
  {
    "id": "WF-NB-2606",
    "level": "SKU",
    "subject": "1M-LWCS-R0174-NS4",
    "deadKg": 1,
    "volKg": 0.44,
    "chargeKg": 1,
    "packId": "FLY-L",
    "evidence": 2,
    "evidenceAwbs": [
      "14900983686",
      "14900984419",
      "14900985152"
    ],
    "covers": [
      "1M-LWCS-R0174-NS4"
    ],
    "remarks": "Requested via Initiate — evidence attached where available.",
    "raisedBy": "Prozo Ops",
    "raisedOn": "2026-07-13",
    "history": [
      {
        "date": "2026-07-13",
        "actor": "Prozo Ops",
        "round": 1,
        "kind": "RAISED",
        "text": "Freeze request raised."
      },
      {
        "date": "2026-07-16",
        "actor": "Prozo Ops",
        "round": 2,
        "kind": "INFO_REQUESTED",
        "text": "Additional data requested: clearer video evidence and 2 more stills.",
        "requestedItems": [
          "Video evidence",
          "Additional stills"
        ]
      },
      {
        "date": "2026-07-18",
        "actor": "Nobero",
        "round": 2,
        "kind": "SUBMITTED",
        "text": "Merchant submitted additional evidence."
      }
    ],
    "kind": "review",
    "state": "Data submitted — awaiting decision",
    "round": 2,
    "submissions": 2
  },
  {
    "id": "WF-NB-2607",
    "level": "SKU",
    "subject": "1M-BWJG-R0149-BL3",
    "deadKg": 1,
    "volKg": 0.63,
    "chargeKg": 1,
    "packId": "FLY-L",
    "evidence": 4,
    "evidenceAwbs": [
      "14900985885",
      "14900986618",
      "14900987351"
    ],
    "covers": [
      "1M-BWJG-R0149-BL3"
    ],
    "remarks": "Requested via Initiate — evidence attached where available.",
    "raisedBy": "Prozo Ops",
    "raisedOn": "2026-05-09",
    "history": [
      {
        "date": "2026-05-09",
        "actor": "Prozo Ops",
        "round": 1,
        "kind": "RAISED",
        "text": "Freeze request raised."
      },
      {
        "date": "2026-05-13",
        "actor": "Prozo Ops",
        "round": 1,
        "kind": "DECLINED",
        "text": "Rejected — Contradictory data available"
      }
    ],
    "kind": "rejected",
    "reason": "Contradictory data available"
  },
  {
    "id": "WF-NB-2608",
    "level": "Sub-category",
    "subject": "Polo T-Shirts",
    "deadKg": 0.75,
    "volKg": 0.48,
    "chargeKg": 0.75,
    "packId": "FLY-GEN",
    "evidence": 0,
    "evidenceAwbs": [
      "14900988084",
      "14900988817",
      "14900989550"
    ],
    "covers": [
      "1M-TWPO-R0002-BU3",
      "1M-TWPO-R0002-BU4",
      "1M-TWPO-R0002-NB4",
      "1M-TWPO-R0002-CY3",
      "1M-TWPO-R0002-CY5"
    ],
    "remarks": "Requested via Initiate — evidence attached where available.",
    "raisedBy": "Prozo Ops",
    "raisedOn": "2026-04-19",
    "history": [
      {
        "date": "2026-04-19",
        "actor": "Prozo Ops",
        "round": 1,
        "kind": "RAISED",
        "text": "Freeze request raised."
      },
      {
        "date": "2026-04-23",
        "actor": "Prozo Ops",
        "round": 1,
        "kind": "DECLINED",
        "text": "Rejected — Not possible due to courier-side constraints"
      }
    ],
    "kind": "rejected",
    "reason": "Not possible due to courier-side constraints"
  },
  {
    "id": "WF-NB-2609",
    "level": "Combo",
    "subject": "Laugh + Live + Love & Eat Bamboo Oversized T-Shirt Bone White / XL (sku - 1M-TWRT-C2581-BWH5)",
    "deadKg": 0.75,
    "volKg": 0.53,
    "chargeKg": 0.75,
    "packId": "FLY-GEN",
    "evidence": 0,
    "evidenceAwbs": [
      "14900990283",
      "14900991016",
      "14900991749"
    ],
    "covers": [
      "Laugh + Live + Love & Eat Bamboo Oversized T-Shirt Bone White / XL (sku - 1M-TWRT-C2581-BWH5)"
    ],
    "remarks": "Requested via Initiate — evidence attached where available.",
    "raisedBy": "Prozo Ops",
    "raisedOn": "2026-03-30",
    "history": [
      {
        "date": "2026-03-30",
        "actor": "Prozo Ops",
        "round": 1,
        "kind": "RAISED",
        "text": "Freeze request raised."
      },
      {
        "date": "2026-04-01",
        "actor": "Prozo Ops",
        "round": 3,
        "kind": "LIMIT_REACHED",
        "text": "Submission limit reached (3 of 3)."
      },
      {
        "date": "2026-04-03",
        "actor": "Prozo Ops",
        "round": 3,
        "kind": "DECLINED",
        "text": "Rejected — Additional data inadequate after 3 submissions"
      }
    ],
    "kind": "rejected",
    "reason": "Additional data inadequate after 3 submissions",
    "submissions": 3
  },
  {
    "id": "WF-NB-2610",
    "level": "Sub-category",
    "subject": "Sweatshirts",
    "deadKg": 1,
    "volKg": 0.44,
    "chargeKg": 1,
    "packId": "FLY-L",
    "evidence": 1,
    "evidenceAwbs": [
      "14900992482",
      "14900993215",
      "14900993948"
    ],
    "covers": [
      "1M-TWSS-R0032-BL3",
      "NBAW24MT02-Men-Sweatshirts-Black-L",
      "1M-TWSS-R0032-NB3"
    ],
    "remarks": "Requested via Initiate — evidence attached where available.",
    "raisedBy": "Prozo Ops",
    "raisedOn": "2026-08-02",
    "history": [
      {
        "date": "2026-08-02",
        "actor": "Prozo Ops",
        "round": 1,
        "kind": "RAISED",
        "text": "Freeze request raised."
      }
    ],
    "kind": "review",
    "state": "Awaiting Prozo review",
    "round": 1,
    "submissions": 0
  }
]

export const MERGE_CANDIDATES = [
  {
    "a": "1M-BWJG-R0084-BL3",
    "b": "1M-BWJG-R0149-PB6",
    "sub": "Joggers",
    "packId": "FLY-GEN",
    "mpw": 0.75,
    "seeded": true
  },
  {
    "a": "1M-BWJG-R0090-AW4",
    "b": "1M-BWCP-R0002-BL3",
    "sub": "Cargo Pants",
    "packId": "FLY-GEN",
    "mpw": 0.75,
    "seeded": true
  },
  {
    "a": "1M-TWPO-R0002-BU6",
    "b": "1M-TWPO-R0002-DR3",
    "sub": "Polo T-Shirts",
    "packId": "FLY-GEN",
    "mpw": 0.75,
    "seeded": true
  }
]
